export type Lang = "en" | "de" | "fr" | "es" | "it" | "pt";

type ServiceItem = {
  title: string;
  description: string;
  tags: string[];
};

type FAQItem = {
  question: string;
  answer: string;
};

type TestimonialItem = {
  quote: string;
  name: string;
  title: string;
  facility: string;
};

type SectorItem = {
  name: string;
  description: string;
};

type TrustItem = {
  text: string;
};

export const translations: Record<Lang, any> = {
  /* ===================== EN ===================== */
  
  en: {

    backToTop: "Back to top",
    explore: "Explore",

    common: {
    back: "Back",
    },

    nav: {
      services: "Services",
      pricing: "Pricing",
      about: "About",
      contact: "Contact",
      cta: "Discovery Call",
      viewDemo: "View Demo",
    },
    hero: {
      badge: "Outcome-Driven Critical Industries",
      title: "Operational complexity erodes",
      titleHighlight: "reliability and breaks execution discipline",
      subtitle:
        "When coordination turns manual, maintenance becomes reactive, and operations rely on workarounds, control is already lost.",
      subtitle2:
        "We restore predictable, disciplined execution inside live operations — without replacing core systems or disrupting daily workflows.",
      subtitleMobile: "When coordination turns manual, control is already lost.",
      cta: "Take Back Control Over Operations",
      simulatorCta: "Demand & Execution Intake",
      
    },
     problem: {
       badge: "The Challenge",
       title: "Operational friction costs more than you realize.",
       description: "Every missed call, booking error, and overloaded staff moment represents lost revenue and diminished experience. Your operations pipeline shouldn't be your bottleneck.",
       pain1: "Missed calls during peak hours",
       pain2: "Double bookings and scheduling conflicts",
       pain3: "Staff overwhelmed with administrative tasks",
       pain4: "Disjointed client interactions",
       pain5: "Revenue lost to operational gaps",
     },
     process: {
       badge: "The Process",
       title: "Seamless integration with your operations.",
       step1: { title: "Capture", description: "Every call, message, and request is handled with precision the moment it arrives." },
       step2: { title: "Coordinate", description: "Real-time alignment with your calendar and workflows ensures seamless scheduling." },
       step3: { title: "Confirm", description: "Automatic confirmations, updates, and escalations when needed—nothing falls through." },
     },
     workflowDemo: {
       badge: "Interactive Demo",
       title: "See Ovelon Prime in action.",
        tabs: { workflow: "Workflow Automation", call: "Call Recovery", appointment: "Incident Scheduling", safety: "Safety Manuals" },
        workflow: {
          description: "Watch how a single inquiry triggers a complete operational workflow—automatically.",
          runDemo: "RUN WORKFLOW DEMO",
          complete: "Complete workflow executed in under 60 seconds",
          reset: "RESET DEMO",
          processing: "Processing workflow...",
          step1: { title: "Request/Incident Received", desc: "All incoming requests or incidents are captured and logged in real time, ensuring full traceability." },
          step2: { title: "Availability Checked", desc: "System verifies inventory, capacity, or schedule to confirm feasibility immediately." },
          step3: { title: "Order / Task Created", desc: "Requests are transformed into actionable orders or tasks within the workflow management system." },
          step4: { title: "Confirmation Sent", desc: "Automated notifications alert all relevant stakeholders via email, SMS, or internal dashboards." },
          step5: { title: "Team Notified", desc: "Operations teams receive instant alerts to begin processing or fulfillment." },
          step6: { title: "Profile & History Updated", desc: "All client, asset, or task information is automatically recorded for future reference and optimization." },
        },
        call: {
          description: "See how Ovelon Prime transforms a potentially missed opportunity into a confirmed deal or incident request.",
          simulate: "SIMULATE INCOMING CALL",
          stage1: "Incoming call...",
          stage2: "Without Ovelon Prime: Call missed, no deal",
          stage3: "With Ovelon Prime: Call answered instantly",
          stage4: "Client incident request captured",
          stage5: "Incident request confirmed",
          revenueLost: "Revenue/Tracking lost. client not satisfied and no tracking.",
          revenueCaptured: "Revenue/Tracking captured. client satisfied.",
        },
        appointment: {
          description: "See how incoming requests, incidents, and escalations are automatically scheduled, assigned, and tracked — without manual coordination.",
          runDemo: "RUN INCIDENT SCHEDULING DEMO",
          complete: "Full incident scheduling cycle completed in under 60 seconds",
          reset: "RESET DEMO",
          processing: "Processing incident...",
          step1: { title: "Incident Reported", desc: "An incident or service request is submitted via phone, form, or automated alert system." },
          step2: { title: "Priority Classified", desc: "System classifies severity and urgency level based on predefined escalation rules." },
          step3: { title: "Response Team Assigned", desc: "Qualified responders are identified and assigned based on availability and expertise." },
          step4: { title: "Escalation Path Activated", desc: "If SLA thresholds are breached, automatic escalation notifies supervisors and management." },
          step5: { title: "Resolution Tracked", desc: "Progress is monitored in real-time with status updates sent to all stakeholders." },
          step6: { title: "Post-Incident Report Filed", desc: "Incident details, response times, and resolution steps are documented for compliance and review." },
        },
        safety: {
          description: "See how standardized animated safety video manuals are deployed across industrial warehouses — ensuring consistent HSE compliance.",
          runDemo: "RUN SAFETY MANUAL DEMO",
          complete: "Safety manual deployment completed successfully",
          reset: "RESET DEMO",
          processing: "Deploying safety content...",
          step1: { title: "Hazard Identified", desc: "Workplace hazard or procedure update triggers creation of a new visual safety instruction." },
          step2: { title: "Animated Manual Created", desc: "Step-by-step animated video manual is generated following ISO/VCA/OSHA standards." },
          step3: { title: "Multi-Language Localized", desc: "Content is automatically translated and localized for multilingual workforce compliance." },
          step4: { title: "Distributed to Stations", desc: "Video manuals are pushed to warehouse kiosks, tablets, and operator devices instantly." },
          step5: { title: "Acknowledgment Tracked", desc: "Each operator's viewing and comprehension is logged for audit trail and certification." },
          step6: { title: "Compliance Report Generated", desc: "Automated compliance reports are generated showing training coverage across all shifts." },
        },
      },
    video: {
      title: "Designed for uninterrupted operations",
      subtitle: "See how Ovelon Prime supports mission-critical environments without disrupting live systems.",
      cta: "Discovery Call",
    },
    services: {
      label: "WHAT WE STABILIZE",
      title: "Operational reliability in environments",
      titleHighlight: "that cannot afford mistakes",
      subtitle:
        "We remove operational breakdowns caused by manual coordination, unclear execution, and reactive incident handling — restoring control, accountability, and predictable outcomes inside live operations.",
      items: [
        {
          title: "Request & Demand Handling",
          description:
            "Incoming requests are captured, validated, and routed consistently — without reliance on ad-hoc follow-up or individual judgment.",
          tags: ["Smart Intake control", "Qualification rules", "System Routing"],
        },
        {
          title: "Safety & Execution Standards",
          description:
            "Standardized visual instructions used to align operators, contractors, and teams on safety-critical procedures — reducing interpretation errors, deviations, and execution risk.",
          tags: [
            "HSE procedures",
            "ISO / VCA / OSHA alignment",
            "Operator & contractor onboarding",
          ],
        },
        {
          title: "Incident Handling & Escalation",
          description:
            "Issues are logged, classified, and escalated in a controlled manner — ensuring accountability, response clarity, and predictable resolution paths",
          tags: ["Issue classification", "Escalation paths", "Response ownership"],
        },
        {
          title: "Appointment Setting Efficiency",
          description:
            "Seamless voice-powered scheduling that handles bookings, confirmations, and rescheduling with precision — freeing your team, keeping every connection on track, and delivering a flawless client experience.",
          tags: ["Voice scheduling", "24/7 availability", "Callback auto-reply"],
        },
      ] as ServiceItem[],
      clickToExplore: "Click to explore",
    },
    serviceDetails: {
      keyBenefits: "Key Benefits",
      cta: "Explore Demo",
      benefits: [
        [
          "Automated intake validation eliminates manual review bottlenecks",
          "Intelligent routing reduces response time by up to 60%",
          "Full audit trail for compliance and accountability",
          "Seamless integration with existing ticketing systems",
        ],
        [
          "Visual SOPs reduce training time and interpretation errors",
          "Real-time compliance tracking across all shifts",
          "Contractor onboarding standardized across locations",
          "HSE-aligned procedures ready for regulatory audits",
        ],
        [
          "Automated classification ensures consistent issue prioritization",
          "Clear escalation paths with defined ownership at each level",
          "Response SLAs tracked and enforced automatically",
          "Post-incident analysis for continuous improvement",
        ],
        [
          "24/7 availability without increasing headcount",
          "Intelligent rescheduling minimizes no-shows",
          "Multi-channel confirmation (voice, SMS, email)",
          "Seamless calendar sync with your existing systems",
        ],
      ],
    },
    sectors: {
      label: "Sectors",
      title: "Operational Reliability For",
      titleHighlight: "Mission-Critical Industries",
      subtitle: "We support operational environments where downtime, misalignment, or delayed response directly impacts safety, continuity, or output.",
      items: [
        {
          name: "Facility Management",
          description: "Managing distributed assets and service requests across complex facilities — with limited tolerance for delay or miscoordination.",
        },
        {
          name: "Maintenance & Repair",
          description: "Operating under constant pressure to prevent downtime across reactive and preventive maintenance — often with incomplete information and tight response windows.",
        },
        {
          name: "Logistics & Warehousing",
          description: "Coordinating workforce, equipment, and workflows under strict timelines and continuous operational pressure.",
        },
      ] as SectorItem[],
    },
    testimonials: {
      label: "CLIENT VOICES",
      title: "Client Testimonials",
      items: [
        {
          quote: "Before Ovelon Prime, shift coordination depended on individual follow-up. Today, maintenance and planning execute predictably across all shifts — without manual intervention.",
          name: "Patrick Sullivan",
          title: "Logistics Operations Lead",
          facility: "Distribution Center, Nebraska",
        },
        {
          quote: "In our environment, delays directly affect safety and output. Ovelon Prime helped us standardize execution across teams, reducing response time by 45% without increasing operational risk.",
          name: "Alicia Zhang",
          title: "Director of Operations",
          facility: "Warehouse Facility, Ottawa",
        },
        {
          quote: "We operate under continuous pressure. Since implementing the system, routing and escalation follow fixed rules — not assumptions. Downtime caused by coordination errors is no longer a factor.",
          name: "Wyatt Coleman",
          title: "Plant Manager",
          facility: "Manufacturing Plant, Austin",
        },
        {
          quote: "Managing a facility this size means thousands of moving parts. The system gave us a single source of truth for task status, equipment cycling, and compliance checks. We stopped chasing updates and started acting on them.",
          name: "James Whitfield",
          title: "Facility Operations Director",
          facility: "Industrial Warehouse, Liverpool",
        },
        {
          quote: "In our environment, even small delays cascade fast. The platform enforces real-time accountability across every department. We've eliminated the grey areas that used to stall production for hours.",
          name: "Ciarán O'Sullivan",
          title: "Senior Facility Lead",
          facility: "Processing Facility, Tennessee"
        },
        {
          quote: "We were losing hours every week to manual dispatching and misrouted work orders. Since going live, our teams follow structured workflows end to end — no guesswork, no bottlenecks. Throughput is up and our error rate has flatlined.",
          name: "Rachel Moreno",
          title: "Operations Control Manager",
          facility: "Logistics Hub, Dublin",
        }
      ] as TestimonialItem[],
    },
    operationalTrust: {
      heading: "Designed for regulated & safety-critical environments",
      items: [
        { text: "ISO 27001-aligned security principles" },
        { text: "ISO 9001-style process control" },
        { text: "HSE-aware operational design" },
        { text: "Audit-ready documentation & controls" },
        { text: "Full client ownership — no black-box systems" },
      ] as TrustItem[],
      footer: "Designed for CISO-reviewed environments with internal security governance, access control policies, and change-management procedures.",
    },
    about: {
      label: "About Us",
      title: "Built on operational expertise,",
      titleHighlight: "driven by precision",
      description1:
        "Ovelon Prime works with organizations operating under constant pressure — where downtime, delays, and manual processes directly impact cost, safety, and reliability.",
      description2:
        "We combine deep operational domain expertise with operational control layers to design systems that integrate seamlessly into existing workflows, without disrupting critical operations.",
      quote:
        '"Precision is not optional. In environments where reliability determines outcomes, systems must be engineered to perform under pressure."',
      quoteAuthor: "Leadership Team · Ovelon Prime",
      stats: {
        uptime: "Operational System Uptime",
        deployments: "Automation Deployments",
        industries: "Critical Industries Served",
      },
    },
    pricing: {
      label: "Pricing",
      title: "Engagement-based pricing, with clearly defined outcomes",
      subtitle:
        "Clearly scoped engagements focused on improving reliability, reducing operational friction, and delivering measurable impact.",
      focused: {
        name: "Focused Operational Engagement",
        description:
          "A fixed-scope engagement designed to resolve a clearly defined operational bottleneck with measurable impact.",
        requestQuote: "Request Quote",
        cta: "Request Engagement Assessment",
        features: [
          "Single, clearly defined operational improvement",
          "Process & responsibility mapping",
          "Controlled implementation & rollout",
          "Documentation & operational handover",
          "Post-implementation validation",
        ],
      },
      enterprise: {
        name: "Enterprise Operational Program",
        description:
          "Tailored operational systems for complex organizations with strict governance, compliance, and continuity requirements.",
        priceLabel: "Tailored pricing",
        price: "Custom Service",
        period: "one-time fee",
        cta: "Request Initial Assessment",
        badge: "Enterprise",
        features: [
          "Multi-system operational architecture",
          "Custom integrations across existing systems",
          "Dedicated implementation & transition support",
          "Compliance-aware operational design",
          "Custom SLA & governance framework",
        ],
      },
      securityNote:
        "Every engagement is built with enterprise-grade security, data protection, and operational compliance.",
      talkToExpert: "Discovery Call",
      intakeNote:
        "Structured intake · No sales pressure · Directly with an experienced expert",
      reviewNote: "All requests are reviewed before scheduling.",
      comparisonTable: {
        title: "Plan Comparison",
        featureCol: "Feature",
        focusedCol: "Focused",
        enterpriseCol: "Enterprise",
        rows: [
          { feature: "Operational assessment & scoping", focused: true, enterprise: true },
          { feature: "Process & responsibility mapping", focused: true, enterprise: true },
          { feature: "Controlled implementation & rollout", focused: true, enterprise: true },
          { feature: "Post-implementation validation", focused: true, enterprise: true },
          { feature: "Documentation & operational handover", focused: true, enterprise: true },
          { feature: "Multi-system operational architecture", focused: false, enterprise: true },
          { feature: "Custom system integrations", focused: false, enterprise: true },
          { feature: "Dedicated transition support team", focused: false, enterprise: true },
          { feature: "Compliance-aware operational design", focused: false, enterprise: true },
          { feature: "Custom SLA & governance framework", focused: false, enterprise: true },
          { feature: "Multi-site rollout capability", focused: false, enterprise: true },
        ],
      },
    },
    contact: {
      label: "ENGAGE",
      title: "Ready to regain control of",
      titleHighlight: " your operations?",
      subtitle: "All engagements are scoped upfront and subject to a formal review before continuation.",
      cta: "Schedule Consultation",
      guarantee: "No long-term commitments.",
      guaranteeHighlight: "Engagements continue only when operational value is demonstrated",
      guaranteeSuffix: " on qualifying engagements.",
      enterpriseLabel: "Enterprise inquiries: ",
      rightTitle: "Engage with Ovelon Prime",
      infoText1: "If you have any questions or would like to discuss how we can support your business, please do not hesitate to contact us.",
      infoText2: "Customer Support is available 24/7.",
    },
    contactForm: {
      firstName: "First Name",
      firstNamePlaceholder: "John",
      lastName: "Last Name",
      lastNamePlaceholder: "Doe",
      businessName: "Business Name",
      businessNamePlaceholder: "Acme Industries",
      businessEmail: "Business Email",
      businessEmailPlaceholder: "john@acme.com",
      requestDetails: "Request Details",
      requestDetailsPlaceholder: "Tell us about your operational challenges and what you're looking to achieve...",
      submit: "Submit Request",
      submitting: "Submitting...",
      validationError: "Please fill in all required fields correctly.",
      successTitle: "Submitted Successfully",
      successMessage: "We will review your request carefully.",
      successCta: "Book a Strategic Consultation",
    },
    faq: {
      title: "FAQs",
      category: "Engagement & Delivery",
      items: [
        {
          question: "How does Ovelon Prime work?",
          answer:
            "We start with a structured intake to understand the operational constraint. We design and validate a fixed-scope solution against real workflows. We implement, test under load, and hand over with full documentation.",
        },
        {
          question: "What industries do you specialize in?",
          answer:
            "We work with organizations operating in mission-critical environments, including manufacturing, logistics, and facility management. If delays, errors, or downtime directly impact safety, output, or continuity, we are in scope.",
        },
        {
          question: "Is this a subscription or ongoing service?",
          answer:
            "No. Engagements are fixed-scope and outcome-driven. There is no mandatory subscription, license dependency, or ongoing fee. You own the system after delivery.",
        },
        {
          question: "How long does a typical implementation take?",
          answer:
            "Most focused engagements are delivered within 12-16 days, depending on scope and system access. Enterprise programs follow a phased rollout aligned with operational priorities.",
        },
        {
          question: "Will this integrate with our existing systems?",
          answer:
            "Yes. Solutions are designed to integrate with existing systems without replacing core platforms. We work within your current tooling and data boundaries.",
        },
        {
          question: "Do we need to change our current workflows?",
          answer:
            "No. We design around your existing workflows. Where changes are required, they are minimal, documented, and validated with your operational teams before deployment.",
        },
        {
          question: "Who owns the optimization after delivery?",
          answer:
            "You do. All systems, configurations, and documentation are handed over upon completion. There is no vendor lock-in. You retain full operational and technical ownership.",
        },
        {
          question: "How do you handle security and compliance?",
          answer:
            "Security and compliance are treated as design constraints, not afterthoughts. Every engagement is scoped around your existing security policies, access controls, and compliance requirements. We work within your approved infrastructure, data boundaries, and governance frameworks. No data is extracted or reused beyond the agreed scope, and all systems are delivered with full documentation to support internal audits, risk reviews, and handover.",
        },
        {
          question: "What level of internal involvement is required?",
          answer:
            "Minimal, structured involvement. We require limited input during intake to understand operational constraints and validate assumptions. Implementation is handled independently, with clear checkpoints for review and approval. Operational teams are not disrupted, and no ongoing internal resources are required after delivery.",
        },
        {
          question: "Is Ovelon Prime suitable for large enterprise environments?",
          answer:
            "Yes. We are designed for complex, multi-stakeholder environments with strict governance, compliance, and operational requirements. Engagements are structured to align with enterprise change management, security reviews, and phased rollouts — without forcing platform replacements or disrupting critical systems.",
        },
      ] as FAQItem[],
    },
    metricsStrip: {
      items: [
        { value: "40%", label: "Downtime Reduction" },
        { value: "200+", label: "Workflows Automated" },
        { value: "3", label: "Continents Served" },
        { value: "98%", label: "SLA Compliance Rate" },
      ],
    },
    footer: {
      privacy: "Privacy",
      terms: "Terms",
      copyright: "© {{year}} Ovelon Prime. All rights reserved.",
      tagline: "Enterprise operating systems built for clarity, control, and scale.",
      trustHeading: "Designed for regulated & safety-critical environments",
      trustItems: [
        "ISO 27001-aligned security principles",
        "SOC 2 Type II compliant controls",
        "NIS2 Directive readiness",
        "Audit-ready documentation & controls",
        "HSE-aware operational design",
        "Full client ownership — no black-box systems",
      ],
      badgeIsoTitle: "ISO 27001",
      badgeIsoDesc: "Certified Information Security Management",
      badgeSocTitle: "SOC 2 Type II",
      badgeSocDesc: "Service Organization Controls",
      badgeGdprTitle: "GDPR",
      badgeGdprDesc: "Data Protection & Privacy Regulation",
    },
    industrialTrust: {
      heading: "Trusted by operators in mission-critical environments",
      disclaimer: "Representative operators from industrial, logistics, manufacturing, and infrastructure sectors. Engagements vary by scope and environment.",
    },
    intake: {
      title: "Consultation Qualification",
      subtitle: "Please complete the short intake below. All submissions are reviewed before scheduling.",
    },
    cookie: {
      message: "We use cookies to enhance your experience. By continuing to visit this site, you agree to our use of cookies.",
      accept: "Accept",
      decline: "Decline",
      learnMore: "Learn more about our Privacy Policy",
    },
    privacy: {
      title: "Privacy Policy",
      lastUpdated: "Last Updated: 01.12.2025",
      sections: {
        introduction: {
          title: "1. Introduction",
          p1: "At Ovelon Prime, we take data protection and confidentiality seriously. This Privacy Policy explains how we collect, use, and safeguard personal and business information when you interact with our website, intake forms, and services.",
          p2: "We process data responsibly and only to the extent required to deliver our services, ensure operational integrity, and meet legal obligations.",
        },
        informationCollected: {
          title: "2. Information We Collect",
          personal: "Personal Information:",
          personalDesc: "Name, business email address, phone number, and information submitted via forms or direct correspondence.",
          business: "Business & Operational Information:",
          businessDesc: "Company name, business address or jurisdiction, industry classification, operational context, and compliance-related information voluntarily provided during intake.",
          technical: "Technical Information:",
          technicalDesc: "IP address, browser and device metadata, cookies, and basic usage analytics.",
          note: "We do not intentionally collect sensitive personal data unless explicitly required for compliance or service delivery.",
        },
        howWeUse: {
          title: "3. How We Use Your Information",
          items: [
            "Assessing and qualifying consultation requests",
            "Delivering and supporting operational automation services",
            "Communicating regarding engagements, updates, or clarifications",
            "Maintaining platform reliability, security, and performance",
            "Fulfilling legal, regulatory, or contractual obligations",
          ],
          note: "We do not use your data for unsolicited marketing, profiling, or third-party advertising purposes.",
        },
        dataSharing: {
          title: "4. Data Sharing & Disclosure",
          p1: "Ovelon Prime does not sell, rent, or trade personal or business data.",
          p2: "Information may only be shared when strictly necessary:",
          items: [
            "With vetted service providers required for service delivery",
            "To comply with legal or regulatory obligations",
            "To protect the security, integrity, or rights of our systems and clients",
          ],
          note: "Data is never shared for third-party marketing or promotional purposes.",
        },
        dataSecurity: {
          title: "5. Data Security",
          p1: "We apply industry-standard technical and organizational safeguards, including encryption, access controls, and secure infrastructure practices.",
          p2: "While no system can guarantee absolute security, we operate under strict internal protocols designed to minimize risk and protect confidentiality at all times.",
        },
        dataRetention: {
          title: "6. Data Retention & Your Rights",
          p1: "We retain data only for as long as necessary to fulfill the purposes outlined in this policy or to meet legal requirements.",
          p2: "You have the right to request access, correction, or deletion of your data, where applicable.",
        },
        contact: {
          title: "7. Contact",
          p1: "For questions regarding this Privacy Policy or data handling practices, please contact:",
        },
      },
    },
    terms: {
      title: "Terms & Conditions",
      lastUpdated: "Last Updated: 01 December 2025",
      sections: {
        introduction: {
          title: "1. Introduction",
          p1: "These Terms & Conditions (\"Terms\") govern your access to and use of the Ovelon Prime website, consultation processes, and professional automation services. By accessing the website or engaging our services, you agree to be bound by these Terms.",
          p2: "If you do not agree with these Terms, you must discontinue use of the website and services immediately.",
        },
        services: {
          title: "2. Services Provided",
          p1: "Ovelon Prime provides professional automation, systems design, and implementation services for industrial, logistics, facility management, infrastructure, and other operationally critical environments.",
          p2: "All services are delivered on a project or engagement basis. Specific scope, deliverables, assumptions, and timelines are defined per engagement and may evolve based on operational requirements.",
        },
        websiteUse: {
          title: "3. Use of the Website",
          items: [
            "You may not use the website for unlawful or fraudulent purposes.",
            "You may not attempt to gain unauthorized access to systems or data.",
            "You may not scrape, reverse-engineer, disrupt, or interfere with website functionality or security mechanisms.",
          ],
        },
        clientResponsibilities: {
          title: "4. Client Responsibilities",
          items: [
            "Providing accurate, complete, and up-to-date business information.",
            "Ensuring timely access to systems, data, and stakeholders when required for delivery.",
            "Maintaining internal security controls, credential management, and access governance.",
            "Using delivered systems in compliance with applicable laws, regulations, and internal policies.",
          ],
        },
        payments: {
          title: "5. Payments & Billing",
          p1: "Unless otherwise agreed in writing, payment is required prior to the commencement of services. Custom engagements require a signed proposal and a mutually agreed payment schedule.",
          p2: "Fees are non-refundable once work has commenced, except where explicitly stated otherwise in a written agreement.",
        },
        noGuarantee: {
          title: "6. No Guarantee of Outcomes",
          p1: "While Ovelon Prime designs systems with reliability, performance, and operational efficiency in mind, no guarantees are made regarding specific operational, financial, or performance outcomes.",
        },
        ip: {
          title: "7. Intellectual Property",
          p1: "Unless otherwise agreed in writing, all methodologies, architectures, workflows, documentation, diagrams, and technical materials developed by Ovelon Prime remain our intellectual property.",
          p2: "Clients are granted a non-transferable, non-exclusive license for internal business use only.",
        },
        confidentiality: {
          title: "8. Confidentiality",
          p1: "Both parties agree to maintain strict confidentiality of all non-public business, technical, operational, and commercial information exchanged during an engagement.",
        },
        liability: {
          title: "9. Limitation of Liability",
          p1: "To the maximum extent permitted by law, Ovelon Prime shall not be liable for any indirect, incidental, special, or consequential damages.",
          p2: "Total liability shall not exceed the total fees paid for the specific service giving rise to the claim.",
        },
        thirdParty: {
          title: "10. Third-Party Tools & Integrations",
          p1: "Services may involve integrations with third-party platforms or tools. Ovelon Prime is not responsible for third-party outages, changes, or policy decisions.",
        },
        termination: {
          title: "11. Termination",
          p1: "We reserve the right to suspend or terminate services in cases of non-payment, misuse, or material breach of these Terms.",
        },
        governingLaw: {
          title: "12. Governing Law",
          p1: "These Terms are governed by the laws of the jurisdiction in which Ovelon Prime is legally established, without regard to conflict-of-law principles.",
        },
        changes: {
          title: "13. Changes to These Terms",
          p1: "We may update these Terms from time to time. Continued use of the website or services constitutes acceptance of the updated Terms.",
        },
        contactInfo: {
          title: "14. Contact Information",
          p1: "For questions regarding these Terms, please contact:",
        },
      },
    },
    demo: {
      header: "Operational Trade-Off Analysis",
      close: "Close",
      beforeAlignment: "Before Alignment",
      afterAlignment: "After Alignment",
      tradeoff: "Trade-off",
      footer: "Representative operational scenarios from industrial, logistics, and facility environments.",
      intake: {
        title: "Work Intake & Demand Handling",
        metrics: {
          coordinationTime: "Coordination Time",
          reworkCycles: "Rework Cycles",
          ownership: "Ownership",
          ownershipValue: "Defined upfront",
        },
        before: {
          title: "Fragmented intake and reactive coordination",
          text: "Work enters through emails, calls, and informal channels. Information quality varies and ownership shifts during execution.",
          bullet1: "Multiple intake paths",
          bullet2: "Manual clarification loops",
          bullet3: "Priority driven by urgency",
          bullet4: "Execution depends on individuals",
          tradeoff: "Time lost to coordination and correction.",
        },
        after: {
          title: "Structured intake with controlled execution",
          text: "Requests follow a single intake path. Information is validated upfront and ownership is assigned before execution begins.",
          bullet1: "Single structured intake",
          bullet2: "Clear classification and routing",
          bullet3: "Ownership assigned once",
          bullet4: "Predictable execution flow",
          tradeoff: "Coordination drops, execution stabilizes.",
        },
      },
      safety: {
        title: "Safety & Execution Standards",
        metrics: {
          procedureDeviations: "Procedure Deviations",
          onboardingTime: "Onboarding Time",
          consistency: "Consistency",
          consistencyValue: "Standardized",
        },
        before: {
          title: "Interpretation-based execution",
          text: "Safety procedures exist across documents and tribal knowledge. Operators interpret standards differently.",
          bullet1: "Inconsistent procedures",
          bullet2: "High reliance on senior staff",
          bullet3: "Contractors onboarded informally",
          bullet4: "Audit exposure",
          tradeoff: "Safety depends on experience, not structure.",
        },
        after: {
          title: "Standardized execution with shared understanding",
          text: "Execution standards are clear, visual, and uniform across teams and contractor groups.",
          bullet1: "Uniform execution standards",
          bullet2: "Faster onboarding",
          bullet3: "Reduced interpretation errors",
          bullet4: "Aligned safety framework",
          tradeoff: "Safety becomes repeatable and auditable.",
        },
      },
      incident: {
        title: "Incident Handling & Escalation",
        metrics: {
          escalationLoops: "Escalation Loops",
          responseTime: "Response Time",
          accountability: "Accountability",
          accountabilityValue: "Explicit",
        },
        before: {
          title: "Reactive incident handling",
          text: "Incidents are reported inconsistently and escalated manually. Responsibility is unclear and visibility is limited.",
          bullet1: "Unclear escalation thresholds",
          bullet2: "Delayed handovers",
          bullet3: "Parallel communication threads",
          bullet4: "Limited management visibility",
          tradeoff: "Incidents consume attention instead of being resolved.",
        },
        after: {
          title: "Controlled escalation with clear accountability",
          text: "Incidents are logged, classified, and escalated through predefined paths. Each step has a clear owner and response window.",
          bullet1: "Defined escalation levels",
          bullet2: "Clear ownership per incident",
          bullet3: "Predictable response timelines",
          bullet4: "Full operational visibility",
          tradeoff: "Incidents are resolved systematically.",
        },
      },
      appointment: {
        title: "Appointment Setting Efficiency",
        metrics: {
          schedulingTime: "Scheduling Time",
          noShows: "No-Shows",
          utilization: "Utilization",
          utilizationValue: "Maximized",
        },
        before: {
          title: "Manual scheduling with constant friction",
          text: "Appointments are coordinated via phone, email, and spreadsheets. Confirmations are inconsistent, no-shows are frequent, and scheduling conflicts drain operational capacity.",
          bullet1: "Phone-based scheduling during business hours only",
          bullet2: "Manual calendar checks for availability",
          bullet3: "No automated reminders or confirmations",
          bullet4: "Frequent double-bookings and conflicts",
          tradeoff: "Staff time consumed by scheduling instead of operations.",
        },
        after: {
          title: "Automated scheduling with predictable capacity",
          text: "Appointments are captured and confirmed automatically through voice and digital channels. Real-time availability checks, automated reminders, and intelligent rescheduling eliminate scheduling friction.",
          bullet1: "24/7 voice and digital appointment intake",
          bullet2: "Real-time calendar sync and conflict prevention",
          bullet3: "Automated confirmations and reminder sequences",
          bullet4: "Self-service rescheduling with capacity optimization",
          tradeoff: "Scheduling becomes frictionless, utilization increases.",
        },
      },
    },
    simulator: {
      title: "Demand & Execution Intake",
      close: "Close",
      back: "Back",
      next: "Continue",
      requestReview: "Request Operational Review",
      submitting: "Submitting...",
      submitError: "Failed to submit request. Please try again or contact support.",
      submitErrorTitle: "Submission Failed",
      validationErrorTitle: "Required Fields Missing",
      validationErrorDescription: "Please fill in all required fields before submitting.",
      stepIndicator: "Step {{current}} of {{total}}",
      step1: {
        title: "Operational Context",
        description: "Let's start by understanding your operational environment. Select the options that best describe your organization.",
        industryLabel: "What industry do you operate in?",
        operationLabel: "What type of operations do you run?",
        operationHint: "Select all that apply",
        scaleLabel: "What is the scale of your operations?",
      },
      step2: {
        title: "Current Operating Model",
        description: "Help us understand how you handle day-to-day work today — no judgment, just clarity.",
        handlingLabel: "How are requests, tasks, or incidents currently handled?",
        handlingHint: "Select all methods in use",
        structureLabel: "What is the level of structure in your operations?",
        toolsLabel: "What tools or systems are in use?",
      },
      step3: {
        title: "Operational Hurdles & Pain Points",
        description: "Identify where work slows down or becomes unpredictable today.",
        frictionLabel: "What causes friction in your operations?",
        frictionHint: "Select all that apply",
        notesLabel: "Additional context (optional)",
        notesPlaceholder: "Describe specific challenges or context...",
      },
      step4: {
        title: "Constraints & Requirements",
        description: "Define the boundaries and requirements that affect your operations.",
        downtimeLabel: "How sensitive are your operations to downtime?",
        complianceLabel: "What safety or compliance frameworks apply?",
        complianceHint: "Select all that apply",
        complexityLabel: "How complex is internal coordination?",
      },
      step5: {
        title: "Summary & Reflection",
        description: "Here's a summary of what you've shared — a clear snapshot of your current operational reality.",
        noData: "No data to display. Go back and complete previous steps.",
        contextTitle: "Operational Context",
        modelTitle: "Current Operating Model",
        hurdlesTitle: "Identified Hurdles",
        constraintsTitle: "Constraints & Requirements",
        industry: "Industry",
        operations: "Operations",
        scale: "Scale",
        handling: "Request Handling",
        structure: "Structure Level",
        tools: "Tools",
        downtime: "Downtime Sensitivity",
        compliance: "Compliance",
        complexity: "Coordination Complexity",
        disclaimer: "This simulation is indicative and reflects user-provided information. It does not replace professional assessment. All findings are subject to formal review.",
      },
      industries: {
        manufacturing: "Manufacturing",
        logistics: "Logistics",
        facility: "Facility Management",
        infrastructure: "Infrastructure",
        energy: "Energy & Utilities",
        other: "Other",
      },
      operationTypes: {
        facility: "Facility Operations",
        logistics: "Logistics & Warehousing",
        maintenance: "Maintenance & Repair",
        production: "Production Lines",
        fieldService: "Field Service",
      },
      scales: {
        single: "Single Site",
        multi: "Multi-Site",
        distributed: "Distributed / Regional",
      },
      handlingMethods: {
        email: "Email",
        phone: "Phone Calls",
        tickets: "Ticketing System",
        verbal: "Verbal Handoffs",
        spreadsheets: "Spreadsheets",
        erp: "ERP / CMMS System",
      },
      structureLevels: {
        adhoc: {
          title: "Mostly Ad-Hoc",
          desc: "Work is handled as it comes, minimal standardization",
        },
        semiStructured: {
          title: "Semi-Structured",
          desc: "Some processes defined, but execution varies",
        },
        fullyStructured: {
          title: "Fully Structured",
          desc: "Clear processes with consistent execution",
        },
      },
      toolTypes: {
        manual: "Mostly Manual",
        semiAutomated: "Semi-Automated",
        fragmented: "Fragmented Systems",
        integrated: "Integrated Platform",
      },
      frictionPoints: {
        slowResponse: "Slow response times",
        unclearOwnership: "Unclear task ownership",
        manualCoordination: "Excessive manual coordination",
        inconsistentExecution: "Inconsistent execution",
        lackVisibility: "Lack of real-time visibility",
        escalationDelays: "Escalation delays",
        documentationGaps: "Documentation gaps",
        communicationBreakdown: "Communication breakdown",
      },
      downtimeLevels: {
        critical: {
          title: "Critical",
          desc: "Every minute of downtime costs significantly",
        },
        high: {
          title: "High",
          desc: "Downtime seriously impacts operations",
        },
        moderate: {
          title: "Moderate",
          desc: "Some tolerance, but affects productivity",
        },
        low: {
          title: "Low",
          desc: "Can manage short-term interruptions",
        },
      },
      complianceOptions: {
        iso: "ISO 9001 / 27001",
        hse: "HSE / Health & Safety",
        osha: "OSHA",
        vca: "VCA / SCC",
        gdpr: "GDPR / Data Protection",
        sectorSpecific: "Sector-Specific Regulations",
      },
      complexityLevels: {
        simple: "Simple",
        moderate: "Moderate",
        complex: "Complex",
        veryComplex: "Very Complex",
      },
      step6: {
        title: "Operational Analysis",
        description: "Based on the information provided, here is an executive-level operational analysis of your current state.",
        noData: "Insufficient data to generate analysis. Please complete previous steps.",
        observationsTitle: "Operational Observations",
        risksTitle: "Risk & Exposure Areas",
        readinessTitle: "Execution Readiness Assessment",
        advisoryTitle: "Advisory Direction (High-Level)",
        disclaimer: "This analysis is indicative and based on self-reported information. It does not replace professional assessment. All findings are subject to formal review before engagement.",
      },
      step7: {
        title: "Contact Details",
        description: "These details are required to attach the operational analysis to a formal review request.",
        personalTitle: "Personal Information",
        companyTitle: "Company Information",
        fullName: "Full Name",
        fullNamePlaceholder: "Enter your full name",
        email: "Email Address",
        emailPlaceholder: "Enter your business email",
        phone: "Phone Number",
        phonePlaceholder: "Enter your phone number",
        companyName: "Company Name",
        companyNamePlaceholder: "Enter company name",
        role: "Role / Function",
        rolePlaceholder: "e.g. Operations Manager",
        country: "Country",
        countryPlaceholder: "Enter country",
        city: "City",
        cityPlaceholder: "Enter city",
        requiredNote: "All fields marked with * are required. Your information will only be used to process this review request.",
      },
      analysis: {
        observations: {
          industry: {
            manufacturing: "Operating in a manufacturing environment typically involves complex production scheduling, equipment maintenance cycles, and quality control requirements.",
            logistics: "Logistics operations require tight coordination across multiple stakeholders, time-critical deliveries, and real-time visibility across supply chains.",
            facility: "Facility management operations span multiple service categories and require coordination across internal teams and external contractors.",
            infrastructure: "Infrastructure operations often involve geographically distributed assets with varying maintenance requirements and regulatory oversight.",
            energy: "Energy sector operations require strict adherence to safety protocols, regulatory compliance, and continuous monitoring of critical systems.",
            other: "Operations span multiple domains with varying requirements for coordination, compliance, and execution control.",
          },
          scale: {
            single: "Single-site operations allow for centralized control but may still face coordination challenges across shifts and departments.",
            multi: "Multi-site operations introduce complexity in standardization, communication, and ensuring consistent execution across locations.",
            distributed: "Distributed or regional operations require robust coordination mechanisms to maintain visibility and control across dispersed teams.",
          },
          structure: {
            adhoc: "Ad-hoc operational structure suggests high dependency on individual judgment and potential inconsistency in execution.",
            semiStructured: "Semi-structured processes indicate partial standardization with room for variation, which may lead to inconsistent outcomes.",
            fullyStructured: "Fully structured operations provide a foundation for predictable execution, though effectiveness depends on adherence and enforcement.",
          },
          tools: {
            fragmented: "Fragmented tooling landscape typically leads to information silos, manual data reconciliation, and coordination overhead.",
            manual: "Heavy reliance on manual processes increases error risk and limits scalability of operations.",
          },
        },
        risks: {
          predictability: "Predictability risk: Inconsistent execution and unclear ownership patterns may lead to unpredictable operational outcomes.",
          coordination: "Coordination risk: Manual coordination and communication gaps can result in delays, errors, and escalation failures.",
          downtime: "Downtime exposure: High sensitivity to downtime combined with current operational patterns suggests elevated risk of costly disruptions.",
          compliance: "Compliance exposure: Gap between compliance requirements and current structure level may create audit and regulatory risks.",
          escalation: "Escalation risk: Delays in issue resolution and limited visibility may allow minor issues to escalate into significant operational problems.",
        },
        readiness: {
          high: "The organization appears reasonably prepared for operational change, with existing structure and tools providing a foundation for improvement initiatives.",
          moderate: "The organization shows mixed readiness indicators. Some foundational elements exist, but gaps in structure or tooling may require attention before scaling changes.",
          low: "Current operational patterns suggest limited readiness for scale or significant change without first addressing foundational coordination and structure gaps.",
        },
        advisory: {
          ownership: "Consider establishing clearer ownership structures and accountability frameworks to reduce coordination overhead and improve response predictability.",
          standardization: "Focus on standardizing core operational processes to reduce variation and establish consistent execution patterns across teams.",
          escalation: "Implement structured escalation pathways with defined response windows and clear ownership at each level.",
          compliance: "Align operational structure with compliance requirements to reduce audit exposure and ensure consistent adherence to regulatory standards.",
          scale: "For multi-site or distributed operations, prioritize mechanisms that ensure consistency and visibility across locations.",
        },
      },
    },
    submission: {
      title: "Submission Received",
      body1: "Your operational review has been submitted successfully.",
      body2: "Our team will carefully review your request and follow up accordingly.",
      body3: "To accelerate next steps, you may schedule a strategic consultation below.",
      scheduleBtn: "Schedule Strategic Consultation",
      closeBtn: "Close",
      referenceLabel: "Reference ID",
      emailSent: "Confirmation sent to",
      pdfAttached: "PDF summary attached for your records",
    },
    governanceTrends: {
      title: "Operating in a Regulated Reality — ISO 27001, NIS2 & SOC 2",
      subtitle: "As operational environments become more regulated, interconnected, and scrutinized, leadership is increasingly defined by control, documentation, and consistency — not by speed or improvisation.",
      column1: {
        title: "What Operating Leaders Prioritize",
        items: [
          "Clear, enforceable work instructions",
          "Predictable execution across shifts and teams",
          "Safety procedures that leave little room for interpretation",
          "Fewer ad-hoc decisions during incidents",
          "Documentation that stands on its own"
        ]
      },
      column2: {
        title: "Standards, Compliance & Liability",
        items: [
          "Formal management systems based on recognized standards",
          "Traceable procedures and corrective actions",
          "Verifiable training and competency records",
          "Data protection and access control by default",
          "Decisions that can be reconstructed after the fact"
        ]
      },
      column3: {
        title: "Safety, Hygiene & Operational Reality",
        items: [
          "Safety rules must work under pressure, not only on paper",
          "Hygiene procedures must be consistent across locations",
          "Deviations must be logged, not normalized",
          "Informal workarounds increase exposure",
          "Personal accountability is moving closer to the floor"
        ]
      },
      trend1: "Looking ahead, the trend is clear: operations are expected to be explainable, auditable, and repeatable — even under disruption.",
      trend2: "Organizations that succeed will be those that embed standards, safety, hygiene, and data discipline directly into daily execution. Not as overhead, but as operational structure."
    },
    chatbot: {
      greeting: "Hi! I'm Brooks. How can I help you today?\n\nI can assist with Product Info, Operational Support, Schedule a Demo, try our Simulator, Compliance & Governance, or answer questions about Pricing.",
      subtitle: "Ovelon Prime Assistant",
      inputPlaceholder: "Type a message...",
      buttons: {
        productInfo: "Product Info",
        operationalSupport: "Support",
        scheduleDemo: "Schedule Demo",
        intake: "Diagnostic Intake",
        simulation: "Simulator",
        pricing: "Pricing",
        contact: "Contact",
        warehouseSolutions: "Warehouse Solutions",
        workflowSolutions: "Workflow Solutions",
        backToMain: "← Back",
        connectSupport: "Connect to Support",
        bookDemo: "Book with us",
        startIntake: "Start Intake →",
        viewPricing: "View Pricing",
        goToForm: "Go to Contact Form",
        openSimulator: "Open Simulator →",
      },
      responses: {
        productInfo: "We offer warehouse and workflow solutions tailored to your operations.\n\nQuick links:\n• Warehouse Solutions\n• Workflow Solutions\n\nAnything specific you want to know?",
        operationalSupport: "Can you tell me the issue type? I can guide you or connect you with a human expert.",
        scheduleDemo: "I can schedule a demo for you. Click the button below to pick a time that works for you!",
        intake: "Our 7-step Demand & Execution Intake helps diagnose your operational challenges:\n\n1️⃣ Operational Context\n2️⃣ Current Challenges\n3️⃣ Process Analysis\n4️⃣ Technology Stack\n5️⃣ Goals & Priorities\n6️⃣ Executive Analysis\n7️⃣ Contact Details\n\nYou'll receive a branded PDF summary. Ready to start?",
        pricing: "Our platform starts at $4,999/month and includes the full suite of warehouse and workflow solutions.\n\nWant to see the detailed pricing section or schedule a call to discuss your needs?",
        contact: "You can reach us at info@ovelon-prime.com or use our contact form below. Would you like me to take you there?",
        simulation: "Our Project Simulator helps you visualize potential improvements in your operations. Try it out!",
        simulatorOpened: "I've opened the Project Simulator for you. Follow the steps to see your potential improvements!",
        connectSupport: "I'm connecting you with our support team. You can also reach us directly at info@ovelon-prime.com",
        bookingOpened: "I've opened our booking calendar for you. Pick a time that works best!",
        backToMain: "No problem! What else can I help you with?",
        fallback: "I don't have that info, but the team can follow up with you shortly. Would you like to leave your contact details or try the contact form?",
        compliance: "We help organizations navigate complex regulatory landscapes:\n\n🛡️ NIS2 Directive — Network and Information Security compliance readiness for critical infrastructure.\n\n📋 ISO 27001 — Information security management system implementation and audit preparation.\n\n🔒 SOC 2 — Service Organization Control audits (Type I & Type II) for trust services criteria.\n\n📑 Operational Governance — Policy frameworks, audit trails, and compliance dashboards for full visibility.\n\nAsk me any question about these frameworks, or explore the articles below!",
        complianceThinking: "Let me look into that for you...",
        complianceError: "I couldn't find an answer right now. Please check our compliance articles or contact info@ovelon-prime.com.",
      },
      complianceHint: "Ask me anything about SOC 2, ISO 27001, or NIS2 ↵",
    },
    subscribe: {
      title: "Stay Ahead of the Curve",
      subtitle: "Get exclusive insights on workflow optimization, operations management, and industry trends delivered to your inbox.",
      promo: "🎁 Free Operations E-Book if you subscribe today!",
      fullName: "Full Name",
      fullNamePlaceholder: "Enter your full name",
      email: "Email Address",
      emailPlaceholder: "name@email.com",
      submit: "Subscribe Now",
      submitting: "Subscribing...",
      successTitle: "Welcome Aboard!",
      successMessage: "Thank you for subscribing. You'll receive our latest insights soon.",
      privacyText: "By subscribing, you agree to our",
      privacyLink: "Privacy Policy",
      termsLink: "Terms & Conditions",
      andText: "and",
      missingInfo: "Missing information",
      missingInfoDesc: "Please fill in both name and email.",
      failedTitle: "Subscription failed",
    },
    caseStudies: {
      badge: "Proven Results",
      title: "Transforming operations for mission-critical industries.",
      challengeLabel: "The Challenge",
      solutionLabel: "The Ovelon Prime Solution",
      resultsLabel: "Quantifiable Results",
      cases: {
        facility: {
          industry: "Facility Management",
          client: "Multi-Site Facility Operator",
          challenge: "Managing service requests across 40+ distributed sites led to inconsistent response times, missed maintenance windows, and fragmented communication between on-site teams and central coordination.",
          solution: "Ovelon Prime unified all inbound requests into a structured intake system with automated routing, priority classification, and real-time status tracking — enabling centralized oversight without replacing existing tools.",
          results: [
            { value: "42%", label: "Faster response times" },
            { value: "95%", label: "SLA compliance rate" },
            { value: "60%", label: "Reduction in coordination errors" },
          ],
        },
        supplyChain: {
          industry: "Supply Chain",
          client: "Regional Distribution Network",
          challenge: "Manual coordination between suppliers, warehouses, and transport partners created blind spots in the supply chain, leading to delayed shipments, inventory mismatches, and escalating operational costs.",
          solution: "Ovelon Prime implemented end-to-end workflow automation linking procurement, inventory management, and logistics scheduling — providing real-time visibility and predictive alerting across the entire supply chain.",
          results: [
            { value: "38%", label: "Reduction in lead times" },
            { value: "99.2%", label: "Order accuracy rate" },
            { value: "28%", label: "Lower operational costs" },
          ],
        },
        warehouse: {
          industry: "Warehouse & Storage",
          client: "Industrial Storage Complex",
          challenge: "Inventory tracking relied on fragmented spreadsheets and manual counts, causing frequent stock discrepancies, misallocated storage space, and delayed order fulfillment across multiple warehouse zones.",
          solution: "Ovelon Prime standardized inventory workflows with automated intake logging, zone-based allocation rules, and real-time stock visibility — reducing manual intervention while improving accuracy and throughput.",
          results: [
            { value: "55%", label: "Reduction in stock discrepancies" },
            { value: "40%", label: "Faster order fulfillment" },
            { value: "35%", label: "Improved space utilization" },
          ],
        },
        maintenance: {
          industry: "Repair & Maintenance",
          client: "Industrial Equipment Services",
          challenge: "Reactive maintenance workflows dominated operations. Work orders were logged inconsistently, parts tracking was manual, and technician scheduling was based on availability rather than priority or proximity.",
          solution: "Ovelon Prime introduced structured work order management with automated priority routing, parts inventory integration, and optimized technician dispatch — shifting operations from reactive to predictive maintenance.",
          results: [
            { value: "47%", label: "Reduction in equipment downtime" },
            { value: "65%", label: "Faster work order completion" },
            { value: "30%", label: "Decrease in repeat incidents" },
          ],
        },
      },
    },
    complianceBlog: {
      badge: "Compliance & Governance",
      title: "NIS2, ISO 27001 & SOC 2: What operational leaders must know now.",
      subtitle: "As regulatory pressure intensifies across critical infrastructure, understanding NIS2, ISO 27001 and SOC 2 is no longer optional — it's operational survival. Here's how Ovelon Prime aligns your operations with these frameworks.",
      articles: {
        nis2: {
          tag: "EU Directive 2022/2555",
          title: "NIS2 Directive: The new operational reality for critical infrastructure",
          excerpt: "The NIS2 Directive (Network and Information Security 2) is the EU's most significant cybersecurity regulation to date, expanding scope to 18 critical sectors and imposing severe penalties for non-compliance. Operational leaders must act now.",
          facts: [
            { label: "Sectors covered", value: "18" },
            { label: "Max penalty", value: "€10M or 2% revenue" },
            { label: "Management liability", value: "Personal" },
          ],
          sections: [
            {
              heading: "What is NIS2 and why does it matter?",
              text: "NIS2 replaces the original NIS Directive from 2016 and dramatically expands the scope of cybersecurity requirements across the EU. It now covers essential entities (energy, transport, health, water, digital infrastructure) and important entities (manufacturing, food production, waste management, postal services). Organizations must implement risk management measures, incident reporting, supply chain security, and business continuity planning — or face penalties of up to €10 million or 2% of global annual turnover.",
            },
            {
              heading: "Operational accountability is now personal",
              text: "Under NIS2, senior management can be held personally liable for non-compliance. This means CISOs, COOs, and board members must demonstrate active governance over cybersecurity and operational resilience. The directive mandates management-level approval of risk management measures, mandatory cybersecurity training for leadership, and documented evidence of compliance measures. Operational negligence is no longer just a reputational risk — it's a legal liability.",
            },
            {
              heading: "Incident reporting requirements are strict",
              text: "NIS2 mandates a tiered incident reporting framework: an early warning within 24 hours of detection, a detailed incident notification within 72 hours, and a final report within one month. Organizations must have structured incident detection, classification, and escalation processes in place. Manual, ad-hoc incident handling will not meet these requirements.",
            },
            {
              heading: "Supply chain security becomes mandatory",
              text: "The directive requires organizations to assess and manage cybersecurity risks across their entire supply chain. This includes vendor assessments, contractual security requirements, and continuous monitoring of third-party risks. Organizations relying on fragmented vendor management and manual oversight will struggle to demonstrate compliance.",
            },
          ],
          ovelonTitle: "How Ovelon Prime supports NIS2 compliance",
          ovelonPoints: [
            "Structured incident intake and classification with automated escalation paths that meet 24/72-hour reporting deadlines",
            "Full audit trails and documentation for every operational event, supporting evidence-based compliance demonstrations",
            "Standardized execution procedures that eliminate interpretation-based operations and reduce human error",
            "Real-time operational visibility with dashboards that support management-level governance and oversight obligations",
            "Supply chain coordination workflows that enforce security requirements across vendor and contractor interactions",
            "Automated notification systems that ensure timely stakeholder communication during incidents",
          ],
        },
        iso27001: {
          tag: "International Standard",
          title: "ISO 27001: Building an Information Security Management System that actually works",
          excerpt: "ISO 27001 is the global gold standard for information security management. But certification is only the beginning — maintaining an effective ISMS requires operational discipline, continuous monitoring, and evidence-based control execution.",
          facts: [
            { label: "Controls in Annex A", value: "93" },
            { label: "Certified orgs worldwide", value: "70,000+" },
            { label: "Recertification cycle", value: "3 years" },
          ],
          sections: [
            {
              heading: "Why ISO 27001 is essential for operational leaders",
              text: "ISO 27001 provides a systematic approach to managing sensitive information and ensuring its confidentiality, integrity, and availability. For operational leaders in critical infrastructure, it's not just about IT security — it's about demonstrating to clients, regulators, and stakeholders that your organization has rigorous controls in place. The 2022 revision reorganized controls into four domains (Organizational, People, Physical, and Technological) with 93 controls in Annex A, making it more aligned with modern operational environments.",
            },
            {
              heading: "The gap between certification and operational reality",
              text: "Many organizations achieve ISO 27001 certification but struggle to maintain the operational discipline required between audits. Policies exist on paper but aren't enforced in daily operations. Risk assessments become checkbox exercises. Incident logs are incomplete or inconsistent. The gap between documented procedures and actual execution creates both security vulnerabilities and audit exposure. True compliance requires continuous alignment between policy, process, and practice.",
            },
            {
              heading: "Annex A controls that demand operational automation",
              text: "Several ISO 27001 Annex A controls directly relate to operational processes: A.5.24 (Information security incident management planning), A.5.25 (Assessment and decision on information security events), A.5.26 (Response to information security incidents), A.5.28 (Collection of evidence), and A.8.16 (Monitoring activities). These controls require structured, repeatable processes with documented evidence — exactly what manual operations cannot reliably deliver.",
            },
            {
              heading: "Continuous improvement is mandatory, not optional",
              text: "ISO 27001 Clause 10 requires continual improvement of the ISMS. This means organizations must systematically identify non-conformities, implement corrective actions, and evaluate the effectiveness of those actions. Without structured operational data, trend analysis, and automated monitoring, continuous improvement becomes guesswork rather than evidence-based management.",
            },
          ],
          ovelonTitle: "How Ovelon Prime supports ISO 27001 compliance",
          ovelonPoints: [
            "Automated incident classification and response workflows aligned with Annex A controls 5.24-5.28",
            "Complete audit trails for every operational event, supporting evidence collection requirements (A.5.28)",
            "Standardized execution procedures that enforce consistent control implementation across teams and shifts",
            "Real-time monitoring dashboards that support continuous compliance verification (A.8.16)",
            "Structured change management workflows that document all modifications with approval chains",
            "Operational metrics and trend analysis that enable evidence-based continuous improvement under Clause 10",
          ],
        },
        soc2: {
          tag: "AICPA Trust Service Criteria",
          title: "SOC 2: Demonstrating operational trust through verified security controls",
          excerpt: "SOC 2 (Service Organization Control 2) is the leading audit framework for verifying that an organization's information security practices meet rigorous standards across five Trust Service Criteria. A SOC 2 report is increasingly required by enterprise clients and regulated industries.",
          type1Label: "Type I",
          type2Label: "Type II",
          type1: {
            facts: [
              { label: "Audit scope", value: "Point-in-time" },
              { label: "Duration", value: "Single date" },
              { label: "Trust criteria", value: "Up to 5" },
            ],
            sections: [
              {
                heading: "What is SOC 2 Type I?",
                text: "A SOC 2 Type I report evaluates whether an organization's security controls are suitably designed at a specific point in time. It answers the question: 'Are the right controls in place today?' Type I is typically the starting point for organizations pursuing SOC 2 certification, providing a baseline assessment without testing operational effectiveness over time.",
              },
              {
                heading: "The five Trust Service Criteria",
                text: "SOC 2 evaluates controls across five criteria: Security (mandatory), Availability, Processing Integrity, Confidentiality, and Privacy. Most organizations begin with Security alone. Type I verifies that policies, procedures, and technical controls addressing these criteria are designed appropriately — not that they function consistently over time.",
              },
              {
                heading: "When Type I is appropriate",
                text: "Type I is commonly used when an organization needs to quickly demonstrate a baseline level of security assurance to clients or partners. It is also the natural precursor to Type II, allowing organizations to identify and remediate control gaps before committing to a sustained audit period.",
              },
            ],
          },
          type2: {
            facts: [
              { label: "Audit period", value: "6–12 months" },
              { label: "Trust criteria", value: "Up to 5" },
              { label: "Market standard", value: "Enterprise" },
            ],
            sections: [
              {
                heading: "What is SOC 2 Type II?",
                text: "A SOC 2 Type II report evaluates whether an organization's security controls not only exist but operate effectively over an extended period — typically 6 to 12 months. It is the gold standard for enterprise trust and is increasingly required by procurement teams, regulators, and enterprise clients in sectors such as financial services, healthcare, and critical infrastructure.",
              },
              {
                heading: "Operational discipline is the foundation",
                text: "Unlike Type I, Type II requires consistent, documented, and auditable execution of controls across the review period. This means incident logs must be complete, access controls must be enforced consistently, change management must be documented, and monitoring must be continuous. Ad-hoc operations and manual processes will struggle to produce the evidence required for a clean Type II opinion.",
              },
              {
                heading: "Common findings that derail Type II audits",
                text: "The most common Type II failures involve incomplete incident records, inconsistent user access reviews, missing change approval documentation, and gaps in security monitoring. Each finding requires evidence remediation and may trigger auditor qualifications. Organizations with fragmented operations and manual coordination are at highest risk.",
              },
              {
                heading: "SOC 2 Type II and continuous compliance",
                text: "Unlike a one-time certification, Type II compliance is an ongoing operational discipline. Organizations must maintain audit-ready documentation year-round, not just in the weeks before the audit window. This requires embedding compliance controls directly into daily operational workflows — from incident handling to access provisioning to vendor management.",
              },
            ],
          },
          ovelonTitle: "How Ovelon Prime supports SOC 2 readiness",
          ovelonPoints: [
            "Structured incident intake and response workflows that produce complete, timestamped audit evidence for every security event",
            "Automated access control workflows that enforce consistent user provisioning and de-provisioning across systems",
            "Change management documentation that captures every modification with approval chains and timestamps",
            "Real-time operational monitoring aligned with SOC 2 Security and Availability criteria",
            "Standardized execution procedures that eliminate undocumented workarounds and informal processes",
            "Compliance reporting dashboards that provide continuous visibility into control effectiveness across the audit period",
          ],
        },
      },
    },
  },
  de: {

    backToTop: "Nach oben",
    explore: "Entdecken",

    common: {
    back: "Zurück",
    },
    nav: {
      services: "Leistungen",
      pricing: "Preise",
      about: "Über uns",
      contact: "Kontakt",
      cta: "Discovery Call",
      viewDemo: "Demo ansehen",
    },
    hero: {
      badge: "Ergebnisorientierte kritische Industrien",
      title: "Operative Komplexität untergräbt",
      titleHighlight: "Zuverlässigkeit und bricht die Ausführungsdisziplin",
      subtitle:
        "Wenn Koordination manuell wird, Wartung reaktiv erfolgt und Betriebsabläufe auf Workarounds basieren, ist die Kontrolle bereits verloren.",
      subtitle2:
        "Wir stellen vorhersehbare, disziplinierte Ausführung in laufenden Betrieben wieder her — ohne Kernsysteme zu ersetzen oder tägliche Arbeitsabläufe zu stören.",
      subtitleMobile: "Wenn Koordination manuell wird, ist die Kontrolle bereits verloren.",
      cta: "Kontrolle über den Betrieb zurückgewinnen",
      simulatorCta: "Bedarfs- & Ausführungsaufnahme",
      
    },
     problem: {
       badge: "Die Herausforderung",
       title: "Betriebliche Reibung kostet mehr als Sie denken.",
       description: "Jeder verpasste Anruf, jeder Buchungsfehler und jeder überforderte Mitarbeiter bedeutet verlorene Einnahmen und ein schlechteres Gästeerlebnis. Ihre Rezeption sollte nicht Ihr Engpass sein.",
       pain1: "Verpasste Anrufe zu Stoßzeiten",
       pain2: "Doppelbuchungen und Terminkonflkte",
       pain3: "Mitarbeiter überlastet mit Verwaltungsaufgaben",
       pain4: "Inkonsistente Gästekommunikation",
       pain5: "Umsatzverluste durch betriebliche Lücken",
     },
     process: {
       badge: "Der Prozess",
       title: "Nahtlose Integration in Ihre Abläufe.",
       step1: { title: "Erfassen", description: "Jeder Anruf, jede Nachricht und jede Buchungsanfrage wird präzise bearbeitet, sobald sie eingeht." },
       step2: { title: "Koordinieren", description: "Echtzeit-Abstimmung mit Ihrem Kalender und Arbeitsabläufen gewährleistet nahtlose Terminplanung." },
       step3: { title: "Bestätigen", description: "Automatische Bestätigungen, Updates und Eskalationen bei Bedarf — nichts fällt durch." },
     },
     workflowDemo: {
       badge: "Interaktive Demo",
       title: "Sehen Sie Ovelon Prime in Aktion.",
        tabs: { workflow: "Workflow-Automatisierung", call: "Anrufwiederherstellung", appointment: "Vorfallplanung", safety: "Sicherheitshandbücher" },
        workflow: {
          description: "Sehen Sie, wie eine einzelne Anfrage einen kompletten Betriebsworkflow automatisch auslöst.",
          runDemo: "WORKFLOW-DEMO STARTEN",
          complete: "Vollständiger Workflow in unter 60 Sekunden ausgeführt",
          reset: "DEMO ZURÜCKSETZEN",
          processing: "Workflow wird verarbeitet...",
          step1: { title: "Anfrage eingegangen", desc: "Alle eingehenden Anfragen oder Vorfälle werden in Echtzeit erfasst und protokolliert." },
          step2: { title: "Verfügbarkeit geprüft", desc: "System prüft Inventar, Kapazität oder Zeitplan zur sofortigen Machbarkeitsprüfung." },
          step3: { title: "Auftrag/Aufgabe erstellt", desc: "Anfragen werden in umsetzbare Aufträge oder Aufgaben umgewandelt." },
          step4: { title: "Bestätigung gesendet", desc: "Automatische Benachrichtigungen informieren alle Beteiligten per E-Mail, SMS oder Dashboard." },
          step5: { title: "Team benachrichtigt", desc: "Betriebsteams erhalten sofortige Alarme zur Bearbeitung oder Erfüllung." },
          step6: { title: "Profil & Historie aktualisiert", desc: "Alle Kunden-, Anlagen- oder Aufgabeninformationen werden automatisch erfasst." },
        },
        call: {
          description: "Sehen Sie, wie Ovelon Prime eine potenziell verpasste Gelegenheit in einen bestätigten Auftrag verwandelt.",
          simulate: "EINGEHENDEN ANRUF SIMULIEREN",
          stage1: "Eingehender Anruf...",
          stage2: "Ohne Ovelon Prime: Anruf verpasst, kein Auftrag",
          stage3: "Mit Ovelon Prime: Anruf sofort angenommen",
          stage4: "Kundenvorfall erfasst",
          stage5: "Vorfallsanfrage bestätigt",
          revenueLost: "Umsatz/Tracking verloren. Kunde unzufrieden.",
          revenueCaptured: "Umsatz/Tracking gesichert. Kunde zufrieden.",
        },
        appointment: {
          description: "Sehen Sie, wie eingehende Anfragen, Vorfälle und Eskalationen automatisch geplant, zugewiesen und nachverfolgt werden — ohne manuelle Koordination.",
          runDemo: "VORFALLPLANUNG STARTEN",
          complete: "Vollständiger Vorfallplanungszyklus in unter 60 Sekunden abgeschlossen",
          reset: "DEMO ZURÜCKSETZEN",
          processing: "Vorfall wird verarbeitet...",
          step1: { title: "Vorfall gemeldet", desc: "Ein Vorfall oder Serviceanfrage wird per Telefon, Formular oder automatischem Alarmsystem eingereicht." },
          step2: { title: "Priorität klassifiziert", desc: "System klassifiziert Schweregrad und Dringlichkeit basierend auf vordefinierten Eskalationsregeln." },
          step3: { title: "Einsatzteam zugewiesen", desc: "Qualifizierte Mitarbeiter werden basierend auf Verfügbarkeit und Expertise identifiziert und zugewiesen." },
          step4: { title: "Eskalationspfad aktiviert", desc: "Bei SLA-Überschreitungen werden Vorgesetzte und Management automatisch benachrichtigt." },
          step5: { title: "Lösung verfolgt", desc: "Fortschritt wird in Echtzeit überwacht mit Statusupdates an alle Beteiligten." },
          step6: { title: "Nachbericht erstellt", desc: "Vorfalldetails, Reaktionszeiten und Lösungsschritte werden für Compliance und Überprüfung dokumentiert." },
        },
        safety: {
          description: "Sehen Sie, wie standardisierte animierte Sicherheitsvideos in Industrielagern bereitgestellt werden — für konsistente HSE-Konformität.",
          runDemo: "SICHERHEITSHANDBUCH-DEMO STARTEN",
          complete: "Sicherheitshandbuch-Bereitstellung erfolgreich abgeschlossen",
          reset: "DEMO ZURÜCKSETZEN",
          processing: "Sicherheitsinhalte werden bereitgestellt...",
          step1: { title: "Gefährdung erkannt", desc: "Eine Arbeitsplatzgefährdung oder Verfahrensaktualisierung löst die Erstellung einer neuen visuellen Sicherheitsanweisung aus." },
          step2: { title: "Animiertes Handbuch erstellt", desc: "Schritt-für-Schritt animiertes Videomanual wird gemäß ISO/VCA/OSHA-Standards erstellt." },
          step3: { title: "Mehrsprachig lokalisiert", desc: "Inhalte werden automatisch übersetzt und für mehrsprachige Belegschaften lokalisiert." },
          step4: { title: "An Stationen verteilt", desc: "Videomanuals werden sofort an Lagerkioske, Tablets und Bedienergeräte übertragen." },
          step5: { title: "Bestätigung nachverfolgt", desc: "Jede Ansicht und jedes Verständnis der Bediener wird für Prüfpfad und Zertifizierung protokolliert." },
          step6: { title: "Compliance-Bericht erstellt", desc: "Automatisierte Compliance-Berichte zeigen die Schulungsabdeckung über alle Schichten." },
        },
      },
    video: {
      title: "Konzipiert für unterbrechungsfreien Betrieb",
      subtitle: "Sehen Sie, wie Ovelon Prime missionskritische Umgebungen unterstützt, ohne laufende Systeme zu stören.",
      cta: "Discovery Call",
    },
    services: {
      label: "WAS WIR STABILISIEREN",
      title: "Operative Zuverlässigkeit in Umgebungen",
      titleHighlight: "die sich keine Fehler leisten können",
      subtitle:
        "Wir beseitigen operative Ausfälle, die durch manuelle Koordination, unklare Ausführung und reaktive Störungsbehandlung verursacht werden — und stellen Kontrolle, Verantwortlichkeit und vorhersehbare Ergebnisse in laufenden Betrieben wieder her.",
      items: [
        {
          title: "Anfragen- & Bedarfsmanagement",
          description:
            "Eingehende Anfragen werden erfasst, validiert und konsistent weitergeleitet — ohne Abhängigkeit von Ad-hoc-Nachverfolgung oder individueller Einschätzung.",
          tags: ["Intelligente Aufnahmesteuerung", "Qualifizierungsregeln", "Systemweiterleitung"],
        },
        {
          title: "Sicherheits- & Ausführungsstandards",
          description:
            "Standardisierte visuelle Anweisungen zur Abstimmung von Bedienern, Auftragnehmern und Teams bei sicherheitskritischen Verfahren — zur Reduzierung von Interpretationsfehlern, Abweichungen und Ausführungsrisiken.",
          tags: [
            "HSE-Verfahren",
            "ISO / VCA / OSHA-Konformität",
            "Bediener- & Auftragnehmereinführung",
          ],
        },
        {
          title: "Störungsmanagement & Eskalation",
          description:
            "Probleme werden kontrolliert protokolliert, klassifiziert und eskaliert — um Verantwortlichkeit, Reaktionsklarheit und vorhersehbare Lösungswege zu gewährleisten.",
          tags: ["Problemklassifizierung", "Eskalationspfade", "Reaktionsverantwortung"],
        },
        {
          title: "Effizienz bei der Terminvereinbarung",
          description:
            "Nahtlose sprachgesteuerte Terminplanung, die Buchungen, Bestätigungen und Terminänderungen präzise abwickelt - entlastet Ihr Team, hält alle Verbindungen auf Kurs und sorgt für ein makelloses Kundenerlebnis.",
          tags: ["Sprachplanung", "24/7 Verfügbarkeit", "Automatische Rückrufantwort"],
        },
      ] as ServiceItem[],
      clickToExplore: "Klicken zum Erkunden",
    },
    serviceDetails: {
      keyBenefits: "Hauptvorteile",
      cta: "Demo erkunden",
      benefits: [
        [
          "Automatisierte Aufnahmevalidierung beseitigt manuelle Prüfungsengpässe",
          "Intelligentes Routing reduziert die Reaktionszeit um bis zu 60%",
          "Vollständiger Audit-Trail für Compliance und Verantwortlichkeit",
          "Nahtlose Integration in bestehende Ticketing-Systeme",
        ],
        [
          "Visuelle SOPs reduzieren Schulungszeit und Interpretationsfehler",
          "Echtzeit-Compliance-Überwachung über alle Schichten",
          "Auftragnehmer-Onboarding standortübergreifend standardisiert",
          "HSE-konforme Verfahren für regulatorische Audits bereit",
        ],
        [
          "Automatisierte Klassifizierung gewährleistet konsistente Problempriorisierung",
          "Klare Eskalationspfade mit definierter Verantwortung auf jeder Ebene",
          "Reaktions-SLAs automatisch verfolgt und durchgesetzt",
          "Post-Incident-Analyse zur kontinuierlichen Verbesserung",
        ],
        [
          "24/7-Verfügbarkeit ohne Personalaufstockung",
          "Intelligente Terminverschiebung minimiert No-Shows",
          "Multi-Kanal-Bestätigung (Sprache, SMS, E-Mail)",
          "Nahtlose Kalender-Synchronisation mit Ihren bestehenden Systemen",
        ],
      ],
    },
    sectors: {
      label: "Branchen",
      title: "Operative Zuverlässigkeit für",
      titleHighlight: "missionskritische Industrien",
      subtitle: "Wir unterstützen operative Umgebungen, in denen Ausfallzeiten, Fehlausrichtung oder verzögerte Reaktionen direkt Sicherheit, Kontinuität oder Leistung beeinträchtigen.",
      items: [
        {
          name: "Gebäudemanagement",
          description: "Verwaltung verteilter Anlagen und Serviceanfragen in komplexen Einrichtungen — mit geringer Toleranz für Verzögerungen oder Fehlkoordination.",
        },
        {
          name: "Wartung & Reparatur",
          description: "Betrieb unter ständigem Druck zur Vermeidung von Ausfallzeiten bei reaktiver und präventiver Wartung — oft mit unvollständigen Informationen und engen Reaktionsfenstern.",
        },
        {
          name: "Logistik & Lagerhaltung",
          description: "Koordination von Personal, Ausrüstung und Arbeitsabläufen unter strengen Zeitvorgaben und kontinuierlichem operativem Druck.",
        },
      ] as SectorItem[],
    },
    testimonials: {
      label: "KUNDENSTIMMEN",
      title: "Kundenstimmen",
      items: [
        {
          quote: "Vor Ovelon Prime hing die Schichtkoordination von individueller Nachverfolgung ab. Heute werden Wartung und Planung über alle Schichten hinweg vorhersehbar ausgeführt — ohne manuelle Eingriffe.",
          name: "Patrick Sullivan",
          title: "Leiter Logistikbetrieb",
          facility: "Distributionszentrum, Nebraska",
        },
        {
          quote: "In unserer Umgebung wirken sich Verzögerungen direkt auf Sicherheit und Leistung aus. Ovelon Prime hat uns geholfen, die Ausführung teamübergreifend zu standardisieren und die Reaktionszeit um 45% zu reduzieren, ohne das operative Risiko zu erhöhen.",
          name: "Alicia Zhang",
          title: "Betriebsleiterin",
          facility: "Lageranlage, Ottawa",
        },
        {
          quote: "Wir arbeiten unter ständigem Druck. Seit der Implementierung folgen Weiterleitung und Eskalation festen Regeln — nicht Annahmen. Ausfallzeiten durch Koordinationsfehler sind kein Faktor mehr.",
          name: "Wyatt Coleman",
          title: "Werksleiter",
          facility: "Produktionswerk, Austin",
        },
        {
          quote: "Eine Anlage dieser Größe zu verwalten bedeutet Tausende beweglicher Teile. Das System hat uns eine zentrale Informationsquelle für Aufgabenstatus, Gerätewartungszyklen und Compliance-Prüfungen gegeben. Wir haben aufgehört, Updates hinterherzulaufen, und begonnen, danach zu handeln.",
          name: "James Whitfield",
          title: "Direktor für Gebäudebetrieb",
          facility: "Industrielager, Liverpool",  
        },
        {
          quote: "In unserem Umfeld eskalieren selbst kleine Verzögerungen schnell. Die Plattform sorgt für Echtzeit-Verantwortlichkeit in jeder Abteilung. Wir haben die Grauzonen beseitigt, die die Produktion früher stundenlang aufgehalten haben.",
          name: "Ciarán O'Sullivan",
          title: "Leitender Betriebsleiter",
          facility: "Verarbeitungsanlage, Tennessee",
        },
        {
          quote: "Wir haben jede Woche Stunden durch manuelle Disposition und fehlgeleitete Arbeitsaufträge verloren. Seit der Einführung folgen unsere Teams durchgängig strukturierten Arbeitsabläufen — kein Rätselraten, keine Engpässe. Der Durchsatz ist gestiegen und unsere Fehlerquote hat sich stabilisiert.",
          name: "Rachel Moreno",
          title: "Leiterin Betriebssteuerung",
          facility: "Logistikzentrum, Dublin",
        }
      ] as TestimonialItem[],
    },
    operationalTrust: {
      heading: "Konzipiert für regulierte & sicherheitskritische Umgebungen",
      items: [
        { text: "ISO 27001-konforme Sicherheitsprinzipien" },
        { text: "ISO 9001-orientierte Prozesskontrolle" },
        { text: "HSE-bewusstes operatives Design" },
        { text: "Prüfungsbereite Dokumentation & Kontrollen" },
        { text: "Vollständige Kundenhoheit — keine Black-Box-Systeme" },
      ] as TrustItem[],
      footer: "Konzipiert für CISO-geprüfte Umgebungen mit interner Sicherheitsgovernance, Zugriffskontrollrichtlinien und Change-Management-Verfahren.",
    },
    about: {
      label: "Über uns",
      title: "Aufgebaut auf operativer Expertise,",
      titleHighlight: "angetrieben von Präzision",
      description1:
        "Ovelon Prime arbeitet mit Organisationen, die unter ständigem Druck operieren — wo Ausfallzeiten, Verzögerungen und manuelle Prozesse direkt Kosten, Sicherheit und Zuverlässigkeit beeinflussen.",
      description2:
        "Wir kombinieren tiefes operatives Fachwissen mit operativen Kontrollebenen, um Systeme zu entwerfen, die sich nahtlos in bestehende Arbeitsabläufe integrieren, ohne kritische Abläufe zu stören.",
      quote:
        '"Präzision ist keine Option. In Umgebungen, in denen Zuverlässigkeit Ergebnisse bestimmt, müssen Systeme so konstruiert sein, dass sie unter Druck funktionieren."',
      quoteAuthor: "Führungsteam · Ovelon Prime",
      stats: {
        uptime: "Operative Systemverfügbarkeit",
        deployments: "Automatisierungsbereitstellungen",
        industries: "Kritische Branchen bedient",
      },
    },
    pricing: {
      label: "Preise",
      title: "Engagement-basierte Preisgestaltung mit klar definierten Ergebnissen",
      subtitle:
        "Klar abgegrenzte Engagements mit Fokus auf Zuverlässigkeitssteigerung, Reduzierung operativer Reibungsverluste und messbarer Wirkung.",
      focused: {
        name: "Fokussiertes operatives Engagement",
        description:
          "Ein fest umrissenes Engagement zur Lösung eines klar definierten operativen Engpasses mit messbarer Wirkung.",
        requestQuote: "Angebot anfordern",
        cta: "Engagement-Bewertung anfordern",
        features: [
          "Einzelne, klar definierte operative Verbesserung",
          "Prozess- & Verantwortlichkeitszuordnung",
          "Kontrollierte Implementierung & Einführung",
          "Dokumentation & operative Übergabe",
          "Validierung nach der Implementierung",
        ],
      },
      enterprise: {
        name: "Enterprise-Betriebsprogramm",
        description:
          "Maßgeschneiderte operative Systeme für komplexe Organisationen mit strengen Governance-, Compliance- und Kontinuitätsanforderungen.",
        priceLabel: "Individuelle Preisgestaltung",
        price: "Individuelle Leistung",
        period: "einmalige Gebühr",
        cta: "Erstbewertung anfordern",
        badge: "Enterprise",
        features: [
          "Multi-System-operative Architektur",
          "Kundenspezifische Integrationen über bestehende Systeme",
          "Dedizierte Implementierungs- & Übergangsunterstützung",
          "Compliance-bewusstes operatives Design",
          "Kundenspezifisches SLA- & Governance-Framework",
        ],
      },
      securityNote:
        "Jedes Engagement wird mit Enterprise-Sicherheit, Datenschutz und operativer Compliance erstellt.",
      talkToExpert: "Discovery Call",
      intakeNote:
        "Strukturierte Aufnahme · Kein Verkaufsdruck · Direkt mit einem erfahrenen Experten",
      reviewNote: "Alle Anfragen werden vor der Terminplanung geprüft.",
    },
    contact: {
      label: "ENGAGEMENT",
      title: "Bereit, die Kontrolle über",
      titleHighlight: " Ihre Betriebsabläufe zurückzugewinnen?",
      subtitle: "Alle Engagements werden im Voraus festgelegt und unterliegen einer formellen Prüfung vor der Fortsetzung.",
      cta: "Beratung vereinbaren",
      guarantee: "Keine langfristigen Verpflichtungen.",
      guaranteeHighlight: "Engagements werden nur fortgesetzt, wenn operativer Mehrwert nachgewiesen wird",
      guaranteeSuffix: " bei qualifizierenden Engagements.",
      enterpriseLabel: "Enterprise-Anfragen: ",
      rightTitle: "Kontaktieren Sie Ovelon Prime",
      infoText1: "Wenn Sie Fragen haben oder besprechen möchten, wie wir Ihr Unternehmen unterstützen können, zögern Sie bitte nicht, uns zu kontaktieren.",
      infoText2: "Der Kundensupport ist rund um die Uhr erreichbar.",
    },
    contactForm: {
      firstName: "Vorname",
      firstNamePlaceholder: "Max",
      lastName: "Nachname",
      lastNamePlaceholder: "Mustermann",
      businessName: "Firmenname",
      businessNamePlaceholder: "Muster GmbH",
      businessEmail: "Geschäftliche E-Mail",
      businessEmailPlaceholder: "max@musterfirma.de",
      requestDetails: "Anfrage-Details",
      requestDetailsPlaceholder: "Beschreiben Sie Ihre operativen Herausforderungen und Ziele...",
      submit: "Anfrage absenden",
      submitting: "Wird gesendet...",
      validationError: "Bitte füllen Sie alle Pflichtfelder korrekt aus.",
      successTitle: "Erfolgreich gesendet",
      successMessage: "Wir werden Ihre Anfrage sorgfältig prüfen.",
      successCta: "Strategische Beratung buchen",
    },
    faq: {
      title: "Häufig gestellte Fragen",
      category: "Engagement & Lieferung",
      items: [
        {
          question: "Wie funktioniert Ovelon Prime?",
          answer:
            "Wir beginnen mit einer strukturierten Aufnahme, um die operative Einschränkung zu verstehen. Wir entwerfen und validieren eine fest umrissene Lösung anhand realer Arbeitsabläufe. Wir implementieren, testen unter Last und übergeben mit vollständiger Dokumentation.",
        },
        {
          question: "Auf welche Branchen sind Sie spezialisiert?",
          answer:
            "Wir arbeiten mit Organisationen in missionskritischen Umgebungen, einschließlich Fertigung, Logistik und Gebäudemanagement. Wenn Verzögerungen, Fehler oder Ausfallzeiten direkt Sicherheit, Leistung oder Kontinuität beeinflussen, sind wir der richtige Partner.",
        },
        {
          question: "Ist dies ein Abonnement oder ein laufender Service?",
          answer:
            "Nein. Engagements haben einen festen Umfang und sind ergebnisorientiert. Es gibt kein obligatorisches Abonnement, keine Lizenzabhängigkeit und keine laufenden Gebühren. Nach der Lieferung gehört das System Ihnen.",
        },
        {
          question: "Wie lange dauert eine typische Implementierung?",
          answer:
            "Die meisten fokussierten Engagements werden innerhalb von 12-16 Tagen geliefert, abhängig von Umfang und Systemzugang. Enterprise-Programme folgen einem phasenweisen Rollout, der auf operative Prioritäten abgestimmt ist.",
        },
        {
          question: "Wird dies mit unseren bestehenden Systemen integriert?",
          answer:
            "Ja. Lösungen sind so konzipiert, dass sie sich in bestehende Systeme integrieren, ohne Kernplattformen zu ersetzen. Wir arbeiten innerhalb Ihrer aktuellen Tools und Datengrenzen.",
        },
        {
          question: "Müssen wir unsere aktuellen Arbeitsabläufe ändern?",
          answer:
            "Nein. Wir entwerfen um Ihre bestehenden Arbeitsabläufe herum. Wo Änderungen erforderlich sind, sind sie minimal, dokumentiert und werden mit Ihren operativen Teams vor der Bereitstellung validiert.",
        },
        {
          question: "Wem gehört die Optimierung nach der Lieferung?",
          answer:
            "Ihnen. Alle Systeme, Konfigurationen und Dokumentationen werden nach Abschluss übergeben. Es gibt keine Anbieterabhängigkeit. Sie behalten die vollständige operative und technische Eigentümerschaft.",
        },
        {
          question: "Wie gehen Sie mit Sicherheit und Compliance um?",
          answer:
            "Sicherheit und Compliance werden als Designvorgaben behandelt, nicht als Nachgedanken. Jedes Engagement wird um Ihre bestehenden Sicherheitsrichtlinien, Zugriffskontrollen und Compliance-Anforderungen herum gestaltet. Wir arbeiten innerhalb Ihrer genehmigten Infrastruktur, Datengrenzen und Governance-Frameworks. Es werden keine Daten über den vereinbarten Umfang hinaus extrahiert oder wiederverwendet, und alle Systeme werden mit vollständiger Dokumentation geliefert, um interne Audits, Risikobewertungen und Übergaben zu unterstützen.",
        },
        {
          question: "Welches Maß an interner Beteiligung ist erforderlich?",
          answer:
            "Minimale, strukturierte Beteiligung. Wir benötigen begrenzte Eingaben während der Aufnahme, um operative Einschränkungen zu verstehen und Annahmen zu validieren. Die Implementierung erfolgt unabhängig, mit klaren Prüfpunkten für Überprüfung und Genehmigung. Operative Teams werden nicht gestört, und nach der Lieferung sind keine laufenden internen Ressourcen erforderlich.",
        },
        {
          question: "Ist Ovelon Prime für große Unternehmensumgebungen geeignet?",
          answer:
            "Ja. Wir sind für komplexe Multi-Stakeholder-Umgebungen mit strengen Governance-, Compliance- und operativen Anforderungen konzipiert. Engagements sind so strukturiert, dass sie sich an Enterprise-Change-Management, Sicherheitsüberprüfungen und phasenweise Rollouts anpassen — ohne Plattformersetzungen zu erzwingen oder kritische Systeme zu stören.",
        },
      ] as FAQItem[],
    },
    metricsStrip: {
      items: [
        { value: "40%", label: "Weniger Ausfallzeiten" },
        { value: "200+", label: "Automatisierte Workflows" },
        { value: "3", label: "Bediente Kontinente" },
        { value: "98%", label: "SLA-Erfüllungsrate" },
      ],
    },
    footer: {
      privacy: "Datenschutz",
      terms: "AGB",
      copyright: "© {{year}} Ovelon Prime. Alle Rechte vorbehalten.",
      tagline: "Enterprise-Betriebssysteme für Klarheit, Kontrolle und Skalierbarkeit.",
      trustHeading: "Konzipiert für regulierte & sicherheitskritische Umgebungen",
      trustItems: [
        "ISO 27001-konforme Sicherheitsprinzipien",
        "SOC 2 Typ II-konforme Kontrollen",
        "NIS2-Richtlinie Bereitschaft",
        "Prüfungsfertige Dokumentation & Kontrollen",
        "HSE-bewusstes operatives Design",
        "Vollständige Kundeneigenverantwortung — keine Black-Box-Systeme",
      ],
      badgeIsoTitle: "ISO 27001",
      badgeIsoDesc: "Zertifiziertes Informationssicherheitsmanagement",
      badgeSocTitle: "SOC 2 Type II",
      badgeSocDesc: "Kontrollen für Dienstleistungsorganisationen",
      badgeGdprTitle: "DSGVO",
      badgeGdprDesc: "Datenschutz-Grundverordnung",
    },
    industrialTrust: {
      heading: "Vertrauenswürdig für Betreiber in missionskritischen Umgebungen",
      disclaimer: "Repräsentative Betreiber aus Industrie-, Logistik-, Fertigungs- und Infrastruktursektoren. Engagements variieren nach Umfang und Umgebung.",
    },
    intake: {
      title: "Beratungsqualifizierung",
      subtitle: "Bitte füllen Sie das kurze Formular unten aus. Alle Einsendungen werden vor der Terminplanung geprüft.",
    },
    cookie: {
      message: "Wir verwenden Cookies, um Ihre Erfahrung zu verbessern. Durch die weitere Nutzung dieser Website stimmen Sie der Verwendung von Cookies zu.",
      accept: "Akzeptieren",
      decline: "Ablehnen",
      learnMore: "Mehr über unsere Datenschutzrichtlinie erfahren",
    },
    privacy: {
      title: "Datenschutzerklärung",
      lastUpdated: "Zuletzt aktualisiert: 01.01.2026",
      sections: {
        introduction: {
          title: "1. Einleitung",
          p1: "Bei Ovelon Prime nehmen wir Datenschutz und Vertraulichkeit ernst. Diese Datenschutzerklärung erklärt, wie wir persönliche und geschäftliche Informationen erfassen, verwenden und schützen, wenn Sie mit unserer Website, Aufnahmeformularen und Dienstleistungen interagieren.",
          p2: "Wir verarbeiten Daten verantwortungsvoll und nur in dem Umfang, der für die Erbringung unserer Dienstleistungen, die Gewährleistung der operativen Integrität und die Erfüllung gesetzlicher Verpflichtungen erforderlich ist.",
        },
        informationCollected: {
          title: "2. Erfasste Informationen",
          personal: "Persönliche Informationen:",
          personalDesc: "Name, geschäftliche E-Mail-Adresse, Telefonnummer und über Formulare oder direkte Korrespondenz übermittelte Informationen.",
          business: "Geschäfts- & Betriebsinformationen:",
          businessDesc: "Firmenname, Geschäftsadresse oder Rechtsordnung, Branchenklassifizierung, operativer Kontext und freiwillig während der Aufnahme bereitgestellte Compliance-bezogene Informationen.",
          technical: "Technische Informationen:",
          technicalDesc: "IP-Adresse, Browser- und Gerätemetadaten, Cookies und grundlegende Nutzungsanalysen.",
          note: "Wir erfassen keine sensiblen personenbezogenen Daten, es sei denn, dies ist ausdrücklich für Compliance oder Serviceerbringung erforderlich.",
        },
        howWeUse: {
          title: "3. Wie wir Ihre Informationen verwenden",
          items: [
            "Bewertung und Qualifizierung von Beratungsanfragen",
            "Bereitstellung und Unterstützung von Automatisierungsdiensten",
            "Kommunikation bezüglich Engagements, Updates oder Klärungen",
            "Aufrechterhaltung von Plattformzuverlässigkeit, Sicherheit und Leistung",
            "Erfüllung gesetzlicher, regulatorischer oder vertraglicher Verpflichtungen",
          ],
          note: "Wir verwenden Ihre Daten nicht für unaufgeforderte Marketingzwecke, Profiling oder Werbung durch Dritte.",
        },
        dataSharing: {
          title: "4. Datenweitergabe & Offenlegung",
          p1: "Ovelon Prime verkauft, vermietet oder handelt nicht mit persönlichen oder geschäftlichen Daten.",
          p2: "Informationen werden nur dann weitergegeben, wenn dies unbedingt erforderlich ist:",
          items: [
            "An geprüfte Dienstleister, die für die Serviceerbringung erforderlich sind",
            "Zur Erfüllung gesetzlicher oder regulatorischer Verpflichtungen",
            "Zum Schutz der Sicherheit, Integrität oder Rechte unserer Systeme und Kunden",
          ],
          note: "Daten werden niemals für Marketing- oder Werbezwecke Dritter weitergegeben.",
        },
        dataSecurity: {
          title: "5. Datensicherheit",
          p1: "Wir wenden branchenübliche technische und organisatorische Schutzmaßnahmen an, einschließlich Verschlüsselung, Zugriffskontrollen und sicherer Infrastrukturpraktiken.",
          p2: "Obwohl kein System absolute Sicherheit garantieren kann, arbeiten wir nach strengen internen Protokollen, die darauf ausgelegt sind, Risiken zu minimieren und die Vertraulichkeit jederzeit zu schützen.",
        },
        dataRetention: {
          title: "6. Datenspeicherung & Ihre Rechte",
          p1: "Wir speichern Daten nur so lange, wie es zur Erfüllung der in dieser Richtlinie dargelegten Zwecke oder zur Erfüllung gesetzlicher Anforderungen erforderlich ist.",
          p2: "Sie haben das Recht, Zugang, Berichtigung oder Löschung Ihrer Daten zu verlangen, sofern zutreffend.",
        },
        contact: {
          title: "7. Kontakt",
          p1: "Bei Fragen zu dieser Datenschutzerklärung oder zu Datenverarbeitungspraktiken wenden Sie sich bitte an:",
        },
      },
    },
    terms: {
      title: "Allgemeine Geschäftsbedingungen",
      lastUpdated: "Zuletzt aktualisiert: 01. Dezember 2025",
      sections: {
        introduction: {
          title: "1. Einleitung",
          p1: "Diese Allgemeinen Geschäftsbedingungen (\"AGB\") regeln Ihren Zugang zu und die Nutzung der Ovelon Prime-Website, der Beratungsprozesse und der professionellen Automatisierungsdienste. Durch den Zugriff auf die Website oder die Inanspruchnahme unserer Dienste erklären Sie sich mit diesen AGB einverstanden.",
          p2: "Wenn Sie mit diesen AGB nicht einverstanden sind, müssen Sie die Nutzung der Website und der Dienste unverzüglich einstellen.",
        },
        services: {
          title: "2. Erbrachte Dienstleistungen",
          p1: "Ovelon Prime bietet professionelle Automatisierung, Systemdesign und Implementierungsdienste für industrielle, logistische, Facility-Management-, Infrastruktur- und andere betriebskritische Umgebungen.",
          p2: "Alle Dienstleistungen werden auf Projekt- oder Engagement-Basis erbracht. Spezifischer Umfang, Liefergegenstände, Annahmen und Zeitpläne werden pro Engagement definiert und können sich basierend auf operativen Anforderungen entwickeln.",
        },
        websiteUse: {
          title: "3. Nutzung der Website",
          items: [
            "Sie dürfen die Website nicht für rechtswidrige oder betrügerische Zwecke nutzen.",
            "Sie dürfen nicht versuchen, unbefugten Zugang zu Systemen oder Daten zu erlangen.",
            "Sie dürfen die Website-Funktionalität oder Sicherheitsmechanismen nicht scrapen, reverse-engineeren, stören oder beeinträchtigen.",
          ],
        },
        clientResponsibilities: {
          title: "4. Kundenpflichten",
          items: [
            "Bereitstellung genauer, vollständiger und aktueller Geschäftsinformationen.",
            "Gewährleistung des rechtzeitigen Zugangs zu Systemen, Daten und Stakeholdern, wenn dies für die Lieferung erforderlich ist.",
            "Aufrechterhaltung interner Sicherheitskontrollen, Anmeldedatenverwaltung und Zugangsgovernance.",
            "Nutzung gelieferter Systeme in Übereinstimmung mit geltenden Gesetzen, Vorschriften und internen Richtlinien.",
          ],
        },
        payments: {
          title: "5. Zahlungen & Abrechnung",
          p1: "Sofern nicht schriftlich anders vereinbart, ist die Zahlung vor Beginn der Dienstleistungen erforderlich. Kundenspezifische Engagements erfordern einen unterzeichneten Vorschlag und einen gegenseitig vereinbarten Zahlungsplan.",
          p2: "Gebühren sind nach Arbeitsbeginn nicht erstattungsfähig, es sei denn, dies ist ausdrücklich in einer schriftlichen Vereinbarung anders angegeben.",
        },
        noGuarantee: {
          title: "6. Keine Ergebnisgarantie",
          p1: "Obwohl Ovelon Prime Systeme mit Blick auf Zuverlässigkeit, Leistung und operative Effizienz entwirft, werden keine Garantien für spezifische operative, finanzielle oder leistungsbezogene Ergebnisse gegeben.",
        },
        ip: {
          title: "7. Geistiges Eigentum",
          p1: "Sofern nicht schriftlich anders vereinbart, verbleiben alle von Ovelon Prime entwickelten Methoden, Architekturen, Workflows, Dokumentationen, Diagramme und technischen Materialien in unserem geistigen Eigentum.",
          p2: "Kunden wird eine nicht übertragbare, nicht-exklusive Lizenz nur für den internen Geschäftsgebrauch gewährt.",
        },
        confidentiality: {
          title: "8. Vertraulichkeit",
          p1: "Beide Parteien verpflichten sich, alle nicht-öffentlichen geschäftlichen, technischen, operativen und kommerziellen Informationen, die während eines Engagements ausgetauscht werden, streng vertraulich zu behandeln.",
        },
        liability: {
          title: "9. Haftungsbeschränkung",
          p1: "Im maximal gesetzlich zulässigen Umfang haftet Ovelon Prime nicht für indirekte, zufällige, besondere oder Folgeschäden.",
          p2: "Die Gesamthaftung darf die Gesamtgebühren für den spezifischen Service, der den Anspruch begründet, nicht übersteigen.",
        },
        thirdParty: {
          title: "10. Drittanbieter-Tools & Integrationen",
          p1: "Dienstleistungen können Integrationen mit Drittanbieterplattformen oder -tools beinhalten. Ovelon Prime ist nicht verantwortlich für Ausfälle, Änderungen oder Richtlinienentscheidungen Dritter.",
        },
        termination: {
          title: "11. Kündigung",
          p1: "Wir behalten uns das Recht vor, Dienstleistungen bei Nichtzahlung, Missbrauch oder wesentlicher Verletzung dieser AGB auszusetzen oder zu beenden.",
        },
        governingLaw: {
          title: "12. Anwendbares Recht",
          p1: "Diese AGB unterliegen den Gesetzen der Rechtsordnung, in der Ovelon Prime rechtlich ansässig ist, ohne Berücksichtigung von Kollisionsnormen.",
        },
        changes: {
          title: "13. Änderungen dieser AGB",
          p1: "Wir können diese AGB von Zeit zu Zeit aktualisieren. Die fortgesetzte Nutzung der Website oder der Dienste gilt als Annahme der aktualisierten AGB.",
        },
        contactInfo: {
          title: "14. Kontaktinformationen",
          p1: "Bei Fragen zu diesen AGB wenden Sie sich bitte an:",
        },
      },
    },
    demo: {
      header: "Operative Kompromissanalyse",
      close: "Schließen",
      beforeAlignment: "Vor der Ausrichtung",
      afterAlignment: "Nach der Ausrichtung",
      tradeoff: "Kompromiss",
      footer: "Repräsentative Betriebsszenarien aus Industrie-, Logistik- und Gebäudeumgebungen.",
      intake: {
        title: "Arbeitsaufnahme & Bedarfssteuerung",
        metrics: {
          coordinationTime: "Koordinationszeit",
          reworkCycles: "Nacharbeitszyklen",
          ownership: "Verantwortung",
          ownershipValue: "Vorab definiert",
        },
        before: {
          title: "Fragmentierte Aufnahme und reaktive Koordination",
          text: "Arbeit kommt über E-Mails, Anrufe und informelle Kanäle. Informationsqualität variiert und Verantwortung wechselt während der Ausführung.",
          bullet1: "Mehrere Aufnahmepfade",
          bullet2: "Manuelle Klärungsschleifen",
          bullet3: "Priorität durch Dringlichkeit",
          bullet4: "Ausführung hängt von Einzelpersonen ab",
          tradeoff: "Zeitverlust durch Koordination und Korrektur.",
        },
        after: {
          title: "Strukturierte Aufnahme mit kontrollierter Ausführung",
          text: "Anfragen folgen einem einzigen Aufnahmepfad. Informationen werden vorab validiert und Verantwortung wird vor Beginn der Ausführung zugewiesen.",
          bullet1: "Einzelne strukturierte Aufnahme",
          bullet2: "Klare Klassifizierung und Weiterleitung",
          bullet3: "Verantwortung einmal zugewiesen",
          bullet4: "Vorhersehbarer Ausführungsablauf",
          tradeoff: "Koordination sinkt, Ausführung stabilisiert sich.",
        },
      },
      safety: {
        title: "Sicherheits- & Ausführungsstandards",
        metrics: {
          procedureDeviations: "Verfahrensabweichungen",
          onboardingTime: "Einarbeitungszeit",
          consistency: "Konsistenz",
          consistencyValue: "Standardisiert",
        },
        before: {
          title: "Interpretationsbasierte Ausführung",
          text: "Sicherheitsverfahren existieren in Dokumenten und Erfahrungswissen. Bediener interpretieren Standards unterschiedlich.",
          bullet1: "Inkonsistente Verfahren",
          bullet2: "Hohe Abhängigkeit von erfahrenen Mitarbeitern",
          bullet3: "Auftragnehmer informell eingearbeitet",
          bullet4: "Audit-Risiko",
          tradeoff: "Sicherheit hängt von Erfahrung ab, nicht von Struktur.",
        },
        after: {
          title: "Standardisierte Ausführung mit gemeinsamen Verständnis",
          text: "Ausführungsstandards sind klar, visuell und einheitlich über Teams und Auftragnehmergruppen hinweg.",
          bullet1: "Einheitliche Ausführungsstandards",
          bullet2: "Schnellere Einarbeitung",
          bullet3: "Reduzierte Interpretationsfehler",
          bullet4: "Abgestimmtes Sicherheitsframework",
          tradeoff: "Sicherheit wird wiederholbar und prüfbar.",
        },
      },
      incident: {
        title: "Vorfallbehandlung & Eskalation",
        metrics: {
          escalationLoops: "Eskalationsschleifen",
          responseTime: "Reaktionszeit",
          accountability: "Verantwortlichkeit",
          accountabilityValue: "Explizit",
        },
        before: {
          title: "Reaktive Vorfallbehandlung",
          text: "Vorfälle werden inkonsistent gemeldet und manuell eskaliert. Verantwortung ist unklar und Sichtbarkeit begrenzt.",
          bullet1: "Unklare Eskalationsschwellen",
          bullet2: "Verzögerte Übergaben",
          bullet3: "Parallele Kommunikationsstränge",
          bullet4: "Begrenzte Management-Sichtbarkeit",
          tradeoff: "Vorfälle binden Aufmerksamkeit statt gelöst zu werden.",
        },
        after: {
          title: "Kontrollierte Eskalation mit klarer Verantwortlichkeit",
          text: "Vorfälle werden protokolliert, klassifiziert und über vordefinierte Pfade eskaliert. Jeder Schritt hat einen klaren Verantwortlichen und ein Reaktionszeitfenster.",
          bullet1: "Definierte Eskalationsstufen",
          bullet2: "Klare Verantwortung pro Vorfall",
          bullet3: "Vorhersehbare Reaktionszeiten",
          bullet4: "Volle operative Sichtbarkeit",
          tradeoff: "Vorfälle werden systematisch gelöst.",
        },
      },
      appointment: {
        title: "Terminplanungs-Effizienz",
        metrics: {
          schedulingTime: "Planungszeit",
          noShows: "Nichterscheinen",
          utilization: "Auslastung",
          utilizationValue: "Maximiert",
        },
        before: {
          title: "Manuelle Terminplanung mit ständiger Reibung",
          text: "Termine werden per Telefon, E-Mail und Tabellen koordiniert. Bestätigungen sind inkonsistent, Nichterscheinen ist häufig und Terminüberschneidungen belasten die operative Kapazität.",
          bullet1: "Telefonische Terminplanung nur während der Geschäftszeiten",
          bullet2: "Manuelle Verfügbarkeitsprüfungen im Kalender",
          bullet3: "Keine automatisierten Erinnerungen oder Bestätigungen",
          bullet4: "Häufige Doppelbuchungen und Konflikte",
          tradeoff: "Mitarbeiterzeit wird durch Terminplanung statt Betrieb verbraucht.",
        },
        after: {
          title: "Automatisierte Terminplanung mit vorhersagbarer Kapazität",
          text: "Termine werden automatisch über Sprach- und Digitalkanäle erfasst und bestätigt. Echtzeit-Verfügbarkeitsprüfungen, automatisierte Erinnerungen und intelligente Umplanung beseitigen Reibung.",
          bullet1: "24/7 Sprach- und digitale Terminannahme",
          bullet2: "Echtzeit-Kalendersynchronisation und Konfliktprävention",
          bullet3: "Automatisierte Bestätigungen und Erinnerungssequenzen",
          bullet4: "Selbstbedienungs-Umplanung mit Kapazitätsoptimierung",
          tradeoff: "Terminplanung wird reibungslos, die Auslastung steigt.",
        },
      },
    },
    simulator: {
      title: "Bedarfs- & Ausführungsaufnahme",
      close: "Schließen",
      back: "Zurück",
      next: "Weiter",
      requestReview: "Operative Bewertung anfordern",
      submitting: "Wird gesendet...",
      submitError: "Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.",
      submitErrorTitle: "Übermittlung fehlgeschlagen",
      validationErrorTitle: "Pflichtfelder fehlen",
      validationErrorDescription: "Bitte füllen Sie alle Pflichtfelder aus, bevor Sie absenden.",
      stepIndicator: "Schritt {{current}} von {{total}}",
      step1: {
        title: "Operativer Kontext",
        description: "Beginnen wir damit, Ihre operative Umgebung zu verstehen. Wählen Sie die Optionen, die Ihre Organisation am besten beschreiben.",
        industryLabel: "In welcher Branche sind Sie tätig?",
        operationLabel: "Welche Art von Operationen führen Sie durch?",
        operationHint: "Wählen Sie alle zutreffenden aus",
        scaleLabel: "Wie groß ist der Umfang Ihrer Operationen?",
      },
      step2: {
        title: "Aktuelles Betriebsmodell",
        description: "Helfen Sie uns zu verstehen, wie Sie heute den täglichen Betrieb handhaben — ohne Bewertung, nur Klarheit.",
        handlingLabel: "Wie werden Anfragen, Aufgaben oder Vorfälle derzeit bearbeitet?",
        handlingHint: "Wählen Sie alle verwendeten Methoden",
        structureLabel: "Wie strukturiert sind Ihre Abläufe?",
        toolsLabel: "Welche Werkzeuge oder Systeme werden verwendet?",
      },
      step3: {
        title: "Operative Hürden & Schmerzpunkte",
        description: "Identifizieren Sie, wo die Arbeit heute verlangsamt wird oder unvorhersehbar wird.",
        frictionLabel: "Was verursacht Reibung in Ihren Abläufen?",
        frictionHint: "Wählen Sie alle zutreffenden aus",
        notesLabel: "Zusätzlicher Kontext (optional)",
        notesPlaceholder: "Beschreiben Sie spezifische Herausforderungen oder Kontext...",
      },
      step4: {
        title: "Einschränkungen & Anforderungen",
        description: "Definieren Sie die Grenzen und Anforderungen, die Ihre Abläufe beeinflussen.",
        downtimeLabel: "Wie empfindlich sind Ihre Abläufe gegenüber Ausfallzeiten?",
        complianceLabel: "Welche Sicherheits- oder Compliance-Frameworks gelten?",
        complianceHint: "Wählen Sie alle zutreffenden aus",
        complexityLabel: "Wie komplex ist die interne Koordination?",
      },
      step5: {
        title: "Zusammenfassung & Reflexion",
        description: "Hier ist eine Zusammenfassung dessen, was Sie geteilt haben — ein klares Bild Ihrer aktuellen operativen Realität.",
        noData: "Keine Daten anzuzeigen. Gehen Sie zurück und vervollständigen Sie vorherige Schritte.",
        contextTitle: "Operativer Kontext",
        modelTitle: "Aktuelles Betriebsmodell",
        hurdlesTitle: "Identifizierte Hürden",
        constraintsTitle: "Einschränkungen & Anforderungen",
        industry: "Branche",
        operations: "Operationen",
        scale: "Umfang",
        handling: "Anfragebearbeitung",
        structure: "Strukturebene",
        tools: "Werkzeuge",
        downtime: "Ausfallempfindlichkeit",
        compliance: "Compliance",
        complexity: "Koordinationskomplexität",
        disclaimer: "Diese Simulation ist indikativ und spiegelt vom Benutzer bereitgestellte Informationen wider. Sie ersetzt keine professionelle Bewertung. Alle Erkenntnisse unterliegen einer formellen Überprüfung.",
      },
      industries: {
        manufacturing: "Fertigung",
        logistics: "Logistik",
        facility: "Gebäudemanagement",
        infrastructure: "Infrastruktur",
        energy: "Energie & Versorgung",
        other: "Andere",
      },
      operationTypes: {
        facility: "Gebäudebetrieb",
        logistics: "Logistik & Lagerhaltung",
        maintenance: "Wartung & Reparatur",
        production: "Produktionslinien",
        fieldService: "Außendienst",
      },
      scales: {
        single: "Einzelstandort",
        multi: "Mehrere Standorte",
        distributed: "Verteilt / Regional",
      },
      handlingMethods: {
        email: "E-Mail",
        phone: "Telefonanrufe",
        tickets: "Ticketsystem",
        verbal: "Mündliche Übergaben",
        spreadsheets: "Tabellenkalkulationen",
        erp: "ERP / CMMS-System",
      },
      structureLevels: {
        adhoc: {
          title: "Überwiegend Ad-Hoc",
          desc: "Arbeit wird bearbeitet, wie sie anfällt, minimale Standardisierung",
        },
        semiStructured: {
          title: "Teilweise strukturiert",
          desc: "Einige Prozesse definiert, aber Ausführung variiert",
        },
        fullyStructured: {
          title: "Vollständig strukturiert",
          desc: "Klare Prozesse mit konsistenter Ausführung",
        },
      },
      toolTypes: {
        manual: "Überwiegend manuell",
        semiAutomated: "Teilweise automatisiert",
        fragmented: "Fragmentierte Systeme",
        integrated: "Integrierte Plattform",
      },
      frictionPoints: {
        slowResponse: "Langsame Reaktionszeiten",
        unclearOwnership: "Unklare Aufgabenverantwortung",
        manualCoordination: "Übermäßige manuelle Koordination",
        inconsistentExecution: "Inkonsistente Ausführung",
        lackVisibility: "Mangel an Echtzeit-Sichtbarkeit",
        escalationDelays: "Eskalationsverzögerungen",
        documentationGaps: "Dokumentationslücken",
        communicationBreakdown: "Kommunikationszusammenbruch",
      },
      downtimeLevels: {
        critical: {
          title: "Kritisch",
          desc: "Jede Minute Ausfallzeit kostet erheblich",
        },
        high: {
          title: "Hoch",
          desc: "Ausfallzeiten beeinträchtigen den Betrieb ernsthaft",
        },
        moderate: {
          title: "Moderat",
          desc: "Gewisse Toleranz, aber beeinträchtigt Produktivität",
        },
        low: {
          title: "Niedrig",
          desc: "Kann kurzfristige Unterbrechungen bewältigen",
        },
      },
      complianceOptions: {
        iso: "ISO 9001 / 27001",
        hse: "HSE / Arbeitssicherheit",
        osha: "OSHA",
        vca: "VCA / SCC",
        gdpr: "DSGVO / Datenschutz",
        sectorSpecific: "Branchenspezifische Vorschriften",
      },
      complexityLevels: {
        simple: "Einfach",
        moderate: "Moderat",
        complex: "Komplex",
        veryComplex: "Sehr komplex",
      },
      step6: {
        title: "Operative Analyse",
        description: "Basierend auf den bereitgestellten Informationen hier eine Analyse Ihres aktuellen operativen Zustands auf Führungsebene.",
        noData: "Unzureichende Daten für die Analyse. Bitte vervollständigen Sie die vorherigen Schritte.",
        observationsTitle: "Operative Beobachtungen",
        risksTitle: "Risiko- & Expositionsbereiche",
        readinessTitle: "Bewertung der Ausführungsbereitschaft",
        advisoryTitle: "Beratungsrichtung (Überblick)",
        disclaimer: "Diese Analyse ist indikativ und basiert auf selbst gemeldeten Informationen. Sie ersetzt keine professionelle Bewertung. Alle Erkenntnisse unterliegen einer formellen Überprüfung.",
      },
      step7: {
        title: "Kontaktdaten",
        description: "Diese Angaben sind erforderlich, um die operative Analyse mit einem formellen Überprüfungsantrag zu verknüpfen.",
        personalTitle: "Persönliche Informationen",
        companyTitle: "Unternehmensinformationen",
        fullName: "Vollständiger Name",
        fullNamePlaceholder: "Geben Sie Ihren vollständigen Namen ein",
        email: "E-Mail-Adresse",
        emailPlaceholder: "Geben Sie Ihre geschäftliche E-Mail ein",
        phone: "Telefonnummer",
        phonePlaceholder: "Geben Sie Ihre Telefonnummer ein",
        companyName: "Firmenname",
        companyNamePlaceholder: "Firmenname eingeben",
        role: "Position / Funktion",
        rolePlaceholder: "z.B. Betriebsleiter",
        country: "Land",
        countryPlaceholder: "Land eingeben",
        city: "Stadt",
        cityPlaceholder: "Stadt eingeben",
        requiredNote: "Alle mit * markierten Felder sind Pflichtfelder. Ihre Daten werden nur zur Bearbeitung dieser Anfrage verwendet.",
      },
      analysis: {
        observations: {
          industry: {
            manufacturing: "Der Betrieb in einer Fertigungsumgebung umfasst typischerweise komplexe Produktionsplanung, Wartungszyklen und Qualitätskontrollanforderungen.",
            logistics: "Logistikoperationen erfordern enge Koordination zwischen mehreren Beteiligten, zeitkritische Lieferungen und Echtzeit-Sichtbarkeit über Lieferketten.",
            facility: "Facility-Management-Operationen umfassen mehrere Servicekategorien und erfordern Koordination zwischen internen Teams und externen Auftragnehmern.",
            infrastructure: "Infrastrukturoperationen umfassen oft geografisch verteilte Anlagen mit unterschiedlichen Wartungsanforderungen und behördlicher Aufsicht.",
            energy: "Energiesektoroperationen erfordern strikte Einhaltung von Sicherheitsprotokollen, regulatorische Compliance und kontinuierliche Überwachung kritischer Systeme.",
            other: "Operationen erstrecken sich über mehrere Bereiche mit unterschiedlichen Anforderungen an Koordination, Compliance und Ausführungskontrolle.",
          },
          scale: {
            single: "Einzelstandortoperationen ermöglichen zentralisierte Kontrolle, können aber dennoch Koordinationsherausforderungen über Schichten und Abteilungen hinweg haben.",
            multi: "Multi-Site-Operationen führen zu Komplexität bei Standardisierung, Kommunikation und konsistenter Ausführung über Standorte hinweg.",
            distributed: "Verteilte oder regionale Operationen erfordern robuste Koordinationsmechanismen zur Aufrechterhaltung von Sichtbarkeit und Kontrolle über verteilte Teams.",
          },
          structure: {
            adhoc: "Ad-hoc-Betriebsstruktur deutet auf hohe Abhängigkeit von individuellem Urteil und potenzielle Inkonsistenz bei der Ausführung hin.",
            semiStructured: "Semi-strukturierte Prozesse zeigen teilweise Standardisierung mit Raum für Variation, was zu inkonsistenten Ergebnissen führen kann.",
            fullyStructured: "Vollständig strukturierte Operationen bieten eine Grundlage für vorhersehbare Ausführung, wobei die Wirksamkeit von Einhaltung und Durchsetzung abhängt.",
          },
          tools: {
            fragmented: "Fragmentierte Tool-Landschaft führt typischerweise zu Informationssilos, manueller Datenabstimmung und Koordinationsaufwand.",
            manual: "Starke Abhängigkeit von manuellen Prozessen erhöht das Fehlerrisiko und begrenzt die Skalierbarkeit von Operationen.",
          },
        },
        risks: {
          predictability: "Vorhersagbarkeitsrisiko: Inkonsistente Ausführung und unklare Verantwortungsmuster können zu unvorhersehbaren operativen Ergebnissen führen.",
          coordination: "Koordinationsrisiko: Manuelle Koordination und Kommunikationslücken können zu Verzögerungen, Fehlern und Eskalationsversagen führen.",
          downtime: "Ausfallzeitexposition: Hohe Empfindlichkeit gegenüber Ausfallzeiten kombiniert mit aktuellen Betriebsmustern deutet auf erhöhtes Risiko kostspieliger Störungen hin.",
          compliance: "Compliance-Exposition: Lücke zwischen Compliance-Anforderungen und aktuellem Strukturniveau kann Audit- und regulatorische Risiken schaffen.",
          escalation: "Eskalationsrisiko: Verzögerungen bei der Problemlösung und begrenzte Sichtbarkeit können kleinere Probleme zu erheblichen operativen Problemen eskalieren lassen.",
        },
        readiness: {
          high: "Die Organisation erscheint angemessen auf operative Veränderungen vorbereitet, wobei bestehende Struktur und Tools eine Grundlage für Verbesserungsinitiativen bieten.",
          moderate: "Die Organisation zeigt gemischte Bereitschaftsindikatoren. Einige Grundelemente existieren, aber Lücken in Struktur oder Tools erfordern möglicherweise Aufmerksamkeit vor Skalierung.",
          low: "Aktuelle Betriebsmuster deuten auf begrenzte Bereitschaft für Skalierung oder signifikante Veränderungen hin, ohne zuerst grundlegende Koordinations- und Strukturlücken zu adressieren.",
        },
        advisory: {
          ownership: "Erwägen Sie die Etablierung klarerer Verantwortungsstrukturen und Rechenschaftsrahmen, um Koordinationsaufwand zu reduzieren und Reaktionsvorhersehbarkeit zu verbessern.",
          standardization: "Fokussieren Sie auf die Standardisierung von Kernbetriebsprozessen, um Variation zu reduzieren und konsistente Ausführungsmuster über Teams hinweg zu etablieren.",
          escalation: "Implementieren Sie strukturierte Eskalationspfade mit definierten Reaktionsfenstern und klarer Verantwortung auf jeder Ebene.",
          compliance: "Richten Sie die operative Struktur an Compliance-Anforderungen aus, um Audit-Exposition zu reduzieren und konsistente Einhaltung regulatorischer Standards sicherzustellen.",
          scale: "Für Multi-Site- oder verteilte Operationen priorisieren Sie Mechanismen, die Konsistenz und Sichtbarkeit über Standorte hinweg gewährleisten.",
        },
      },
    },
    submission: {
      title: "Anfrage eingereicht",
      body1: "Ihre operative Überprüfung wurde erfolgreich eingereicht.",
      body2: "Unser Team wird Ihre Anfrage sorgfältig prüfen und entsprechend nachfassen.",
      body3: "Um die nächsten Schritte zu beschleunigen, können Sie unten eine strategische Beratung vereinbaren.",
      scheduleBtn: "Strategische Beratung vereinbaren",
      closeBtn: "Schließen",
      referenceLabel: "Referenz-ID",
      emailSent: "Bestätigung gesendet an",
      pdfAttached: "PDF-Zusammenfassung für Ihre Unterlagen beigefügt",
    },
    governanceTrends: {
      title: "Operieren in einer regulierten Realität — ISO 27001, NIS2 & SOC 2",
      subtitle: "Da operative Umgebungen zunehmend reguliert, vernetzt und überprüft werden, wird Führung immer mehr durch Kontrolle, Dokumentation und Konsistenz definiert — nicht durch Geschwindigkeit oder Improvisation.",
      column1: {
        title: "Was Betriebsleiter priorisieren",
        items: [
          "Klare, durchsetzbare Arbeitsanweisungen",
          "Vorhersehbare Ausführung über Schichten und Teams hinweg",
          "Sicherheitsverfahren, die wenig Raum für Interpretation lassen",
          "Weniger Ad-hoc-Entscheidungen bei Vorfällen",
          "Dokumentation, die für sich selbst steht"
        ]
      },
      column2: {
        title: "Standards, Compliance & Haftung",
        items: [
          "Formale Managementsysteme basierend auf anerkannten Standards",
          "Nachverfolgbare Verfahren und Korrekturmaßnahmen",
          "Überprüfbare Schulungs- und Kompetenznachweise",
          "Datenschutz und Zugriffskontrolle standardmäßig",
          "Entscheidungen, die im Nachhinein rekonstruiert werden können"
        ]
      },
      column3: {
        title: "Sicherheit, Hygiene & operative Realität",
        items: [
          "Sicherheitsregeln müssen unter Druck funktionieren, nicht nur auf dem Papier",
          "Hygieneverfahren müssen standortübergreifend konsistent sein",
          "Abweichungen müssen protokolliert, nicht normalisiert werden",
          "Informelle Workarounds erhöhen das Risiko",
          "Persönliche Verantwortlichkeit rückt näher an die operative Ebene"
        ]
      },
      trend1: "Mit Blick auf die Zukunft ist der Trend klar: Operationen müssen erklärbar, prüfbar und wiederholbar sein — auch unter Störungen.",
      trend2: "Erfolgreiche Organisationen werden diejenigen sein, die Standards, Sicherheit, Hygiene und Datendisziplin direkt in die tägliche Ausführung einbetten. Nicht als Overhead, sondern als operative Struktur."
    },
    chatbot: {
      greeting: "Hallo! Ich bin Brooks. Wie kann ich Ihnen heute helfen?\n\nIch kann Ihnen bei Produktinformationen, operativer Unterstützung, Terminvereinbarung für eine Demo, dem Simulator, Compliance & Governance oder Preisfragen weiterhelfen.",
      subtitle: "Ovelon Prime Assistent",
      inputPlaceholder: "Nachricht eingeben...",
      buttons: {
        productInfo: "Produktinfo",
        operationalSupport: "Support",
        scheduleDemo: "Demo vereinbaren",
        intake: "Diagnose-Aufnahme",
        simulation: "Simulator",
        pricing: "Preise",
        contact: "Kontakt",
        warehouseSolutions: "Lagerlösungen",
        workflowSolutions: "Workflow-Lösungen",
        backToMain: "← Zurück",
        connectSupport: "Support verbinden",
        bookDemo: "Bei uns buchen",
        startIntake: "Aufnahme starten →",
        viewPricing: "Preise ansehen",
        goToForm: "Zum Kontaktformular",
        openSimulator: "Simulator öffnen →",
      },
      responses: {
        productInfo: "Wir bieten Lager- und Workflow-Lösungen, die auf Ihre Betriebsabläufe zugeschnitten sind.\n\nSchnelllinks:\n• Lagerlösungen\n• Workflow-Lösungen\n\nMöchten Sie etwas Bestimmtes wissen?",
        operationalSupport: "Können Sie mir den Problemtyp nennen? Ich kann Sie anleiten oder mit einem Experten verbinden.",
        scheduleDemo: "Ich kann eine Demo für Sie vereinbaren. Klicken Sie auf die Schaltfläche unten, um eine passende Zeit auszuwählen!",
        intake: "Unsere 7-Schritte Bedarfs- & Ausführungsaufnahme hilft, Ihre operativen Herausforderungen zu diagnostizieren:\n\n1️⃣ Operativer Kontext\n2️⃣ Aktuelle Herausforderungen\n3️⃣ Prozessanalyse\n4️⃣ Technologie-Stack\n5️⃣ Ziele & Prioritäten\n6️⃣ Executive-Analyse\n7️⃣ Kontaktdaten\n\nSie erhalten eine gebrandete PDF-Zusammenfassung. Bereit zu starten?",
        pricing: "Unsere Plattform beginnt bei €4.999/Monat und umfasst die vollständige Suite von Lager- und Workflow-Lösungen.\n\nMöchten Sie die detaillierte Preisübersicht sehen oder ein Gespräch vereinbaren?",
        contact: "Sie erreichen uns unter info@ovelon-prime.com oder über unser Kontaktformular. Soll ich Sie dorthin führen?",
        simulation: "Unser Projekt-Simulator hilft Ihnen, potenzielle Verbesserungen in Ihren Abläufen zu visualisieren. Probieren Sie es aus!",
        simulatorOpened: "Ich habe den Projekt-Simulator für Sie geöffnet. Folgen Sie den Schritten, um Ihre potenziellen Verbesserungen zu sehen!",
        connectSupport: "Ich verbinde Sie mit unserem Support-Team. Sie erreichen uns auch direkt unter info@ovelon-prime.com",
        bookingOpened: "Ich habe unseren Buchungskalender für Sie geöffnet. Wählen Sie eine passende Zeit!",
        backToMain: "Kein Problem! Womit kann ich Ihnen sonst noch helfen?",
        compliance: "Wir helfen Organisationen bei der Navigation komplexer regulatorischer Landschaften:\n\n🛡️ NIS2-Richtlinie — Bereitschaft zur Netz- und Informationssicherheit für kritische Infrastrukturen.\n\n📋 ISO 27001 — Implementierung des Informationssicherheits-Managementsystems und Auditvorbereitung.\n\n🔒 SOC 2 — Service Organization Control Audits (Typ I & Typ II) für Trust Services Kriterien.\n\n📑 Operative Governance — Richtlinien, Audit-Trails und Compliance-Dashboards.\n\nStellen Sie mir jede Frage zu diesen Frameworks!",
        complianceThinking: "Lassen Sie mich das für Sie nachschlagen...",
        complianceError: "Ich konnte gerade keine Antwort finden. Bitte sehen Sie unsere Compliance-Artikel oder kontaktieren Sie info@ovelon-prime.com.",
        fallback: "Diese Information habe ich nicht, aber das Team wird sich in Kürze bei Ihnen melden. Möchten Sie Ihre Kontaktdaten hinterlassen oder das Kontaktformular nutzen?",
      },
      complianceHint: "Fragen Sie mich alles über SOC 2, ISO 27001 oder NIS2 ↵",
    },
    subscribe: {
      title: "Bleiben Sie der Entwicklung voraus",
      subtitle: "Erhalten Sie exklusive Einblicke in Workflow-Optimierung, Betriebsmanagement und Branchentrends direkt in Ihr Postfach.",
      promo: "🎁 Kostenloses Operations E-Book bei Anmeldung heute!",
      fullName: "Vollständiger Name",
      fullNamePlaceholder: "Geben Sie Ihren vollständigen Namen ein",
      email: "E-Mail-Adresse",
      emailPlaceholder: "name@email.de",
      submit: "Jetzt abonnieren",
      submitting: "Wird abonniert...",
      successTitle: "Willkommen an Bord!",
      successMessage: "Vielen Dank für Ihr Abonnement. Sie erhalten bald unsere neuesten Einblicke.",
      privacyText: "Mit dem Abonnieren stimmen Sie unserer",
      privacyLink: "Datenschutzerklärung",
      termsLink: "AGB",
      andText: "und",
      missingInfo: "Fehlende Informationen",
      missingInfoDesc: "Bitte füllen Sie sowohl Name als auch E-Mail aus.",
      failedTitle: "Abonnement fehlgeschlagen",
    },
    caseStudies: {
      badge: "Nachgewiesene Ergebnisse",
      title: "Betriebsabläufe für missionskritische Industrien transformieren.",
      challengeLabel: "Die Herausforderung",
      solutionLabel: "Die Ovelon Prime Lösung",
      resultsLabel: "Messbare Ergebnisse",
      cases: {
        facility: {
          industry: "Gebäudemanagement",
          client: "Multi-Standort Facility Betreiber",
          challenge: "Die Verwaltung von Serviceanfragen über 40+ verteilte Standorte führte zu inkonsistenten Reaktionszeiten, verpassten Wartungsfenstern und fragmentierter Kommunikation zwischen Vor-Ort-Teams und zentraler Koordination.",
          solution: "Ovelon Prime vereinheitlichte alle eingehenden Anfragen in einem strukturierten Aufnahmesystem mit automatischer Weiterleitung, Prioritätsklassifizierung und Echtzeit-Statusverfolgung — für zentralisierte Übersicht ohne bestehende Tools zu ersetzen.",
          results: [
            { value: "42%", label: "Schnellere Reaktionszeiten" },
            { value: "95%", label: "SLA-Konformitätsrate" },
            { value: "60%", label: "Reduzierung von Koordinationsfehlern" },
          ],
        },
        supplyChain: {
          industry: "Lieferkette",
          client: "Regionales Distributionsnetzwerk",
          challenge: "Manuelle Koordination zwischen Lieferanten, Lagerhäusern und Transportpartnern schuf blinde Flecken in der Lieferkette, was zu verzögerten Lieferungen, Bestandsabweichungen und steigenden Betriebskosten führte.",
          solution: "Ovelon Prime implementierte durchgängige Workflow-Automatisierung, die Beschaffung, Bestandsmanagement und Logistikplanung verknüpfte — mit Echtzeit-Transparenz und vorausschauenden Warnmeldungen über die gesamte Lieferkette.",
          results: [
            { value: "38%", label: "Reduzierung der Durchlaufzeiten" },
            { value: "99,2%", label: "Bestellgenauigkeitsrate" },
            { value: "28%", label: "Niedrigere Betriebskosten" },
          ],
        },
        warehouse: {
          industry: "Lager & Speicherung",
          client: "Industrieller Lagerkomplex",
          challenge: "Die Bestandsverfolgung basierte auf fragmentierten Tabellen und manuellen Zählungen, was häufige Bestandsabweichungen, falsch zugewiesenen Lagerplatz und verzögerte Auftragsabwicklung über mehrere Lagerzonen verursachte.",
          solution: "Ovelon Prime standardisierte Bestandsabläufe mit automatischer Aufnahmeprotokollierung, zonenbasierter Zuweisungsregeln und Echtzeit-Bestandstransparenz — Reduzierung manueller Eingriffe bei gleichzeitiger Verbesserung von Genauigkeit und Durchsatz.",
          results: [
            { value: "55%", label: "Reduzierung von Bestandsabweichungen" },
            { value: "40%", label: "Schnellere Auftragsabwicklung" },
            { value: "35%", label: "Verbesserte Raumnutzung" },
          ],
        },
        maintenance: {
          industry: "Reparatur & Wartung",
          client: "Industrielle Ausrüstungsdienste",
          challenge: "Reaktive Wartungsabläufe dominierten den Betrieb. Arbeitsaufträge wurden inkonsistent erfasst, Teileverfolgung war manuell und Technikerplanung basierte auf Verfügbarkeit statt Priorität oder Nähe.",
          solution: "Ovelon Prime führte strukturiertes Auftragsmanagement mit automatischer Prioritätsweiterleitung, Teilebestandsintegration und optimierter Technikerzuweisung ein — Umstellung von reaktiver zu vorausschauender Wartung.",
          results: [
            { value: "47%", label: "Reduzierung der Ausfallzeiten" },
            { value: "65%", label: "Schnellere Auftragsabwicklung" },
            { value: "30%", label: "Weniger Wiederholungsvorfälle" },
          ],
        },
      },
    },
    complianceBlog: {
      badge: "Compliance & Governance",
      title: "NIS2, ISO 27001 & SOC 2: Was operative Führungskräfte jetzt wissen müssen.",
      subtitle: "Mit zunehmendem regulatorischem Druck auf kritische Infrastrukturen ist das Verständnis von NIS2, ISO 27001 und SOC 2 nicht mehr optional — es ist operatives Überleben.",
      articles: {
        nis2: {
          tag: "EU-Richtlinie 2022/2555",
          title: "NIS2-Richtlinie: Die neue operative Realität für kritische Infrastrukturen",
          excerpt: "Die NIS2-Richtlinie ist die bedeutendste Cybersicherheitsverordnung der EU, die 18 kritische Sektoren abdeckt und schwere Strafen bei Nichteinhaltung verhängt.",
          facts: [
            { label: "Abgedeckte Sektoren", value: "18" },
            { label: "Höchststrafe", value: "10 Mio.€ oder 2% Umsatz" },
            { label: "Management-Haftung", value: "Persönlich" },
          ],
          sections: [
            { heading: "Was ist NIS2 und warum ist es wichtig?", text: "NIS2 ersetzt die ursprüngliche NIS-Richtlinie von 2016 und erweitert den Umfang der Cybersicherheitsanforderungen in der EU dramatisch. Organisationen müssen Risikomanagement, Vorfallmeldung, Lieferkettensicherheit und Kontinuitätsplanung umsetzen — oder Strafen bis zu 10 Millionen Euro oder 2% des weltweiten Jahresumsatzes riskieren." },
            { heading: "Operative Verantwortung ist jetzt persönlich", text: "Unter NIS2 kann die Geschäftsleitung persönlich für Nichteinhaltung haftbar gemacht werden. CISOs, COOs und Vorstandsmitglieder müssen aktive Governance über Cybersicherheit und operative Resilienz nachweisen." },
            { heading: "Strenge Anforderungen an die Vorfallmeldung", text: "NIS2 verlangt ein gestuftes Meldesystem: Frühwarnung innerhalb von 24 Stunden, detaillierte Meldung innerhalb von 72 Stunden und Abschlussbericht innerhalb eines Monats. Organisationen benötigen strukturierte Prozesse zur Erkennung, Klassifizierung und Eskalation." },
            { heading: "Lieferkettensicherheit wird Pflicht", text: "Die Richtlinie verlangt die Bewertung und das Management von Cybersicherheitsrisiken in der gesamten Lieferkette — einschließlich Lieferantenbewertungen und kontinuierlicher Überwachung von Drittanbieterrisiken." },
          ],
          ovelonTitle: "Wie Ovelon Prime die NIS2-Compliance unterstützt",
          ovelonPoints: [
            "Strukturierte Vorfallaufnahme und -klassifizierung mit automatisierten Eskalationspfaden, die 24/72-Stunden-Fristen einhalten",
            "Vollständige Audit-Trails für jeden operativen Vorgang zur evidenzbasierten Compliance-Nachweisführung",
            "Standardisierte Ausführungsverfahren, die interpretationsbasierte Abläufe eliminieren",
            "Operative Echtzeit-Sichtbarkeit mit Dashboards zur Unterstützung der Management-Governance",
            "Lieferketten-Koordinationsworkflows, die Sicherheitsanforderungen bei Lieferanteninteraktionen durchsetzen",
            "Automatisierte Benachrichtigungssysteme für zeitnahe Stakeholder-Kommunikation bei Vorfällen",
          ],
        },
        iso27001: {
          tag: "Internationaler Standard",
          title: "ISO 27001: Ein ISMS aufbauen, das wirklich funktioniert",
          excerpt: "ISO 27001 ist der globale Goldstandard für Informationssicherheitsmanagement. Die Zertifizierung ist jedoch nur der Anfang — ein effektives ISMS erfordert operative Disziplin und evidenzbasierte Kontrolle.",
          facts: [
            { label: "Kontrollen in Anhang A", value: "93" },
            { label: "Zertifizierte Organisationen", value: "70.000+" },
            { label: "Rezertifizierungszyklus", value: "3 Jahre" },
          ],
          sections: [
            { heading: "Warum ISO 27001 unverzichtbar ist", text: "ISO 27001 bietet einen systematischen Ansatz zum Management sensibler Informationen. Die Revision 2022 hat die Kontrollen in vier Domänen mit 93 Kontrollen in Anhang A neu organisiert." },
            { heading: "Die Lücke zwischen Zertifizierung und operativer Realität", text: "Viele Organisationen erlangen die Zertifizierung, haben aber Schwierigkeiten, die operative Disziplin zwischen Audits aufrechtzuerhalten. Richtlinien existieren auf Papier, werden aber im Tagesgeschäft nicht umgesetzt." },
            { heading: "Anhang-A-Kontrollen, die Automatisierung erfordern", text: "Mehrere Anhang-A-Kontrollen beziehen sich direkt auf operative Prozesse: A.5.24-A.5.28 (Vorfallmanagement und Beweiserhebung) sowie A.8.16 (Überwachungsaktivitäten). Diese erfordern strukturierte, wiederholbare Prozesse." },
            { heading: "Kontinuierliche Verbesserung ist Pflicht", text: "Klausel 10 verlangt die kontinuierliche Verbesserung des ISMS — systematische Identifikation von Abweichungen, Korrekturmaßnahmen und Wirksamkeitsbewertung. Ohne strukturierte operative Daten wird kontinuierliche Verbesserung zum Ratespiel." },
          ],
          ovelonTitle: "Wie Ovelon Prime die ISO 27001-Compliance unterstützt",
          ovelonPoints: [
            "Automatisierte Vorfallklassifizierung und -reaktion, ausgerichtet an den Anhang-A-Kontrollen A.5.24-A.5.28",
            "Vollständige Audit-Trails für jeden operativen Vorgang (A.5.28)",
            "Standardisierte Ausführungsverfahren für konsistente Kontrollimplementierung",
            "Echtzeit-Monitoring-Dashboards zur kontinuierlichen Compliance-Verifizierung (A.8.16)",
            "Strukturierte Änderungsmanagement-Workflows mit dokumentierten Genehmigungsketten",
            "Operative Metriken und Trendanalysen für evidenzbasierte kontinuierliche Verbesserung unter Klausel 10",
          ],
        },
        soc2: {
          tag: "AICPA Trust Service Kriterien",
          title: "SOC 2: Operatives Vertrauen durch verifizierte Sicherheitskontrollen nachweisen",
          excerpt: "SOC 2 (Service Organization Control 2) ist das führende Prüfrahmenwerk zur Überprüfung, ob die Informationssicherheitspraktiken einer Organisation strenge Standards über fünf Trust Service Kriterien erfüllen. Ein SOC 2-Bericht wird von Enterprise-Kunden und regulierten Industrien zunehmend verlangt.",
          type1Label: "Typ I",
          type2Label: "Typ II",
          type1: {
            facts: [
              { label: "Prüfungsumfang", value: "Stichtagsbezogen" },
              { label: "Dauer", value: "Einzeldatum" },
              { label: "Trust-Kriterien", value: "Bis zu 5" },
            ],
            sections: [
              {
                heading: "Was ist SOC 2 Typ I?",
                text: "Ein SOC 2 Typ I-Bericht bewertet, ob die Sicherheitskontrollen einer Organisation zu einem bestimmten Zeitpunkt geeignet konzipiert sind. Er beantwortet die Frage: 'Sind heute die richtigen Kontrollen vorhanden?' Typ I ist typischerweise der Ausgangspunkt für Organisationen, die eine SOC 2-Zertifizierung anstreben, und liefert eine Basisbewertung ohne Prüfung der operativen Wirksamkeit im Zeitverlauf.",
              },
              {
                heading: "Die fünf Trust Service Kriterien",
                text: "SOC 2 bewertet Kontrollen über fünf Kriterien: Sicherheit (obligatorisch), Verfügbarkeit, Verarbeitungsintegrität, Vertraulichkeit und Datenschutz. Typ I überprüft, ob Richtlinien, Verfahren und technische Kontrollen zu diesen Kriterien angemessen konzipiert sind — nicht ob sie konsistent über die Zeit funktionieren.",
              },
              {
                heading: "Wann Typ I geeignet ist",
                text: "Typ I wird häufig eingesetzt, wenn eine Organisation schnell ein Basissicherheitsniveau gegenüber Kunden oder Partnern nachweisen muss. Es ist auch der natürliche Vorläufer zu Typ II und ermöglicht Organisationen, Kontrollmängel zu identifizieren und zu beheben, bevor sie sich auf einen längeren Prüfungszeitraum einlassen.",
              },
            ],
          },
          type2: {
            facts: [
              { label: "Prüfungszeitraum", value: "6–12 Monate" },
              { label: "Trust-Kriterien", value: "Bis zu 5" },
              { label: "Marktstandard", value: "Enterprise" },
            ],
            sections: [
              {
                heading: "Was ist SOC 2 Typ II?",
                text: "Ein SOC 2 Typ II-Bericht bewertet, ob die Sicherheitskontrollen einer Organisation nicht nur vorhanden sind, sondern über einen längeren Zeitraum (typischerweise 6 bis 12 Monate) effektiv funktionieren. Er ist der Goldstandard für Enterprise-Vertrauen und wird zunehmend von Beschaffungsteams, Regulierungsbehörden und Enterprise-Kunden in Sektoren wie Finanzdienstleistungen und kritischer Infrastruktur verlangt.",
              },
              {
                heading: "Operative Disziplin ist das Fundament",
                text: "Im Gegensatz zu Typ I erfordert Typ II eine konsistente, dokumentierte und prüfbare Durchführung von Kontrollen über den gesamten Überprüfungszeitraum. Vorfallprotokolle müssen vollständig sein, Zugriffskontrollen müssen konsistent durchgesetzt werden, und Änderungsmanagement muss dokumentiert sein.",
              },
              {
                heading: "Häufige Befunde, die Typ II-Prüfungen gefährden",
                text: "Die häufigsten Typ II-Fehler betreffen unvollständige Vorfallprotokolle, inkonsistente Benutzer-Zugriffsüberprüfungen, fehlende Genehmigungsdokumentation und Lücken in der Sicherheitsüberwachung.",
              },
              {
                heading: "SOC 2 Typ II und kontinuierliche Compliance",
                text: "Im Gegensatz zu einer einmaligen Zertifizierung ist Typ II-Compliance eine fortlaufende operative Disziplin. Organisationen müssen ganzjährig prüfungsbereite Dokumentation vorhalten — nicht nur in den Wochen vor dem Prüfungsfenster.",
              },
            ],
          },
          ovelonTitle: "Wie Ovelon Prime die SOC 2-Bereitschaft unterstützt",
          ovelonPoints: [
            "Strukturierte Vorfallaufnahme- und Reaktions-Workflows, die vollständige, zeitgestempelte Prüfnachweise für jedes Sicherheitsereignis erzeugen",
            "Automatisierte Zugriffskontroll-Workflows zur konsistenten Benutzerbereitstellung und -deaktivierung",
            "Änderungsmanagement-Dokumentation mit Genehmigungsketten und Zeitstempeln",
            "Echtzeit-Betriebsüberwachung ausgerichtet auf SOC 2 Sicherheits- und Verfügbarkeitskriterien",
            "Standardisierte Ausführungsverfahren zur Beseitigung undokumentierter Workarounds",
            "Compliance-Reporting-Dashboards für kontinuierliche Kontrolleffektivitäts-Sichtbarkeit",
          ],
        },
      },
    },
  },
  fr: {

    backToTop: "Haut de page",
    explore: "Explorer",

    common: {
    back: "Retour",
    },
    nav: {
      services: "Services",
      pricing: "Tarifs",
      about: "À propos",
      contact: "Contact",
      cta: "Discovery Call",
      viewDemo: "Voir la démo",
    },
    hero: {
      badge: "Industries critiques axées sur les résultats",
      title: "La complexité opérationnelle érode",
      titleHighlight: "la fiabilité et brise la discipline d'exécution",
      subtitle:
        "Lorsque la coordination devient manuelle, que la maintenance devient réactive et que les opérations reposent sur des solutions de contournement, le contrôle est déjà perdu.",
      subtitle2:
        "Nous restaurons une exécution prévisible et disciplinée au sein des opérations en cours — sans remplacer les systèmes de base ni perturber les flux de travail quotidiens.",
      subtitleMobile: "Lorsque la coordination devient manuelle, le contrôle est déjà perdu.",
      cta: "Reprenez le contrôle de vos opérations",
      simulatorCta: "Prise en charge Demande & Exécution",

    },
     problem: {
       badge: "Le Défi",
       title: "Les frictions opérationnelles coûtent plus que vous ne le pensez.",
       description: "Chaque appel manqué, erreur de réservation et moment de surcharge du personnel représente des revenus perdus et une expérience client diminuée. Votre réception ne devrait pas être votre goulot d'étranglement.",
       pain1: "Appels manqués aux heures de pointe",
       pain2: "Doubles réservations et conflits d'horaires",
       pain3: "Personnel débordé par les tâches administratives",
       pain4: "Communication client incohérente",
       pain5: "Revenus perdus à cause des lacunes opérationnelles",
     },
     process: {
       badge: "Le Processus",
       title: "Intégration transparente avec vos opérations.",
       step1: { title: "Capturer", description: "Chaque appel, message et demande de réservation est traité avec précision dès son arrivée." },
       step2: { title: "Coordonner", description: "L'alignement en temps réel avec votre calendrier et vos flux de travail assure une planification fluide." },
       step3: { title: "Confirmer", description: "Confirmations automatiques, mises à jour et escalades si nécessaire — rien ne passe entre les mailles." },
     },
     workflowDemo: {
       badge: "Démo Interactive",
       title: "Voyez Ovelon Prime en action.",
        tabs: { workflow: "Automatisation du Flux", call: "Récupération d'Appels", appointment: "Planification d'Incidents", safety: "Manuels de Sécurité" },
        workflow: {
          description: "Regardez comment une simple demande déclenche un flux de travail opérationnel complet — automatiquement.",
          runDemo: "LANCER LA DÉMO DU FLUX",
          complete: "Flux complet exécuté en moins de 60 secondes",
          reset: "RÉINITIALISER LA DÉMO",
          processing: "Traitement du flux...",
          step1: { title: "Demande reçue", desc: "Toutes les demandes ou incidents entrants sont capturés et enregistrés en temps réel." },
          step2: { title: "Disponibilité vérifiée", desc: "Le système vérifie l'inventaire, la capacité ou le planning pour confirmer la faisabilité." },
          step3: { title: "Ordre/Tâche créé", desc: "Les demandes sont transformées en ordres ou tâches exploitables." },
          step4: { title: "Confirmation envoyée", desc: "Notifications automatiques envoyées par e-mail, SMS ou tableaux de bord." },
          step5: { title: "Équipe notifiée", desc: "Les équipes opérationnelles reçoivent des alertes instantanées pour traiter la demande." },
          step6: { title: "Profil & historique mis à jour", desc: "Toutes les informations sont automatiquement enregistrées pour référence future." },
        },
        call: {
          description: "Découvrez comment Ovelon Prime transforme une opportunité potentiellement manquée en un accord confirmé.",
          simulate: "SIMULER UN APPEL ENTRANT",
          stage1: "Appel entrant...",
          stage2: "Sans Ovelon Prime : Appel manqué, aucun accord",
          stage3: "Avec Ovelon Prime : Appel répondu instantanément",
          stage4: "Demande d'incident client capturée",
          stage5: "Demande d'incident confirmée",
          revenueLost: "Revenus/suivi perdus. Client insatisfait.",
          revenueCaptured: "Revenus/suivi capturés. Client satisfait.",
        },
        appointment: {
          description: "Voyez comment les demandes, incidents et escalades sont automatiquement planifiés, assignés et suivis — sans coordination manuelle.",
          runDemo: "LANCER LA DÉMO D'INCIDENTS",
          complete: "Cycle complet de planification d'incidents en moins de 60 secondes",
          reset: "RÉINITIALISER LA DÉMO",
          processing: "Traitement de l'incident...",
          step1: { title: "Incident signalé", desc: "Un incident ou demande de service est soumis par téléphone, formulaire ou système d'alerte automatique." },
          step2: { title: "Priorité classifiée", desc: "Le système classe la gravité et l'urgence selon des règles d'escalade prédéfinies." },
          step3: { title: "Équipe d'intervention assignée", desc: "Les intervenants qualifiés sont identifiés et assignés selon disponibilité et expertise." },
          step4: { title: "Chemin d'escalade activé", desc: "En cas de dépassement des SLA, l'escalade automatique notifie superviseurs et direction." },
          step5: { title: "Résolution suivie", desc: "Le progrès est surveillé en temps réel avec des mises à jour envoyées à toutes les parties prenantes." },
          step6: { title: "Rapport post-incident déposé", desc: "Détails de l'incident, temps de réponse et étapes de résolution sont documentés pour conformité." },
        },
        safety: {
          description: "Voyez comment les manuels vidéo animés de sécurité standardisés sont déployés dans les entrepôts industriels — assurant une conformité HSE constante.",
          runDemo: "LANCER LA DÉMO SÉCURITÉ",
          complete: "Déploiement du manuel de sécurité terminé avec succès",
          reset: "RÉINITIALISER LA DÉMO",
          processing: "Déploiement du contenu de sécurité...",
          step1: { title: "Danger identifié", desc: "Un danger sur le lieu de travail ou une mise à jour de procédure déclenche la création d'une nouvelle instruction visuelle." },
          step2: { title: "Manuel animé créé", desc: "Un manuel vidéo animé étape par étape est généré selon les normes ISO/VCA/OSHA." },
          step3: { title: "Localisé en plusieurs langues", desc: "Le contenu est automatiquement traduit et localisé pour la conformité des équipes multilingues." },
          step4: { title: "Distribué aux postes", desc: "Les manuels vidéo sont transmis instantanément aux bornes, tablettes et appareils des opérateurs." },
          step5: { title: "Accusé de réception suivi", desc: "Le visionnage et la compréhension de chaque opérateur sont enregistrés pour l'audit et la certification." },
          step6: { title: "Rapport de conformité généré", desc: "Des rapports de conformité automatisés montrent la couverture de formation sur tous les postes." },
        },
      },
    video: {
      title: "Conçu pour des opérations ininterrompues",
      subtitle: "Découvrez comment Ovelon Prime soutient les environnements critiques sans perturber les systèmes en cours.",
      cta: "Discovery Call",
    },
    services: {
      label: "CE QUE NOUS STABILISONS",
      title: "Fiabilité opérationnelle dans les environnements",
      titleHighlight: "qui ne peuvent pas se permettre d'erreurs",
      subtitle:
        "Nous éliminons les pannes opérationnelles causées par la coordination manuelle, l'exécution floue et la gestion réactive des incidents — restaurant le contrôle, la responsabilité et les résultats prévisibles dans les opérations en cours.",
      items: [
        {
          title: "Gestion des demandes et des besoins",
          description:
            "Les demandes entrantes sont capturées, validées et acheminées de manière cohérente — sans dépendre du suivi ad hoc ou du jugement individuel.",
          tags: ["Contrôle d'admission intelligent", "Règles de qualification", "Routage système"],
        },
        {
          title: "Normes de sécurité et d'exécution",
          description:
            "Instructions visuelles standardisées utilisées pour aligner les opérateurs, les sous-traitants et les équipes sur les procédures critiques pour la sécurité — réduisant les erreurs d'interprétation, les écarts et les risques d'exécution.",
          tags: [
            "Procédures HSE",
            "Alignement ISO / VCA / OSHA",
            "Intégration opérateurs et sous-traitants",
          ],
        },
        {
          title: "Gestion des incidents et escalade",
          description:
            "Les problèmes sont enregistrés, classifiés et escaladés de manière contrôlée — assurant la responsabilité, la clarté de la réponse et des chemins de résolution prévisibles.",
          tags: ["Classification des problèmes", "Chemins d'escalade", "Propriété de la réponse"],
        },
        {
          title: "Efficacité dans la prise de rendez-vous",
          description:
            "Une planification vocale fluide qui gère les réservations, les confirmations et les changements d'horaire avec précision, libérant ainsi votre équipe, assurant le bon déroulement de chaque connexion et offrant une expérience client irréprochable.",
          tags: ["Planification vocale", "Disponibilité 24/7", "réponse automatique de rappel"],
        },
      ] as ServiceItem[],
      clickToExplore: "Cliquez pour explorer",
    },
    serviceDetails: {
      keyBenefits: "Avantages clés",
      cta: "Explorer la démo",
      benefits: [
        [
          "La validation automatisée de l'intake élimine les goulots d'étranglement de révision manuelle",
          "Le routage intelligent réduit le temps de réponse jusqu'à 60%",
          "Piste d'audit complète pour la conformité et la responsabilité",
          "Intégration transparente avec les systèmes de ticketing existants",
        ],
        [
          "Les SOP visuels réduisent le temps de formation et les erreurs d'interprétation",
          "Suivi de conformité en temps réel sur tous les quarts",
          "Intégration des sous-traitants standardisée sur tous les sites",
          "Procédures conformes HSE prêtes pour les audits réglementaires",
        ],
        [
          "La classification automatisée assure une priorisation cohérente des problèmes",
          "Chemins d'escalade clairs avec propriété définie à chaque niveau",
          "SLA de réponse suivis et appliqués automatiquement",
          "Analyse post-incident pour l'amélioration continue",
        ],
        [
          "Disponibilité 24/7 sans augmenter les effectifs",
          "La reprogrammation intelligente minimise les absences",
          "Confirmation multicanal (voix, SMS, e-mail)",
          "Synchronisation transparente du calendrier avec vos systèmes existants",
        ],
      ],
    },
    sectors: {
      label: "Secteurs",
      title: "Fiabilité opérationnelle pour",
      titleHighlight: "les industries critiques",
      subtitle: "Nous soutenons les environnements opérationnels où les temps d'arrêt, le désalignement ou les réponses retardées impactent directement la sécurité, la continuité ou la production.",
      items: [
        {
          name: "Gestion des installations",
          description: "Gestion des actifs distribués et des demandes de service dans des installations complexes — avec une faible tolérance aux retards ou à la mauvaise coordination.",
        },
        {
          name: "Maintenance et réparation",
          description: "Opérer sous pression constante pour prévenir les temps d'arrêt dans la maintenance réactive et préventive — souvent avec des informations incomplètes et des fenêtres de réponse serrées.",
        },
        {
          name: "Logistique et entreposage",
          description: "Coordination de la main-d'œuvre, des équipements et des flux de travail sous des délais stricts et une pression opérationnelle continue.",
        },
      ] as SectorItem[],
    },
    testimonials: {
      label: "VOIX DES CLIENTS",
      title: "Témoignages clients",
      items: [
        {
          quote: "Avant Ovelon Prime, la coordination des équipes dépendait du suivi individuel. Aujourd'hui, la maintenance et la planification s'exécutent de manière prévisible sur toutes les équipes — sans intervention manuelle.",
          name: "Patrick Sullivan",
          title: "Responsable des opérations logistiques",
          facility: "Centre de distribution, Nebraska",
        },
        {
          quote: "Dans notre environnement, les retards affectent directement la sécurité et la production. Ovelon Prime nous a aidés à standardiser l'exécution entre les équipes, réduisant le temps de réponse de 45% sans augmenter le risque opérationnel.",
          name: "Alicia Zhang",
          title: "Directrice des opérations",
          facility: "Installation d'entreposage, Ottawa",
        },
        {
          quote: "Nous opérons sous pression continue. Depuis l'implémentation du système, le routage et l'escalade suivent des règles fixes — pas des suppositions. Les temps d'arrêt causés par les erreurs de coordination ne sont plus un facteur.",
          name: "Wyatt Coleman",
          title: "Directeur d'usine",
          facility: "Usine de fabrication, Austin",
        },
        {
          quote: "Gérer une installation de cette taille implique des milliers d'éléments en mouvement. Le système nous a donné une source unique de vérité pour le statut des tâches, les cycles d'équipement et les contrôles de conformité. Nous avons cessé de courir après les mises à jour pour commencer à agir en conséquence.",
          name: "James Whitfield",
          title: "Directeur des opérations d'installations",
          facility: "Entrepôt industriel, Liverpool",
        },
        {
          quote: "Dans notre environnement, même de petits retards s'enchaînent rapidement. La plateforme impose une responsabilisation en temps réel dans chaque département. Nous avons éliminé les zones grises qui bloquaient la production pendant des heures.",
          name: "Ciarán O'Sullivan",
          title: "Responsable principal des installations",
          facility: "Usine de traitement, Tennessee",
        },
        {
          quote: "Nous perdions des heures chaque semaine à cause de la répartition manuelle et des ordres de travail mal acheminés. Depuis la mise en service, nos équipes suivent des processus structurés de bout en bout — plus d'approximations, plus de goulets d'étranglement. Le débit a augmenté et notre taux d'erreur s'est stabilisé.",
          name: "Rachel Moreno",
          title: "Responsable du contrôle des opérations",
          facility: "Pôle logistique, Dublin",
        }
      ] as TestimonialItem[],
    },
    operationalTrust: {
      heading: "Conçu pour les environnements réglementés et critiques pour la sécurité",
      items: [
        { text: "Principes de sécurité alignés ISO 27001" },
        { text: "Contrôle des processus style ISO 9001" },
        { text: "Conception opérationnelle sensible HSE" },
        { text: "Documentation et contrôles prêts pour l'audit" },
        { text: "Propriété totale du client — pas de systèmes boîte noire" },
      ] as TrustItem[],
      footer: "Conçu pour les environnements examinés par le RSSI avec gouvernance de sécurité interne, politiques de contrôle d'accès et procédures de gestion du changement.",
    },
    about: {
      label: "À propos",
      title: "Construit sur l'expertise opérationnelle,",
      titleHighlight: "guidé par la précision",
      description1:
        "Ovelon Prime travaille avec des organisations opérant sous pression constante — où les temps d'arrêt, les retards et les processus manuels impactent directement les coûts, la sécurité et la fiabilité.",
      description2:
        "Nous combinons une expertise approfondie du domaine opérationnel avec des couches de contrôle opérationnel pour concevoir des systèmes qui s'intègrent parfaitement dans les flux de travail existants, sans perturber les opérations critiques.",
      quote:
        '"La précision n\'est pas optionnelle. Dans les environnements où la fiabilité détermine les résultats, les systèmes doivent être conçus pour fonctionner sous pression."',
      quoteAuthor: "Équipe de direction · Ovelon Prime",
      stats: {
        uptime: "Disponibilité du système opérationnel",
        deployments: "Déploiements d'automatisation",
        industries: "Industries critiques servies",
      },
    },
    pricing: {
      label: "Tarifs",
      title: "Tarification basée sur l'engagement, avec des résultats clairement définis",
      subtitle:
        "Engagements clairement définis axés sur l'amélioration de la fiabilité, la réduction des frictions opérationnelles et la création d'un impact mesurable.",
      focused: {
        name: "Engagement opérationnel ciblé",
        description:
          "Un engagement à périmètre fixe conçu pour résoudre un goulot d'étranglement opérationnel clairement défini avec un impact mesurable.",
        requestQuote: "Demander un devis",
        cta: "Demander une évaluation d'engagement",
        features: [
          "Amélioration opérationnelle unique et clairement définie",
          "Cartographie des processus et des responsabilités",
          "Implémentation et déploiement contrôlés",
          "Documentation et transfert opérationnel",
          "Validation post-implémentation",
        ],
      },
      enterprise: {
        name: "Programme opérationnel Enterprise",
        description:
          "Systèmes opérationnels sur mesure pour les organisations complexes avec des exigences strictes de gouvernance, de conformité et de continuité.",
        priceLabel: "Tarification sur mesure",
        price: "Service personnalisé",
        period: "frais unique",
        cta: "Demander une évaluation initiale",
        badge: "Enterprise",
        features: [
          "Architecture opérationnelle multi-systèmes",
          "Intégrations personnalisées avec les systèmes existants",
          "Support dédié à l'implémentation et à la transition",
          "Conception opérationnelle sensible à la conformité",
          "Framework SLA et gouvernance personnalisé",
        ],
      },
      securityNote:
        "Chaque engagement est construit avec une sécurité de niveau entreprise, une protection des données et une conformité opérationnelle.",
      talkToExpert: "Discovery Call",
      intakeNote:
        "Admission structurée · Pas de pression commerciale · Directement avec un expert expérimenté",
      reviewNote: "Toutes les demandes sont examinées avant la planification.",
    },
    contact: {
      label: "ENGAGEMENT",
      title: "Prêt à reprendre le contrôle de",
      titleHighlight: " vos opérations ?",
      subtitle: "Tous les engagements sont définis à l'avance et soumis à un examen formel avant continuation.",
      cta: "Planifier une consultation",
      guarantee: "Pas d'engagements à long terme.",
      guaranteeHighlight: "Les engagements se poursuivent uniquement lorsque la valeur opérationnelle est démontrée",
      guaranteeSuffix: " sur les engagements qualifiants.",
      enterpriseLabel: "Demandes Enterprise : ",
      rightTitle: "Contactez Ovelon Prime",
      infoText1: "Si vous avez des questions ou souhaitez discuter de la manière dont nous pouvons soutenir votre entreprise, n'hésitez pas à nous contacter.",
      infoText2: "Le support client est disponible 24h/24 et 7j/7.",
    },
    contactForm: {
      firstName: "Prénom",
      firstNamePlaceholder: "Jean",
      lastName: "Nom",
      lastNamePlaceholder: "Dupont",
      businessName: "Nom de l'entreprise",
      businessNamePlaceholder: "Acme Industries",
      businessEmail: "Email professionnel",
      businessEmailPlaceholder: "jean@entreprise.fr",
      requestDetails: "Détails de la demande",
      requestDetailsPlaceholder: "Décrivez vos défis opérationnels et ce que vous souhaitez atteindre...",
      submit: "Envoyer la demande",
      submitting: "Envoi en cours...",
      validationError: "Veuillez remplir correctement tous les champs obligatoires.",
      successTitle: "Envoyé avec succès",
      successMessage: "Nous examinerons votre demande attentivement.",
      successCta: "Réserver une consultation stratégique",
    },
    faq: {
      title: "Questions fréquentes",
      category: "Engagement et livraison",
      items: [
        {
          question: "Comment fonctionne Ovelon Prime ?",
          answer:
            "Nous commençons par une admission structurée pour comprendre la contrainte opérationnelle. Nous concevons et validons une solution à périmètre fixe par rapport aux flux de travail réels. Nous implémentons, testons en charge et transférons avec une documentation complète.",
        },
        {
          question: "Dans quelles industries êtes-vous spécialisés ?",
          answer:
            "Nous travaillons avec des organisations opérant dans des environnements critiques, notamment la fabrication, la logistique et la gestion des installations. Si les retards, les erreurs ou les temps d'arrêt impactent directement la sécurité, la production ou la continuité, nous sommes le bon partenaire.",
        },
        {
          question: "Est-ce un abonnement ou un service continu ?",
          answer:
            "Non. Les engagements ont un périmètre fixe et sont axés sur les résultats. Il n'y a pas d'abonnement obligatoire, de dépendance à une licence ou de frais continus. Vous êtes propriétaire du système après la livraison.",
        },
        {
          question: "Combien de temps dure une implémentation typique ?",
          answer:
            "La plupart des engagements ciblés sont livrés dans les 12-16 jours, selon le périmètre et l'accès aux systèmes. Les programmes Enterprise suivent un déploiement progressif aligné sur les priorités opérationnelles.",
        },
        {
          question: "Cela s'intégrera-t-il à nos systèmes existants ?",
          answer:
            "Oui. Les solutions sont conçues pour s'intégrer aux systèmes existants sans remplacer les plateformes de base. Nous travaillons dans vos outils actuels et limites de données.",
        },
        {
          question: "Devons-nous changer nos flux de travail actuels ?",
          answer:
            "Non. Nous concevons autour de vos flux de travail existants. Lorsque des changements sont nécessaires, ils sont minimaux, documentés et validés avec vos équipes opérationnelles avant le déploiement.",
        },
        {
          question: "À qui appartient l'optimisation après la livraison ?",
          answer:
            "À vous. Tous les systèmes, configurations et documentations sont transférés à l'achèvement. Il n'y a pas de dépendance fournisseur. Vous conservez la propriété opérationnelle et technique complète.",
        },
        {
          question: "Comment gérez-vous la sécurité et la conformité ?",
          answer:
            "La sécurité et la conformité sont traitées comme des contraintes de conception, pas comme des réflexions après coup. Chaque engagement est conçu autour de vos politiques de sécurité existantes, de vos contrôles d'accès et de vos exigences de conformité. Nous travaillons dans votre infrastructure approuvée, vos limites de données et vos cadres de gouvernance. Aucune donnée n'est extraite ou réutilisée au-delà du périmètre convenu, et tous les systèmes sont livrés avec une documentation complète pour soutenir les audits internes, les revues de risques et les transferts.",
        },
        {
          question: "Quel niveau d'implication interne est requis ?",
          answer:
            "Une implication minimale et structurée. Nous avons besoin d'une contribution limitée pendant l'admission pour comprendre les contraintes opérationnelles et valider les hypothèses. L'implémentation est gérée de manière indépendante, avec des points de contrôle clairs pour l'examen et l'approbation. Les équipes opérationnelles ne sont pas perturbées et aucune ressource interne continue n'est requise après la livraison.",
        },
        {
          question: "Ovelon Prime convient-il aux grands environnements d'entreprise ?",
          answer:
            "Oui. Nous sommes conçus pour des environnements multi-parties prenantes complexes avec des exigences strictes de gouvernance, de conformité et opérationnelles. Les engagements sont structurés pour s'aligner sur la gestion du changement d'entreprise, les revues de sécurité et les déploiements progressifs — sans forcer les remplacements de plateformes ni perturber les systèmes critiques.",
        },
      ] as FAQItem[],
    },
    metricsStrip: {
      items: [
        { value: "40%", label: "Réduction des pannes" },
        { value: "200+", label: "Workflows automatisés" },
        { value: "3", label: "Continents couverts" },
        { value: "98%", label: "Taux de conformité SLA" },
      ],
    },
    footer: {
      privacy: "Confidentialité",
      terms: "Conditions",
      copyright: "© {{year}} Ovelon Prime. Tous droits réservés.",
      tagline: "Systèmes d'exploitation d'entreprise conçus pour la clarté, le contrôle et l'évolutivité.",
      trustHeading: "Conçu pour les environnements réglementés et à risque sécurité",
      trustItems: [
        "Principes de sécurité alignés ISO 27001",
        "Contrôles conformes SOC 2 Type II",
        "Conformité à la directive NIS2",
        "Documentation prête pour l'audit",
        "Conception opérationnelle HSE",
        "Propriété totale du client — aucun système boîte noire",
      ],
      badgeIsoTitle: "ISO 27001",
      badgeIsoDesc: "Gestion certifiée de la sécurité de l'information",
      badgeSocTitle: "SOC 2 Type II",
      badgeSocDesc: "Contrôles des organisations de services",
      badgeGdprTitle: "RGPD",
      badgeGdprDesc: "Règlement sur la protection des données",
    },
    industrialTrust: {
      heading: "Approuvé par les opérateurs dans les environnements critiques",
      disclaimer: "Opérateurs représentatifs des secteurs industriel, logistique, manufacturier et infrastructure. Les engagements varient selon la portée et l'environnement.",
    },
    intake: {
      title: "Qualification de consultation",
      subtitle: "Veuillez remplir le court formulaire ci-dessous. Toutes les soumissions sont examinées avant la planification.",
    },
    cookie: {
      message: "Nous utilisons des cookies pour améliorer votre expérience. En continuant à visiter ce site, vous acceptez notre utilisation des cookies.",
      accept: "Accepter",
      decline: "Refuser",
      learnMore: "En savoir plus sur notre politique de confidentialité",
    },
    privacy: {
      title: "Politique de confidentialité",
      lastUpdated: "Dernière mise à jour : 01.12.2025",
      sections: {
        introduction: {
          title: "1. Introduction",
          p1: "Chez Ovelon Prime, nous prenons la protection des données et la confidentialité au sérieux. Cette politique de confidentialité explique comment nous collectons, utilisons et protégeons les informations personnelles et professionnelles lorsque vous interagissez avec notre site web, nos formulaires d'admission et nos services.",
          p2: "Nous traitons les données de manière responsable et uniquement dans la mesure nécessaire pour fournir nos services, assurer l'intégrité opérationnelle et respecter les obligations légales.",
        },
        informationCollected: {
          title: "2. Informations collectées",
          personal: "Informations personnelles :",
          personalDesc: "Nom, adresse e-mail professionnelle, numéro de téléphone et informations soumises via des formulaires ou une correspondance directe.",
          business: "Informations commerciales et opérationnelles :",
          businessDesc: "Nom de l'entreprise, adresse commerciale ou juridiction, classification sectorielle, contexte opérationnel et informations liées à la conformité fournies volontairement lors de l'admission.",
          technical: "Informations techniques :",
          technicalDesc: "Adresse IP, métadonnées du navigateur et de l'appareil, cookies et analyses d'utilisation de base.",
          note: "Nous ne collectons pas intentionnellement de données personnelles sensibles sauf si cela est explicitement requis pour la conformité ou la prestation de services.",
        },
        howWeUse: {
          title: "3. Comment nous utilisons vos informations",
          items: [
            "Évaluation et qualification des demandes de consultation",
            "Fourniture et support des services d'automatisation opérationnelle",
            "Communication concernant les engagements, mises à jour ou clarifications",
            "Maintien de la fiabilité, de la sécurité et des performances de la plateforme",
            "Respect des obligations légales, réglementaires ou contractuelles",
          ],
          note: "Nous n'utilisons pas vos données à des fins de marketing non sollicité, de profilage ou de publicité par des tiers.",
        },
        dataSharing: {
          title: "4. Partage et divulgation des données",
          p1: "Ovelon Prime ne vend, ne loue ni n'échange de données personnelles ou professionnelles.",
          p2: "Les informations ne peuvent être partagées que lorsque cela est strictement nécessaire :",
          items: [
            "Avec des prestataires de services vérifiés requis pour la prestation de services",
            "Pour se conformer aux obligations légales ou réglementaires",
            "Pour protéger la sécurité, l'intégrité ou les droits de nos systèmes et clients",
          ],
          note: "Les données ne sont jamais partagées à des fins de marketing ou de promotion par des tiers.",
        },
        dataSecurity: {
          title: "5. Sécurité des données",
          p1: "Nous appliquons des mesures de protection techniques et organisationnelles conformes aux normes de l'industrie, notamment le chiffrement, les contrôles d'accès et les pratiques d'infrastructure sécurisées.",
          p2: "Bien qu'aucun système ne puisse garantir une sécurité absolue, nous opérons selon des protocoles internes stricts conçus pour minimiser les risques et protéger la confidentialité à tout moment.",
        },
        dataRetention: {
          title: "6. Conservation des données et vos droits",
          p1: "Nous conservons les données uniquement le temps nécessaire pour remplir les objectifs décrits dans cette politique ou pour répondre aux exigences légales.",
          p2: "Vous avez le droit de demander l'accès, la correction ou la suppression de vos données, le cas échéant.",
        },
        contact: {
          title: "7. Contact",
          p1: "Pour toute question concernant cette politique de confidentialité ou les pratiques de traitement des données, veuillez contacter :",
        },
      },
    },
    terms: {
      title: "Conditions générales",
      lastUpdated: "Dernière mise à jour : 01 décembre 2025",
      sections: {
        introduction: {
          title: "1. Introduction",
          p1: "Ces conditions générales (\"Conditions\") régissent votre accès et votre utilisation du site web Ovelon Prime, des processus de consultation et des services d'automatisation professionnels. En accédant au site web ou en utilisant nos services, vous acceptez d'être lié par ces Conditions.",
          p2: "Si vous n'êtes pas d'accord avec ces Conditions, vous devez cesser immédiatement d'utiliser le site web et les services.",
        },
        services: {
          title: "2. Services fournis",
          p1: "Ovelon Prime fournit des services d'automatisation professionnelle, de conception de systèmes et d'implémentation pour les environnements industriels, logistiques, de gestion des installations, d'infrastructure et autres environnements critiques sur le plan opérationnel.",
          p2: "Tous les services sont fournis sur une base de projet ou d'engagement. Le périmètre spécifique, les livrables, les hypothèses et les délais sont définis par engagement et peuvent évoluer en fonction des exigences opérationnelles.",
        },
        websiteUse: {
          title: "3. Utilisation du site web",
          items: [
            "Vous ne pouvez pas utiliser le site web à des fins illégales ou frauduleuses.",
            "Vous ne pouvez pas tenter d'obtenir un accès non autorisé aux systèmes ou aux données.",
            "Vous ne pouvez pas scraper, faire de la rétro-ingénierie, perturber ou interférer avec la fonctionnalité ou les mécanismes de sécurité du site web.",
          ],
        },
        clientResponsibilities: {
          title: "4. Responsabilités du client",
          items: [
            "Fournir des informations commerciales exactes, complètes et à jour.",
            "Assurer un accès en temps opportun aux systèmes, données et parties prenantes lorsque requis pour la livraison.",
            "Maintenir les contrôles de sécurité internes, la gestion des identifiants et la gouvernance des accès.",
            "Utiliser les systèmes livrés en conformité avec les lois applicables, les réglementations et les politiques internes.",
          ],
        },
        payments: {
          title: "5. Paiements et facturation",
          p1: "Sauf accord écrit contraire, le paiement est requis avant le début des services. Les engagements personnalisés nécessitent une proposition signée et un calendrier de paiement convenu mutuellement.",
          p2: "Les frais ne sont pas remboursables une fois le travail commencé, sauf mention explicite contraire dans un accord écrit.",
        },
        noGuarantee: {
          title: "6. Aucune garantie de résultats",
          p1: "Bien qu'Ovelon Prime conçoive des systèmes en tenant compte de la fiabilité, des performances et de l'efficacité opérationnelle, aucune garantie n'est donnée concernant des résultats opérationnels, financiers ou de performance spécifiques.",
        },
        ip: {
          title: "7. Propriété intellectuelle",
          p1: "Sauf accord écrit contraire, toutes les méthodologies, architectures, workflows, documentations, diagrammes et matériaux techniques développés par Ovelon Prime restent notre propriété intellectuelle.",
          p2: "Les clients bénéficient d'une licence non transférable et non exclusive pour un usage commercial interne uniquement.",
        },
        confidentiality: {
          title: "8. Confidentialité",
          p1: "Les deux parties conviennent de maintenir une stricte confidentialité de toutes les informations commerciales, techniques, opérationnelles et commerciales non publiques échangées pendant un engagement.",
        },
        liability: {
          title: "9. Limitation de responsabilité",
          p1: "Dans la mesure maximale permise par la loi, Ovelon Prime ne sera pas responsable des dommages indirects, accessoires, spéciaux ou consécutifs.",
          p2: "La responsabilité totale ne dépassera pas le total des frais payés pour le service spécifique donnant lieu à la réclamation.",
        },
        thirdParty: {
          title: "10. Outils et intégrations tiers",
          p1: "Les services peuvent impliquer des intégrations avec des plateformes ou outils tiers. Ovelon Prime n'est pas responsable des pannes, changements ou décisions politiques des tiers.",
        },
        termination: {
          title: "11. Résiliation",
          p1: "Nous nous réservons le droit de suspendre ou de résilier les services en cas de non-paiement, d'utilisation abusive ou de violation substantielle de ces Conditions.",
        },
        governingLaw: {
          title: "12. Droit applicable",
          p1: "Ces Conditions sont régies par les lois de la juridiction dans laquelle Ovelon Prime est légalement établie, sans tenir compte des principes de conflit de lois.",
        },
        changes: {
          title: "13. Modifications de ces Conditions",
          p1: "Nous pouvons mettre à jour ces Conditions de temps à autre. L'utilisation continue du site web ou des services constitue l'acceptation des Conditions mises à jour.",
        },
        contactInfo: {
          title: "14. Informations de contact",
          p1: "Pour toute question concernant ces Conditions, veuillez contacter :",
        },
      },
    },
    demo: {
      header: "Analyse des compromis opérationnels",
      close: "Fermer",
      beforeAlignment: "Avant l'alignement",
      afterAlignment: "Après l'alignement",
      tradeoff: "Compromis",
      footer: "Scénarios opérationnels représentatifs des environnements industriels, logistiques et de gestion des installations.",
      intake: {
        title: "Gestion des demandes et de la charge",
        metrics: {
          coordinationTime: "Temps de coordination",
          reworkCycles: "Cycles de reprise",
          ownership: "Responsabilité",
          ownershipValue: "Définie en amont",
        },
        before: {
          title: "Prise en charge fragmentée et coordination réactive",
          text: "Le travail arrive par e-mails, appels et canaux informels. La qualité des informations varie et la responsabilité change pendant l'exécution.",
          bullet1: "Multiples voies d'entrée",
          bullet2: "Boucles de clarification manuelles",
          bullet3: "Priorité dictée par l'urgence",
          bullet4: "Exécution dépend des individus",
          tradeoff: "Temps perdu en coordination et correction.",
        },
        after: {
          title: "Prise en charge structurée avec exécution contrôlée",
          text: "Les demandes suivent un seul chemin d'entrée. Les informations sont validées en amont et la responsabilité est attribuée avant le début de l'exécution.",
          bullet1: "Entrée unique structurée",
          bullet2: "Classification et routage clairs",
          bullet3: "Responsabilité attribuée une fois",
          bullet4: "Flux d'exécution prévisible",
          tradeoff: "La coordination diminue, l'exécution se stabilise.",
        },
      },
      safety: {
        title: "Normes de sécurité et d'exécution",
        metrics: {
          procedureDeviations: "Écarts de procédure",
          onboardingTime: "Temps d'intégration",
          consistency: "Cohérence",
          consistencyValue: "Standardisée",
        },
        before: {
          title: "Exécution basée sur l'interprétation",
          text: "Les procédures de sécurité existent dans des documents et le savoir tacite. Les opérateurs interprètent les normes différemment.",
          bullet1: "Procédures incohérentes",
          bullet2: "Forte dépendance au personnel expérimenté",
          bullet3: "Sous-traitants intégrés de manière informelle",
          bullet4: "Exposition aux audits",
          tradeoff: "La sécurité dépend de l'expérience, pas de la structure.",
        },
        after: {
          title: "Exécution standardisée avec compréhension partagée",
          text: "Les normes d'exécution sont claires, visuelles et uniformes entre les équipes et les groupes de sous-traitants.",
          bullet1: "Normes d'exécution uniformes",
          bullet2: "Intégration plus rapide",
          bullet3: "Erreurs d'interprétation réduites",
          bullet4: "Cadre de sécurité aligné",
          tradeoff: "La sécurité devient reproductible et auditable.",
        },
      },
      incident: {
        title: "Gestion des incidents et escalade",
        metrics: {
          escalationLoops: "Boucles d'escalade",
          responseTime: "Temps de réponse",
          accountability: "Responsabilité",
          accountabilityValue: "Explicite",
        },
        before: {
          title: "Gestion réactive des incidents",
          text: "Les incidents sont signalés de manière incohérente et escaladés manuellement. La responsabilité est floue et la visibilité limitée.",
          bullet1: "Seuils d'escalade flous",
          bullet2: "Transferts retardés",
          bullet3: "Fils de communication parallèles",
          bullet4: "Visibilité de la direction limitée",
          tradeoff: "Les incidents consomment l'attention au lieu d'être résolus.",
        },
        after: {
          title: "Escalade contrôlée avec responsabilité claire",
          text: "Les incidents sont enregistrés, classifiés et escaladés via des chemins prédéfinis. Chaque étape a un responsable clair et une fenêtre de réponse.",
          bullet1: "Niveaux d'escalade définis",
          bullet2: "Responsabilité claire par incident",
          bullet3: "Délais de réponse prévisibles",
          bullet4: "Visibilité opérationnelle complète",
          tradeoff: "Les incidents sont résolus systématiquement.",
        },
      },
      appointment: {
        title: "Efficacité de Prise de Rendez-vous",
        metrics: {
          schedulingTime: "Temps de Planification",
          noShows: "Absences",
          utilization: "Utilisation",
          utilizationValue: "Maximisée",
        },
        before: {
          title: "Planification manuelle avec friction constante",
          text: "Les rendez-vous sont coordonnés par téléphone, email et tableurs. Les confirmations sont incohérentes, les absences fréquentes et les conflits d'horaires drainent la capacité opérationnelle.",
          bullet1: "Planification téléphonique uniquement aux heures de bureau",
          bullet2: "Vérifications manuelles de disponibilité dans le calendrier",
          bullet3: "Aucun rappel ou confirmation automatisé",
          bullet4: "Fréquentes doubles réservations et conflits",
          tradeoff: "Temps du personnel consommé par la planification au lieu des opérations.",
        },
        after: {
          title: "Planification automatisée avec capacité prévisible",
          text: "Les rendez-vous sont capturés et confirmés automatiquement via des canaux vocaux et numériques. Vérifications de disponibilité en temps réel, rappels automatisés et reprogrammation intelligente éliminent les frictions.",
          bullet1: "Prise de rendez-vous vocale et numérique 24/7",
          bullet2: "Synchronisation calendrier en temps réel et prévention des conflits",
          bullet3: "Confirmations automatisées et séquences de rappels",
          bullet4: "Reprogrammation en libre-service avec optimisation de capacité",
          tradeoff: "La planification devient fluide, l'utilisation augmente.",
        },
      },
    },
    simulator: {
      title: "Prise en charge Demande & Exécution",
      close: "Fermer",
      back: "Retour",
      next: "Continuer",
      requestReview: "Demander une revue opérationnelle",
      submitting: "Envoi en cours...",
      submitError: "Échec de l'envoi de la demande. Veuillez réessayer ou contacter le support.",
      submitErrorTitle: "Échec de l'envoi",
      validationErrorTitle: "Champs obligatoires manquants",
      validationErrorDescription: "Veuillez remplir tous les champs obligatoires avant de soumettre.",
      stepIndicator: "Étape {{current}} sur {{total}}",
      step1: {
        title: "Contexte opérationnel",
        description: "Commençons par comprendre votre environnement opérationnel. Sélectionnez les options qui décrivent le mieux votre organisation.",
        industryLabel: "Dans quel secteur opérez-vous ?",
        operationLabel: "Quel type d'opérations menez-vous ?",
        operationHint: "Sélectionnez tout ce qui s'applique",
        scaleLabel: "Quelle est l'échelle de vos opérations ?",
      },
      step2: {
        title: "Modèle opérationnel actuel",
        description: "Aidez-nous à comprendre comment vous gérez le travail quotidien aujourd'hui — sans jugement, juste de la clarté.",
        handlingLabel: "Comment les demandes, tâches ou incidents sont-ils actuellement traités ?",
        handlingHint: "Sélectionnez toutes les méthodes utilisées",
        structureLabel: "Quel est le niveau de structure dans vos opérations ?",
        toolsLabel: "Quels outils ou systèmes sont utilisés ?",
      },
      step3: {
        title: "Obstacles opérationnels et points de friction",
        description: "Identifiez où le travail ralentit ou devient imprévisible aujourd'hui.",
        frictionLabel: "Qu'est-ce qui cause des frictions dans vos opérations ?",
        frictionHint: "Sélectionnez tout ce qui s'applique",
        notesLabel: "Contexte supplémentaire (optionnel)",
        notesPlaceholder: "Décrivez des défis spécifiques ou le contexte...",
      },
      step4: {
        title: "Contraintes et exigences",
        description: "Définissez les limites et exigences qui affectent vos opérations.",
        downtimeLabel: "À quel point vos opérations sont-elles sensibles aux temps d'arrêt ?",
        complianceLabel: "Quels cadres de sécurité ou de conformité s'appliquent ?",
        complianceHint: "Sélectionnez tout ce qui s'applique",
        complexityLabel: "Quelle est la complexité de la coordination interne ?",
      },
      step5: {
        title: "Résumé et réflexion",
        description: "Voici un résumé de ce que vous avez partagé — un aperçu clair de votre réalité opérationnelle actuelle.",
        noData: "Aucune donnée à afficher. Revenez en arrière et complétez les étapes précédentes.",
        contextTitle: "Contexte opérationnel",
        modelTitle: "Modèle opérationnel actuel",
        hurdlesTitle: "Obstacles identifiés",
        constraintsTitle: "Contraintes et exigences",
        industry: "Secteur",
        operations: "Opérations",
        scale: "Échelle",
        handling: "Traitement des demandes",
        structure: "Niveau de structure",
        tools: "Outils",
        downtime: "Sensibilité aux temps d'arrêt",
        compliance: "Conformité",
        complexity: "Complexité de coordination",
        disclaimer: "Cette simulation est indicative et reflète les informations fournies par l'utilisateur. Elle ne remplace pas une évaluation professionnelle. Toutes les conclusions sont soumises à un examen formel.",
      },
      industries: {
        manufacturing: "Fabrication",
        logistics: "Logistique",
        facility: "Gestion des installations",
        infrastructure: "Infrastructure",
        energy: "Énergie et services publics",
        other: "Autre",
      },
      operationTypes: {
        facility: "Opérations d'installations",
        logistics: "Logistique et entreposage",
        maintenance: "Maintenance et réparation",
        production: "Lignes de production",
        fieldService: "Service sur le terrain",
      },
      scales: {
        single: "Site unique",
        multi: "Multi-sites",
        distributed: "Distribué / Régional",
      },
      handlingMethods: {
        email: "Email",
        phone: "Appels téléphoniques",
        tickets: "Système de tickets",
        verbal: "Transmissions verbales",
        spreadsheets: "Tableurs",
        erp: "Système ERP / GMAO",
      },
      structureLevels: {
        adhoc: {
          title: "Principalement ad-hoc",
          desc: "Le travail est traité au fur et à mesure, standardisation minimale",
        },
        semiStructured: {
          title: "Semi-structuré",
          desc: "Certains processus définis, mais l'exécution varie",
        },
        fullyStructured: {
          title: "Entièrement structuré",
          desc: "Processus clairs avec exécution cohérente",
        },
      },
      toolTypes: {
        manual: "Principalement manuel",
        semiAutomated: "Semi-automatisé",
        fragmented: "Systèmes fragmentés",
        integrated: "Plateforme intégrée",
      },
      frictionPoints: {
        slowResponse: "Temps de réponse lents",
        unclearOwnership: "Responsabilité des tâches floue",
        manualCoordination: "Coordination manuelle excessive",
        inconsistentExecution: "Exécution incohérente",
        lackVisibility: "Manque de visibilité en temps réel",
        escalationDelays: "Retards d'escalade",
        documentationGaps: "Lacunes de documentation",
        communicationBreakdown: "Rupture de communication",
      },
      downtimeLevels: {
        critical: {
          title: "Critique",
          desc: "Chaque minute d'arrêt coûte considérablement",
        },
        high: {
          title: "Élevé",
          desc: "Les temps d'arrêt impactent sérieusement les opérations",
        },
        moderate: {
          title: "Modéré",
          desc: "Certaine tolérance, mais affecte la productivité",
        },
        low: {
          title: "Faible",
          desc: "Peut gérer des interruptions à court terme",
        },
      },
      complianceOptions: {
        iso: "ISO 9001 / 27001",
        hse: "HSE / Santé et sécurité",
        osha: "OSHA",
        vca: "VCA / SCC",
        gdpr: "RGPD / Protection des données",
        sectorSpecific: "Réglementations sectorielles",
      },
      complexityLevels: {
        simple: "Simple",
        moderate: "Modéré",
        complex: "Complexe",
        veryComplex: "Très complexe",
      },
      step6: {
        title: "Analyse opérationnelle",
        description: "Sur la base des informations fournies, voici une analyse opérationnelle de niveau exécutif de votre état actuel.",
        noData: "Données insuffisantes pour générer l'analyse. Veuillez compléter les étapes précédentes.",
        observationsTitle: "Observations opérationnelles",
        risksTitle: "Zones de risque et d'exposition",
        readinessTitle: "Évaluation de la préparation à l'exécution",
        advisoryTitle: "Direction consultative (Haut niveau)",
        disclaimer: "Cette analyse est indicative et basée sur des informations auto-déclarées. Elle ne remplace pas une évaluation professionnelle. Tous les résultats sont soumis à un examen formel.",
      },
      step7: {
        title: "Coordonnées",
        description: "Ces informations sont requises pour joindre l'analyse opérationnelle à une demande d'examen formel.",
        personalTitle: "Informations personnelles",
        companyTitle: "Informations sur l'entreprise",
        fullName: "Nom complet",
        fullNamePlaceholder: "Entrez votre nom complet",
        email: "Adresse e-mail",
        emailPlaceholder: "Entrez votre e-mail professionnel",
        phone: "Numéro de téléphone",
        phonePlaceholder: "Entrez votre numéro de téléphone",
        companyName: "Nom de l'entreprise",
        companyNamePlaceholder: "Entrez le nom de l'entreprise",
        role: "Rôle / Fonction",
        rolePlaceholder: "ex. Directeur des opérations",
        country: "Pays",
        countryPlaceholder: "Entrez le pays",
        city: "Ville",
        cityPlaceholder: "Entrez la ville",
        requiredNote: "Tous les champs marqués d'un * sont obligatoires. Vos informations seront utilisées uniquement pour traiter cette demande.",
      },
      analysis: {
        observations: {
          industry: {
            manufacturing: "L'exploitation dans un environnement manufacturier implique généralement une planification de production complexe, des cycles de maintenance d'équipement et des exigences de contrôle qualité.",
            logistics: "Les opérations logistiques nécessitent une coordination étroite entre plusieurs parties prenantes, des livraisons critiques en temps et une visibilité en temps réel sur les chaînes d'approvisionnement.",
            facility: "Les opérations de gestion des installations couvrent plusieurs catégories de services et nécessitent une coordination entre les équipes internes et les sous-traitants externes.",
            infrastructure: "Les opérations d'infrastructure impliquent souvent des actifs géographiquement distribués avec des exigences de maintenance variables et une supervision réglementaire.",
            energy: "Les opérations du secteur énergétique exigent le strict respect des protocoles de sécurité, la conformité réglementaire et la surveillance continue des systèmes critiques.",
            other: "Les opérations couvrent plusieurs domaines avec des exigences variables en matière de coordination, conformité et contrôle d'exécution.",
          },
          scale: {
            single: "Les opérations sur site unique permettent un contrôle centralisé mais peuvent encore faire face à des défis de coordination entre les équipes et les départements.",
            multi: "Les opérations multi-sites introduisent de la complexité dans la standardisation, la communication et l'assurance d'une exécution cohérente entre les sites.",
            distributed: "Les opérations distribuées ou régionales nécessitent des mécanismes de coordination robustes pour maintenir la visibilité et le contrôle sur les équipes dispersées.",
          },
          structure: {
            adhoc: "Une structure opérationnelle ad hoc suggère une forte dépendance au jugement individuel et une potentielle incohérence dans l'exécution.",
            semiStructured: "Les processus semi-structurés indiquent une standardisation partielle avec une marge de variation, ce qui peut conduire à des résultats incohérents.",
            fullyStructured: "Les opérations entièrement structurées fournissent une base pour une exécution prévisible, bien que l'efficacité dépende de l'adhésion et de l'application.",
          },
          tools: {
            fragmented: "Un paysage d'outils fragmenté conduit généralement à des silos d'information, une réconciliation manuelle des données et une surcharge de coordination.",
            manual: "Une forte dépendance aux processus manuels augmente le risque d'erreur et limite l'évolutivité des opérations.",
          },
        },
        risks: {
          predictability: "Risque de prévisibilité : Une exécution incohérente et des modèles de responsabilité flous peuvent conduire à des résultats opérationnels imprévisibles.",
          coordination: "Risque de coordination : La coordination manuelle et les lacunes de communication peuvent entraîner des retards, des erreurs et des échecs d'escalade.",
          downtime: "Exposition aux temps d'arrêt : Une haute sensibilité aux temps d'arrêt combinée aux modèles opérationnels actuels suggère un risque élevé de perturbations coûteuses.",
          compliance: "Exposition à la conformité : L'écart entre les exigences de conformité et le niveau de structure actuel peut créer des risques d'audit et réglementaires.",
          escalation: "Risque d'escalade : Les retards dans la résolution des problèmes et la visibilité limitée peuvent permettre à des problèmes mineurs de se transformer en problèmes opérationnels significatifs.",
        },
        readiness: {
          high: "L'organisation semble raisonnablement préparée au changement opérationnel, avec une structure et des outils existants fournissant une base pour les initiatives d'amélioration.",
          moderate: "L'organisation présente des indicateurs de préparation mixtes. Certains éléments fondamentaux existent, mais des lacunes dans la structure ou les outils peuvent nécessiter une attention avant de passer à l'échelle.",
          low: "Les modèles opérationnels actuels suggèrent une préparation limitée pour l'échelle ou un changement significatif sans d'abord combler les lacunes fondamentales de coordination et de structure.",
        },
        advisory: {
          ownership: "Envisagez d'établir des structures de responsabilité plus claires et des cadres de responsabilisation pour réduire la surcharge de coordination et améliorer la prévisibilité des réponses.",
          standardization: "Concentrez-vous sur la standardisation des processus opérationnels de base pour réduire la variation et établir des modèles d'exécution cohérents entre les équipes.",
          escalation: "Mettez en place des voies d'escalade structurées avec des fenêtres de réponse définies et une responsabilité claire à chaque niveau.",
          compliance: "Alignez la structure opérationnelle avec les exigences de conformité pour réduire l'exposition aux audits et assurer une adhésion cohérente aux normes réglementaires.",
          scale: "Pour les opérations multi-sites ou distribuées, priorisez les mécanismes qui assurent la cohérence et la visibilité entre les sites.",
        },
      },
    },
    submission: {
      title: "Demande reçue",
      body1: "Votre évaluation opérationnelle a été soumise avec succès.",
      body2: "Notre équipe examinera attentivement votre demande et vous recontactera en conséquence.",
      body3: "Pour accélérer les prochaines étapes, vous pouvez planifier une consultation stratégique ci-dessous.",
      scheduleBtn: "Planifier une consultation stratégique",
      closeBtn: "Fermer",
      referenceLabel: "Numéro de référence",
      emailSent: "Confirmation envoyée à",
      pdfAttached: "Résumé PDF joint pour vos archives",
    },
    governanceTrends: {
      title: "Opérer dans une réalité réglementée — ISO 27001, NIS2 & SOC 2",
      subtitle: "À mesure que les environnements opérationnels deviennent plus réglementés, interconnectés et scrutés, le leadership est de plus en plus défini par le contrôle, la documentation et la cohérence — et non par la vitesse ou l'improvisation.",
      column1: {
        title: "Ce que les leaders opérationnels priorisent",
        items: [
          "Instructions de travail claires et applicables",
          "Exécution prévisible entre les équipes et les postes",
          "Procédures de sécurité qui laissent peu de place à l'interprétation",
          "Moins de décisions ad hoc lors des incidents",
          "Documentation qui se suffit à elle-même"
        ]
      },
      column2: {
        title: "Normes, conformité et responsabilité",
        items: [
          "Systèmes de gestion formels basés sur des normes reconnues",
          "Procédures et actions correctives traçables",
          "Dossiers de formation et de compétence vérifiables",
          "Protection des données et contrôle d'accès par défaut",
          "Décisions qui peuvent être reconstituées après coup"
        ]
      },
      column3: {
        title: "Sécurité, hygiène et réalité opérationnelle",
        items: [
          "Les règles de sécurité doivent fonctionner sous pression, pas seulement sur le papier",
          "Les procédures d'hygiène doivent être cohérentes entre les sites",
          "Les écarts doivent être enregistrés, pas normalisés",
          "Les solutions de contournement informelles augmentent l'exposition",
          "La responsabilité personnelle se rapproche du terrain"
        ]
      },
      trend1: "À l'avenir, la tendance est claire : les opérations doivent être explicables, auditables et reproductibles — même en cas de perturbation.",
      trend2: "Les organisations qui réussiront seront celles qui intégreront les normes, la sécurité, l'hygiène et la discipline des données directement dans l'exécution quotidienne. Pas comme une surcharge, mais comme une structure opérationnelle."
    },
    chatbot: {
      greeting: "Bonjour ! Je suis Brooks. Comment puis-je vous aider aujourd'hui ?\n\nJe peux vous aider avec les informations produit, le support opérationnel, planifier une démo, essayer notre simulateur, la Conformité & Gouvernance ou répondre aux questions sur les tarifs.",
      subtitle: "Assistant Ovelon Prime",
      inputPlaceholder: "Écrivez un message...",
      buttons: {
        productInfo: "Info Produit",
        operationalSupport: "Support",
        scheduleDemo: "Planifier une démo",
        intake: "Diagnostic",
        simulation: "Simulateur",
        pricing: "Tarifs",
        contact: "Contact",
        warehouseSolutions: "Solutions d'entrepôt",
        workflowSolutions: "Solutions de flux",
        backToMain: "← Retour",
        connectSupport: "Contacter le support",
        bookDemo: "Réserver avec nous",
        startIntake: "Commencer →",
        viewPricing: "Voir les tarifs",
        goToForm: "Formulaire de contact",
        openSimulator: "Ouvrir le simulateur →",
      },
      responses: {
        productInfo: "Nous proposons des solutions d'entrepôt et de flux de travail adaptées à vos opérations.\n\nLiens rapides :\n• Solutions d'entrepôt\n• Solutions de flux\n\nY a-t-il quelque chose de spécifique que vous souhaitez savoir ?",
        operationalSupport: "Pouvez-vous me préciser le type de problème ? Je peux vous guider ou vous mettre en contact avec un expert.",
        scheduleDemo: "Je peux planifier une démo pour vous. Cliquez sur le bouton ci-dessous pour choisir un horaire qui vous convient !",
        intake: "Notre diagnostic en 7 étapes aide à identifier vos défis opérationnels :\n\n1️⃣ Contexte opérationnel\n2️⃣ Défis actuels\n3️⃣ Analyse des processus\n4️⃣ Stack technologique\n5️⃣ Objectifs & priorités\n6️⃣ Analyse exécutive\n7️⃣ Coordonnées\n\nVous recevrez un résumé PDF personnalisé. Prêt à commencer ?",
        pricing: "Notre plateforme commence à €4.999/mois et inclut la suite complète de solutions d'entrepôt et de flux.\n\nVoulez-vous voir la section tarifs détaillée ou planifier un appel pour discuter de vos besoins ?",
        contact: "Vous pouvez nous joindre à info@ovelon-prime.com ou utiliser notre formulaire de contact. Souhaitez-vous que je vous y conduise ?",
        simulation: "Notre simulateur de projet vous aide à visualiser les améliorations potentielles de vos opérations. Essayez-le !",
        simulatorOpened: "J'ai ouvert le simulateur de projet pour vous. Suivez les étapes pour voir vos améliorations potentielles !",
        connectSupport: "Je vous mets en contact avec notre équipe support. Vous pouvez aussi nous joindre directement à info@ovelon-prime.com",
        bookingOpened: "J'ai ouvert notre calendrier de réservation pour vous. Choisissez l'horaire qui vous convient le mieux !",
        backToMain: "Pas de problème ! Comment puis-je vous aider autrement ?",
        compliance: "Nous aidons les organisations à naviguer dans des paysages réglementaires complexes :\n\n🛡️ Directive NIS2 — Préparation à la conformité en matière de sécurité des réseaux et de l'information pour les infrastructures critiques.\n\n📋 ISO 27001 — Mise en œuvre du système de gestion de la sécurité de l'information et préparation aux audits.\n\n🔒 SOC 2 — Audits SOC 2 (Type I & Type II) pour les critères de services de confiance.\n\n📑 Gouvernance opérationnelle — Cadres de politiques, pistes d'audit et tableaux de bord de conformité.\n\nPosez-moi toute question sur ces cadres !",
        complianceThinking: "Laissez-moi vérifier cela pour vous...",
        complianceError: "Je n'ai pas pu trouver de réponse pour le moment. Veuillez consulter nos articles de conformité ou contacter info@ovelon-prime.com.",
        fallback: "Je n'ai pas cette information, mais l'équipe vous recontactera rapidement. Souhaitez-vous laisser vos coordonnées ou utiliser le formulaire de contact ?",
      },
      complianceHint: "Posez-moi vos questions sur SOC 2, ISO 27001 ou NIS2 ↵",
    },
    subscribe: {
      title: "Gardez une longueur d'avance",
      subtitle: "Recevez des informations exclusives sur l'optimisation des flux de travail, la gestion des opérations et les tendances du secteur directement dans votre boîte de réception.",
      promo: "🎁 E-Book Opérations gratuit si vous vous abonnez aujourd'hui !",
      fullName: "Nom complet",
      fullNamePlaceholder: "Entrez votre nom complet",
      email: "Adresse e-mail",
      emailPlaceholder: "nom@email.fr",
      submit: "S'abonner maintenant",
      submitting: "Abonnement en cours...",
      successTitle: "Bienvenue à bord !",
      successMessage: "Merci de vous être abonné. Vous recevrez bientôt nos dernières informations.",
      privacyText: "En vous abonnant, vous acceptez notre",
      privacyLink: "Politique de confidentialité",
      termsLink: "Conditions générales",
      andText: "et",
      missingInfo: "Informations manquantes",
      missingInfoDesc: "Veuillez remplir le nom et l'e-mail.",
      failedTitle: "Échec de l'abonnement",
    },
    caseStudies: {
      badge: "Résultats prouvés",
      title: "Transformer les opérations pour les industries critiques.",
      challengeLabel: "Le défi",
      solutionLabel: "La solution Ovelon Prime",
      resultsLabel: "Résultats quantifiables",
      cases: {
        facility: {
          industry: "Gestion d'installations",
          client: "Opérateur multi-sites",
          challenge: "La gestion des demandes de service sur plus de 40 sites distribués entraînait des temps de réponse incohérents, des fenêtres de maintenance manquées et une communication fragmentée entre les équipes sur site et la coordination centrale.",
          solution: "Ovelon Prime a unifié toutes les demandes entrantes dans un système d'intake structuré avec routage automatique, classification par priorité et suivi d'état en temps réel — permettant une supervision centralisée sans remplacer les outils existants.",
          results: [
            { value: "42%", label: "Temps de réponse plus rapides" },
            { value: "95%", label: "Taux de conformité SLA" },
            { value: "60%", label: "Réduction des erreurs de coordination" },
          ],
        },
        supplyChain: {
          industry: "Chaîne d'approvisionnement",
          client: "Réseau de distribution régional",
          challenge: "La coordination manuelle entre fournisseurs, entrepôts et partenaires de transport créait des angles morts dans la chaîne d'approvisionnement, entraînant des retards de livraison, des écarts d'inventaire et des coûts opérationnels croissants.",
          solution: "Ovelon Prime a mis en place une automatisation de bout en bout reliant approvisionnement, gestion des stocks et planification logistique — offrant une visibilité en temps réel et des alertes prédictives sur l'ensemble de la chaîne.",
          results: [
            { value: "38%", label: "Réduction des délais" },
            { value: "99,2%", label: "Taux de précision des commandes" },
            { value: "28%", label: "Coûts opérationnels réduits" },
          ],
        },
        warehouse: {
          industry: "Entreposage & Stockage",
          client: "Complexe de stockage industriel",
          challenge: "Le suivi des stocks reposait sur des tableurs fragmentés et des comptages manuels, causant de fréquentes divergences, une mauvaise allocation de l'espace et des retards de préparation de commandes.",
          solution: "Ovelon Prime a standardisé les flux d'inventaire avec un enregistrement automatique, des règles d'allocation par zone et une visibilité des stocks en temps réel — réduisant les interventions manuelles tout en améliorant la précision.",
          results: [
            { value: "55%", label: "Réduction des écarts de stock" },
            { value: "40%", label: "Exécution des commandes plus rapide" },
            { value: "35%", label: "Meilleure utilisation de l'espace" },
          ],
        },
        maintenance: {
          industry: "Réparation & Maintenance",
          client: "Services d'équipements industriels",
          challenge: "Les flux de maintenance réactive dominaient les opérations. Les ordres de travail étaient enregistrés de manière incohérente, le suivi des pièces était manuel et la planification des techniciens se basait sur la disponibilité plutôt que la priorité.",
          solution: "Ovelon Prime a introduit une gestion structurée des ordres de travail avec routage automatique par priorité, intégration de l'inventaire des pièces et dispatch optimisé des techniciens — passant d'une maintenance réactive à prédictive.",
          results: [
            { value: "47%", label: "Réduction des temps d'arrêt" },
            { value: "65%", label: "Exécution plus rapide des ordres" },
            { value: "30%", label: "Moins d'incidents récurrents" },
          ],
        },
      },
    },
    complianceBlog: {
      badge: "Conformité et Gouvernance",
      title: "NIS2, ISO 27001 & SOC 2 : Ce que les dirigeants opérationnels doivent savoir maintenant.",
      subtitle: "Alors que la pression réglementaire s'intensifie sur les infrastructures critiques, comprendre NIS2, ISO 27001 et SOC 2 n'est plus optionnel — c'est une question de survie opérationnelle.",
      articles: {
        nis2: {
          tag: "Directive UE 2022/2555",
          title: "Directive NIS2 : La nouvelle réalité opérationnelle pour les infrastructures critiques",
          excerpt: "La Directive NIS2 est la réglementation cybersécurité la plus significative de l'UE, couvrant 18 secteurs critiques et imposant des sanctions sévères pour non-conformité.",
          facts: [
            { label: "Secteurs couverts", value: "18" },
            { label: "Sanction maximale", value: "10M€ ou 2% CA" },
            { label: "Responsabilité direction", value: "Personnelle" },
          ],
          sections: [
            { heading: "Qu'est-ce que NIS2 et pourquoi est-ce important ?", text: "NIS2 remplace la Directive NIS originale de 2016 et élargit considérablement la portée des exigences de cybersécurité dans l'UE. Les organisations doivent mettre en œuvre la gestion des risques, le signalement des incidents, la sécurité de la chaîne d'approvisionnement et la planification de continuité — ou faire face à des sanctions allant jusqu'à 10 millions d'euros ou 2% du chiffre d'affaires mondial." },
            { heading: "La responsabilité opérationnelle est désormais personnelle", text: "Sous NIS2, la direction peut être tenue personnellement responsable de la non-conformité. Les RSSI, DG et membres du conseil doivent démontrer une gouvernance active de la cybersécurité et de la résilience opérationnelle." },
            { heading: "Des exigences strictes de signalement des incidents", text: "NIS2 exige un cadre de signalement échelonné : alerte précoce en 24 heures, notification détaillée en 72 heures et rapport final en un mois. Les organisations doivent disposer de processus structurés de détection, classification et escalade." },
            { heading: "La sécurité de la chaîne d'approvisionnement devient obligatoire", text: "La directive exige d'évaluer et de gérer les risques de cybersécurité dans toute la chaîne d'approvisionnement — y compris les évaluations de fournisseurs et la surveillance continue des risques tiers." },
          ],
          ovelonTitle: "Comment Ovelon Prime soutient la conformité NIS2",
          ovelonPoints: [
            "Admission et classification structurée des incidents avec des chemins d'escalade automatisés respectant les délais de 24/72 heures",
            "Pistes d'audit complètes pour chaque événement opérationnel",
            "Procédures d'exécution standardisées éliminant les opérations basées sur l'interprétation",
            "Visibilité opérationnelle en temps réel avec des tableaux de bord soutenant les obligations de gouvernance",
            "Flux de coordination de la chaîne d'approvisionnement imposant les exigences de sécurité",
            "Systèmes de notification automatisés garantissant une communication rapide lors des incidents",
          ],
        },
        iso27001: {
          tag: "Norme Internationale",
          title: "ISO 27001 : Construire un SMSI qui fonctionne réellement",
          excerpt: "ISO 27001 est la référence mondiale pour la gestion de la sécurité de l'information. Mais la certification n'est que le début — maintenir un SMSI efficace exige discipline opérationnelle et contrôle basé sur les preuves.",
          facts: [
            { label: "Contrôles Annexe A", value: "93" },
            { label: "Organisations certifiées", value: "70 000+" },
            { label: "Cycle de recertification", value: "3 ans" },
          ],
          sections: [
            { heading: "Pourquoi ISO 27001 est essentielle", text: "ISO 27001 fournit une approche systématique de la gestion des informations sensibles. La révision 2022 a réorganisé les contrôles en quatre domaines avec 93 contrôles dans l'Annexe A." },
            { heading: "L'écart entre certification et réalité opérationnelle", text: "Beaucoup d'organisations obtiennent la certification mais peinent à maintenir la discipline opérationnelle entre les audits. Les politiques existent sur papier mais ne sont pas appliquées dans les opérations quotidiennes." },
            { heading: "Contrôles de l'Annexe A nécessitant l'automatisation", text: "Plusieurs contrôles de l'Annexe A sont directement liés aux processus opérationnels : A.5.24-A.5.28 (gestion des incidents et collecte de preuves) et A.8.16 (activités de surveillance). Ces contrôles exigent des processus structurés et reproductibles." },
            { heading: "L'amélioration continue est obligatoire", text: "La Clause 10 exige l'amélioration continue du SMSI — identification systématique des non-conformités, actions correctives et évaluation de l'efficacité. Sans données opérationnelles structurées, l'amélioration continue devient de la conjecture." },
          ],
          ovelonTitle: "Comment Ovelon Prime soutient la conformité ISO 27001",
          ovelonPoints: [
            "Classification et réponse automatisées aux incidents alignées sur les contrôles A.5.24-A.5.28 de l'Annexe A",
            "Pistes d'audit complètes pour chaque événement opérationnel (A.5.28)",
            "Procédures d'exécution standardisées pour une mise en œuvre cohérente des contrôles",
            "Tableaux de bord de surveillance en temps réel pour la vérification continue de conformité (A.8.16)",
            "Flux de gestion du changement structurés avec chaînes d'approbation documentées",
            "Métriques opérationnelles et analyse des tendances pour l'amélioration continue sous la Clause 10",
          ],
        },
        soc2: {
          tag: "Critères de services de confiance AICPA",
          title: "SOC 2 : Démontrer la confiance opérationnelle par des contrôles de sécurité vérifiés",
          excerpt: "SOC 2 (Service Organization Control 2) est le principal cadre d'audit pour vérifier que les pratiques de sécurité de l'information d'une organisation répondent à des normes rigoureuses selon cinq critères de services de confiance. Un rapport SOC 2 est de plus en plus exigé par les clients enterprise et les secteurs réglementés.",
          type1Label: "Type I",
          type2Label: "Type II",
          type1: {
            facts: [
              { label: "Périmètre d'audit", value: "Point dans le temps" },
              { label: "Durée", value: "Date unique" },
              { label: "Critères de confiance", value: "Jusqu'à 5" },
            ],
            sections: [
              {
                heading: "Qu'est-ce que le SOC 2 Type I ?",
                text: "Un rapport SOC 2 Type I évalue si les contrôles de sécurité d'une organisation sont convenablement conçus à un moment précis. Il répond à la question : 'Les bons contrôles sont-ils en place aujourd'hui ?' Le Type I est généralement le point de départ pour les organisations qui visent la certification SOC 2.",
              },
              {
                heading: "Les cinq critères de services de confiance",
                text: "Le SOC 2 évalue les contrôles selon cinq critères : Sécurité (obligatoire), Disponibilité, Intégrité du traitement, Confidentialité et Vie privée. Le Type I vérifie que les politiques, procédures et contrôles techniques sont convenablement conçus — pas qu'ils fonctionnent de manière cohérente dans le temps.",
              },
              {
                heading: "Quand le Type I est approprié",
                text: "Le Type I est couramment utilisé lorsqu'une organisation doit rapidement démontrer un niveau de base d'assurance sécurité à des clients ou partenaires. C'est également le précurseur naturel du Type II.",
              },
            ],
          },
          type2: {
            facts: [
              { label: "Période d'audit", value: "6–12 mois" },
              { label: "Critères de confiance", value: "Jusqu'à 5" },
              { label: "Standard marché", value: "Enterprise" },
            ],
            sections: [
              {
                heading: "Qu'est-ce que le SOC 2 Type II ?",
                text: "Un rapport SOC 2 Type II évalue si les contrôles de sécurité d'une organisation non seulement existent mais fonctionnent efficacement sur une période prolongée — généralement 6 à 12 mois. C'est l'étalon-or pour la confiance enterprise et est de plus en plus exigé par les équipes de procurement dans les secteurs de la finance et des infrastructures critiques.",
              },
              {
                heading: "La discipline opérationnelle est le fondement",
                text: "Contrairement au Type I, le Type II exige une exécution cohérente, documentée et auditable des contrôles sur toute la période d'examen. Les journaux d'incidents doivent être complets, les contrôles d'accès appliqués systématiquement et la gestion des changements documentée.",
              },
              {
                heading: "Constats fréquents qui compromettent les audits Type II",
                text: "Les échecs Type II les plus courants concernent des journaux d'incidents incomplets, des révisions d'accès incohérentes, une documentation d'approbation manquante et des lacunes dans la surveillance de la sécurité.",
              },
              {
                heading: "SOC 2 Type II et conformité continue",
                text: "Contrairement à une certification ponctuelle, la conformité Type II est une discipline opérationnelle permanente. Les organisations doivent maintenir une documentation prête pour l'audit tout au long de l'année — pas seulement dans les semaines précédant la fenêtre d'audit.",
              },
            ],
          },
          ovelonTitle: "Comment Ovelon Prime soutient la préparation SOC 2",
          ovelonPoints: [
            "Flux de travail structurés d'intake et de réponse aux incidents produisant des preuves d'audit complètes et horodatées",
            "Flux de contrôle d'accès automatisés pour le provisionnement et le déprovisionnement cohérents des utilisateurs",
            "Documentation de gestion des changements avec chaînes d'approbation et horodatages",
            "Surveillance opérationnelle en temps réel alignée sur les critères SOC 2 de Sécurité et Disponibilité",
            "Procédures d'exécution standardisées éliminant les contournements non documentés",
            "Tableaux de bord de reporting de conformité pour une visibilité continue sur l'efficacité des contrôles",
          ],
        },
      },
    },
  },
  /* ===================== ES ===================== */
  es: {
    backToTop: "Volver arriba",
    explore: "Explorar",

    common: {
      back: "Volver",
    },

    nav: {
      services: "Servicios",
      pricing: "Precios",
      about: "Nosotros",
      contact: "Contacto",
      cta: "Discovery Call",
      viewDemo: "Ver demo",
    },
    hero: {
      badge: "Industrias críticas orientadas a resultados",
      title: "La complejidad operacional erosiona",
      titleHighlight: "la fiabilidad y rompe la disciplina de ejecución",
      subtitle:
        "Cuando la coordinación se vuelve manual, el mantenimiento se vuelve reactivo y las operaciones dependen de soluciones alternativas, el control ya se ha perdido.",
      subtitle2:
        "Restauramos la ejecución predecible y disciplinada dentro de las operaciones en vivo — sin reemplazar sistemas centrales ni interrumpir los flujos de trabajo diarios.",
      subtitleMobile: "Cuando la coordinación se vuelve manual, el control ya se ha perdido.",
      cta: "Recupere el control de las operaciones",
      simulatorCta: "Ingesta de Demanda y Ejecución",
    },
     problem: {
       badge: "El Desafío",
       title: "La fricción operacional cuesta más de lo que crees.",
       description: "Cada llamada perdida, error de reserva y momento de sobrecarga del personal representa ingresos perdidos y una experiencia del huésped disminuida. Tu recepción no debería ser tu cuello de botella.",
       pain1: "Llamadas perdidas en horas pico",
       pain2: "Reservas dobles y conflictos de horarios",
       pain3: "Personal abrumado con tareas administrativas",
       pain4: "Comunicación inconsistente con huéspedes",
       pain5: "Ingresos perdidos por brechas operacionales",
     },
     process: {
       badge: "El Proceso",
       title: "Integración perfecta con tus operaciones.",
       step1: { title: "Capturar", description: "Cada llamada, mensaje y solicitud de reserva se maneja con precisión en el momento en que llega." },
       step2: { title: "Coordinar", description: "La alineación en tiempo real con tu calendario y flujos de trabajo asegura una programación fluida." },
       step3: { title: "Confirmar", description: "Confirmaciones automáticas, actualizaciones y escalaciones cuando sea necesario — nada se escapa." },
     },
     workflowDemo: {
       badge: "Demo Interactiva",
       title: "Ve Ovelon Prime en acción.",
        tabs: { workflow: "Automatización de Flujos", call: "Recuperación de Llamadas", appointment: "Programación de Incidentes", safety: "Manuales de Seguridad" },
        workflow: {
          description: "Observa cómo una simple consulta activa un flujo de trabajo operacional completo — automáticamente.",
          runDemo: "EJECUTAR DEMO DEL FLUJO",
          complete: "Flujo completo ejecutado en menos de 60 segundos",
          reset: "REINICIAR DEMO",
          processing: "Procesando flujo...",
          step1: { title: "Solicitud recibida", desc: "Todas las solicitudes o incidentes entrantes se capturan y registran en tiempo real." },
          step2: { title: "Disponibilidad verificada", desc: "El sistema verifica inventario, capacidad o calendario para confirmar viabilidad." },
          step3: { title: "Orden/Tarea creada", desc: "Las solicitudes se transforman en órdenes o tareas accionables." },
          step4: { title: "Confirmación enviada", desc: "Notificaciones automáticas por correo, SMS o paneles internos." },
          step5: { title: "Equipo notificado", desc: "Los equipos operativos reciben alertas instantáneas para procesamiento." },
          step6: { title: "Perfil e historial actualizado", desc: "Toda la información se registra automáticamente para referencia futura." },
        },
        call: {
          description: "Ve cómo Ovelon Prime transforma una oportunidad potencialmente perdida en un acuerdo confirmado.",
          simulate: "SIMULAR LLAMADA ENTRANTE",
          stage1: "Llamada entrante...",
          stage2: "Sin Ovelon Prime: Llamada perdida, sin acuerdo",
          stage3: "Con Ovelon Prime: Llamada contestada al instante",
          stage4: "Solicitud de incidente del cliente capturada",
          stage5: "Solicitud de incidente confirmada",
          revenueLost: "Ingresos/seguimiento perdidos. Cliente insatisfecho.",
          revenueCaptured: "Ingresos/seguimiento capturados. Cliente satisfecho.",
        },
        appointment: {
          description: "Ve cómo las solicitudes, incidentes y escalaciones se programan, asignan y rastrean automáticamente — sin coordinación manual.",
          runDemo: "EJECUTAR DEMO DE INCIDENTES",
          complete: "Ciclo completo de programación de incidentes en menos de 60 segundos",
          reset: "REINICIAR DEMO",
          processing: "Procesando incidente...",
          step1: { title: "Incidente reportado", desc: "Un incidente o solicitud de servicio se envía por teléfono, formulario o sistema de alerta automatizado." },
          step2: { title: "Prioridad clasificada", desc: "El sistema clasifica la gravedad y urgencia según reglas de escalación predefinidas." },
          step3: { title: "Equipo de respuesta asignado", desc: "Los respondedores calificados se identifican y asignan según disponibilidad y experiencia." },
          step4: { title: "Ruta de escalación activada", desc: "Si se superan los umbrales de SLA, la escalación automática notifica a supervisores y dirección." },
          step5: { title: "Resolución rastreada", desc: "El progreso se monitorea en tiempo real con actualizaciones de estado enviadas a todas las partes." },
          step6: { title: "Informe post-incidente archivado", desc: "Detalles del incidente, tiempos de respuesta y pasos de resolución se documentan para cumplimiento." },
        },
        safety: {
          description: "Ve cómo los manuales de video animados de seguridad estandarizados se despliegan en almacenes industriales — asegurando el cumplimiento HSE consistente.",
          runDemo: "EJECUTAR DEMO DE SEGURIDAD",
          complete: "Despliegue del manual de seguridad completado exitosamente",
          reset: "REINICIAR DEMO",
          processing: "Desplegando contenido de seguridad...",
          step1: { title: "Peligro identificado", desc: "Un peligro en el lugar de trabajo o actualización de procedimiento activa la creación de una nueva instrucción visual." },
          step2: { title: "Manual animado creado", desc: "Se genera un manual de video animado paso a paso siguiendo las normas ISO/VCA/OSHA." },
          step3: { title: "Localizado en múltiples idiomas", desc: "El contenido se traduce y localiza automáticamente para el cumplimiento de la fuerza laboral multilingüe." },
          step4: { title: "Distribuido a estaciones", desc: "Los manuales de video se envían instantáneamente a quioscos, tabletas y dispositivos de operadores." },
          step5: { title: "Acuse de recibo rastreado", desc: "La visualización y comprensión de cada operador se registra para auditoría y certificación." },
          step6: { title: "Informe de cumplimiento generado", desc: "Se generan informes de cumplimiento automatizados que muestran la cobertura de capacitación en todos los turnos." },
        },
      },
    video: {
      title: "Diseñado para operaciones ininterrumpidas",
      subtitle: "Vea cómo Ovelon Prime apoya entornos de misión crítica sin interrumpir los sistemas en vivo.",
      cta: "Discovery Call",
    },
    services: {
      label: "LO QUE ESTABILIZAMOS",
      title: "Fiabilidad operacional en entornos",
      titleHighlight: "que no pueden permitirse errores",
      subtitle:
        "Eliminamos las averías operacionales causadas por la coordinación manual, la ejecución poco clara y el manejo reactivo de incidentes — restaurando el control, la responsabilidad y los resultados predecibles dentro de las operaciones en vivo.",
      items: [
        {
          title: "Gestión de solicitudes y demanda",
          description:
            "Las solicitudes entrantes se capturan, validan y enrutan de manera consistente — sin depender del seguimiento ad hoc o del juicio individual.",
          tags: ["Control de admisión inteligente", "Reglas de calificación", "Enrutamiento del sistema"],
        },
        {
          title: "Estándares de seguridad y ejecución",
          description:
            "Instrucciones visuales estandarizadas utilizadas para alinear a operadores, contratistas y equipos en procedimientos críticos de seguridad — reduciendo errores de interpretación, desviaciones y riesgo de ejecución.",
          tags: [
            "Procedimientos HSE",
            "Alineación ISO / VCA / OSHA",
            "Incorporación de operadores y contratistas",
          ],
        },
        {
          title: "Gestión de incidentes y escalamiento",
          description:
            "Los problemas se registran, clasifican y escalan de manera controlada — asegurando responsabilidad, claridad de respuesta y rutas de resolución predecibles.",
          tags: ["Clasificación de problemas", "Rutas de escalamiento", "Propiedad de la respuesta"],
        },
        {
          title: "Eficiencia en la concertación de citas",
          description:
            "Programación fluida mediante voz que gestiona reservas, confirmaciones y cambios de horario con precisión, liberando a tu equipo, manteniendo todas las conexiones al día y ofreciendo una experiencia impecable al cliente.",
          tags: ["Programación de voz", "Disponibilidad 24/7", "respuesta automática de devolución de llamada"],
        },
      ],
      clickToExplore: "Haga clic para explorar",
    },
    serviceDetails: {
      keyBenefits: "Beneficios clave",
      cta: "Explorar demo",
      benefits: [
        [
          "La validación automatizada de admisión elimina cuellos de botella de revisión manual",
          "El enrutamiento inteligente reduce el tiempo de respuesta hasta un 60%",
          "Rastro de auditoría completo para cumplimiento y responsabilidad",
          "Integración transparente con sistemas de tickets existentes",
        ],
        [
          "Los SOP visuales reducen el tiempo de capacitación y los errores de interpretación",
          "Seguimiento de cumplimiento en tiempo real en todos los turnos",
          "Incorporación de contratistas estandarizada en todas las ubicaciones",
          "Procedimientos alineados con HSE listos para auditorías regulatorias",
        ],
        [
          "La clasificación automatizada asegura una priorización consistente de problemas",
          "Rutas de escalamiento claras con propiedad definida en cada nivel",
          "SLA de respuesta rastreados y aplicados automáticamente",
          "Análisis post-incidente para la mejora continua",
        ],
        [
          "Disponibilidad 24/7 sin aumentar la plantilla",
          "La reprogramación inteligente minimiza las ausencias",
          "Confirmación multicanal (voz, SMS, correo electrónico)",
          "Sincronización de calendario transparente con sus sistemas existentes",
        ],
      ],
    },
    sectors: {
      label: "Sectores",
      title: "Fiabilidad operacional para",
      titleHighlight: "industrias de misión crítica",
      subtitle: "Apoyamos entornos operacionales donde el tiempo de inactividad, la desalineación o la respuesta tardía impactan directamente la seguridad, la continuidad o la producción.",
      items: [
        {
          name: "Gestión de instalaciones",
          description: "Gestión de activos distribuidos y solicitudes de servicio en instalaciones complejas — con baja tolerancia a retrasos o mala coordinación.",
        },
        {
          name: "Mantenimiento y reparación",
          description: "Operar bajo presión constante para prevenir tiempos de inactividad en mantenimiento reactivo y preventivo — a menudo con información incompleta y ventanas de respuesta ajustadas.",
        },
        {
          name: "Logística y almacenamiento",
          description: "Coordinación de personal, equipos y flujos de trabajo bajo plazos estrictos y presión operacional continua.",
        },
      ],
    },
    testimonials: {
      label: "VOCES DE CLIENTES",
      title: "Testimonios de clientes",
      items: [
        {
          quote: "Antes de Ovelon Prime, la coordinación de turnos dependía del seguimiento individual. Hoy, el mantenimiento y la planificación se ejecutan de manera predecible en todos los turnos — sin intervención manual.",
          name: "Patrick Sullivan",
          title: "Líder de operaciones logísticas",
          facility: "Centro de distribución, Nebraska",
        },
        {
          quote: "En nuestro entorno, los retrasos afectan directamente la seguridad y la producción. Ovelon Prime nos ayudó a estandarizar la ejecución entre equipos, reduciendo el tiempo de respuesta en un 45% sin aumentar el riesgo operacional.",
          name: "Alicia Zhang",
          title: "Directora de operaciones",
          facility: "Instalación de almacenamiento, Ottawa",
        },
        {
          quote: "Operamos bajo presión continua. Desde que implementamos el sistema, el enrutamiento y el escalamiento siguen reglas fijas — no suposiciones. El tiempo de inactividad causado por errores de coordinación ya no es un factor.",
          name: "Wyatt Coleman",
          title: "Gerente de planta",
          facility: "Planta de fabricación, Austin",
        },{
  quote: "Gestionar una instalación de este tamaño implica miles de piezas en movimiento. El sistema nos dio una fuente única de verdad para el estado de las tareas, los ciclos de equipos y las verificaciones de cumplimiento. Dejamos de perseguir actualizaciones y empezamos a actuar en base a ellas.",
  name: "James Whitfield",
  title: "Director de operaciones de instalaciones",
  facility: "Almacén industrial, Liverpool",
},
{
  quote: "En nuestro entorno, incluso pequeños retrasos se acumulan rápidamente. La plataforma impone responsabilidad en tiempo real en cada departamento. Hemos eliminado las zonas grises que solían paralizar la producción durante horas.",
  name: "Ciarán O'Sullivan",
  title: "Responsable principal de instalaciones",
  facility: "Planta de procesamiento, Tennessee",
},
{
  quote: "Perdíamos horas cada semana con el despacho manual y órdenes de trabajo mal dirigidas. Desde la puesta en marcha, nuestros equipos siguen flujos de trabajo estructurados de principio a fin — sin conjeturas, sin cuellos de botella. El rendimiento ha aumentado y nuestra tasa de errores se ha estabilizado.",
  name: "Rachel Moreno",
  title: "Gerente de control de operaciones",
  facility: "Centro logístico, Dublín",
}
      ],
    },
    operationalTrust: {
      heading: "Diseñado para entornos regulados y críticos para la seguridad",
      items: [
        { text: "Principios de seguridad alineados con ISO 27001" },
        { text: "Control de procesos estilo ISO 9001" },
        { text: "Diseño operacional consciente de HSE" },
        { text: "Documentación y controles listos para auditoría" },
        { text: "Propiedad total del cliente — sin sistemas de caja negra" },
      ],
      footer: "Diseñado para entornos revisados por CISO con gobernanza de seguridad interna, políticas de control de acceso y procedimientos de gestión del cambio.",
    },
    about: {
      label: "Nosotros",
      title: "Construido sobre experiencia operacional,",
      titleHighlight: "impulsado por la precisión",
      description1:
        "Ovelon Prime trabaja con organizaciones que operan bajo presión constante — donde el tiempo de inactividad, los retrasos y los procesos manuales impactan directamente el costo, la seguridad y la fiabilidad.",
      description2:
        "Combinamos una profunda experiencia en el dominio operacional con capas de control operacional para diseñar sistemas que se integran perfectamente en los flujos de trabajo existentes, sin interrumpir las operaciones críticas.",
      quote:
        '"La precisión no es opcional. En entornos donde la fiabilidad determina los resultados, los sistemas deben estar diseñados para funcionar bajo presión."',
      quoteAuthor: "Equipo directivo · Ovelon Prime",
      stats: {
        uptime: "Tiempo de actividad del sistema operacional",
        deployments: "Despliegues de automatización",
        industries: "Industrias críticas atendidas",
      },
    },
    pricing: {
      label: "Precios",
      title: "Precios basados en compromiso, con resultados claramente definidos",
      subtitle:
        "Compromisos claramente definidos enfocados en mejorar la fiabilidad, reducir la fricción operacional y entregar un impacto medible.",
      focused: {
        name: "Compromiso operacional enfocado",
        description:
          "Un compromiso de alcance fijo diseñado para resolver un cuello de botella operacional claramente definido con impacto medible.",
        requestQuote: "Solicitar presupuesto",
        cta: "Solicitar evaluación de compromiso",
        features: [
          "Mejora operacional única y claramente definida",
          "Mapeo de procesos y responsabilidades",
          "Implementación y despliegue controlados",
          "Documentación y transferencia operacional",
          "Validación post-implementación",
        ],
      },
      enterprise: {
        name: "Programa operacional empresarial",
        description:
          "Sistemas operacionales personalizados para organizaciones complejas con requisitos estrictos de gobernanza, cumplimiento y continuidad.",
        priceLabel: "Precios personalizados",
        price: "Servicio personalizado",
        period: "tarifa única",
        cta: "Solicitar evaluación inicial",
        badge: "Empresa",
        features: [
          "Arquitectura operacional multi-sistema",
          "Integraciones personalizadas con sistemas existentes",
          "Soporte dedicado de implementación y transición",
          "Diseño operacional consciente del cumplimiento",
          "Marco SLA y gobernanza personalizado",
        ],
      },
      securityNote:
        "Cada compromiso se construye con seguridad de nivel empresarial, protección de datos y cumplimiento operacional.",
      talkToExpert: "Discovery Call",
      intakeNote:
        "Admisión estructurada · Sin presión de ventas · Directamente con un experto experimentado",
      reviewNote: "Todas las solicitudes se revisan antes de programar.",
    },
    contact: {
      label: "COMPROMISO",
      title: "¿Listo para recuperar el control de",
      titleHighlight: " sus operaciones?",
      subtitle: "Todos los compromisos se definen por adelantado y están sujetos a una revisión formal antes de continuar.",
      cta: "Programar consulta",
      guarantee: "Sin compromisos a largo plazo.",
      guaranteeHighlight: "Los compromisos continúan solo cuando se demuestra el valor operacional",
      guaranteeSuffix: " en compromisos calificados.",
      enterpriseLabel: "Consultas empresariales: ",
      rightTitle: "Contacte con Ovelon Prime",
      infoText1: "Si tiene alguna pregunta o desea hablar sobre cómo podemos apoyar su negocio, no dude en contactarnos.",
      infoText2: "El soporte al cliente está disponible las 24 horas, los 7 días de la semana.",
    },
    contactForm: {
      firstName: "Nombre",
      firstNamePlaceholder: "Juan",
      lastName: "Apellido",
      lastNamePlaceholder: "García",
      businessName: "Nombre de la empresa",
      businessNamePlaceholder: "Industrias Acme",
      businessEmail: "Correo electrónico empresarial",
      businessEmailPlaceholder: "juan@empresa.es",
      requestDetails: "Detalles de la solicitud",
      requestDetailsPlaceholder: "Cuéntenos sobre sus desafíos operacionales y lo que desea lograr...",
      submit: "Enviar solicitud",
      submitting: "Enviando...",
      validationError: "Por favor complete todos los campos obligatorios correctamente.",
      successTitle: "Enviado con éxito",
      successMessage: "Revisaremos su solicitud cuidadosamente.",
      successCta: "Reservar una consulta estratégica",
    },
    faq: {
      title: "Preguntas frecuentes",
      category: "Compromiso y entrega",
      items: [
        {
          question: "¿Cómo funciona Ovelon Prime?",
          answer:
            "Comenzamos con una admisión estructurada para entender la restricción operacional. Diseñamos y validamos una solución de alcance fijo contra flujos de trabajo reales. Implementamos, probamos bajo carga y entregamos con documentación completa.",
        },
        {
          question: "¿En qué industrias se especializan?",
          answer:
            "Trabajamos con organizaciones que operan en entornos de misión crítica, incluyendo manufactura, logística y gestión de instalaciones. Si los retrasos, errores o tiempos de inactividad impactan directamente la seguridad, la producción o la continuidad, somos el socio adecuado.",
        },
        {
          question: "¿Es esto una suscripción o servicio continuo?",
          answer:
            "No. Los compromisos tienen alcance fijo y están orientados a resultados. No hay suscripción obligatoria, dependencia de licencia ni tarifas continuas. Usted es dueño del sistema después de la entrega.",
        },
        {
          question: "¿Cuánto tiempo dura una implementación típica?",
          answer:
            "La mayoría de los compromisos enfocados se entregan en 12-16 días, dependiendo del alcance y acceso al sistema. Los programas empresariales siguen un despliegue por fases alineado con las prioridades operacionales.",
        },
        {
          question: "¿Esto se integrará con nuestros sistemas existentes?",
          answer:
            "Sí. Las soluciones están diseñadas para integrarse con sistemas existentes sin reemplazar plataformas centrales. Trabajamos dentro de sus herramientas actuales y límites de datos.",
        },
        {
          question: "¿Necesitamos cambiar nuestros flujos de trabajo actuales?",
          answer:
            "No. Diseñamos alrededor de sus flujos de trabajo existentes. Donde se requieran cambios, son mínimos, documentados y validados con sus equipos operacionales antes del despliegue.",
        },
        {
          question: "¿Quién es dueño de la optimización después de la entrega?",
          answer:
            "Usted. Todos los sistemas, configuraciones y documentación se entregan al finalizar. No hay dependencia del proveedor. Usted retiene la propiedad operacional y técnica completa.",
        },
        {
          question: "¿Cómo manejan la seguridad y el cumplimiento?",
          answer:
            "La seguridad y el cumplimiento se tratan como restricciones de diseño, no como ocurrencias tardías. Cada compromiso se diseña alrededor de sus políticas de seguridad existentes, controles de acceso y requisitos de cumplimiento. Trabajamos dentro de su infraestructura aprobada, límites de datos y marcos de gobernanza.",
        },
        {
          question: "¿Qué nivel de participación interna se requiere?",
          answer:
            "Participación mínima y estructurada. Requerimos entrada limitada durante la admisión para entender las restricciones operacionales y validar suposiciones. La implementación se maneja de forma independiente, con puntos de control claros para revisión y aprobación.",
        },
        {
          question: "¿Es Ovelon Prime adecuado para grandes entornos empresariales?",
          answer:
            "Sí. Estamos diseñados para entornos complejos de múltiples partes interesadas con requisitos estrictos de gobernanza, cumplimiento y operacionales. Los compromisos están estructurados para alinearse con la gestión del cambio empresarial, revisiones de seguridad y despliegues por fases.",
        },
      ],
    },
    metricsStrip: {
      items: [
        { value: "40%", label: "Reducción de inactividad" },
        { value: "200+", label: "Flujos automatizados" },
        { value: "3", label: "Continentes cubiertos" },
        { value: "98%", label: "Cumplimiento de SLA" },
      ],
    },
    footer: {
      privacy: "Privacidad",
      terms: "Términos",
      copyright: "© {{year}} Ovelon Prime. Todos los derechos reservados.",
      tagline: "Sistemas operativos empresariales construidos para claridad, control y escala.",
      trustHeading: "Diseñado para entornos regulados y críticos para la seguridad",
      trustItems: [
        "Principios de seguridad alineados con ISO 27001",
        "Controles conformes con SOC 2 Tipo II",
        "Preparación para la Directiva NIS2",
        "Documentación lista para auditoría",
        "Diseño operativo consciente de HSE",
        "Propiedad total del cliente — sin sistemas de caja negra",
      ],
      badgeIsoTitle: "ISO 27001",
      badgeIsoDesc: "Gestión certificada de seguridad de la información",
      badgeSocTitle: "SOC 2 Type II",
      badgeSocDesc: "Controles de organizaciones de servicios",
      badgeGdprTitle: "RGPD",
      badgeGdprDesc: "Reglamento de protección de datos y privacidad",
    },
    industrialTrust: {
      heading: "Confiado por operadores en entornos de misión crítica",
      disclaimer: "Operadores representativos de sectores industriales, logísticos, manufactureros e infraestructura. Los compromisos varían según alcance y entorno.",
    },
    intake: {
      title: "Calificación de consulta",
      subtitle: "Por favor complete el breve formulario a continuación. Todas las presentaciones se revisan antes de programar.",
    },
    cookie: {
      message: "Usamos cookies para mejorar su experiencia. Al continuar visitando este sitio, acepta nuestro uso de cookies.",
      accept: "Aceptar",
      decline: "Rechazar",
      learnMore: "Más información sobre nuestra política de privacidad",
    },
    privacy: {
      title: "Política de privacidad",
      lastUpdated: "Última actualización: 01.12.2025",
      sections: {
        introduction: {
          title: "1. Introducción",
          p1: "En Ovelon Prime, tomamos en serio la protección de datos y la confidencialidad. Esta Política de Privacidad explica cómo recopilamos, usamos y protegemos la información personal y empresarial cuando interactúa con nuestro sitio web, formularios de admisión y servicios.",
          p2: "Procesamos datos de manera responsable y solo en la medida necesaria para entregar nuestros servicios, asegurar la integridad operacional y cumplir con las obligaciones legales.",
        },
        informationCollected: {
          title: "2. Información que recopilamos",
          personal: "Información personal:",
          personalDesc: "Nombre, dirección de correo electrónico empresarial, número de teléfono e información enviada a través de formularios o correspondencia directa.",
          business: "Información empresarial y operacional:",
          businessDesc: "Nombre de la empresa, dirección comercial o jurisdicción, clasificación de la industria, contexto operacional e información relacionada con el cumplimiento proporcionada voluntariamente durante la admisión.",
          technical: "Información técnica:",
          technicalDesc: "Dirección IP, metadatos del navegador y dispositivo, cookies y análisis de uso básico.",
          note: "No recopilamos intencionalmente datos personales sensibles a menos que se requiera explícitamente para cumplimiento o entrega de servicios.",
        },
        howWeUse: {
          title: "3. Cómo usamos su información",
          items: [
            "Evaluar y calificar solicitudes de consulta",
            "Entregar y apoyar servicios de automatización operacional",
            "Comunicar sobre compromisos, actualizaciones o aclaraciones",
            "Mantener la fiabilidad, seguridad y rendimiento de la plataforma",
            "Cumplir con obligaciones legales, regulatorias o contractuales",
          ],
          note: "No usamos sus datos para marketing no solicitado, perfilado o propósitos de publicidad de terceros.",
        },
        dataSharing: {
          title: "4. Compartir y divulgación de datos",
          p1: "Ovelon Prime no vende, alquila ni intercambia datos personales o empresariales.",
          p2: "La información solo puede compartirse cuando sea estrictamente necesario:",
          items: [
            "Con proveedores de servicios examinados requeridos para la entrega del servicio",
            "Para cumplir con obligaciones legales o regulatorias",
            "Para proteger la seguridad, integridad o derechos de nuestros sistemas y clientes",
          ],
          note: "Los datos nunca se comparten para marketing o propósitos promocionales de terceros.",
        },
        dataSecurity: {
          title: "5. Seguridad de datos",
          p1: "Aplicamos salvaguardas técnicas y organizacionales estándar de la industria, incluyendo cifrado, controles de acceso y prácticas de infraestructura segura.",
          p2: "Si bien ningún sistema puede garantizar seguridad absoluta, operamos bajo protocolos internos estrictos diseñados para minimizar el riesgo y proteger la confidencialidad en todo momento.",
        },
        dataRetention: {
          title: "6. Retención de datos y sus derechos",
          p1: "Retenemos datos solo mientras sea necesario para cumplir con los propósitos descritos en esta política o para cumplir con requisitos legales.",
          p2: "Usted tiene derecho a solicitar acceso, corrección o eliminación de sus datos, donde corresponda.",
        },
        contact: {
          title: "7. Contacto",
          p1: "Para preguntas sobre esta Política de Privacidad o prácticas de manejo de datos, por favor contacte:",
        },
      },
    },
    terms: {
      title: "Términos y condiciones",
      lastUpdated: "Última actualización: 01 de diciembre de 2025",
      sections: {
        introduction: {
          title: "1. Introducción",
          p1: "Estos Términos y Condiciones (\"Términos\") rigen su acceso y uso del sitio web de Ovelon Prime, procesos de consulta y servicios de automatización profesional. Al acceder al sitio web o contratar nuestros servicios, acepta estar sujeto a estos Términos.",
          p2: "Si no está de acuerdo con estos Términos, debe suspender el uso del sitio web y los servicios inmediatamente.",
        },
        services: {
          title: "2. Servicios proporcionados",
          p1: "Ovelon Prime proporciona servicios profesionales de automatización, diseño de sistemas e implementación para entornos industriales, logísticos, de gestión de instalaciones, infraestructura y otros entornos operacionalmente críticos.",
          p2: "Todos los servicios están sujetos a disponibilidad y revisión. Nos reservamos el derecho de modificar o suspender servicios en cualquier momento sin previo aviso.",
        },
        engagement: {
          title: "3. Alcance del compromiso",
          p1: "Cada compromiso se define a través de un proceso estructurado de admisión y está sujeto a un acuerdo de alcance formal.",
          p2: "Ninguna comunicación, propuesta o discusión constituye un compromiso vinculante a menos que se acuerde explícitamente por escrito.",
        },
        clientResponsibilities: {
          title: "4. Responsabilidades del cliente",
          items: [
            "Proporcionar información precisa y oportuna durante la admisión y el compromiso",
            "Mantener la confidencialidad de las credenciales de acceso y cuentas",
            "Cumplir con todas las leyes y regulaciones aplicables",
            "Notificarnos cualquier cambio que pueda afectar el alcance del compromiso",
          ],
        },
        intellectualProperty: {
          title: "5. Propiedad intelectual",
          p1: "Todo el contenido, diseño e implementaciones entregados bajo un compromiso se convierten en propiedad del cliente tras el pago completo.",
          p2: "Ovelon Prime retiene todos los derechos sobre metodologías propietarias, marcos y herramientas no entregados explícitamente como parte del compromiso.",
        },
        confidentiality: {
          title: "6. Confidencialidad",
          p1: "Ambas partes acuerdan mantener la confidencialidad de toda la información propietaria compartida durante el compromiso.",
          p2: "Los términos de confidencialidad pueden formalizarse en acuerdos separados según sea necesario.",
        },
        limitation: {
          title: "7. Limitación de responsabilidad",
          p1: "Ovelon Prime no será responsable de daños indirectos, incidentales o consecuentes que surjan del uso de nuestros servicios.",
          p2: "Nuestra responsabilidad máxima está limitada a las tarifas pagadas por el compromiso específico en cuestión.",
        },
        termination: {
          title: "8. Terminación",
          p1: "Cualquiera de las partes puede terminar un compromiso con aviso por escrito.",
          p2: "Tras la terminación, el cliente tendrá derecho a todo el trabajo completado hasta la fecha de terminación.",
        },
        governing: {
          title: "9. Ley aplicable",
          p1: "Estos Términos se regirán e interpretarán de acuerdo con las leyes de los Países Bajos.",
        },
        contact: {
          title: "10. Contacto",
          p1: "Para preguntas sobre estos Términos, por favor contacte:",
        },
      },
    },
    demo: {
      header: "Análisis de Compensaciones Operacionales",
      close: "Cerrar",
      beforeAlignment: "Antes del Alineamiento",
      afterAlignment: "Después del Alineamiento",
      tradeoff: "Compensación",
      footer: "Escenarios operacionales representativos de entornos industriales, logísticos y de instalaciones.",
      intake: {
        title: "Recepción de Trabajo y Gestión de Demanda",
        metrics: {
          coordinationTime: "Tiempo de Coordinación",
          reworkCycles: "Ciclos de Retrabajo",
          ownership: "Propiedad",
          ownershipValue: "Definida por adelantado",
        },
        before: {
          title: "Recepción fragmentada y coordinación reactiva",
          text: "El trabajo entra a través de correos electrónicos, llamadas y canales informales. La calidad de la información varía y la propiedad cambia durante la ejecución.",
          bullet1: "Múltiples vías de entrada",
          bullet2: "Ciclos de aclaración manuales",
          bullet3: "Prioridad impulsada por urgencia",
          bullet4: "La ejecución depende de individuos",
          tradeoff: "Tiempo perdido en coordinación y corrección.",
        },
        after: {
          title: "Recepción estructurada con ejecución controlada",
          text: "Las solicitudes siguen una única vía de entrada. La información se valida por adelantado y la propiedad se asigna antes de que comience la ejecución.",
          bullet1: "Entrada única estructurada",
          bullet2: "Clasificación y enrutamiento claros",
          bullet3: "Propiedad asignada una vez",
          bullet4: "Flujo de ejecución predecible",
          tradeoff: "La coordinación disminuye, la ejecución se estabiliza.",
        },
      },
      safety: {
        title: "Seguridad y Estándares de Ejecución",
        metrics: {
          procedureDeviations: "Desviaciones de Procedimiento",
          onboardingTime: "Tiempo de Incorporación",
          consistency: "Consistencia",
          consistencyValue: "Estandarizada",
        },
        before: {
          title: "Ejecución basada en interpretación",
          text: "Los procedimientos de seguridad existen en documentos y conocimiento tribal. Los operadores interpretan los estándares de manera diferente.",
          bullet1: "Procedimientos inconsistentes",
          bullet2: "Alta dependencia del personal senior",
          bullet3: "Contratistas incorporados informalmente",
          bullet4: "Exposición a auditorías",
          tradeoff: "La seguridad depende de la experiencia, no de la estructura.",
        },
        after: {
          title: "Ejecución estandarizada con comprensión compartida",
          text: "Los estándares de ejecución son claros, visuales y uniformes en todos los equipos y grupos de contratistas.",
          bullet1: "Estándares de ejecución uniformes",
          bullet2: "Incorporación más rápida",
          bullet3: "Errores de interpretación reducidos",
          bullet4: "Marco de seguridad alineado",
          tradeoff: "La seguridad se vuelve repetible y auditable.",
        },
      },
      incident: {
        title: "Gestión de Incidentes y Escalamiento",
        metrics: {
          escalationLoops: "Ciclos de Escalamiento",
          responseTime: "Tiempo de Respuesta",
          accountability: "Responsabilidad",
          accountabilityValue: "Explícita",
        },
        before: {
          title: "Gestión de incidentes reactiva",
          text: "Los incidentes se reportan de manera inconsistente y se escalan manualmente. La responsabilidad es poco clara y la visibilidad es limitada.",
          bullet1: "Umbrales de escalamiento poco claros",
          bullet2: "Traspasos retrasados",
          bullet3: "Hilos de comunicación paralelos",
          bullet4: "Visibilidad gerencial limitada",
          tradeoff: "Los incidentes consumen atención en lugar de resolverse.",
        },
        after: {
          title: "Escalamiento controlado con responsabilidad clara",
          text: "Los incidentes se registran, clasifican y escalan a través de rutas predefinidas. Cada paso tiene un propietario claro y una ventana de respuesta.",
          bullet1: "Niveles de escalamiento definidos",
          bullet2: "Propiedad clara por incidente",
          bullet3: "Plazos de respuesta predecibles",
          bullet4: "Visibilidad operacional completa",
          tradeoff: "Los incidentes se resuelven sistemáticamente.",
        },
      },
      appointment: {
        title: "Eficiencia en Programación de Citas",
        metrics: {
          schedulingTime: "Tiempo de Programación",
          noShows: "Ausencias",
          utilization: "Utilización",
          utilizationValue: "Maximizada",
        },
        before: {
          title: "Programación manual con fricción constante",
          text: "Las citas se coordinan por teléfono, email y hojas de cálculo. Las confirmaciones son inconsistentes, las ausencias frecuentes y los conflictos de horario drenan la capacidad operativa.",
          bullet1: "Programación telefónica solo en horario laboral",
          bullet2: "Verificaciones manuales de disponibilidad en calendario",
          bullet3: "Sin recordatorios ni confirmaciones automatizadas",
          bullet4: "Frecuentes dobles reservas y conflictos",
          tradeoff: "Tiempo del personal consumido por programación en vez de operaciones.",
        },
        after: {
          title: "Programación automatizada con capacidad predecible",
          text: "Las citas se capturan y confirman automáticamente a través de canales de voz y digitales. Verificaciones de disponibilidad en tiempo real, recordatorios automatizados y reprogramación inteligente eliminan la fricción.",
          bullet1: "Recepción de citas por voz y digital 24/7",
          bullet2: "Sincronización de calendario en tiempo real y prevención de conflictos",
          bullet3: "Confirmaciones automatizadas y secuencias de recordatorios",
          bullet4: "Autoreprogramación con optimización de capacidad",
          tradeoff: "La programación se vuelve fluida, la utilización aumenta.",
        },
      },
    },
    simulator: {
      title: "Ingesta de Demanda y Ejecución",
      close: "Cerrar",
      back: "Atrás",
      next: "Siguiente",
      stepIndicator: "Paso {{current}} de {{total}}",
      requestReview: "Solicitar revisión operacional",
      submitting: "Enviando...",
      submitError: "Error al enviar. Por favor intente de nuevo.",
      submitErrorTitle: "Error de envío",
      validationErrorTitle: "Campos obligatorios faltantes",
      validationErrorDescription: "Complete todos los campos obligatorios antes de enviar.",
      step1: {
        title: "Contexto operacional",
        description: "Ayúdenos a entender su entorno operacional y el alcance de sus actividades.",
        industryLabel: "¿En qué industria opera su organización?",
        operationLabel: "¿Qué tipos de operaciones gestiona?",
        operationHint: "Seleccione todas las que apliquen",
        scaleLabel: "¿Cuál es la escala de sus operaciones?",
      },
      step2: {
        title: "Modelo operacional actual",
        description: "Ayúdenos a entender cómo maneja el trabajo hoy — sin juicios, solo claridad.",
        handlingLabel: "¿Cómo se manejan las solicitudes, tareas o incidentes?",
        handlingHint: "Seleccione todos los métodos utilizados",
        structureLabel: "¿Cuál es el nivel de estructura en sus operaciones?",
        toolsLabel: "¿Qué herramientas o sistemas están en uso?",
      },
      step3: {
        title: "Obstáculos y puntos de dolor",
        description: "Identifique dónde el trabajo se ralentiza o se vuelve impredecible hoy.",
        frictionLabel: "¿Qué causa fricción en sus operaciones?",
        frictionHint: "Seleccione todas las que apliquen",
        notesLabel: "Notas adicionales (opcional)",
        notesPlaceholder: "Describa desafíos específicos o contexto...",
      },
      step4: {
        title: "Restricciones y requisitos",
        description: "Defina los límites y requisitos que afectan sus operaciones.",
        downtimeLabel: "¿Qué tan sensibles son sus operaciones al tiempo de inactividad?",
        complianceLabel: "¿Qué marcos de seguridad o cumplimiento aplican?",
        complianceHint: "Seleccione todos los que apliquen",
        complexityLabel: "¿Qué tan compleja es la coordinación interna?",
      },
      step5: {
        title: "Resumen y reflexión",
        description: "Aquí hay un resumen de lo que ha compartido — una imagen clara de su realidad operacional actual.",
        noData: "No hay datos para mostrar. Por favor vuelva y complete los pasos anteriores.",
        contextTitle: "Contexto operacional",
        modelTitle: "Modelo operacional actual",
        hurdlesTitle: "Obstáculos identificados",
        constraintsTitle: "Restricciones y requisitos",
        industry: "Industria",
        operations: "Operaciones",
        scale: "Escala",
        handling: "Manejo de solicitudes",
        structure: "Nivel de estructura",
        tools: "Herramientas",
        downtime: "Sensibilidad al tiempo de inactividad",
        compliance: "Cumplimiento",
        complexity: "Complejidad de coordinación",
        disclaimer: "Esta simulación es indicativa y refleja la información proporcionada por usted. No reemplaza una evaluación profesional. Todos los hallazgos están sujetos a revisión formal.",
      },
      industries: {
        manufacturing: "Manufactura",
        logistics: "Logística",
        facility: "Gestión de instalaciones",
        infrastructure: "Infraestructura",
        energy: "Energía",
        other: "Otro",
      },
      operationTypes: {
        facility: "Operaciones de instalaciones",
        logistics: "Logística y almacenamiento",
        maintenance: "Mantenimiento y reparación",
        production: "Líneas de producción",
        fieldService: "Servicio de campo",
      },
      scales: {
        single: "Ubicación única",
        multi: "Multi-ubicación",
        distributed: "Distribuida/regional",
      },
      handlingMethods: {
        email: "Correo electrónico",
        phone: "Teléfono",
        tickets: "Sistema de tickets",
        verbal: "Transferencia verbal",
        spreadsheets: "Hojas de cálculo",
        erp: "Sistema ERP/CMMS",
      },
      structureLevels: {
        adhoc: {
          title: "Principalmente ad-hoc",
          desc: "El trabajo se maneja según llega, estandarización mínima",
        },
        semiStructured: {
          title: "Semi-estructurado",
          desc: "Algunos procesos están definidos pero la ejecución varía",
        },
        fullyStructured: {
          title: "Totalmente estructurado",
          desc: "Procesos claros con ejecución consistente",
        },
      },
      toolTypes: {
        manual: "Principalmente manual",
        semiAutomated: "Semi-automatizado",
        fragmented: "Sistemas fragmentados",
        integrated: "Plataforma integrada",
      },
      frictionPoints: {
        slowResponse: "Tiempos de respuesta lentos",
        unclearOwnership: "Propiedad de tareas poco clara",
        manualCoordination: "Coordinación manual excesiva",
        inconsistentExecution: "Ejecución inconsistente",
        lackVisibility: "Falta de visibilidad en tiempo real",
        escalationDelays: "Retrasos en el escalamiento",
        documentationGaps: "Brechas en la documentación",
        communicationBreakdown: "Ruptura de comunicación",
      },
      downtimeLevels: {
        critical: {
          title: "Crítico",
          desc: "Cada minuto de inactividad cuesta significativamente",
        },
        high: {
          title: "Alto",
          desc: "El tiempo de inactividad impacta severamente las operaciones",
        },
        moderate: {
          title: "Moderado",
          desc: "Cierta tolerancia pero afecta el rendimiento",
        },
        low: {
          title: "Bajo",
          desc: "Puede gestionar interrupciones a corto plazo",
        },
      },
      complianceOptions: {
        iso: "ISO 9001/27001",
        hse: "HSE/EHS",
        osha: "OSHA",
        vca: "VCA/SCC",
        gdpr: "GDPR/Protección de datos",
        sectorSpecific: "Regulaciones sectoriales",
      },
      complexityLevels: {
        simple: "Simple",
        moderate: "Moderada",
        complex: "Compleja",
        veryComplex: "Muy compleja",
      },
      step6: {
        title: "Análisis operacional",
        description: "Basándose en la información proporcionada, aquí hay un análisis operacional de nivel ejecutivo de su estado actual.",
        noData: "Datos insuficientes para generar análisis. Por favor complete los pasos anteriores.",
        observationsTitle: "Observaciones operacionales",
        risksTitle: "Áreas de riesgo y exposición",
        readinessTitle: "Evaluación de preparación para la ejecución",
        advisoryTitle: "Dirección de asesoramiento (alto nivel)",
        disclaimer: "Este análisis es indicativo y se basa en información auto-reportada. No reemplaza una evaluación profesional. Todos los hallazgos están sujetos a revisión formal.",
      },
      step7: {
        title: "Datos de contacto",
        description: "Estos datos son necesarios para adjuntar el análisis operacional a una solicitud de revisión formal.",
        personalTitle: "Datos personales",
        companyTitle: "Datos de la empresa",
        fullName: "Nombre completo",
        fullNamePlaceholder: "Ingrese su nombre completo",
        email: "Dirección de correo electrónico",
        emailPlaceholder: "Ingrese correo electrónico empresarial",
        phone: "Número de teléfono",
        phonePlaceholder: "Ingrese número de teléfono",
        companyName: "Nombre de la empresa",
        companyNamePlaceholder: "Ingrese nombre de la empresa",
        role: "Cargo / Función",
        rolePlaceholder: "ej. Gerente de operaciones",
        country: "País",
        countryPlaceholder: "Ingrese país",
        city: "Ciudad",
        cityPlaceholder: "Ingrese ciudad",
        requiredNote: "Todos los campos marcados con * son obligatorios. Sus datos se usarán únicamente para procesar esta solicitud.",
      },
      analysis: {
        observations: {
          industry: {
            manufacturing: "Operar en un entorno de manufactura típicamente involucra programación de producción compleja, ciclos de mantenimiento de equipos y requisitos de control de calidad.",
            logistics: "Las operaciones logísticas requieren coordinación estrecha entre múltiples partes interesadas, entregas críticas en tiempo y visibilidad en tiempo real a través de las cadenas de suministro.",
            facility: "Las operaciones de gestión de instalaciones abarcan múltiples categorías de servicio y requieren coordinación entre equipos internos y contratistas externos.",
            infrastructure: "Las operaciones de infraestructura a menudo involucran activos distribuidos geográficamente con requisitos de mantenimiento variables y supervisión regulatoria.",
            energy: "Las operaciones del sector energético requieren adherencia estricta a protocolos de seguridad, cumplimiento regulatorio y monitoreo continuo de sistemas críticos.",
            other: "Las operaciones abarcan múltiples dominios con requisitos variables para coordinación, cumplimiento y control de ejecución.",
          },
          scale: {
            single: "Las operaciones de ubicación única permiten control centralizado pero aún pueden enfrentar desafíos de coordinación entre turnos y departamentos.",
            multi: "Las operaciones multi-ubicación introducen complejidad en la estandarización, comunicación y asegurar ejecución consistente a través de ubicaciones.",
            distributed: "Las operaciones distribuidas o regionales requieren mecanismos de coordinación robustos para mantener visibilidad y control sobre equipos dispersos.",
          },
          structure: {
            adhoc: "La estructura operacional ad-hoc sugiere alta dependencia del juicio individual e inconsistencia potencial en la ejecución.",
            semiStructured: "Los procesos semi-estructurados indican estandarización parcial con espacio para variación, lo que puede llevar a resultados inconsistentes.",
            fullyStructured: "Las operaciones totalmente estructuradas proporcionan una base para ejecución predecible, aunque la efectividad depende de la adherencia y el cumplimiento.",
          },
          tools: {
            fragmented: "El panorama de herramientas fragmentado típicamente lleva a silos de información, reconciliación manual de datos y sobrecarga de coordinación.",
            manual: "La fuerte dependencia de procesos manuales aumenta el riesgo de errores y limita la escalabilidad de las operaciones.",
          },
        },
        risks: {
          predictability: "Riesgo de predictibilidad: La ejecución inconsistente y los patrones de propiedad poco claros pueden llevar a resultados operacionales impredecibles.",
          coordination: "Riesgo de coordinación: La coordinación manual y las brechas de comunicación pueden resultar en retrasos, errores y fallas de escalamiento.",
          downtime: "Exposición al tiempo de inactividad: La alta sensibilidad al tiempo de inactividad combinada con patrones operacionales actuales sugiere riesgo elevado de interrupciones costosas.",
          compliance: "Exposición al cumplimiento: La brecha entre requisitos de cumplimiento y nivel de estructura actual puede crear riesgos de auditoría y regulatorios.",
          escalation: "Riesgo de escalamiento: Los retrasos en la resolución de problemas y la visibilidad limitada pueden permitir que problemas menores escalen a problemas operacionales significativos.",
        },
        readiness: {
          high: "La organización parece razonablemente preparada para el cambio operacional, con estructura existente y herramientas proporcionando una base para iniciativas de mejora.",
          moderate: "La organización muestra indicadores de preparación mixtos. Algunos elementos fundamentales existen, pero brechas en estructura o herramientas pueden requerir atención antes de escalar cambios.",
          low: "Los patrones operacionales actuales sugieren preparación limitada para escala o cambio significativo sin primero abordar brechas fundamentales de coordinación y estructura.",
        },
        advisory: {
          ownership: "Considere establecer estructuras de propiedad más claras y marcos de responsabilidad para reducir la sobrecarga de coordinación y mejorar la predictibilidad de respuesta.",
          standardization: "Enfóquese en estandarizar procesos operacionales centrales para reducir la variación y establecer patrones de ejecución consistentes entre equipos.",
          escalation: "Implemente rutas de escalamiento estructuradas con ventanas de respuesta definidas y propiedad clara en cada nivel.",
          compliance: "Alinee la estructura operacional con los requisitos de cumplimiento para reducir la exposición a auditorías y asegurar adherencia consistente a estándares regulatorios.",
          scale: "Para operaciones multi-sitio o distribuidas, priorice mecanismos que aseguren consistencia y visibilidad a través de ubicaciones.",
        },
      },
    },
    submission: {
      title: "Envío recibido",
      body1: "Su revisión operacional ha sido enviada exitosamente.",
      body2: "Nuestro equipo revisará cuidadosamente su solicitud y hará seguimiento en consecuencia.",
      body3: "Para acelerar los próximos pasos, puede programar una consulta estratégica a continuación.",
      scheduleBtn: "Programar consulta estratégica",
      closeBtn: "Cerrar",
      referenceLabel: "ID de referencia",
      emailSent: "Confirmación enviada a",
      pdfAttached: "Resumen PDF adjunto para sus registros",
    },
    governanceTrends: {
      title: "Operar en una realidad regulada — ISO 27001, NIS2 & SOC 2",
      subtitle: "A medida que los entornos operativos se vuelven más regulados, interconectados y escrutados, el liderazgo se define cada vez más por el control, la documentación y la consistencia — no por la velocidad o la improvisación.",
      column1: {
        title: "Lo que priorizan los líderes operativos",
        items: [
          "Instrucciones de trabajo claras y aplicables",
          "Ejecución predecible entre turnos y equipos",
          "Procedimientos de seguridad que dejan poco margen a la interpretación",
          "Menos decisiones improvisadas durante incidentes",
          "Documentación que habla por sí misma"
        ]
      },
      column2: {
        title: "Estándares, cumplimiento y responsabilidad",
        items: [
          "Sistemas de gestión formales basados en estándares reconocidos",
          "Procedimientos y acciones correctivas trazables",
          "Registros de capacitación y competencia verificables",
          "Protección de datos y control de acceso por defecto",
          "Decisiones que pueden reconstruirse después del hecho"
        ]
      },
      column3: {
        title: "Seguridad, higiene y realidad operativa",
        items: [
          "Las reglas de seguridad deben funcionar bajo presión, no solo en papel",
          "Los procedimientos de higiene deben ser consistentes en todas las ubicaciones",
          "Las desviaciones deben registrarse, no normalizarse",
          "Las soluciones informales aumentan la exposición",
          "La responsabilidad personal se acerca al nivel operativo"
        ]
      },
      trend1: "Mirando hacia adelante, la tendencia es clara: las operaciones deben ser explicables, auditables y repetibles — incluso bajo interrupción.",
      trend2: "Las organizaciones que tendrán éxito serán aquellas que integren estándares, seguridad, higiene y disciplina de datos directamente en la ejecución diaria. No como sobrecarga, sino como estructura operativa."
    },
    chatbot: {
      greeting: "¡Hola! Soy Brooks. ¿En qué puedo ayudarte hoy?\n\nPuedo asistirte con información del producto, soporte operacional, programar una demo, probar nuestro simulador, Cumplimiento & Gobernanza o responder preguntas sobre precios.",
      subtitle: "Asistente Ovelon Prime",
      inputPlaceholder: "Escribe un mensaje...",
      buttons: {
        productInfo: "Info Producto",
        operationalSupport: "Soporte",
        scheduleDemo: "Agendar Demo",
        intake: "Diagnóstico",
        simulation: "Simulador",
        pricing: "Precios",
        contact: "Contacto",
        warehouseSolutions: "Soluciones de Almacén",
        workflowSolutions: "Soluciones de Flujo",
        backToMain: "← Volver",
        connectSupport: "Conectar con Soporte",
        bookDemo: "Reservar con nosotros",
        startIntake: "Iniciar →",
        viewPricing: "Ver Precios",
        goToForm: "Ir al Formulario",
        openSimulator: "Abrir Simulador →",
      },
      responses: {
        productInfo: "Ofrecemos soluciones de almacén y flujo de trabajo adaptadas a tus operaciones.\n\nEnlaces rápidos:\n• Soluciones de Almacén\n• Soluciones de Flujo\n\n¿Hay algo específico que quieras saber?",
        operationalSupport: "¿Puedes indicarme el tipo de problema? Puedo guiarte o conectarte con un experto.",
        scheduleDemo: "Puedo programar una demo para ti. ¡Haz clic en el botón de abajo para elegir un horario que te convenga!",
        intake: "Nuestro diagnóstico de 7 pasos ayuda a identificar tus desafíos operacionales:\n\n1️⃣ Contexto Operacional\n2️⃣ Desafíos Actuales\n3️⃣ Análisis de Procesos\n4️⃣ Stack Tecnológico\n5️⃣ Objetivos y Prioridades\n6️⃣ Análisis Ejecutivo\n7️⃣ Datos de Contacto\n\nRecibirás un resumen PDF personalizado. ¿Listo para empezar?",
        pricing: "Nuestra plataforma comienza en €4.999/mes e incluye la suite completa de soluciones de almacén y flujo de trabajo.\n\n¿Quieres ver la sección de precios detallada o programar una llamada para discutir tus necesidades?",
        contact: "Puedes contactarnos en info@ovelon-prime.com o usar nuestro formulario de contacto. ¿Te llevo allí?",
        simulation: "Nuestro Simulador de Proyectos te ayuda a visualizar mejoras potenciales en tus operaciones. ¡Pruébalo!",
        simulatorOpened: "He abierto el Simulador de Proyectos para ti. ¡Sigue los pasos para ver tus mejoras potenciales!",
        connectSupport: "Te estoy conectando con nuestro equipo de soporte. También puedes contactarnos directamente en info@ovelon-prime.com",
        bookingOpened: "He abierto nuestro calendario de reservas para ti. ¡Elige el horario que mejor te convenga!",
        backToMain: "¡Sin problema! ¿En qué más puedo ayudarte?",
        compliance: "Ayudamos a las organizaciones a navegar paisajes regulatorios complejos:\n\n🛡️ Directiva NIS2 — Preparación para el cumplimiento de seguridad de redes e información para infraestructuras críticas.\n\n📋 ISO 27001 — Implementación del sistema de gestión de seguridad de la información y preparación de auditorías.\n\n🔒 SOC 2 — Auditorías SOC 2 (Tipo I y Tipo II) para criterios de servicios de confianza.\n\n📑 Gobernanza operativa — Marcos de políticas, registros de auditoría y paneles de cumplimiento.\n\n¡Pregúntame lo que quieras sobre estos marcos!",
        complianceThinking: "Déjame investigar eso para ti...",
        complianceError: "No pude encontrar una respuesta ahora. Consulta nuestros artículos de cumplimiento o contacta info@ovelon-prime.com.",
        fallback: "No tengo esa información, pero el equipo te contactará pronto. ¿Te gustaría dejar tus datos de contacto o probar el formulario de contacto?",
      },
      complianceHint: "Pregúntame sobre SOC 2, ISO 27001 o NIS2 ↵",
    },
    subscribe: {
      title: "Mantente a la vanguardia",
      subtitle: "Recibe información exclusiva sobre optimización de flujos de trabajo, gestión de operaciones y tendencias del sector directamente en tu bandeja de entrada.",
      promo: "🎁 ¡E-Book de Operaciones gratis si te suscribes hoy!",
      fullName: "Nombre completo",
      fullNamePlaceholder: "Ingresa tu nombre completo",
      email: "Correo electrónico",
      emailPlaceholder: "nombre@email.es",
      submit: "Suscribirse ahora",
      submitting: "Suscribiendo...",
      successTitle: "¡Bienvenido a bordo!",
      successMessage: "Gracias por suscribirte. Pronto recibirás nuestros últimos insights.",
      privacyText: "Al suscribirte, aceptas nuestra",
      privacyLink: "Política de privacidad",
      termsLink: "Términos y condiciones",
      andText: "y",
      missingInfo: "Información faltante",
      missingInfoDesc: "Por favor completa nombre y correo electrónico.",
      failedTitle: "Suscripción fallida",
    },
    caseStudies: {
      badge: "Resultados probados",
      title: "Transformando operaciones para industrias críticas.",
      challengeLabel: "El desafío",
      solutionLabel: "La solución Ovelon Prime",
      resultsLabel: "Resultados cuantificables",
      cases: {
        facility: {
          industry: "Gestión de instalaciones",
          client: "Operador de múltiples sitios",
          challenge: "La gestión de solicitudes de servicio en más de 40 sitios distribuidos generaba tiempos de respuesta inconsistentes, ventanas de mantenimiento perdidas y comunicación fragmentada entre equipos en sitio y coordinación central.",
          solution: "Ovelon Prime unificó todas las solicitudes entrantes en un sistema de intake estructurado con enrutamiento automático, clasificación por prioridad y seguimiento de estado en tiempo real — permitiendo supervisión centralizada sin reemplazar herramientas existentes.",
          results: [
            { value: "42%", label: "Tiempos de respuesta más rápidos" },
            { value: "95%", label: "Tasa de cumplimiento SLA" },
            { value: "60%", label: "Reducción de errores de coordinación" },
          ],
        },
        supplyChain: {
          industry: "Cadena de suministro",
          client: "Red de distribución regional",
          challenge: "La coordinación manual entre proveedores, almacenes y socios de transporte creaba puntos ciegos en la cadena de suministro, provocando envíos retrasados, discrepancias de inventario y costos operativos crecientes.",
          solution: "Ovelon Prime implementó automatización de flujos de extremo a extremo vinculando adquisición, gestión de inventario y planificación logística — proporcionando visibilidad en tiempo real y alertas predictivas en toda la cadena.",
          results: [
            { value: "38%", label: "Reducción de plazos de entrega" },
            { value: "99,2%", label: "Tasa de precisión de pedidos" },
            { value: "28%", label: "Menores costos operativos" },
          ],
        },
        warehouse: {
          industry: "Almacenamiento",
          client: "Complejo de almacenamiento industrial",
          challenge: "El seguimiento de inventario dependía de hojas de cálculo fragmentadas y conteos manuales, causando frecuentes discrepancias de stock, espacio mal asignado y retrasos en el cumplimiento de pedidos.",
          solution: "Ovelon Prime estandarizó los flujos de inventario con registro automático de entradas, reglas de asignación por zona y visibilidad de stock en tiempo real — reduciendo la intervención manual mientras mejoraba la precisión.",
          results: [
            { value: "55%", label: "Reducción de discrepancias de stock" },
            { value: "40%", label: "Cumplimiento de pedidos más rápido" },
            { value: "35%", label: "Mejor utilización del espacio" },
          ],
        },
        maintenance: {
          industry: "Reparación y Mantenimiento",
          client: "Servicios de equipos industriales",
          challenge: "Los flujos de mantenimiento reactivo dominaban las operaciones. Las órdenes de trabajo se registraban de forma inconsistente, el seguimiento de piezas era manual y la programación de técnicos se basaba en disponibilidad en lugar de prioridad.",
          solution: "Ovelon Prime introdujo gestión estructurada de órdenes de trabajo con enrutamiento automático por prioridad, integración de inventario de piezas y despacho optimizado de técnicos — pasando de mantenimiento reactivo a predictivo.",
          results: [
            { value: "47%", label: "Reducción del tiempo de inactividad" },
            { value: "65%", label: "Finalización más rápida de órdenes" },
            { value: "30%", label: "Menos incidentes recurrentes" },
          ],
        },
    },
    complianceBlog: {
      badge: "Cumplimiento y Gobernanza",
      title: "NIS2, ISO 27001 y SOC 2: Lo que los líderes operativos deben saber ahora.",
      subtitle: "A medida que la presión regulatoria se intensifica en infraestructuras críticas, comprender NIS2, ISO 27001 y SOC 2 ya no es opcional — es supervivencia operativa.",
      articles: {
        nis2: {
          tag: "Directiva UE 2022/2555",
          title: "Directiva NIS2: La nueva realidad operativa para infraestructuras críticas",
          excerpt: "La Directiva NIS2 es la regulación de ciberseguridad más significativa de la UE, abarcando 18 sectores críticos e imponiendo sanciones severas por incumplimiento.",
          facts: [
            { label: "Sectores cubiertos", value: "18" },
            { label: "Sanción máxima", value: "€10M o 2% ingresos" },
            { label: "Responsabilidad gerencial", value: "Personal" },
          ],
          sections: [
            { heading: "¿Qué es NIS2 y por qué importa?", text: "NIS2 reemplaza la Directiva NIS original de 2016 y expande drásticamente el alcance de los requisitos de ciberseguridad en la UE. Las organizaciones deben implementar gestión de riesgos, reporte de incidentes, seguridad de la cadena de suministro y planificación de continuidad — o enfrentar sanciones de hasta €10 millones o 2% de la facturación global." },
            { heading: "La responsabilidad operativa ahora es personal", text: "Bajo NIS2, la alta dirección puede ser personalmente responsable por incumplimiento. CISOs, COOs y miembros del consejo deben demostrar gobernanza activa sobre ciberseguridad y resiliencia operativa." },
            { heading: "Requisitos estrictos de reporte de incidentes", text: "NIS2 exige un marco escalonado: alerta temprana en 24 horas, notificación detallada en 72 horas y reporte final en un mes. Las organizaciones deben tener procesos estructurados de detección, clasificación y escalamiento." },
            { heading: "La seguridad de la cadena de suministro es obligatoria", text: "La directiva requiere evaluar y gestionar riesgos de ciberseguridad en toda la cadena de suministro — incluyendo evaluaciones de proveedores y monitoreo continuo de riesgos de terceros." },
          ],
          ovelonTitle: "Cómo Ovelon Prime apoya el cumplimiento NIS2",
          ovelonPoints: [
            "Admisión y clasificación estructurada de incidentes con rutas de escalamiento automatizadas que cumplen plazos de 24/72 horas",
            "Rastros de auditoría completos para cada evento operativo",
            "Procedimientos de ejecución estandarizados que eliminan operaciones basadas en interpretación",
            "Visibilidad operativa en tiempo real con paneles que apoyan obligaciones de gobernanza",
            "Flujos de coordinación de cadena de suministro que imponen requisitos de seguridad",
            "Sistemas de notificación automatizados que garantizan comunicación oportuna durante incidentes",
          ],
        },
        iso27001: {
          tag: "Estándar Internacional",
          title: "ISO 27001: Construir un SGSI que realmente funcione",
          excerpt: "ISO 27001 es el estándar de referencia global para gestión de seguridad de la información. Pero la certificación es solo el inicio — mantener un SGSI eficaz requiere disciplina operativa.",
          facts: [
            { label: "Controles en Anexo A", value: "93" },
            { label: "Organizaciones certificadas", value: "70.000+" },
            { label: "Ciclo de recertificación", value: "3 años" },
          ],
          sections: [
            { heading: "Por qué ISO 27001 es esencial", text: "ISO 27001 proporciona un enfoque sistemático para gestionar información sensible. La revisión 2022 reorganizó controles en cuatro dominios con 93 controles en el Anexo A." },
            { heading: "La brecha entre certificación y realidad operativa", text: "Muchas organizaciones obtienen la certificación pero luchan por mantener la disciplina operativa entre auditorías. Las políticas existen en papel pero no se aplican en las operaciones diarias." },
            { heading: "Controles del Anexo A que requieren automatización", text: "Varios controles del Anexo A se relacionan directamente con procesos operativos: A.5.24-A.5.28 (gestión de incidentes y recolección de evidencias) y A.8.16 (actividades de monitoreo)." },
            { heading: "La mejora continua es obligatoria", text: "La Cláusula 10 requiere mejora continua del SGSI — identificación sistemática de no conformidades, acciones correctivas y evaluación de eficacia. Sin datos operativos estructurados, la mejora se convierte en conjetura." },
          ],
          ovelonTitle: "Cómo Ovelon Prime apoya el cumplimiento ISO 27001",
          ovelonPoints: [
            "Clasificación y respuesta automatizada de incidentes alineada con controles A.5.24-A.5.28 del Anexo A",
            "Rastros de auditoría completos para cada evento operativo (A.5.28)",
            "Procedimientos de ejecución estandarizados para implementación consistente de controles",
            "Paneles de monitoreo en tiempo real para verificación continua de cumplimiento (A.8.16)",
            "Flujos de gestión de cambios estructurados con cadenas de aprobación documentadas",
            "Métricas operativas y análisis de tendencias para mejora continua bajo Cláusula 10",
          ],
        },
        soc2: {
          tag: "Criterios de servicios de confianza AICPA",
          title: "SOC 2: Demostrar confianza operacional mediante controles de seguridad verificados",
          excerpt: "SOC 2 (Service Organization Control 2) es el principal marco de auditoría para verificar que las prácticas de seguridad de la información de una organización cumplen estándares rigurosos en cinco Criterios de Servicios de Confianza. Un informe SOC 2 es cada vez más requerido por clientes empresariales e industrias reguladas.",
          type1Label: "Tipo I",
          type2Label: "Tipo II",
          type1: {
            facts: [
              { label: "Alcance de auditoría", value: "Punto en el tiempo" },
              { label: "Duración", value: "Fecha única" },
              { label: "Criterios de confianza", value: "Hasta 5" },
            ],
            sections: [
              {
                heading: "¿Qué es SOC 2 Tipo I?",
                text: "Un informe SOC 2 Tipo I evalúa si los controles de seguridad de una organización están adecuadamente diseñados en un momento específico. Responde la pregunta: '¿Están los controles correctos en su lugar hoy?' El Tipo I es típicamente el punto de partida para organizaciones que buscan certificación SOC 2.",
              },
              {
                heading: "Los cinco Criterios de Servicios de Confianza",
                text: "SOC 2 evalúa controles en cinco criterios: Seguridad (obligatorio), Disponibilidad, Integridad del procesamiento, Confidencialidad y Privacidad. El Tipo I verifica que las políticas y controles técnicos están adecuadamente diseñados — no que funcionen consistentemente en el tiempo.",
              },
              {
                heading: "Cuándo el Tipo I es apropiado",
                text: "El Tipo I se usa comúnmente cuando una organización necesita demostrar rápidamente un nivel básico de seguridad a clientes o socios. También es el precursor natural del Tipo II.",
              },
            ],
          },
          type2: {
            facts: [
              { label: "Período de auditoría", value: "6–12 meses" },
              { label: "Criterios de confianza", value: "Hasta 5" },
              { label: "Estándar del mercado", value: "Enterprise" },
            ],
            sections: [
              {
                heading: "¿Qué es SOC 2 Tipo II?",
                text: "Un informe SOC 2 Tipo II evalúa si los controles de seguridad de una organización no solo existen sino que operan efectivamente durante un período prolongado — típicamente 6 a 12 meses. Es el estándar de oro para la confianza enterprise y es requerido crecientemente por equipos de adquisiciones en sectores como servicios financieros e infraestructura crítica.",
              },
              {
                heading: "La disciplina operacional es el fundamento",
                text: "A diferencia del Tipo I, el Tipo II requiere ejecución consistente, documentada y auditable de controles durante todo el período de revisión. Los registros de incidentes deben estar completos, los controles de acceso aplicados consistentemente y la gestión de cambios documentada.",
              },
              {
                heading: "Hallazgos frecuentes que frustran auditorías Tipo II",
                text: "Los fallos Tipo II más comunes involucran registros de incidentes incompletos, revisiones de acceso inconsistentes, documentación de aprobación faltante y brechas en el monitoreo de seguridad.",
              },
              {
                heading: "SOC 2 Tipo II y cumplimiento continuo",
                text: "A diferencia de una certificación única, el cumplimiento Tipo II es una disciplina operacional continua. Las organizaciones deben mantener documentación lista para auditoría durante todo el año, no solo en las semanas previas a la ventana de auditoría.",
              },
            ],
          },
          ovelonTitle: "Cómo Ovelon Prime apoya la preparación SOC 2",
          ovelonPoints: [
            "Flujos de trabajo estructurados de intake y respuesta a incidentes que producen evidencia de auditoría completa y con marca de tiempo",
            "Flujos de control de acceso automatizados para aprovisionamiento y desaprovisionamiento consistente de usuarios",
            "Documentación de gestión de cambios con cadenas de aprobación y marcas de tiempo",
            "Monitoreo operacional en tiempo real alineado con criterios SOC 2 de Seguridad y Disponibilidad",
            "Procedimientos de ejecución estandarizados que eliminan workarounds no documentados",
            "Paneles de reporting de cumplimiento para visibilidad continua sobre la efectividad de controles",
          ],
        },
      },
    },
  },
  },

  /* ===================== IT ===================== */
  it: {
    backToTop: "Torna in cima",
    explore: "Esplora",

    common: {
      back: "Indietro",
    },

    nav: {
      services: "Servizi",
      pricing: "Prezzi",
      about: "Chi siamo",
      contact: "Contatto",
      cta: "Discovery Call",
      viewDemo: "Vedi demo",
    },
    hero: {
      badge: "Industrie critiche orientate ai risultati",
      title: "La complessità operativa erode",
      titleHighlight: "l'affidabilità e spezza la disciplina di esecuzione",
      subtitle:
        "Quando il coordinamento diventa manuale, la manutenzione diventa reattiva e le operazioni si affidano a soluzioni alternative, il controllo è già perso.",
      subtitle2:
        "Ripristiniamo un'esecuzione prevedibile e disciplinata all'interno delle operazioni attive — senza sostituire i sistemi core né interrompere i flussi di lavoro quotidiani.",
      subtitleMobile: "Quando il coordinamento diventa manuale, il controllo è già perso.",
      cta: "Riprendi il controllo delle operazioni",
      simulatorCta: "Acquisizione Domanda ed Esecuzione",
    },
     problem: {
       badge: "La Sfida",
       title: "L'attrito operativo costa più di quanto pensi.",
       description: "Ogni chiamata persa, errore di prenotazione e momento di sovraccarico del personale rappresenta ricavi persi e un'esperienza ospite diminuita. La tua reception non dovrebbe essere il tuo collo di bottiglia.",
       pain1: "Chiamate perse nelle ore di punta",
       pain2: "Doppie prenotazioni e conflitti di orario",
       pain3: "Personale sopraffatto da compiti amministrativi",
       pain4: "Comunicazione incoerente con gli ospiti",
       pain5: "Ricavi persi a causa di lacune operative",
     },
     process: {
       badge: "Il Processo",
       title: "Integrazione perfetta con le tue operazioni.",
       step1: { title: "Cattura", description: "Ogni chiamata, messaggio e richiesta di prenotazione viene gestita con precisione nel momento in cui arriva." },
       step2: { title: "Coordina", description: "L'allineamento in tempo reale con il tuo calendario e i flussi di lavoro garantisce una programmazione fluida." },
       step3: { title: "Conferma", description: "Conferme automatiche, aggiornamenti ed escalation quando necessario — nulla sfugge." },
     },
     workflowDemo: {
       badge: "Demo Interattiva",
       title: "Guarda Ovelon Prime in azione.",
        tabs: { workflow: "Automazione del Flusso", call: "Recupero Chiamate", appointment: "Pianificazione Incidenti", safety: "Manuali di Sicurezza" },
        workflow: {
          description: "Guarda come una singola richiesta attiva un flusso di lavoro operativo completo — automaticamente.",
          runDemo: "ESEGUI DEMO DEL FLUSSO",
          complete: "Flusso completo eseguito in meno di 60 secondi",
          reset: "REIMPOSTA DEMO",
          processing: "Elaborazione del flusso...",
          step1: { title: "Richiesta ricevuta", desc: "Tutte le richieste o incidenti in arrivo vengono acquisiti e registrati in tempo reale." },
          step2: { title: "Disponibilità verificata", desc: "Il sistema verifica inventario, capacità o calendario per confermare la fattibilità." },
          step3: { title: "Ordine/Attività creata", desc: "Le richieste vengono trasformate in ordini o attività operative." },
          step4: { title: "Conferma inviata", desc: "Notifiche automatiche inviate via email, SMS o dashboard interni." },
          step5: { title: "Team notificato", desc: "I team operativi ricevono avvisi istantanei per l'elaborazione." },
          step6: { title: "Profilo e storico aggiornato", desc: "Tutte le informazioni vengono registrate automaticamente per riferimento futuro." },
        },
        call: {
          description: "Guarda come Ovelon Prime trasforma un'opportunità potenzialmente persa in un accordo confermato.",
          simulate: "SIMULA CHIAMATA IN ARRIVO",
          stage1: "Chiamata in arrivo...",
          stage2: "Senza Ovelon Prime: Chiamata persa, nessun accordo",
          stage3: "Con Ovelon Prime: Chiamata risposta istantaneamente",
          stage4: "Richiesta incidente cliente acquisita",
          stage5: "Richiesta incidente confermata",
          revenueLost: "Ricavi/tracciamento persi. Cliente insoddisfatto.",
          revenueCaptured: "Ricavi/tracciamento acquisiti. Cliente soddisfatto.",
        },
        appointment: {
          description: "Guarda come richieste, incidenti ed escalation vengono automaticamente pianificati, assegnati e tracciati — senza coordinamento manuale.",
          runDemo: "ESEGUI DEMO INCIDENTI",
          complete: "Ciclo completo di pianificazione incidenti completato in meno di 60 secondi",
          reset: "REIMPOSTA DEMO",
          processing: "Elaborazione incidente...",
          step1: { title: "Incidente segnalato", desc: "Un incidente o richiesta di servizio viene inviata tramite telefono, modulo o sistema di allerta automatico." },
          step2: { title: "Priorità classificata", desc: "Il sistema classifica gravità e urgenza in base a regole di escalation predefinite." },
          step3: { title: "Team di risposta assegnato", desc: "I rispondenti qualificati vengono identificati e assegnati in base a disponibilità ed esperienza." },
          step4: { title: "Percorso di escalation attivato", desc: "In caso di superamento degli SLA, l'escalation automatica notifica supervisori e dirigenza." },
          step5: { title: "Risoluzione tracciata", desc: "Il progresso viene monitorato in tempo reale con aggiornamenti di stato inviati a tutti gli stakeholder." },
          step6: { title: "Rapporto post-incidente depositato", desc: "Dettagli dell'incidente, tempi di risposta e passaggi di risoluzione documentati per conformità." },
        },
        safety: {
          description: "Guarda come i manuali video animati di sicurezza standardizzati vengono distribuiti nei magazzini industriali — garantendo una conformità HSE costante.",
          runDemo: "ESEGUI DEMO SICUREZZA",
          complete: "Distribuzione del manuale di sicurezza completata con successo",
          reset: "REIMPOSTA DEMO",
          processing: "Distribuzione contenuti di sicurezza...",
          step1: { title: "Pericolo identificato", desc: "Un pericolo sul posto di lavoro o aggiornamento delle procedure attiva la creazione di una nuova istruzione visiva." },
          step2: { title: "Manuale animato creato", desc: "Un manuale video animato passo-passo viene generato secondo gli standard ISO/VCA/OSHA." },
          step3: { title: "Localizzato in più lingue", desc: "Il contenuto viene tradotto e localizzato automaticamente per la conformità della forza lavoro multilingue." },
          step4: { title: "Distribuito alle stazioni", desc: "I manuali video vengono inviati istantaneamente a chioschi, tablet e dispositivi degli operatori." },
          step5: { title: "Conferma di ricezione tracciata", desc: "La visualizzazione e comprensione di ogni operatore viene registrata per audit e certificazione." },
          step6: { title: "Rapporto di conformità generato", desc: "Rapporti di conformità automatizzati mostrano la copertura formativa su tutti i turni." },
        },
      },
    video: {
      title: "Progettato per operazioni ininterrotte",
      subtitle: "Scopri come Ovelon Prime supporta ambienti mission-critical senza interrompere i sistemi attivi.",
      cta: "Discovery Call",
    },
    services: {
      label: "COSA STABILIZIAMO",
      title: "Affidabilità operativa in ambienti",
      titleHighlight: "che non possono permettersi errori",
      subtitle:
        "Eliminiamo i guasti operativi causati dal coordinamento manuale, dall'esecuzione poco chiara e dalla gestione reattiva degli incidenti — ripristinando controllo, responsabilità e risultati prevedibili all'interno delle operazioni attive.",
      items: [
        {
          title: "Gestione richieste e domanda",
          description:
            "Le richieste in arrivo vengono acquisite, validate e instradate in modo coerente — senza dipendere dal follow-up ad hoc o dal giudizio individuale.",
          tags: ["Controllo intelligente dell'intake", "Regole di qualificazione", "Instradamento di sistema"],
        },
        {
          title: "Standard di sicurezza ed esecuzione",
          description:
            "Istruzioni visive standardizzate utilizzate per allineare operatori, appaltatori e team su procedure critiche per la sicurezza — riducendo errori di interpretazione, deviazioni e rischi di esecuzione.",
          tags: [
            "Procedure HSE",
            "Allineamento ISO / VCA / OSHA",
            "Onboarding operatori e appaltatori",
          ],
        },
        {
          title: "Gestione incidenti e escalation",
          description:
            "I problemi vengono registrati, classificati e escalati in modo controllato — garantendo responsabilità, chiarezza nella risposta e percorsi di risoluzione prevedibili.",
          tags: ["Classificazione dei problemi", "Percorsi di escalation", "Proprietà della risposta"],
        },
        {
          title: "Efficienza nella fissazione degli appuntamenti",
          description:
            "Pianificazione vocale senza interruzioni che gestisce prenotazioni, conferme e riprogrammazioni con precisione, liberando il tuo team, mantenendo ogni connessione in carreggiata e offrendo un'esperienza cliente impeccabile.",
          tags: ["Pianificazione vocale", "Disponibilità 24/7", "Risposta automatica di richiamata"],
        },
      ],
      clickToExplore: "Clicca per esplorare",
    },
    serviceDetails: {
      keyBenefits: "Vantaggi principali",
      cta: "Esplora la demo",
      benefits: [
        [
          "La validazione automatizzata dell'intake elimina i colli di bottiglia della revisione manuale",
          "L'instradamento intelligente riduce i tempi di risposta fino al 60%",
          "Traccia di audit completa per conformità e responsabilità",
          "Integrazione trasparente con i sistemi di ticketing esistenti",
        ],
        [
          "Le SOP visive riducono i tempi di formazione e gli errori di interpretazione",
          "Monitoraggio della conformità in tempo reale su tutti i turni",
          "Onboarding degli appaltatori standardizzato in tutte le sedi",
          "Procedure allineate HSE pronte per audit regolamentari",
        ],
        [
          "La classificazione automatizzata garantisce una prioritizzazione coerente dei problemi",
          "Percorsi di escalation chiari con proprietà definita a ogni livello",
          "SLA di risposta monitorati e applicati automaticamente",
          "Analisi post-incidente per il miglioramento continuo",
        ],
        [
          "Disponibilità 24/7 senza aumentare l'organico",
          "La riprogrammazione intelligente minimizza le assenze",
          "Conferma multicanale (voce, SMS, email)",
          "Sincronizzazione del calendario trasparente con i sistemi esistenti",
        ],
      ],
    },
    sectors: {
      label: "Settori",
      title: "Affidabilità operativa per",
      titleHighlight: "industrie mission-critical",
      subtitle: "Supportiamo ambienti operativi dove il downtime, il disallineamento o la risposta ritardata impattano direttamente sicurezza, continuità o produzione.",
      items: [
        {
          name: "Gestione delle strutture",
          description: "Gestione di asset distribuiti e richieste di servizio in strutture complesse — con bassa tolleranza per ritardi o cattivo coordinamento.",
        },
        {
          name: "Manutenzione e riparazione",
          description: "Operare sotto pressione costante per prevenire il downtime nella manutenzione reattiva e preventiva — spesso con informazioni incomplete e finestre di risposta strette.",
        },
        {
          name: "Logistica e magazzinaggio",
          description: "Coordinamento di personale, attrezzature e flussi di lavoro sotto scadenze rigide e pressione operativa continua.",
        },
      ],
    },
    testimonials: {
      label: "VOCI DEI CLIENTI",
      title: "Testimonianze dei clienti",
      items: [
        {
          quote: "Prima di Ovelon Prime, il coordinamento dei turni dipendeva dal follow-up individuale. Oggi, manutenzione e pianificazione si eseguono in modo prevedibile su tutti i turni — senza intervento manuale.",
          name: "Patrick Sullivan",
          title: "Responsabile operazioni logistiche",
          facility: "Centro di distribuzione, Nebraska",
        },
        {
          quote: "Nel nostro ambiente, i ritardi influenzano direttamente sicurezza e produzione. Ovelon Prime ci ha aiutato a standardizzare l'esecuzione tra i team, riducendo i tempi di risposta del 45% senza aumentare il rischio operativo.",
          name: "Alicia Zhang",
          title: "Direttrice delle operazioni",
          facility: "Struttura di magazzinaggio, Ottawa",
        },
        {
          quote: "Operiamo sotto pressione continua. Da quando abbiamo implementato il sistema, l'instradamento e l'escalation seguono regole fisse — non supposizioni. Il downtime causato da errori di coordinamento non è più un fattore.",
          name: "Wyatt Coleman",
          title: "Direttore di stabilimento",
          facility: "Stabilimento produttivo, Austin",
        },
        {
          quote: "Gestire una struttura di queste dimensioni significa migliaia di elementi in movimento. Il sistema ci ha fornito un'unica fonte di verità per lo stato delle attività, i cicli delle attrezzature e i controlli di conformità. Abbiamo smesso di rincorrere aggiornamenti e abbiamo iniziato ad agire su di essi.",
          name: "James Whitfield",
          title: "Direttore delle operazioni di struttura",
          facility: "Magazzino industriale, Liverpool",
        },
        {
          quote: "Nel nostro ambiente, anche piccoli ritardi si propagano rapidamente. La piattaforma impone responsabilità in tempo reale in ogni reparto. Abbiamo eliminato le zone grigie che bloccavano la produzione per ore.",
          name: "Ciarán O'Sullivan",
          title: "Responsabile senior della struttura",
          facility: "Impianto di lavorazione, Tennessee",
        },
        {
          quote: "Perdevamo ore ogni settimana a causa della gestione manuale degli ordini di lavoro e dell'instradamento errato. Dal momento dell'attivazione, i nostri team seguono flussi di lavoro strutturati dall'inizio alla fine — nessuna approssimazione, nessun collo di bottiglia. La produttività è aumentata e il nostro tasso di errore si è stabilizzato.",
          name: "Rachel Moreno",
          title: "Responsabile del controllo operativo",
          facility: "Polo logistico, Dublino",
        }
      ],
    },
    operationalTrust: {
      heading: "Progettato per ambienti regolamentati e critici per la sicurezza",
      items: [
        { text: "Principi di sicurezza allineati ISO 27001" },
        { text: "Controllo dei processi in stile ISO 9001" },
        { text: "Design operativo consapevole HSE" },
        { text: "Documentazione e controlli pronti per l'audit" },
        { text: "Proprietà completa del cliente — nessun sistema black-box" },
      ],
      footer: "Progettato per ambienti esaminati dal CISO con governance della sicurezza interna, politiche di controllo degli accessi e procedure di change management.",
    },
    about: {
      label: "Chi siamo",
      title: "Costruito sull'esperienza operativa,",
      titleHighlight: "guidato dalla precisione",
      description1:
        "Ovelon Prime lavora con organizzazioni che operano sotto pressione costante — dove downtime, ritardi e processi manuali impattano direttamente costi, sicurezza e affidabilità.",
      description2:
        "Combiniamo una profonda esperienza nel dominio operativo con livelli di controllo operativo per progettare sistemi che si integrano perfettamente nei flussi di lavoro esistenti, senza interrompere le operazioni critiche.",
      quote:
        '"La precisione non è opzionale. In ambienti dove l\'affidabilità determina i risultati, i sistemi devono essere progettati per funzionare sotto pressione."',
      quoteAuthor: "Team dirigenziale · Ovelon Prime",
      stats: {
        uptime: "Uptime del sistema operativo",
        deployments: "Deployment di automazione",
        industries: "Industrie critiche servite",
      },
    },
    pricing: {
      label: "Prezzi",
      title: "Prezzi basati sull'impegno, con risultati chiaramente definiti",
      subtitle:
        "Impegni chiaramente definiti focalizzati sul miglioramento dell'affidabilità, la riduzione dell'attrito operativo e la creazione di un impatto misurabile.",
      focused: {
        name: "Impegno operativo mirato",
        description:
          "Un impegno a scope fisso progettato per risolvere un collo di bottiglia operativo chiaramente definito con impatto misurabile.",
        requestQuote: "Richiedi preventivo",
        cta: "Richiedi valutazione dell'impegno",
        features: [
          "Miglioramento operativo singolo e chiaramente definito",
          "Mappatura dei processi e delle responsabilità",
          "Implementazione e rollout controllati",
          "Documentazione e passaggio di consegne operativo",
          "Validazione post-implementazione",
        ],
      },
      enterprise: {
        name: "Programma operativo Enterprise",
        description:
          "Sistemi operativi personalizzati per organizzazioni complesse con requisiti rigorosi di governance, conformità e continuità.",
        priceLabel: "Prezzi personalizzati",
        price: "Servizio personalizzato",
        period: "tariffa una tantum",
        cta: "Richiedi valutazione iniziale",
        badge: "Enterprise",
        features: [
          "Architettura operativa multi-sistema",
          "Integrazioni personalizzate con sistemi esistenti",
          "Supporto dedicato per implementazione e transizione",
          "Design operativo consapevole della conformità",
          "Framework SLA e governance personalizzato",
        ],
      },
      securityNote:
        "Ogni impegno è costruito con sicurezza di livello enterprise, protezione dei dati e conformità operativa.",
      talkToExpert: "Discovery Call",
      intakeNote:
        "Acquisizione strutturata · Nessuna pressione commerciale · Direttamente con un esperto esperto",
      reviewNote: "Tutte le richieste vengono esaminate prima della programmazione.",
    },
    contact: {
      label: "IMPEGNO",
      title: "Pronto a riprendere il controllo delle",
      titleHighlight: " tue operazioni?",
      subtitle: "Tutti gli impegni sono definiti in anticipo e soggetti a una revisione formale prima di continuare.",
      cta: "Pianifica una consulenza",
      guarantee: "Nessun impegno a lungo termine.",
      guaranteeHighlight: "Gli impegni continuano solo quando il valore operativo è dimostrato",
      guaranteeSuffix: " sugli impegni qualificanti.",
      enterpriseLabel: "Richieste Enterprise: ",
      rightTitle: "Contatta Ovelon Prime",
      infoText1: "Se hai domande o desideri discutere di come possiamo supportare la tua attività, non esitare a contattarci.",
      infoText2: "L'assistenza clienti è disponibile 24 ore su 24, 7 giorni su 7.",
    },
    contactForm: {
      firstName: "Nome",
      firstNamePlaceholder: "Marco",
      lastName: "Cognome",
      lastNamePlaceholder: "Rossi",
      businessName: "Nome azienda",
      businessNamePlaceholder: "Acme Industrie",
      businessEmail: "Email aziendale",
      businessEmailPlaceholder: "marco@azienda.it",
      requestDetails: "Dettagli della richiesta",
      requestDetailsPlaceholder: "Descriva le sue sfide operative e cosa desidera ottenere...",
      submit: "Invia richiesta",
      submitting: "Invio in corso...",
      validationError: "Compilare correttamente tutti i campi obbligatori.",
      successTitle: "Inviato con successo",
      successMessage: "Esamineremo attentamente la sua richiesta.",
      successCta: "Prenota una consulenza strategica",
    },
    faq: {
      title: "Domande frequenti",
      category: "Impegno e consegna",
      items: [
        {
          question: "Come funziona Ovelon Prime?",
          answer:
            "Iniziamo con un'acquisizione strutturata per comprendere il vincolo operativo. Progettiamo e validiamo una soluzione a scope fisso rispetto ai flussi di lavoro reali. Implementiamo, testiamo sotto carico e consegniamo con documentazione completa.",
        },
        {
          question: "In quali industrie siete specializzati?",
          answer:
            "Lavoriamo con organizzazioni che operano in ambienti mission-critical, inclusi manifattura, logistica e gestione delle strutture. Se ritardi, errori o downtime impattano direttamente sicurezza, produzione o continuità, siamo il partner giusto.",
        },
        {
          question: "È un abbonamento o un servizio continuativo?",
          answer:
            "No. Gli impegni hanno scope fisso e sono orientati ai risultati. Non c'è abbonamento obbligatorio, dipendenza da licenza o tariffe continuative. Sei proprietario del sistema dopo la consegna.",
        },
        {
          question: "Quanto dura un'implementazione tipica?",
          answer:
            "La maggior parte degli impegni mirati viene consegnata entro 12-16 giorni, a seconda dello scope e dell'accesso ai sistemi. I programmi Enterprise seguono un rollout graduale allineato alle priorità operative.",
        },
        {
          question: "Questo si integrerà con i nostri sistemi esistenti?",
          answer:
            "Sì. Le soluzioni sono progettate per integrarsi con i sistemi esistenti senza sostituire le piattaforme core. Lavoriamo all'interno dei vostri strumenti attuali e dei confini dei dati.",
        },
        {
          question: "Dobbiamo cambiare i nostri flussi di lavoro attuali?",
          answer:
            "No. Progettiamo intorno ai vostri flussi di lavoro esistenti. Dove sono necessari cambiamenti, sono minimi, documentati e validati con i vostri team operativi prima del deployment.",
        },
        {
          question: "Chi è proprietario dell'ottimizzazione dopo la consegna?",
          answer:
            "Voi. Tutti i sistemi, le configurazioni e la documentazione vengono consegnati al completamento. Non c'è lock-in del vendor. Mantenete la proprietà operativa e tecnica completa.",
        },
        {
          question: "Come gestite sicurezza e conformità?",
          answer:
            "Sicurezza e conformità sono trattate come vincoli di progettazione, non come ripensamenti. Ogni impegno è progettato intorno alle vostre politiche di sicurezza esistenti, ai controlli di accesso e ai requisiti di conformità. Lavoriamo all'interno della vostra infrastruttura approvata, dei confini dei dati e dei framework di governance.",
        },
        {
          question: "Quale livello di coinvolgimento interno è richiesto?",
          answer:
            "Coinvolgimento minimo e strutturato. Richiediamo input limitato durante l'acquisizione per comprendere i vincoli operativi e validare le ipotesi. L'implementazione è gestita in modo indipendente, con checkpoint chiari per revisione e approvazione.",
        },
        {
          question: "Ovelon Prime è adatto per grandi ambienti enterprise?",
          answer:
            "Sì. Siamo progettati per ambienti complessi multi-stakeholder con requisiti rigorosi di governance, conformità e operativi. Gli impegni sono strutturati per allinearsi con il change management enterprise, le revisioni di sicurezza e i rollout graduali.",
        },
      ],
    },
    metricsStrip: {
      items: [
        { value: "40%", label: "Riduzione dei tempi di inattività" },
        { value: "200+", label: "Workflow automatizzati" },
        { value: "3", label: "Continenti serviti" },
        { value: "98%", label: "Tasso conformità SLA" },
      ],
    },
    footer: {
      privacy: "Privacy",
      terms: "Termini",
      copyright: "© {{year}} Ovelon Prime. Tutti i diritti riservati.",
      tagline: "Sistemi operativi enterprise costruiti per chiarezza, controllo e scala.",
      trustHeading: "Progettato per ambienti regolamentati e critici per la sicurezza",
      trustItems: [
        "Principi di sicurezza allineati ISO 27001",
        "Controlli conformi SOC 2 Tipo II",
        "Conformità alla Direttiva NIS2",
        "Documentazione pronta per l'audit",
        "Design operativo consapevole HSE",
        "Piena proprietà del cliente — nessun sistema black-box",
      ],
      badgeIsoTitle: "ISO 27001",
      badgeIsoDesc: "Gestione certificata della sicurezza delle informazioni",
      badgeSocTitle: "SOC 2 Type II",
      badgeSocDesc: "Controlli per organizzazioni di servizi",
      badgeGdprTitle: "GDPR",
      badgeGdprDesc: "Regolamento sulla protezione dei dati e la privacy",
    },
    industrialTrust: {
      heading: "Affidato dagli operatori in ambienti mission-critical",
      disclaimer: "Operatori rappresentativi dei settori industriale, logistico, manifatturiero e infrastrutturale. Gli impegni variano per scope e ambiente.",
    },
    intake: {
      title: "Qualificazione della consulenza",
      subtitle: "Si prega di compilare il breve modulo qui sotto. Tutte le richieste vengono esaminate prima della programmazione.",
    },
    cookie: {
      message: "Utilizziamo i cookie per migliorare la tua esperienza. Continuando a visitare questo sito, accetti il nostro utilizzo dei cookie.",
      accept: "Accetta",
      decline: "Rifiuta",
      learnMore: "Maggiori informazioni sulla nostra politica sulla privacy",
    },
    privacy: {
      title: "Informativa sulla privacy",
      lastUpdated: "Ultimo aggiornamento: 01.12.2025",
      sections: {
        introduction: {
          title: "1. Introduzione",
          p1: "In Ovelon Prime, prendiamo sul serio la protezione dei dati e la riservatezza. Questa Informativa sulla Privacy spiega come raccogliamo, utilizziamo e proteggiamo le informazioni personali e aziendali quando interagisci con il nostro sito web, i moduli di acquisizione e i servizi.",
          p2: "Elaboriamo i dati in modo responsabile e solo nella misura necessaria per fornire i nostri servizi, garantire l'integrità operativa e soddisfare gli obblighi legali.",
        },
        informationCollected: {
          title: "2. Informazioni che raccogliamo",
          personal: "Informazioni personali:",
          personalDesc: "Nome, indirizzo email aziendale, numero di telefono e informazioni inviate tramite moduli o corrispondenza diretta.",
          business: "Informazioni aziendali e operative:",
          businessDesc: "Nome dell'azienda, indirizzo aziendale o giurisdizione, classificazione del settore, contesto operativo e informazioni relative alla conformità fornite volontariamente durante l'acquisizione.",
          technical: "Informazioni tecniche:",
          technicalDesc: "Indirizzo IP, metadati del browser e del dispositivo, cookie e analisi di utilizzo di base.",
          note: "Non raccogliamo intenzionalmente dati personali sensibili a meno che non sia esplicitamente richiesto per conformità o fornitura di servizi.",
        },
        howWeUse: {
          title: "3. Come utilizziamo le tue informazioni",
          items: [
            "Valutare e qualificare le richieste di consulenza",
            "Fornire e supportare i servizi di automazione operativa",
            "Comunicare riguardo a impegni, aggiornamenti o chiarimenti",
            "Mantenere l'affidabilità, la sicurezza e le prestazioni della piattaforma",
            "Adempiere agli obblighi legali, normativi o contrattuali",
          ],
          note: "Non utilizziamo i tuoi dati per marketing non richiesto, profilazione o scopi pubblicitari di terze parti.",
        },
        dataSharing: {
          title: "4. Condivisione e divulgazione dei dati",
          p1: "Ovelon Prime non vende, affitta o scambia dati personali o aziendali.",
          p2: "Le informazioni possono essere condivise solo quando strettamente necessario:",
          items: [
            "Con fornitori di servizi verificati necessari per la fornitura del servizio",
            "Per conformarsi agli obblighi legali o normativi",
            "Per proteggere la sicurezza, l'integrità o i diritti dei nostri sistemi e clienti",
          ],
          note: "I dati non vengono mai condivisi per scopi di marketing o promozionali di terze parti.",
        },
        dataSecurity: {
          title: "5. Sicurezza dei dati",
          p1: "Applichiamo salvaguardie tecniche e organizzative standard del settore, inclusa crittografia, controlli di accesso e pratiche di infrastruttura sicura.",
          p2: "Mentre nessun sistema può garantire sicurezza assoluta, operiamo secondo protocolli interni rigorosi progettati per minimizzare il rischio e proteggere la riservatezza in ogni momento.",
        },
        dataRetention: {
          title: "6. Conservazione dei dati e i tuoi diritti",
          p1: "Conserviamo i dati solo per il tempo necessario a soddisfare gli scopi delineati in questa politica o per soddisfare i requisiti legali.",
          p2: "Hai il diritto di richiedere l'accesso, la correzione o la cancellazione dei tuoi dati, ove applicabile.",
        },
        contact: {
          title: "7. Contatto",
          p1: "Per domande riguardanti questa Informativa sulla Privacy o le pratiche di gestione dei dati, si prega di contattare:",
        },
      },
    },
    terms: {
      title: "Termini e condizioni",
      lastUpdated: "Ultimo aggiornamento: 01 dicembre 2025",
      sections: {
        introduction: {
          title: "1. Introduzione",
          p1: "Questi Termini e Condizioni (\"Termini\") regolano il tuo accesso e utilizzo del sito web Ovelon Prime, dei processi di consulenza e dei servizi di automazione professionale. Accedendo al sito web o utilizzando i nostri servizi, accetti di essere vincolato da questi Termini.",
          p2: "Se non sei d'accordo con questi Termini, devi interrompere immediatamente l'uso del sito web e dei servizi.",
        },
        services: {
          title: "2. Servizi forniti",
          p1: "Ovelon Prime fornisce servizi professionali di automazione, progettazione di sistemi e implementazione per ambienti industriali, logistici, di gestione delle strutture, infrastrutturali e altri ambienti operativamente critici.",
          p2: "Tutti i servizi sono soggetti a disponibilità e revisione. Ci riserviamo il diritto di modificare o sospendere i servizi in qualsiasi momento senza preavviso.",
        },
        engagement: {
          title: "3. Scope dell'impegno",
          p1: "Ogni impegno è definito attraverso un processo di acquisizione strutturato ed è soggetto a un accordo di scope formale.",
          p2: "Nessuna comunicazione, proposta o discussione costituisce un impegno vincolante a meno che non sia esplicitamente concordato per iscritto.",
        },
        clientResponsibilities: {
          title: "4. Responsabilità del cliente",
          items: [
            "Fornire informazioni accurate e tempestive durante l'acquisizione e l'impegno",
            "Mantenere la riservatezza delle credenziali di accesso e degli account",
            "Rispettare tutte le leggi e i regolamenti applicabili",
            "Notificarci eventuali cambiamenti che potrebbero influenzare lo scope dell'impegno",
          ],
        },
        intellectualProperty: {
          title: "5. Proprietà intellettuale",
          p1: "Tutti i contenuti, i design e le implementazioni consegnati nell'ambito di un impegno diventano proprietà del cliente al pagamento completo.",
          p2: "Ovelon Prime mantiene tutti i diritti su metodologie proprietarie, framework e strumenti non esplicitamente consegnati come parte dell'impegno.",
        },
        confidentiality: {
          title: "6. Riservatezza",
          p1: "Entrambe le parti concordano di mantenere la riservatezza di tutte le informazioni proprietarie condivise durante l'impegno.",
          p2: "I termini di riservatezza possono essere formalizzati in accordi separati secondo necessità.",
        },
        limitation: {
          title: "7. Limitazione di responsabilità",
          p1: "Ovelon Prime non sarà responsabile per danni indiretti, incidentali o consequenziali derivanti dall'uso dei nostri servizi.",
          p2: "La nostra massima responsabilità è limitata alle tariffe pagate per l'impegno specifico in questione.",
        },
        termination: {
          title: "8. Risoluzione",
          p1: "Entrambe le parti possono risolvere un impegno con preavviso scritto.",
          p2: "Alla risoluzione, il cliente avrà diritto a tutto il lavoro completato fino alla data di risoluzione.",
        },
        governing: {
          title: "9. Legge applicabile",
          p1: "Questi Termini saranno regolati e interpretati secondo le leggi dei Paesi Bassi.",
        },
        contact: {
          title: "10. Contatto",
          p1: "Per domande riguardanti questi Termini, si prega di contattare:",
        },
      },
    },
    demo: {
      header: "Analisi dei Compromessi Operativi",
      close: "Chiudi",
      beforeAlignment: "Prima dell'Allineamento",
      afterAlignment: "Dopo l'Allineamento",
      tradeoff: "Compromesso",
      footer: "Scenari operativi rappresentativi da ambienti industriali, logistici e di facility.",
      intake: {
        title: "Acquisizione Lavoro e Gestione Domanda",
        metrics: {
          coordinationTime: "Tempo di Coordinamento",
          reworkCycles: "Cicli di Rilavorazione",
          ownership: "Proprietà",
          ownershipValue: "Definita in anticipo",
        },
        before: {
          title: "Acquisizione frammentata e coordinamento reattivo",
          text: "Il lavoro entra attraverso email, chiamate e canali informali. La qualità delle informazioni varia e la proprietà cambia durante l'esecuzione.",
          bullet1: "Molteplici percorsi di ingresso",
          bullet2: "Cicli di chiarimento manuali",
          bullet3: "Priorità guidata dall'urgenza",
          bullet4: "L'esecuzione dipende dagli individui",
          tradeoff: "Tempo perso in coordinamento e correzione.",
        },
        after: {
          title: "Acquisizione strutturata con esecuzione controllata",
          text: "Le richieste seguono un unico percorso di ingresso. Le informazioni sono validate in anticipo e la proprietà viene assegnata prima che inizi l'esecuzione.",
          bullet1: "Ingresso unico strutturato",
          bullet2: "Classificazione e instradamento chiari",
          bullet3: "Proprietà assegnata una volta",
          bullet4: "Flusso di esecuzione prevedibile",
          tradeoff: "Il coordinamento diminuisce, l'esecuzione si stabilizza.",
        },
      },
      safety: {
        title: "Sicurezza e Standard di Esecuzione",
        metrics: {
          procedureDeviations: "Deviazioni dalle Procedure",
          onboardingTime: "Tempo di Onboarding",
          consistency: "Coerenza",
          consistencyValue: "Standardizzata",
        },
        before: {
          title: "Esecuzione basata sull'interpretazione",
          text: "Le procedure di sicurezza esistono tra documenti e conoscenza tribale. Gli operatori interpretano gli standard in modo diverso.",
          bullet1: "Procedure incoerenti",
          bullet2: "Alta dipendenza dal personale senior",
          bullet3: "Appaltatori inseriti informalmente",
          bullet4: "Esposizione agli audit",
          tradeoff: "La sicurezza dipende dall'esperienza, non dalla struttura.",
        },
        after: {
          title: "Esecuzione standardizzata con comprensione condivisa",
          text: "Gli standard di esecuzione sono chiari, visivi e uniformi tra team e gruppi di appaltatori.",
          bullet1: "Standard di esecuzione uniformi",
          bullet2: "Onboarding più veloce",
          bullet3: "Errori di interpretazione ridotti",
          bullet4: "Framework di sicurezza allineato",
          tradeoff: "La sicurezza diventa ripetibile e verificabile.",
        },
      },
      incident: {
        title: "Gestione Incidenti ed Escalation",
        metrics: {
          escalationLoops: "Cicli di Escalation",
          responseTime: "Tempo di Risposta",
          accountability: "Responsabilità",
          accountabilityValue: "Esplicita",
        },
        before: {
          title: "Gestione incidenti reattiva",
          text: "Gli incidenti vengono segnalati in modo incoerente e scalati manualmente. La responsabilità è poco chiara e la visibilità è limitata.",
          bullet1: "Soglie di escalation poco chiare",
          bullet2: "Passaggi di consegna ritardati",
          bullet3: "Thread di comunicazione paralleli",
          bullet4: "Visibilità manageriale limitata",
          tradeoff: "Gli incidenti consumano attenzione invece di essere risolti.",
        },
        after: {
          title: "Escalation controllata con responsabilità chiara",
          text: "Gli incidenti vengono registrati, classificati e scalati attraverso percorsi predefiniti. Ogni passo ha un proprietario chiaro e una finestra di risposta.",
          bullet1: "Livelli di escalation definiti",
          bullet2: "Proprietà chiara per incidente",
          bullet3: "Tempi di risposta prevedibili",
          bullet4: "Visibilità operativa completa",
          tradeoff: "Gli incidenti vengono risolti sistematicamente.",
        },
      },
      appointment: {
        title: "Efficienza di Pianificazione Appuntamenti",
        metrics: {
          schedulingTime: "Tempo di Pianificazione",
          noShows: "Assenze",
          utilization: "Utilizzo",
          utilizationValue: "Massimizzato",
        },
        before: {
          title: "Pianificazione manuale con attrito costante",
          text: "Gli appuntamenti vengono coordinati tramite telefono, email e fogli di calcolo. Le conferme sono inconsistenti, le assenze frequenti e i conflitti di orario drenano la capacità operativa.",
          bullet1: "Pianificazione telefonica solo in orario lavorativo",
          bullet2: "Controlli manuali di disponibilità sul calendario",
          bullet3: "Nessun promemoria o conferma automatizzata",
          bullet4: "Frequenti doppie prenotazioni e conflitti",
          tradeoff: "Tempo del personale consumato dalla pianificazione invece che dalle operazioni.",
        },
        after: {
          title: "Pianificazione automatizzata con capacità prevedibile",
          text: "Gli appuntamenti vengono acquisiti e confermati automaticamente tramite canali vocali e digitali. Verifiche di disponibilità in tempo reale, promemoria automatizzati e ripianificazione intelligente eliminano l'attrito.",
          bullet1: "Acquisizione appuntamenti vocale e digitale 24/7",
          bullet2: "Sincronizzazione calendario in tempo reale e prevenzione conflitti",
          bullet3: "Conferme automatizzate e sequenze di promemoria",
          bullet4: "Ripianificazione self-service con ottimizzazione capacità",
          tradeoff: "La pianificazione diventa fluida, l'utilizzo aumenta.",
        },
      },
    },
    simulator: {
      title: "Acquisizione Domanda ed Esecuzione",
      close: "Chiudi",
      back: "Indietro",
      next: "Avanti",
      stepIndicator: "Passo {{current}} di {{total}}",
      requestReview: "Richiedi revisione operativa",
      submitError: "Errore durante l'invio. Riprova.",
      step1: {
        title: "Contesto operativo",
        description: "Aiutaci a comprendere il tuo ambiente operativo e l'ambito delle tue attività.",
        industryLabel: "In quale settore opera la tua organizzazione?",
        operationLabel: "Che tipi di operazioni gestisci?",
        operationHint: "Seleziona tutti quelli applicabili",
        scaleLabel: "Qual è la scala delle tue operazioni?",
      },
      step2: {
        title: "Modello operativo attuale",
        description: "Aiutaci a capire come gestisci il lavoro oggi — senza giudizi, solo chiarezza.",
        handlingLabel: "Come vengono gestite richieste, task o incidenti?",
        handlingHint: "Seleziona tutti i metodi utilizzati",
        structureLabel: "Qual è il livello di struttura nelle tue operazioni?",
        toolsLabel: "Quali strumenti o sistemi sono in uso?",
      },
      step3: {
        title: "Ostacoli e punti critici",
        description: "Identifica dove il lavoro rallenta o diventa imprevedibile oggi.",
        frictionLabel: "Cosa causa attrito nelle tue operazioni?",
        frictionHint: "Seleziona tutti quelli applicabili",
        notesLabel: "Note aggiuntive (opzionale)",
        notesPlaceholder: "Descrivi sfide specifiche o contesto...",
      },
      step4: {
        title: "Vincoli e requisiti",
        description: "Definisci i confini e i requisiti che influenzano le tue operazioni.",
        downtimeLabel: "Quanto sono sensibili le tue operazioni al downtime?",
        complianceLabel: "Quali framework di sicurezza o conformità si applicano?",
        complianceHint: "Seleziona tutti quelli applicabili",
        complexityLabel: "Quanto è complesso il coordinamento interno?",
      },
      step5: {
        title: "Riepilogo e riflessione",
        description: "Ecco un riepilogo di ciò che hai condiviso — un quadro chiaro della tua attuale realtà operativa.",
        noData: "Nessun dato da visualizzare. Torna indietro e completa i passaggi precedenti.",
        contextTitle: "Contesto operativo",
        modelTitle: "Modello operativo attuale",
        hurdlesTitle: "Ostacoli identificati",
        constraintsTitle: "Vincoli e requisiti",
        industry: "Settore",
        operations: "Operazioni",
        scale: "Scala",
        handling: "Gestione richieste",
        structure: "Livello di struttura",
        tools: "Strumenti",
        downtime: "Sensibilità al downtime",
        compliance: "Conformità",
        complexity: "Complessità del coordinamento",
        disclaimer: "Questa simulazione è indicativa e riflette le informazioni da te fornite. Non sostituisce una valutazione professionale. Tutti i risultati sono soggetti a revisione formale.",
      },
      industries: {
        manufacturing: "Manifattura",
        logistics: "Logistica",
        facility: "Gestione delle strutture",
        infrastructure: "Infrastruttura",
        energy: "Energia",
        other: "Altro",
      },
      operationTypes: {
        facility: "Operazioni delle strutture",
        logistics: "Logistica e magazzinaggio",
        maintenance: "Manutenzione e riparazione",
        production: "Linee di produzione",
        fieldService: "Servizio sul campo",
      },
      scales: {
        single: "Singola sede",
        multi: "Multi-sede",
        distributed: "Distribuita/regionale",
      },
      handlingMethods: {
        email: "Email",
        phone: "Telefono",
        tickets: "Sistema di ticketing",
        verbal: "Trasferimento verbale",
        spreadsheets: "Fogli di calcolo",
        erp: "Sistema ERP/CMMS",
      },
      structureLevels: {
        adhoc: {
          title: "Principalmente ad-hoc",
          desc: "Il lavoro viene gestito man mano che arriva, standardizzazione minima",
        },
        semiStructured: {
          title: "Semi-strutturato",
          desc: "Alcuni processi sono definiti ma l'esecuzione varia",
        },
        fullyStructured: {
          title: "Completamente strutturato",
          desc: "Processi chiari con esecuzione coerente",
        },
      },
      toolTypes: {
        manual: "Principalmente manuale",
        semiAutomated: "Semi-automatizzato",
        fragmented: "Sistemi frammentati",
        integrated: "Piattaforma integrata",
      },
      frictionPoints: {
        slowResponse: "Tempi di risposta lenti",
        unclearOwnership: "Proprietà dei task poco chiara",
        manualCoordination: "Coordinamento manuale eccessivo",
        inconsistentExecution: "Esecuzione incoerente",
        lackVisibility: "Mancanza di visibilità in tempo reale",
        escalationDelays: "Ritardi nell'escalation",
        documentationGaps: "Lacune nella documentazione",
        communicationBreakdown: "Interruzione della comunicazione",
      },
      downtimeLevels: {
        critical: {
          title: "Critico",
          desc: "Ogni minuto di downtime costa significativamente",
        },
        high: {
          title: "Alto",
          desc: "Il downtime impatta severamente le operazioni",
        },
        moderate: {
          title: "Moderato",
          desc: "Qualche tolleranza ma influenza le prestazioni",
        },
        low: {
          title: "Basso",
          desc: "È possibile gestire interruzioni a breve termine",
        },
      },
      complianceOptions: {
        iso: "ISO 9001/27001",
        hse: "HSE/SSL",
        osha: "OSHA",
        vca: "VCA/SCC",
        gdpr: "GDPR/Protezione dati",
        sectorSpecific: "Regolamenti settoriali",
      },
      complexityLevels: {
        simple: "Semplice",
        moderate: "Moderata",
        complex: "Complessa",
        veryComplex: "Molto complessa",
      },
      step6: {
        title: "Analisi operativa",
        description: "Sulla base delle informazioni fornite, ecco un'analisi operativa a livello executive del tuo stato attuale.",
        noData: "Dati insufficienti per generare l'analisi. Completa i passaggi precedenti.",
        observationsTitle: "Osservazioni operative",
        risksTitle: "Aree di rischio ed esposizione",
        readinessTitle: "Valutazione della prontezza all'esecuzione",
        advisoryTitle: "Direzione consultiva (alto livello)",
        disclaimer: "Questa analisi è indicativa e si basa su informazioni auto-riportate. Non sostituisce una valutazione professionale. Tutti i risultati sono soggetti a revisione formale.",
      },
      step7: {
        title: "Dati di contatto",
        description: "Questi dati sono necessari per allegare l'analisi operativa a una richiesta di revisione formale.",
        personalTitle: "Dati personali",
        companyTitle: "Dati aziendali",
        fullName: "Nome completo",
        fullNamePlaceholder: "Inserisci il tuo nome completo",
        email: "Indirizzo email",
        emailPlaceholder: "Inserisci email aziendale",
        phone: "Numero di telefono",
        phonePlaceholder: "Inserisci numero di telefono",
        companyName: "Nome azienda",
        companyNamePlaceholder: "Inserisci nome azienda",
        role: "Ruolo / Funzione",
        rolePlaceholder: "es. Responsabile operazioni",
        country: "Paese",
        countryPlaceholder: "Inserisci paese",
        city: "Città",
        cityPlaceholder: "Inserisci città",
        requiredNote: "Tutti i campi contrassegnati con * sono obbligatori. I tuoi dati saranno utilizzati esclusivamente per elaborare questa richiesta.",
      },
      submitting: "Invio in corso...",
      analysis: {
        observations: {
          industry: {
            manufacturing: "Operare in un ambiente manifatturiero tipicamente comporta pianificazione della produzione complessa, cicli di manutenzione delle attrezzature e requisiti di controllo qualità.",
            logistics: "Le operazioni logistiche richiedono coordinamento stretto tra più stakeholder, consegne time-critical e visibilità in tempo reale attraverso le supply chain.",
            facility: "Le operazioni di gestione delle strutture coprono molteplici categorie di servizio e richiedono coordinamento tra team interni e appaltatori esterni.",
            infrastructure: "Le operazioni infrastrutturali spesso coinvolgono asset distribuiti geograficamente con requisiti di manutenzione variabili e supervisione normativa.",
            energy: "Le operazioni del settore energetico richiedono rigorosa aderenza ai protocolli di sicurezza, conformità normativa e monitoraggio continuo dei sistemi critici.",
            other: "Le operazioni coprono più domini con requisiti variabili per coordinamento, conformità e controllo dell'esecuzione.",
          },
          scale: {
            single: "Le operazioni a singola sede permettono controllo centralizzato ma possono comunque affrontare sfide di coordinamento tra turni e dipartimenti.",
            multi: "Le operazioni multi-sede introducono complessità nella standardizzazione, comunicazione e nell'assicurare esecuzione coerente tra le sedi.",
            distributed: "Le operazioni distribuite o regionali richiedono meccanismi di coordinamento robusti per mantenere visibilità e controllo su team dispersi.",
          },
          structure: {
            adhoc: "La struttura operativa ad-hoc suggerisce alta dipendenza dal giudizio individuale e potenziale incoerenza nell'esecuzione.",
            semiStructured: "I processi semi-strutturati indicano standardizzazione parziale con spazio per variazione, il che può portare a risultati incoerenti.",
            fullyStructured: "Le operazioni completamente strutturate forniscono una base per l'esecuzione prevedibile, sebbene l'efficacia dipenda dall'aderenza e dall'applicazione.",
          },
          tools: {
            fragmented: "Il panorama degli strumenti frammentato tipicamente porta a silos informativi, riconciliazione manuale dei dati e overhead di coordinamento.",
            manual: "La forte dipendenza dai processi manuali aumenta il rischio di errori e limita la scalabilità delle operazioni.",
          },
        },
        risks: {
          predictability: "Rischio di prevedibilità: Esecuzione incoerente e pattern di proprietà poco chiari possono portare a risultati operativi imprevedibili.",
          coordination: "Rischio di coordinamento: Coordinamento manuale e lacune comunicative possono risultare in ritardi, errori e fallimenti nell'escalation.",
          downtime: "Esposizione al downtime: L'alta sensibilità al downtime combinata con i pattern operativi attuali suggerisce rischio elevato di interruzioni costose.",
          compliance: "Esposizione alla conformità: Il divario tra requisiti di conformità e livello di struttura attuale può creare rischi di audit e normativi.",
          escalation: "Rischio di escalation: Ritardi nella risoluzione dei problemi e visibilità limitata possono permettere a problemi minori di escalare in problemi operativi significativi.",
        },
        readiness: {
          high: "L'organizzazione appare ragionevolmente preparata per il cambiamento operativo, con struttura e strumenti esistenti che forniscono una base per iniziative di miglioramento.",
          moderate: "L'organizzazione mostra indicatori di prontezza misti. Alcuni elementi fondamentali esistono, ma lacune in struttura o strumenti potrebbero richiedere attenzione prima di scalare i cambiamenti.",
          low: "I pattern operativi attuali suggeriscono prontezza limitata per scala o cambiamento significativo senza prima affrontare lacune fondamentali di coordinamento e struttura.",
        },
        advisory: {
          ownership: "Considera di stabilire strutture di proprietà più chiare e framework di responsabilità per ridurre l'overhead di coordinamento e migliorare la prevedibilità della risposta.",
          standardization: "Concentrati sulla standardizzazione dei processi operativi core per ridurre la variazione e stabilire pattern di esecuzione coerenti tra i team.",
          escalation: "Implementa percorsi di escalation strutturati con finestre di risposta definite e proprietà chiara a ogni livello.",
          compliance: "Allinea la struttura operativa con i requisiti di conformità per ridurre l'esposizione agli audit e garantire aderenza coerente agli standard normativi.",
          scale: "Per operazioni multi-sede o distribuite, dai priorità a meccanismi che garantiscano coerenza e visibilità tra le sedi.",
        },
      },
    },
    submission: {
      title: "Invio ricevuto",
      body1: "La tua revisione operativa è stata inviata con successo.",
      body2: "Il nostro team esaminerà attentamente la tua richiesta e farà seguito di conseguenza.",
      body3: "Per accelerare i prossimi passi, puoi pianificare una consultazione strategica qui sotto.",
      scheduleBtn: "Pianifica consultazione strategica",
      closeBtn: "Chiudi",
      referenceLabel: "ID di riferimento",
      emailSent: "Conferma inviata a",
      pdfAttached: "Riepilogo PDF allegato per i tuoi archivi",
    },
    governanceTrends: {
      title: "Operare in una realtà regolamentata — ISO 27001, NIS2 & SOC 2",
      subtitle: "Man mano che gli ambienti operativi diventano più regolamentati, interconnessi e scrutinati, la leadership è sempre più definita dal controllo, dalla documentazione e dalla coerenza — non dalla velocità o dall'improvvisazione.",
      column1: {
        title: "Cosa danno priorità i leader operativi",
        items: [
          "Istruzioni di lavoro chiare e applicabili",
          "Esecuzione prevedibile tra turni e team",
          "Procedure di sicurezza che lasciano poco spazio all'interpretazione",
          "Meno decisioni improvvisate durante gli incidenti",
          "Documentazione che parla da sola"
        ]
      },
      column2: {
        title: "Standard, conformità e responsabilità",
        items: [
          "Sistemi di gestione formali basati su standard riconosciuti",
          "Procedure e azioni correttive tracciabili",
          "Registri di formazione e competenza verificabili",
          "Protezione dei dati e controllo degli accessi di default",
          "Decisioni che possono essere ricostruite a posteriori"
        ]
      },
      column3: {
        title: "Sicurezza, igiene e realtà operativa",
        items: [
          "Le regole di sicurezza devono funzionare sotto pressione, non solo sulla carta",
          "Le procedure igieniche devono essere coerenti in tutte le sedi",
          "Le deviazioni devono essere registrate, non normalizzate",
          "Le soluzioni informali aumentano l'esposizione",
          "La responsabilità personale si avvicina al livello operativo"
        ]
      },
      trend1: "Guardando al futuro, la tendenza è chiara: le operazioni devono essere spiegabili, verificabili e ripetibili — anche in caso di interruzione.",
      trend2: "Le organizzazioni che avranno successo saranno quelle che integreranno standard, sicurezza, igiene e disciplina dei dati direttamente nell'esecuzione quotidiana. Non come sovraccarico, ma come struttura operativa."
    },
    chatbot: {
      greeting: "Ciao! Sono Brooks. Come posso aiutarti oggi?\n\nPosso assisterti con informazioni sul prodotto, supporto operativo, prenotare una demo, provare il nostro simulatore, Conformità & Governance o rispondere a domande sui prezzi.",
      subtitle: "Assistente Ovelon Prime",
      inputPlaceholder: "Scrivi un messaggio...",
      buttons: {
        productInfo: "Info Prodotto",
        operationalSupport: "Supporto",
        scheduleDemo: "Prenota Demo",
        intake: "Diagnostico",
        simulation: "Simulatore",
        pricing: "Prezzi",
        contact: "Contatto",
        warehouseSolutions: "Soluzioni Magazzino",
        workflowSolutions: "Soluzioni Workflow",
        backToMain: "← Indietro",
        connectSupport: "Contatta Supporto",
        bookDemo: "Prenota con noi",
        startIntake: "Inizia →",
        viewPricing: "Vedi Prezzi",
        goToForm: "Vai al Modulo",
        openSimulator: "Apri Simulatore →",
      },
      responses: {
        productInfo: "Offriamo soluzioni di magazzino e workflow personalizzate per le tue operazioni.\n\nLink rapidi:\n• Soluzioni Magazzino\n• Soluzioni Workflow\n\nC'è qualcosa di specifico che vuoi sapere?",
        operationalSupport: "Puoi dirmi il tipo di problema? Posso guidarti o metterti in contatto con un esperto.",
        scheduleDemo: "Posso prenotare una demo per te. Clicca il pulsante qui sotto per scegliere un orario che ti va bene!",
        intake: "Il nostro diagnostico in 7 passaggi aiuta a identificare le tue sfide operative:\n\n1️⃣ Contesto Operativo\n2️⃣ Sfide Attuali\n3️⃣ Analisi dei Processi\n4️⃣ Stack Tecnologico\n5️⃣ Obiettivi e Priorità\n6️⃣ Analisi Esecutiva\n7️⃣ Dati di Contatto\n\nRiceverai un riepilogo PDF personalizzato. Pronto a iniziare?",
        pricing: "La nostra piattaforma parte da €4.999/mese e include la suite completa di soluzioni di magazzino e workflow.\n\nVuoi vedere la sezione prezzi dettagliata o programmare una chiamata per discutere le tue esigenze?",
        contact: "Puoi contattarci a info@ovelon-prime.com o usare il nostro modulo di contatto. Vuoi che ti ci porti?",
        simulation: "Il nostro Simulatore di Progetto ti aiuta a visualizzare i potenziali miglioramenti nelle tue operazioni. Provalo!",
        simulatorOpened: "Ho aperto il Simulatore di Progetto per te. Segui i passaggi per vedere i tuoi potenziali miglioramenti!",
        connectSupport: "Ti sto mettendo in contatto con il nostro team di supporto. Puoi anche contattarci direttamente a info@ovelon-prime.com",
        bookingOpened: "Ho aperto il nostro calendario prenotazioni per te. Scegli l'orario che ti va meglio!",
        backToMain: "Nessun problema! In cos'altro posso aiutarti?",
        compliance: "Aiutiamo le organizzazioni a navigare paesaggi normativi complessi:\n\n🛡️ Direttiva NIS2 — Preparazione alla conformità per la sicurezza delle reti e dell'informazione per le infrastrutture critiche.\n\n📋 ISO 27001 — Implementazione del sistema di gestione della sicurezza delle informazioni e preparazione agli audit.\n\n🔒 SOC 2 — Audit SOC 2 (Tipo I e Tipo II) per i criteri dei servizi fiduciari.\n\n📑 Governance operativa — Framework politici, tracce di audit e dashboard di conformità.\n\nChiedimi qualsiasi cosa su questi framework!",
        complianceThinking: "Fammi controllare per te...",
        complianceError: "Non sono riuscito a trovare una risposta al momento. Consulta i nostri articoli sulla conformità o contatta info@ovelon-prime.com.",
        fallback: "Non ho quell'informazione, ma il team ti contatterà a breve. Vuoi lasciare i tuoi dati di contatto o provare il modulo di contatto?",
      },
      complianceHint: "Chiedimi qualsiasi cosa su SOC 2, ISO 27001 o NIS2 ↵",
    },
    subscribe: {
      title: "Resta un passo avanti",
      subtitle: "Ricevi approfondimenti esclusivi su ottimizzazione dei flussi di lavoro, gestione delle operazioni e tendenze del settore direttamente nella tua casella di posta.",
      promo: "🎁 E-Book Operations gratuito se ti iscrivi oggi!",
      fullName: "Nome completo",
      fullNamePlaceholder: "Inserisci il tuo nome completo",
      email: "Indirizzo email",
      emailPlaceholder: "nome@email.it",
      submit: "Iscriviti ora",
      submitting: "Iscrizione in corso...",
      successTitle: "Benvenuto a bordo!",
      successMessage: "Grazie per esserti iscritto. Riceverai presto i nostri ultimi approfondimenti.",
      privacyText: "Iscrivendoti, accetti la nostra",
      privacyLink: "Informativa sulla privacy",
      termsLink: "Termini e condizioni",
      andText: "e",
      missingInfo: "Informazioni mancanti",
      missingInfoDesc: "Per favore compila nome e email.",
      failedTitle: "Iscrizione fallita",
    },
    caseStudies: {
      badge: "Risultati comprovati",
      title: "Trasformare le operazioni per industrie mission-critical.",
      challengeLabel: "La sfida",
      solutionLabel: "La soluzione Ovelon Prime",
      resultsLabel: "Risultati quantificabili",
      cases: {
        facility: {
          industry: "Gestione strutture",
          client: "Operatore multi-sito",
          challenge: "La gestione delle richieste di servizio su oltre 40 siti distribuiti portava a tempi di risposta incoerenti, finestre di manutenzione mancate e comunicazione frammentata tra team sul campo e coordinamento centrale.",
          solution: "Ovelon Prime ha unificato tutte le richieste in entrata in un sistema di intake strutturato con instradamento automatico, classificazione delle priorità e monitoraggio dello stato in tempo reale — consentendo una supervisione centralizzata senza sostituire gli strumenti esistenti.",
          results: [
            { value: "42%", label: "Tempi di risposta più rapidi" },
            { value: "95%", label: "Tasso di conformità SLA" },
            { value: "60%", label: "Riduzione errori di coordinamento" },
          ],
        },
        supplyChain: {
          industry: "Catena di fornitura",
          client: "Rete di distribuzione regionale",
          challenge: "Il coordinamento manuale tra fornitori, magazzini e partner di trasporto creava punti ciechi nella catena di fornitura, causando ritardi nelle spedizioni, discrepanze di inventario e costi operativi crescenti.",
          solution: "Ovelon Prime ha implementato l'automazione end-to-end collegando approvvigionamento, gestione dell'inventario e pianificazione logistica — fornendo visibilità in tempo reale e avvisi predittivi lungo tutta la catena.",
          results: [
            { value: "38%", label: "Riduzione dei tempi di consegna" },
            { value: "99,2%", label: "Tasso di precisione ordini" },
            { value: "28%", label: "Costi operativi inferiori" },
          ],
        },
        warehouse: {
          industry: "Magazzino e Stoccaggio",
          client: "Complesso di stoccaggio industriale",
          challenge: "Il tracciamento dell'inventario si basava su fogli di calcolo frammentati e conteggi manuali, causando frequenti discrepanze di stock, spazio di stoccaggio mal allocato e ritardi nell'evasione degli ordini.",
          solution: "Ovelon Prime ha standardizzato i flussi di inventario con registrazione automatica, regole di allocazione per zona e visibilità dello stock in tempo reale — riducendo gli interventi manuali migliorando precisione e produttività.",
          results: [
            { value: "55%", label: "Riduzione discrepanze di stock" },
            { value: "40%", label: "Evasione ordini più rapida" },
            { value: "35%", label: "Migliore utilizzo dello spazio" },
          ],
        },
        maintenance: {
          industry: "Riparazione e Manutenzione",
          client: "Servizi attrezzature industriali",
          challenge: "I flussi di manutenzione reattiva dominavano le operazioni. Gli ordini di lavoro venivano registrati in modo incoerente, il tracciamento dei ricambi era manuale e la pianificazione dei tecnici si basava sulla disponibilità anziché sulla priorità.",
          solution: "Ovelon Prime ha introdotto la gestione strutturata degli ordini di lavoro con instradamento automatico per priorità, integrazione dell'inventario ricambi e invio ottimizzato dei tecnici — passando dalla manutenzione reattiva a quella predittiva.",
          results: [
            { value: "47%", label: "Riduzione dei tempi di fermo" },
            { value: "65%", label: "Completamento ordini più rapido" },
            { value: "30%", label: "Meno incidenti ricorrenti" },
          ],
    },
    complianceBlog: {
      badge: "Conformità e Governance",
      title: "NIS2, ISO 27001 e SOC 2: Cosa devono sapere ora i leader operativi.",
      subtitle: "Con l'intensificarsi della pressione normativa sulle infrastrutture critiche, comprendere NIS2, ISO 27001 e SOC 2 non è più facoltativo — è sopravvivenza operativa. Ecco come Ovelon Prime allinea le tue operazioni con questi framework.",
      articles: {
        nis2: {
          tag: "Direttiva UE 2022/2555",
          title: "Direttiva NIS2: La nuova realtà operativa per le infrastrutture critiche",
          excerpt: "La Direttiva NIS2 è la normativa di cybersecurity più significativa dell'UE, che copre 18 settori critici e impone sanzioni severe per la non conformità.",
          facts: [
            { label: "Settori coperti", value: "18" },
            { label: "Sanzione massima", value: "€10M o 2% fatturato" },
            { label: "Responsabilità gestione", value: "Personale" },
          ],
          sections: [
            { heading: "Cos'è la NIS2 e perché è importante?", text: "La NIS2 sostituisce la Direttiva NIS originale del 2016 ed espande drasticamente l'ambito dei requisiti di cybersecurity nell'UE. Copre entità essenziali e importanti in 18 settori. Le organizzazioni devono implementare misure di gestione del rischio, segnalazione incidenti, sicurezza della catena di approvvigionamento e pianificazione della continuità — o affrontare sanzioni fino a €10 milioni o 2% del fatturato globale." },
            { heading: "La responsabilità operativa è ora personale", text: "Sotto la NIS2, il senior management può essere ritenuto personalmente responsabile per la non conformità. CISO, COO e membri del consiglio devono dimostrare governance attiva sulla cybersecurity e resilienza operativa." },
            { heading: "Requisiti rigorosi di segnalazione incidenti", text: "La NIS2 richiede un framework di segnalazione a livelli: allarme precoce entro 24 ore, notifica dettagliata entro 72 ore e rapporto finale entro un mese. Le organizzazioni devono avere processi strutturati di rilevamento, classificazione ed escalation." },
            { heading: "La sicurezza della supply chain diventa obbligatoria", text: "La direttiva richiede alle organizzazioni di valutare e gestire i rischi di cybersecurity lungo tutta la catena di approvvigionamento — incluse valutazioni dei fornitori e monitoraggio continuo dei rischi di terzi." },
          ],
          ovelonTitle: "Come Ovelon Prime supporta la conformità NIS2",
          ovelonPoints: [
            "Acquisizione e classificazione strutturata degli incidenti con percorsi di escalation automatizzati che rispettano le scadenze di 24/72 ore",
            "Tracce di audit complete per ogni evento operativo, supportando dimostrazioni di conformità basate su evidenze",
            "Procedure di esecuzione standardizzate che eliminano operazioni basate sull'interpretazione",
            "Visibilità operativa in tempo reale con dashboard che supportano gli obblighi di governance della direzione",
            "Flussi di coordinamento della supply chain che impongono requisiti di sicurezza nelle interazioni con i fornitori",
            "Sistemi di notifica automatizzati che garantiscono comunicazione tempestiva durante gli incidenti",
          ],
        },
        iso27001: {
          tag: "Standard Internazionale",
          title: "ISO 27001: Costruire un SGSI che funzioni davvero",
          excerpt: "La ISO 27001 è lo standard di riferimento globale per la gestione della sicurezza delle informazioni. Ma la certificazione è solo l'inizio — mantenere un SGSI efficace richiede disciplina operativa e controllo basato su evidenze.",
          facts: [
            { label: "Controlli nell'Annesso A", value: "93" },
            { label: "Organizzazioni certificate", value: "70.000+" },
            { label: "Ciclo di ricertificazione", value: "3 anni" },
          ],
          sections: [
            { heading: "Perché la ISO 27001 è essenziale", text: "La ISO 27001 fornisce un approccio sistematico alla gestione delle informazioni sensibili. La revisione 2022 ha riorganizzato i controlli in quattro domini con 93 controlli nell'Annesso A, rendendola più allineata agli ambienti operativi moderni." },
            { heading: "Il divario tra certificazione e realtà operativa", text: "Molte organizzazioni ottengono la certificazione ma faticano a mantenere la disciplina operativa tra gli audit. Le politiche esistono sulla carta ma non vengono applicate nelle operazioni quotidiane." },
            { heading: "Controlli dell'Annesso A che richiedono automazione", text: "Diversi controlli dell'Annesso A si collegano direttamente ai processi operativi: A.5.24-A.5.28 (gestione incidenti e raccolta evidenze) e A.8.16 (attività di monitoraggio). Questi richiedono processi strutturati e ripetibili." },
            { heading: "Il miglioramento continuo è obbligatorio", text: "La Clausola 10 richiede il miglioramento continuo del SGSI — identificazione sistematica delle non conformità, azioni correttive e valutazione dell'efficacia. Senza dati operativi strutturati, il miglioramento continuo diventa congettura." },
          ],
          ovelonTitle: "Come Ovelon Prime supporta la conformità ISO 27001",
          ovelonPoints: [
            "Classificazione e risposta automatizzata agli incidenti allineata con i controlli A.5.24-A.5.28 dell'Annesso A",
            "Tracce di audit complete per ogni evento operativo (A.5.28)",
            "Procedure di esecuzione standardizzate per implementazione coerente dei controlli",
            "Dashboard di monitoraggio in tempo reale per verifica continua della conformità (A.8.16)",
            "Flussi di gestione del cambiamento strutturati con catene di approvazione documentate",
            "Metriche operative e analisi delle tendenze per miglioramento continuo sotto la Clausola 10",
          ],
        },
        soc2: {
          tag: "Criteri dei servizi di fiducia AICPA",
          title: "SOC 2: Dimostrare la fiducia operativa attraverso controlli di sicurezza verificati",
          excerpt: "SOC 2 (Service Organization Control 2) è il principale framework di audit per verificare che le pratiche di sicurezza delle informazioni di un'organizzazione soddisfino standard rigorosi su cinque Criteri dei Servizi di Fiducia. Un report SOC 2 è sempre più richiesto da clienti enterprise e settori regolamentati.",
          type1Label: "Tipo I",
          type2Label: "Tipo II",
          type1: {
            facts: [
              { label: "Ambito di audit", value: "Punto nel tempo" },
              { label: "Durata", value: "Data singola" },
              { label: "Criteri di fiducia", value: "Fino a 5" },
            ],
            sections: [
              {
                heading: "Cos'è il SOC 2 Tipo I?",
                text: "Un report SOC 2 Tipo I valuta se i controlli di sicurezza di un'organizzazione sono adeguatamente progettati in un momento specifico. Risponde alla domanda: 'I controlli giusti sono in atto oggi?' Il Tipo I è tipicamente il punto di partenza per le organizzazioni che perseguono la certificazione SOC 2.",
              },
              {
                heading: "I cinque Criteri dei Servizi di Fiducia",
                text: "SOC 2 valuta i controlli su cinque criteri: Sicurezza (obbligatorio), Disponibilità, Integrità del trattamento, Riservatezza e Privacy. Il Tipo I verifica che politiche, procedure e controlli tecnici siano adeguatamente progettati — non che funzionino in modo coerente nel tempo.",
              },
              {
                heading: "Quando il Tipo I è appropriato",
                text: "Il Tipo I viene comunemente utilizzato quando un'organizzazione deve dimostrare rapidamente un livello base di garanzia di sicurezza a clienti o partner. È anche il naturale precursore del Tipo II.",
              },
            ],
          },
          type2: {
            facts: [
              { label: "Periodo di audit", value: "6–12 mesi" },
              { label: "Criteri di fiducia", value: "Fino a 5" },
              { label: "Standard di mercato", value: "Enterprise" },
            ],
            sections: [
              {
                heading: "Cos'è il SOC 2 Tipo II?",
                text: "Un report SOC 2 Tipo II valuta se i controlli di sicurezza di un'organizzazione non solo esistono ma operano efficacemente per un periodo prolungato — tipicamente 6-12 mesi. È il gold standard per la fiducia enterprise ed è sempre più richiesto da team di procurement in settori come servizi finanziari e infrastrutture critiche.",
              },
              {
                heading: "La disciplina operativa è il fondamento",
                text: "A differenza del Tipo I, il Tipo II richiede un'esecuzione coerente, documentata e verificabile dei controlli per tutto il periodo di revisione. I log degli incidenti devono essere completi, i controlli di accesso applicati in modo coerente e la gestione dei cambiamenti documentata.",
              },
              {
                heading: "Risultati frequenti che compromettono gli audit Tipo II",
                text: "I fallimenti più comuni del Tipo II riguardano registri di incidenti incompleti, revisioni di accesso degli utenti incoerenti, documentazione di approvazione mancante e lacune nel monitoraggio della sicurezza.",
              },
              {
                heading: "SOC 2 Tipo II e conformità continua",
                text: "A differenza di una certificazione una tantum, la conformità Tipo II è una disciplina operativa continua. Le organizzazioni devono mantenere documentazione pronta per l'audit durante tutto l'anno — non solo nelle settimane prima della finestra di audit.",
              },
            ],
          },
          ovelonTitle: "Come Ovelon Prime supporta la prontezza SOC 2",
          ovelonPoints: [
            "Flussi di lavoro strutturati di intake e risposta agli incidenti che producono prove di audit complete e con timestamp",
            "Flussi di controllo degli accessi automatizzati per provisioning e deprovisioning coerenti degli utenti",
            "Documentazione di gestione dei cambiamenti con catene di approvazione e timestamp",
            "Monitoraggio operativo in tempo reale allineato ai criteri SOC 2 di Sicurezza e Disponibilità",
            "Procedure di esecuzione standardizzate che eliminano workaround non documentati",
            "Dashboard di reporting sulla conformità per visibilità continua sull'efficacia dei controlli",
          ],
        },
      },
    },
  },
    },
  },

  /* ===================== PT ===================== */
  
  pt: {
    backToTop: "Voltar ao topo",
    explore: "Explorar",

    common: {
      back: "Voltar",
    },

    nav: {
      services: "Serviços",
      pricing: "Preços",
      about: "Sobre",
      contact: "Contacto",
      cta: "Discovery Call",
      viewDemo: "Ver Demonstração",
    },
    hero: {
      badge: "Indústrias Críticas Orientadas a Resultados",
      title: "A complexidade operacional corrói",
      titleHighlight: "a fiabilidade e quebra a disciplina de execução",
      subtitle:
        "Quando a coordenação se torna manual, a manutenção reativa e as operações dependem de soluções improvisadas, o controlo já está perdido.",
      subtitle2:
        "Restauramos a execução previsível e disciplinada em operações ativas — sem substituir sistemas centrais ou interromper fluxos de trabalho diários.",
      subtitleMobile: "Quando a coordenação se torna manual, o controlo já está perdido.",
      cta: "Recuperar o Controlo das Operações",
      simulatorCta: "Diagnóstico de Procura e Execução",
    },
     problem: {
       badge: "O Desafio",
       title: "O atrito operacional custa mais do que imagina.",
       description: "Cada chamada perdida, erro de reserva e momento de sobrecarga da equipa representa receita perdida e uma experiência do hóspede diminuída. A sua receção não deveria ser o seu gargalo.",
       pain1: "Chamadas perdidas nas horas de pico",
       pain2: "Reservas duplicadas e conflitos de horários",
       pain3: "Equipa sobrecarregada com tarefas administrativas",
       pain4: "Comunicação inconsistente com hóspedes",
       pain5: "Receita perdida devido a lacunas operacionais",
     },
     process: {
       badge: "O Processo",
       title: "Integração perfeita com as suas operações.",
       step1: { title: "Capturar", description: "Cada chamada, mensagem e pedido de reserva é tratado com precisão no momento em que chega." },
       step2: { title: "Coordenar", description: "O alinhamento em tempo real com o seu calendário e fluxos de trabalho garante uma programação fluida." },
       step3: { title: "Confirmar", description: "Confirmações automáticas, atualizações e escalações quando necessário — nada passa despercebido." },
     },
     workflowDemo: {
       badge: "Demo Interativa",
       title: "Veja Ovelon Prime em ação.",
        tabs: { workflow: "Automação de Fluxo", call: "Recuperação de Chamadas", appointment: "Agendamento de Incidentes", safety: "Manuais de Segurança" },
        workflow: {
          description: "Veja como uma única consulta desencadeia um fluxo de trabalho operacional completo — automaticamente.",
          runDemo: "EXECUTAR DEMO DO FLUXO",
          complete: "Fluxo completo executado em menos de 60 segundos",
          reset: "REINICIAR DEMO",
          processing: "A processar fluxo...",
          step1: { title: "Pedido recebido", desc: "Todos os pedidos ou incidentes são capturados e registados em tempo real." },
          step2: { title: "Disponibilidade verificada", desc: "O sistema verifica inventário, capacidade ou calendário para confirmar viabilidade." },
          step3: { title: "Ordem/Tarefa criada", desc: "Os pedidos são transformados em ordens ou tarefas operacionais." },
          step4: { title: "Confirmação enviada", desc: "Notificações automáticas enviadas por email, SMS ou dashboards internos." },
          step5: { title: "Equipa notificada", desc: "As equipas operacionais recebem alertas instantâneos para processamento." },
          step6: { title: "Perfil e histórico atualizado", desc: "Toda a informação é registada automaticamente para referência futura." },
        },
        call: {
          description: "Veja como Ovelon Prime transforma uma oportunidade potencialmente perdida num acordo confirmado.",
          simulate: "SIMULAR CHAMADA RECEBIDA",
          stage1: "Chamada a entrar...",
          stage2: "Sem Ovelon Prime: Chamada perdida, sem acordo",
          stage3: "Com Ovelon Prime: Chamada atendida instantaneamente",
          stage4: "Pedido de incidente do cliente capturado",
          stage5: "Pedido de incidente confirmado",
          revenueLost: "Receita/rastreamento perdidos. Cliente insatisfeito.",
          revenueCaptured: "Receita/rastreamento capturados. Cliente satisfeito.",
        },
        appointment: {
          description: "Veja como pedidos, incidentes e escalações são automaticamente agendados, atribuídos e rastreados — sem coordenação manual.",
          runDemo: "EXECUTAR DEMO DE INCIDENTES",
          complete: "Ciclo completo de agendamento de incidentes concluído em menos de 60 segundos",
          reset: "REINICIAR DEMO",
          processing: "A processar incidente...",
          step1: { title: "Incidente reportado", desc: "Um incidente ou pedido de serviço é submetido por telefone, formulário ou sistema de alerta automático." },
          step2: { title: "Prioridade classificada", desc: "O sistema classifica a gravidade e urgência com base em regras de escalação predefinidas." },
          step3: { title: "Equipa de resposta atribuída", desc: "Os respondedores qualificados são identificados e atribuídos com base na disponibilidade e experiência." },
          step4: { title: "Caminho de escalação ativado", desc: "Se os limiares de SLA forem ultrapassados, a escalação automática notifica supervisores e gestão." },
          step5: { title: "Resolução rastreada", desc: "O progresso é monitorizado em tempo real com atualizações de estado enviadas a todas as partes." },
          step6: { title: "Relatório pós-incidente arquivado", desc: "Detalhes do incidente, tempos de resposta e passos de resolução são documentados para conformidade." },
        },
        safety: {
          description: "Veja como os manuais de vídeo animados de segurança padronizados são implantados em armazéns industriais — garantindo conformidade HSE consistente.",
          runDemo: "EXECUTAR DEMO DE SEGURANÇA",
          complete: "Implantação do manual de segurança concluída com sucesso",
          reset: "REINICIAR DEMO",
          processing: "A implantar conteúdo de segurança...",
          step1: { title: "Perigo identificado", desc: "Um perigo no local de trabalho ou atualização de procedimento aciona a criação de uma nova instrução visual." },
          step2: { title: "Manual animado criado", desc: "Um manual de vídeo animado passo a passo é gerado seguindo as normas ISO/VCA/OSHA." },
          step3: { title: "Localizado em múltiplos idiomas", desc: "O conteúdo é automaticamente traduzido e localizado para conformidade da força de trabalho multilingue." },
          step4: { title: "Distribuído às estações", desc: "Os manuais de vídeo são enviados instantaneamente para quiosques, tablets e dispositivos dos operadores." },
          step5: { title: "Confirmação de receção rastreada", desc: "A visualização e compreensão de cada operador é registada para auditoria e certificação." },
          step6: { title: "Relatório de conformidade gerado", desc: "Relatórios de conformidade automatizados mostram a cobertura de formação em todos os turnos." },
        },
      },
    video: {
      title: "Concebido para operações ininterruptas",
      subtitle: "Veja como a Ovelon Prime apoia ambientes de missão crítica sem interromper sistemas ativos.",
      cta: "Discovery Call",
    },
    services: {
      label: "O QUE ESTABILIZAMOS",
      title: "Fiabilidade operacional em ambientes",
      titleHighlight: "que não podem dar-se ao luxo de errar",
      subtitle:
        "Eliminamos falhas operacionais causadas por coordenação manual, execução pouco clara e gestão reativa de incidentes — restaurando controlo, responsabilidade e resultados previsíveis em operações ativas.",
      items: [
        {
          title: "Gestão de Pedidos e Procura",
          description:
            "Os pedidos recebidos são capturados, validados e encaminhados de forma consistente — sem depender de acompanhamento ad-hoc ou julgamento individual.",
          tags: ["Controlo de admissão inteligente", "Regras de qualificação", "Encaminhamento de sistema"],
        },
        {
          title: "Normas de Segurança e Execução",
          description:
            "Instruções visuais padronizadas utilizadas para alinhar operadores, empreiteiros e equipas em procedimentos críticos de segurança — reduzindo erros de interpretação, desvios e risco de execução.",
          tags: [
            "Procedimentos HSE",
            "Alinhamento ISO / VCA / OSHA",
            "Integração de operadores e empreiteiros",
          ],
        },
        {
          title: "Gestão de Incidentes e Escalamento",
          description:
            "Os problemas são registados, classificados e escalados de forma controlada — garantindo responsabilidade, clareza de resposta e caminhos de resolução previsíveis.",
          tags: ["Classificação de problemas", "Caminhos de escalamento", "Propriedade da resposta"],
        },
        {
          title: "Eficiência na marcação de consultas",
          description:
            "Agendamento por voz integrado que lida com reservas, confirmações e remarcações com precisão — liberando sua equipe, mantendo todas as conexões em dia e proporcionando uma experiência impecável ao cliente.",
          tags: ["Agendamento de chamadas", "Disponibilidade 24/7", "Resposta automática de retorno de chamada"],
        },
      ],
      clickToExplore: "Clique para explorar",
    },
    serviceDetails: {
      keyBenefits: "Principais vantagens",
      cta: "Explorar demo",
      benefits: [
        [
          "A validação automatizada de admissão elimina gargalos de revisão manual",
          "O encaminhamento inteligente reduz o tempo de resposta em até 60%",
          "Trilha de auditoria completa para conformidade e responsabilidade",
          "Integração transparente com sistemas de ticketing existentes",
        ],
        [
          "SOPs visuais reduzem tempo de formação e erros de interpretação",
          "Monitorização de conformidade em tempo real em todos os turnos",
          "Integração de contratados padronizada em todas as localizações",
          "Procedimentos alinhados com HSE prontos para auditorias regulamentares",
        ],
        [
          "A classificação automatizada garante priorização consistente de problemas",
          "Caminhos de escalamento claros com propriedade definida em cada nível",
          "SLAs de resposta monitorizados e aplicados automaticamente",
          "Análise pós-incidente para melhoria contínua",
        ],
        [
          "Disponibilidade 24/7 sem aumentar o quadro de pessoal",
          "O reagendamento inteligente minimiza as faltas",
          "Confirmação multicanal (voz, SMS, email)",
          "Sincronização de calendário transparente com os seus sistemas existentes",
        ],
      ],
    },
    sectors: {
      label: "Sectores",
      title: "Fiabilidade Operacional Para",
      titleHighlight: "Indústrias de Missão Crítica",
      subtitle: "Apoiamos ambientes operacionais onde tempo de inatividade, desalinhamento ou resposta atrasada impactam diretamente a segurança, continuidade ou produção.",
      items: [
        {
          name: "Gestão de Instalações",
          description: "Gestão de ativos distribuídos e pedidos de serviço em instalações complexas — com tolerância limitada para atrasos ou má coordenação.",
        },
        {
          name: "Manutenção e Reparação",
          description: "Operação sob pressão constante para prevenir tempo de inatividade na manutenção reativa e preventiva — frequentemente com informação incompleta e janelas de resposta apertadas.",
        },
        {
          name: "Logística e Armazenagem",
          description: "Coordenação de força de trabalho, equipamento e fluxos de trabalho sob prazos rigorosos e pressão operacional contínua.",
        },
      ],
    },
    testimonials: {
      label: "VOZES DOS CLIENTES",
      title: "Testemunhos de Clientes",
      items: [
        {
          quote: "Antes da Ovelon Prime, a coordenação de turnos dependia do acompanhamento individual. Hoje, manutenção e planeamento executam previsivelmente em todos os turnos — sem intervenção manual.",
          name: "Patrick Sullivan",
          title: "Líder de Operações Logísticas",
          facility: "Centro de Distribuição, Nebraska",
        },
        {
          quote: "No nosso ambiente, atrasos afetam diretamente a segurança e a produção. A Ovelon Prime ajudou-nos a padronizar a execução entre equipas, reduzindo o tempo de resposta em 45% sem aumentar o risco operacional.",
          name: "Alicia Zhang",
          title: "Diretora de Operações",
          facility: "Instalação de Armazém, Ottawa",
        },
        {
          quote: "Operamos sob pressão contínua. Desde a implementação do sistema, o encaminhamento e escalamento seguem regras fixas — não suposições. O tempo de inatividade causado por erros de coordenação já não é um fator.",
          name: "Wyatt Coleman",
          title: "Gestor de Fábrica",
          facility: "Fábrica Industrial, Austin",
        },
        {
          quote: "Gerir uma instalação deste tamanho significa milhares de peças em movimento. O sistema deu-nos uma fonte única de verdade para o estado das tarefas, os ciclos de equipamentos e as verificações de conformidade. Deixámos de perseguir atualizações e começámos a agir com base nelas.",
          name: "James Whitfield",
          title: "Diretor de operações de instalações",
          facility: "Armazém industrial, Liverpool",
        },
        {
          quote: "No nosso ambiente, até pequenos atrasos se propagam rapidamente. A plataforma impõe responsabilização em tempo real em todos os departamentos. Eliminámos as zonas cinzentas que costumavam paralisar a produção durante horas.",
          name: "Ciarán O'Sullivan",
          title: "Responsável sénior de instalações",
          facility: "Unidade de processamento, Tennessee",
        },
        {
          quote: "Perdíamos horas todas as semanas com expedição manual e ordens de trabalho mal encaminhadas. Desde a implementação, as nossas equipas seguem fluxos de trabalho estruturados de ponta a ponta — sem suposições, sem estrangulamentos. A produtividade aumentou e a nossa taxa de erros estabilizou.",
          name: "Rachel Moreno",
          title: "Gestora de controlo de operações",
          facility: "Centro logístico, Dublin",
        }
      ],
    },
    operationalTrust: {
      heading: "Concebido para ambientes regulados e de segurança crítica",
      items: [
        { text: "Princípios de segurança alinhados com ISO 27001" },
        { text: "Controlo de processos estilo ISO 9001" },
        { text: "Design operacional consciente de HSE" },
        { text: "Documentação e controlos prontos para auditoria" },
        { text: "Propriedade total do cliente — sem sistemas de caixa preta" },
      ],
      footer: "Concebido para ambientes revisados por CISO com governança de segurança interna, políticas de controlo de acesso e procedimentos de gestão de mudanças.",
    },
    about: {
      label: "Sobre Nós",
      title: "Construído sobre expertise operacional,",
      titleHighlight: "impulsionado pela precisão",
      description1:
        "A Ovelon Prime trabalha com organizações que operam sob pressão constante — onde tempo de inatividade, atrasos e processos manuais impactam diretamente custo, segurança e fiabilidade.",
      description2:
        "Combinamos profunda expertise de domínio operacional com camadas de controlo operacional para conceber sistemas que se integram perfeitamente em fluxos de trabalho existentes, sem interromper operações críticas.",
      quote:
        '"A precisão não é opcional. Em ambientes onde a fiabilidade determina resultados, os sistemas devem ser engenhados para funcionar sob pressão."',
      quoteAuthor: "Equipa de Liderança · Ovelon Prime",
      stats: {
        uptime: "Tempo de Atividade do Sistema",
        deployments: "Implementações de Automação",
        industries: "Indústrias Críticas Servidas",
      },
    },
    pricing: {
      label: "Preços",
      title: "Preços baseados em compromisso, com resultados claramente definidos",
      subtitle:
        "Compromissos claramente definidos focados em melhorar a fiabilidade, reduzir fricção operacional e entregar impacto mensurável.",
      focused: {
        name: "Compromisso Operacional Focado",
        description:
          "Um compromisso de âmbito fixo concebido para resolver um estrangulamento operacional claramente definido com impacto mensurável.",
        requestQuote: "Solicitar orçamento",
        cta: "Solicitar Avaliação de Compromisso",
        features: [
          "Melhoria operacional única e claramente definida",
          "Mapeamento de processos e responsabilidades",
          "Implementação e lançamento controlados",
          "Documentação e transferência operacional",
          "Validação pós-implementação",
        ],
      },
      enterprise: {
        name: "Programa Operacional Empresarial",
        description:
          "Sistemas operacionais personalizados para organizações complexas com requisitos rigorosos de governança, conformidade e continuidade.",
        priceLabel: "Preços personalizados",
        price: "Serviço Personalizado",
        period: "taxa única",
        cta: "Solicitar Avaliação Inicial",
        badge: "Enterprise",
        features: [
          "Arquitetura operacional multi-sistema",
          "Integrações personalizadas em sistemas existentes",
          "Suporte dedicado de implementação e transição",
          "Design operacional consciente de conformidade",
          "SLA personalizado e framework de governança",
        ],
      },
      securityNote:
        "Cada compromisso é construído com segurança de nível empresarial, proteção de dados e conformidade operacional.",
      talkToExpert: "Discovery Call",
      intakeNote:
        "Admissão estruturada · Sem pressão de vendas · Diretamente com um especialista experiente",
      reviewNote: "Todos os pedidos são revisados antes do agendamento.",
    },
    contact: {
      label: "COMPROMISSO",
      title: "Pronto para recuperar o controlo das",
      titleHighlight: " suas operações?",
      subtitle: "Todos os compromissos são definidos antecipadamente e sujeitos a uma revisão formal antes de continuação.",
      cta: "Agendar Consulta",
      guarantee: "Sem compromissos de longo prazo.",
      guaranteeHighlight: "Os compromissos continuam apenas quando o valor operacional é demonstrado",
      guaranteeSuffix: " em compromissos qualificados.",
      enterpriseLabel: "Consultas empresariais: ",
      rightTitle: "Contacte a Ovelon Prime",
      infoText1: "Se tiver alguma questão ou quiser discutir como podemos apoiar o seu negócio, não hesite em contactar-nos.",
      infoText2: "O suporte ao cliente está disponível 24 horas por dia, 7 dias por semana.",
    },
    contactForm: {
      firstName: "Nome",
      firstNamePlaceholder: "João",
      lastName: "Apelido",
      lastNamePlaceholder: "Silva",
      businessName: "Nome da empresa",
      businessNamePlaceholder: "Indústrias Acme",
      businessEmail: "Email empresarial",
      businessEmailPlaceholder: "joao@empresa.pt",
      requestDetails: "Detalhes do pedido",
      requestDetailsPlaceholder: "Descreva os seus desafios operacionais e o que pretende alcançar...",
      submit: "Enviar pedido",
      submitting: "A enviar...",
      validationError: "Por favor preencha corretamente todos os campos obrigatórios.",
      successTitle: "Enviado com sucesso",
      successMessage: "Analisaremos o seu pedido cuidadosamente.",
      successCta: "Marcar uma consulta estratégica",
    },
    faq: {
      title: "Perguntas Frequentes",
      category: "Compromisso e Entrega",
      items: [
        {
          question: "Como funciona a Ovelon Prime?",
          answer:
            "Começamos com uma admissão estruturada para compreender a restrição operacional. Concebemos e validamos uma solução de âmbito fixo contra fluxos de trabalho reais. Implementamos, testamos sob carga e entregamos com documentação completa.",
        },
        {
          question: "Em que indústrias se especializam?",
          answer:
            "Trabalhamos com organizações que operam em ambientes de missão crítica, incluindo manufatura, logística e gestão de instalações. Se atrasos, erros ou tempo de inatividade impactam diretamente a segurança, produção ou continuidade, estamos no âmbito.",
        },
        {
          question: "Isto é uma subscrição ou serviço contínuo?",
          answer:
            "Não. Os compromissos são de âmbito fixo e orientados a resultados. Não há subscrição obrigatória, dependência de licença ou taxa contínua. Após a entrega, o sistema é seu.",
        },
        {
          question: "Quanto tempo demora uma implementação típica?",
          answer:
            "A maioria dos compromissos focados são entregues em 12-16 dias, dependendo do âmbito e acesso ao sistema. Programas empresariais seguem um lançamento faseado alinhado com prioridades operacionais.",
        },
        {
          question: "Isto integra-se com os nossos sistemas existentes?",
          answer:
            "Sim. As soluções são concebidas para integrar com sistemas existentes sem substituir plataformas centrais. Trabalhamos dentro das suas ferramentas atuais e limites de dados.",
        },
        {
          question: "Precisamos de mudar os nossos fluxos de trabalho atuais?",
          answer:
            "Não. Concebemos em torno dos seus fluxos de trabalho existentes. Onde mudanças são necessárias, são mínimas, documentadas e validadas com as suas equipas operacionais antes da implementação.",
        },
        {
          question: "Quem é o proprietário da otimização após a entrega?",
          answer:
            "Vocês. Todos os sistemas, configurações e documentação são entregues após a conclusão. Não há dependência de fornecedor. Mantêm total propriedade operacional e técnica.",
        },
        {
          question: "Como lidam com segurança e conformidade?",
          answer:
            "Segurança e conformidade são tratadas como restrições de design, não como considerações tardias. Cada compromisso é definido em torno das suas políticas de segurança existentes, controlos de acesso e requisitos de conformidade. Trabalhamos dentro da sua infraestrutura aprovada, limites de dados e frameworks de governança. Nenhum dado é extraído ou reutilizado além do âmbito acordado, e todos os sistemas são entregues com documentação completa para suportar auditorias internas, revisões de risco e transferências.",
        },
        {
          question: "Que nível de envolvimento interno é necessário?",
          answer:
            "Envolvimento mínimo e estruturado. Requeremos contributos limitados durante a admissão para compreender restrições operacionais e validar suposições. A implementação é tratada de forma independente, com pontos de verificação claros para revisão e aprovação. As equipas operacionais não são interrompidas e não são necessários recursos internos contínuos após a entrega.",
        },
        {
          question: "A Ovelon Prime é adequada para grandes ambientes empresariais?",
          answer:
            "Sim. Somos concebidos para ambientes complexos com múltiplos stakeholders e requisitos rigorosos de governança, conformidade e operacionais. Os compromissos são estruturados para alinhar com gestão de mudanças empresariais, revisões de segurança e lançamentos faseados — sem forçar substituições de plataformas ou interromper sistemas críticos.",
        },
      ],
    },
    metricsStrip: {
      items: [
        { value: "40%", label: "Redução de inatividade" },
        { value: "200+", label: "Fluxos automatizados" },
        { value: "3", label: "Continentes cobertos" },
        { value: "98%", label: "Taxa de conformidade SLA" },
      ],
    },
    footer: {
      privacy: "Privacidade",
      terms: "Termos",
      copyright: "© {{year}} Ovelon Prime. Todos os direitos reservados.",
      tagline: "Sistemas operacionais empresariais construídos para clareza, controlo e escala.",
      trustHeading: "Concebido para ambientes regulamentados e de segurança crítica",
      trustItems: [
        "Princípios de segurança alinhados com ISO 27001",
        "Controlos conformes com SOC 2 Tipo II",
        "Preparação para a Diretiva NIS2",
        "Documentação pronta para auditoria",
        "Design operacional consciente de HSE",
        "Propriedade total do cliente — sem sistemas de caixa negra",
      ],
      badgeIsoTitle: "ISO 27001",
      badgeIsoDesc: "Gestão certificada de segurança da informação",
      badgeSocTitle: "SOC 2 Type II",
      badgeSocDesc: "Controlos de organizações de serviços",
      badgeGdprTitle: "RGPD",
      badgeGdprDesc: "Regulamento de proteção de dados e privacidade",
    },
    industrialTrust: {
      heading: "Confiado por operadores em ambientes de missão crítica",
      disclaimer: "Operadores representativos dos sectores industrial, logístico, manufatureiro e de infraestruturas. Os compromissos variam por âmbito e ambiente.",
    },
    intake: {
      title: "Qualificação de Consulta",
      subtitle: "Por favor complete o breve diagnóstico abaixo. Todas as submissões são revistas antes do agendamento.",
    },
    cookie: {
      message: "Utilizamos cookies para melhorar a sua experiência. Ao continuar a visitar este site, concorda com o nosso uso de cookies.",
      accept: "Aceitar",
      decline: "Recusar",
      learnMore: "Saiba mais sobre a nossa política de privacidade",
    },
    privacy: {
      title: "Política de Privacidade",
      lastUpdated: "Última Atualização: 01.12.2025",
      sections: {
        introduction: {
          title: "1. Introdução",
          p1: "Na Ovelon Prime, levamos a proteção de dados e confidencialidade a sério. Esta Política de Privacidade explica como recolhemos, utilizamos e protegemos informações pessoais e empresariais quando interage com o nosso website, formulários de admissão e serviços.",
          p2: "Processamos dados de forma responsável e apenas na medida necessária para prestar os nossos serviços, garantir integridade operacional e cumprir obrigações legais.",
        },
        informationCollected: {
          title: "2. Informação que Recolhemos",
          personal: "Informação Pessoal:",
          personalDesc: "Nome, endereço de email empresarial, número de telefone e informação submetida através de formulários ou correspondência direta.",
          business: "Informação Empresarial e Operacional:",
          businessDesc: "Nome da empresa, endereço ou jurisdição empresarial, classificação da indústria, contexto operacional e informação relacionada com conformidade voluntariamente fornecida durante a admissão.",
          technical: "Informação Técnica:",
          technicalDesc: "Endereço IP, metadados do navegador e dispositivo, cookies e análises básicas de utilização.",
          note: "Não recolhemos intencionalmente dados pessoais sensíveis a menos que explicitamente necessário para conformidade ou prestação de serviços.",
        },
        howWeUse: {
          title: "3. Como Utilizamos a Sua Informação",
          items: [
            "Avaliar e qualificar pedidos de consulta",
            "Prestar e apoiar serviços de automação operacional",
            "Comunicar relativamente a compromissos, atualizações ou esclarecimentos",
            "Manter fiabilidade, segurança e desempenho da plataforma",
            "Cumprir obrigações legais, regulatórias ou contratuais",
          ],
          note: "Não utilizamos os seus dados para marketing não solicitado, perfis ou publicidade de terceiros.",
        },
        dataSharing: {
          title: "4. Partilha e Divulgação de Dados",
          p1: "A Ovelon Prime não vende, aluga ou comercializa dados pessoais ou empresariais.",
          p2: "A informação só pode ser partilhada quando estritamente necessário:",
          items: [
            "Com prestadores de serviços verificados necessários para a prestação de serviços",
            "Para cumprir obrigações legais ou regulatórias",
            "Para proteger a segurança, integridade ou direitos dos nossos sistemas e clientes",
          ],
          note: "Os dados nunca são partilhados para marketing ou propósitos promocionais de terceiros.",
        },
        dataSecurity: {
          title: "5. Segurança de Dados",
          p1: "Aplicamos salvaguardas técnicas e organizacionais padrão da indústria, incluindo encriptação, controlos de acesso e práticas de infraestrutura segura.",
          p2: "Embora nenhum sistema possa garantir segurança absoluta, operamos sob protocolos internos rigorosos concebidos para minimizar risco e proteger confidencialidade em todos os momentos.",
        },
        dataRetention: {
          title: "6. Retenção de Dados e os Seus Direitos",
          p1: "Retemos dados apenas pelo tempo necessário para cumprir os propósitos descritos nesta política ou para cumprir requisitos legais.",
          p2: "Tem o direito de solicitar acesso, correção ou eliminação dos seus dados, quando aplicável.",
        },
        contact: {
          title: "7. Contacto",
          p1: "Para questões sobre esta Política de Privacidade ou práticas de tratamento de dados, por favor contacte:",
        },
      },
    },
    terms: {
      title: "Termos e Condições",
      lastUpdated: "Última Atualização: 01 Dezembro 2025",
      sections: {
        introduction: {
          title: "1. Introdução",
          p1: "Estes Termos e Condições (\"Termos\") regem o seu acesso e utilização do website Ovelon Prime, processos de consulta e serviços profissionais de automação. Ao aceder ao website ou contratar os nossos serviços, concorda em ficar vinculado a estes Termos.",
          p2: "Se não concordar com estes Termos, deve descontinuar a utilização do website e serviços imediatamente.",
        },
        services: {
          title: "2. Serviços Prestados",
          p1: "A Ovelon Prime fornece serviços profissionais de automação, design de sistemas e implementação para ambientes industriais, logísticos, de gestão de instalações, infraestruturas e outros ambientes operacionalmente críticos.",
          p2: "Os serviços são prestados numa base de projeto, com âmbito, entregas e cronogramas definidos antes do compromisso. Cada compromisso está sujeito a acordo comercial separado.",
        },
        confidentiality: {
          title: "3. Confidencialidade",
          p1: "Toda a informação empresarial e operacional partilhada durante compromissos é tratada como confidencial. Não divulgamos, vendemos ou redistribuímos qualquer informação do cliente a terceiros, exceto quando necessário para prestação de serviços ou quando legalmente requerido.",
        },
        ip: {
          title: "4. Propriedade Intelectual",
          p1: "Os clientes mantêm total propriedade sobre todos os sistemas, configurações e entregas produzidas durante os compromissos, salvo acordo em contrário.",
          p2: "Todo o conteúdo, marcas e materiais proprietários neste website permanecem propriedade da Ovelon Prime.",
        },
        liability: {
          title: "5. Limitação de Responsabilidade",
          p1: "A Ovelon Prime não é responsável por quaisquer danos indiretos, incidentais, especiais, consequenciais ou punitivos resultantes da utilização dos nossos serviços.",
          p2: "Em nenhum caso a nossa responsabilidade excederá o montante total pago pelo cliente pelos serviços prestados.",
        },
        changes: {
          title: "6. Alterações",
          p1: "A Ovelon Prime reserva-se o direito de atualizar estes Termos a qualquer momento. Alterações entram em vigor imediatamente após publicação nesta página.",
        },
        contact: {
          title: "7. Contacto",
          p1: "Para questões sobre estes Termos, por favor contacte:",
        },
      },
    },
    demo: {
      header: "Análise de Compromissos Operacionais",
      close: "Fechar",
      beforeAlignment: "Antes do Alinhamento",
      afterAlignment: "Após o Alinhamento",
      tradeoff: "Compromisso",
      footer: "Cenários operacionais representativos de ambientes industriais, logísticos e de instalações.",
      intake: {
        title: "Receção de Trabalho e Gestão de Procura",
        metrics: {
          coordinationTime: "Tempo de Coordenação",
          reworkCycles: "Ciclos de Retrabalho",
          ownership: "Propriedade",
          ownershipValue: "Definida antecipadamente",
        },
        before: {
          title: "Receção fragmentada e coordenação reativa",
          text: "O trabalho entra através de emails, chamadas e canais informais. A qualidade da informação varia e a propriedade muda durante a execução.",
          bullet1: "Múltiplos caminhos de entrada",
          bullet2: "Ciclos de esclarecimento manuais",
          bullet3: "Prioridade impulsionada pela urgência",
          bullet4: "A execução depende de indivíduos",
          tradeoff: "Tempo perdido em coordenação e correção.",
        },
        after: {
          title: "Receção estruturada com execução controlada",
          text: "Os pedidos seguem um único caminho de entrada. A informação é validada antecipadamente e a propriedade é atribuída antes do início da execução.",
          bullet1: "Entrada única estruturada",
          bullet2: "Classificação e encaminhamento claros",
          bullet3: "Propriedade atribuída uma vez",
          bullet4: "Fluxo de execução previsível",
          tradeoff: "A coordenação diminui, a execução estabiliza.",
        },
      },
      safety: {
        title: "Segurança e Padrões de Execução",
        metrics: {
          procedureDeviations: "Desvios de Procedimento",
          onboardingTime: "Tempo de Integração",
          consistency: "Consistência",
          consistencyValue: "Padronizada",
        },
        before: {
          title: "Execução baseada em interpretação",
          text: "Os procedimentos de segurança existem em documentos e conhecimento tribal. Os operadores interpretam os padrões de forma diferente.",
          bullet1: "Procedimentos inconsistentes",
          bullet2: "Alta dependência de pessoal sénior",
          bullet3: "Contratados integrados informalmente",
          bullet4: "Exposição a auditorias",
          tradeoff: "A segurança depende da experiência, não da estrutura.",
        },
        after: {
          title: "Execução padronizada com compreensão partilhada",
          text: "Os padrões de execução são claros, visuais e uniformes entre equipas e grupos de contratados.",
          bullet1: "Padrões de execução uniformes",
          bullet2: "Integração mais rápida",
          bullet3: "Erros de interpretação reduzidos",
          bullet4: "Framework de segurança alinhado",
          tradeoff: "A segurança torna-se repetível e auditável.",
        },
      },
      incident: {
        title: "Gestão de Incidentes e Escalamento",
        metrics: {
          escalationLoops: "Ciclos de Escalamento",
          responseTime: "Tempo de Resposta",
          accountability: "Responsabilidade",
          accountabilityValue: "Explícita",
        },
        before: {
          title: "Gestão de incidentes reativa",
          text: "Os incidentes são reportados de forma inconsistente e escalados manualmente. A responsabilidade é pouco clara e a visibilidade é limitada.",
          bullet1: "Limiares de escalamento pouco claros",
          bullet2: "Transferências atrasadas",
          bullet3: "Threads de comunicação paralelos",
          bullet4: "Visibilidade de gestão limitada",
          tradeoff: "Os incidentes consomem atenção em vez de serem resolvidos.",
        },
        after: {
          title: "Escalamento controlado com responsabilidade clara",
          text: "Os incidentes são registados, classificados e escalados através de caminhos predefinidos. Cada passo tem um proprietário claro e uma janela de resposta.",
          bullet1: "Níveis de escalamento definidos",
          bullet2: "Propriedade clara por incidente",
          bullet3: "Prazos de resposta previsíveis",
          bullet4: "Visibilidade operacional completa",
          tradeoff: "Os incidentes são resolvidos sistematicamente.",
        },
      },
      appointment: {
        title: "Eficiência de Agendamento",
        metrics: {
          schedulingTime: "Tempo de Agendamento",
          noShows: "Ausências",
          utilization: "Utilização",
          utilizationValue: "Maximizada",
        },
        before: {
          title: "Agendamento manual com fricção constante",
          text: "Marcações são coordenadas por telefone, email e folhas de cálculo. Confirmações são inconsistentes, ausências são frequentes e conflitos de horários drenam capacidade operacional.",
          bullet1: "Agendamento telefónico apenas em horário comercial",
          bullet2: "Verificações manuais de disponibilidade no calendário",
          bullet3: "Sem lembretes ou confirmações automatizadas",
          bullet4: "Frequentes duplas marcações e conflitos",
          tradeoff: "Tempo da equipa consumido por agendamento em vez de operações.",
        },
        after: {
          title: "Agendamento automatizado com capacidade previsível",
          text: "Marcações são capturadas e confirmadas automaticamente através de canais de voz e digitais. Verificações de disponibilidade em tempo real, lembretes automatizados e reagendamento inteligente eliminam a fricção.",
          bullet1: "Admissão de marcações por voz e digital 24/7",
          bullet2: "Sincronização de calendário em tempo real e prevenção de conflitos",
          bullet3: "Confirmações automatizadas e sequências de lembretes",
          bullet4: "Auto-reagendamento com otimização de capacidade",
          tradeoff: "O agendamento torna-se fluido, a utilização aumenta.",
        },
      },
    },
    simulator: {
      title: "Diagnóstico de Procura e Execução",
      close: "Fechar",
      next: "Seguinte",
      back: "Voltar",
      submit: "Submeter Revisão",
      submitting: "A submeter...",
      submitError: "Falha ao submeter. Por favor tente novamente.",
      submitErrorTitle: "Erro de Submissão",
      validationErrorTitle: "Validação necessária",
      validationErrorDescription: "Por favor preencha todos os campos obrigatórios antes de submeter.",
      stepIndicator: "Passo {{current}} de {{total}}",
      step1: {
        title: "Contexto Operacional",
        description: "Ajude-nos a compreender o seu ambiente operacional e escala.",
        industryLabel: "Indústria",
        industryPlaceholder: "Selecione a sua indústria",
        operationTypeLabel: "Tipos de Operação",
        operationTypePlaceholder: "Selecione todos os aplicáveis",
        scaleLabel: "Escala Operacional",
        scalePlaceholder: "Selecione a sua escala",
      },
      step2: {
        title: "Modelo Operacional Atual",
        description: "Descreva como o trabalho é atualmente gerido e coordenado.",
        requestHandlingLabel: "Gestão de Pedidos",
        requestHandlingPlaceholder: "Como são tratados os pedidos recebidos?",
        structureLabel: "Nível de Estrutura",
        structurePlaceholder: "Quão estruturados são os seus processos?",
        toolsLabel: "Ferramentas em Uso",
        toolsPlaceholder: "Que ferramentas estão atualmente em uso?",
      },
      step3: {
        title: "Pontos de Fricção",
        description: "Identifique onde ocorrem falhas ou fricção no seu ambiente.",
        frictionPointsLabel: "Principais Pontos de Fricção",
        frictionPointsPlaceholder: "Selecione todos os aplicáveis",
        frictionNotesLabel: "Notas Adicionais",
        frictionNotesPlaceholder: "Descreva quaisquer desafios específicos...",
      },
      step4: {
        title: "Restrições e Conformidade",
        description: "Compreenda as suas restrições operacionais e requisitos de conformidade.",
        downtimeLabel: "Sensibilidade a Tempo de Inatividade",
        downtimePlaceholder: "Quão crítico é o tempo de inatividade?",
        complianceLabel: "Requisitos de Segurança e Conformidade",
        compliancePlaceholder: "Selecione todos os aplicáveis",
        coordinationLabel: "Complexidade de Coordenação",
        coordinationPlaceholder: "Quão complexa é a coordenação de stakeholders?",
      },
      step5: {
        title: "Resumo",
        description: "Reveja a informação que forneceu antes de continuar para a análise.",
        sectionOperational: "Contexto Operacional",
        sectionModel: "Modelo Operacional",
        sectionFriction: "Pontos de Fricção",
        sectionConstraints: "Restrições",
        industry: "Indústria",
        operationType: "Tipos de Operação",
        scale: "Escala",
        requestHandling: "Gestão de Pedidos",
        structureLevel: "Nível de Estrutura",
        toolsInUse: "Ferramentas em Uso",
        frictionPoints: "Pontos de Fricção",
        frictionNotes: "Notas de Fricção",
        downtimeSensitivity: "Sensibilidade a Tempo de Inatividade",
        safetyCompliance: "Segurança e Conformidade",
        coordinationComplexity: "Complexidade de Coordenação",
        notSpecified: "Não especificado",
      },
      industries: {
        manufacturing: "Manufatura",
        logistics: "Logística e Armazenagem",
        facility: "Gestão de Instalações",
        infrastructure: "Infraestrutura",
        energy: "Energia e Utilities",
        other: "Outra Indústria",
      },
      operationTypes: {
        facility: "Operações de Instalações",
        logistics: "Logística e Distribuição",
        maintenance: "Manutenção e Reparação",
        production: "Produção e Manufatura",
        fieldService: "Serviço de Campo",
      },
      scales: {
        single: "Local Único",
        multi: "Multi-local",
        distributed: "Distribuído/Regional",
      },
      handlingMethods: {
        email: "Email",
        phone: "Telefone",
        tickets: "Sistema de Tickets",
        verbal: "Transferência Verbal",
        spreadsheets: "Folhas de Cálculo",
        erp: "Sistema ERP/CMMS",
      },
      structureLevels: {
        adhoc: {
          title: "Maioritariamente Ad-hoc",
          desc: "O trabalho é tratado à medida que surge, padronização mínima",
        },
        semiStructured: {
          title: "Semi-estruturado",
          desc: "Alguns processos são definidos mas a execução varia",
        },
        fullyStructured: {
          title: "Totalmente Estruturado",
          desc: "Processos claros com execução consistente",
        },
      },
      toolTypes: {
        manual: "Maioritariamente Manual",
        semiAutomated: "Semi-automatizado",
        fragmented: "Sistemas Fragmentados",
        integrated: "Plataforma Integrada",
      },
      frictionPoints: {
        slowResponse: "Tempos de Resposta Lentos",
        unclearOwnership: "Propriedade de Tarefas Pouco Clara",
        manualCoordination: "Sobrecarga de Coordenação Manual",
        inconsistentExecution: "Execução Inconsistente",
        lackVisibility: "Falta de Visibilidade em Tempo Real",
        escalationDelays: "Atrasos de Escalamento",
        documentationGaps: "Lacunas de Documentação",
        communicationBreakdown: "Falha de Comunicação",
      },
      downtimeLevels: {
        critical: {
          title: "Crítico",
          desc: "Cada minuto de inatividade custa significativamente",
        },
        high: {
          title: "Alto",
          desc: "Tempo de inatividade impacta severamente as operações",
        },
        moderate: {
          title: "Moderado",
          desc: "Alguma tolerância mas afeta o desempenho",
        },
        low: {
          title: "Baixo",
          desc: "Pode gerir interrupções de curto prazo",
        },
      },
      complianceOptions: {
        iso: "ISO 9001/27001",
        hse: "HSE/SST",
        osha: "OSHA",
        vca: "VCA/SCC",
        gdpr: "RGPD/Proteção de Dados",
        sectorSpecific: "Regulamentos Sectoriais",
      },
      complexityLevels: {
        simple: "Simples",
        moderate: "Moderada",
        complex: "Complexa",
        veryComplex: "Muito Complexa",
      },
      step6: {
        title: "Análise Operacional",
        description: "Com base nas informações fornecidas, aqui está uma análise operacional de nível executivo do seu estado atual.",
        noData: "Dados insuficientes para gerar análise. Por favor complete os passos anteriores.",
        observationsTitle: "Observações Operacionais",
        risksTitle: "Áreas de Risco e Exposição",
        readinessTitle: "Avaliação de Prontidão de Execução",
        advisoryTitle: "Direção Consultiva (Alto Nível)",
        disclaimer: "Esta análise é indicativa e baseada em informação auto-reportada. Não substitui uma avaliação profissional. Todos os resultados estão sujeitos a revisão formal.",
      },
      step7: {
        title: "Dados de Contacto",
        description: "Estes dados são necessários para anexar a análise operacional a um pedido formal de revisão.",
        personalTitle: "Dados Pessoais",
        companyTitle: "Dados da Empresa",
        fullName: "Nome Completo",
        fullNamePlaceholder: "Introduza o seu nome completo",
        email: "Endereço de Email",
        emailPlaceholder: "Introduza email empresarial",
        phone: "Número de Telefone",
        phonePlaceholder: "Introduza número de telefone",
        companyName: "Nome da Empresa",
        companyNamePlaceholder: "Introduza nome da empresa",
        role: "Cargo / Função",
        rolePlaceholder: "ex: Gestor de Operações",
        country: "País",
        countryPlaceholder: "Introduza país",
        city: "Cidade",
        cityPlaceholder: "Introduza cidade",
        requiredNote: "Todos os campos marcados com * são obrigatórios. As suas informações serão utilizadas apenas para processar este pedido de revisão.",
      },
      analysis: {
        observations: {
          industry: {
            manufacturing: "Operar num ambiente de manufatura tipicamente envolve planeamento de produção complexo, ciclos de manutenção de equipamentos e requisitos de controlo de qualidade.",
            logistics: "Operações logísticas requerem coordenação apertada entre múltiplos stakeholders, entregas time-critical e visibilidade em tempo real através das cadeias de fornecimento.",
            facility: "Operações de gestão de instalações abrangem múltiplas categorias de serviço e requerem coordenação entre equipas internas e empreiteiros externos.",
            infrastructure: "Operações de infraestrutura frequentemente envolvem ativos geograficamente distribuídos com requisitos de manutenção variáveis e supervisão regulatória.",
            energy: "Operações do sector energético requerem aderência rigorosa a protocolos de segurança, conformidade regulatória e monitorização contínua de sistemas críticos.",
            other: "As operações abrangem múltiplos domínios com requisitos variáveis de coordenação, conformidade e controlo de execução.",
          },
          scale: {
            single: "Operações de local único permitem controlo centralizado mas podem ainda enfrentar desafios de coordenação entre turnos e departamentos.",
            multi: "Operações multi-local introduzem complexidade na padronização, comunicação e na garantia de execução consistente entre localizações.",
            distributed: "Operações distribuídas ou regionais requerem mecanismos de coordenação robustos para manter visibilidade e controlo através de equipas dispersas.",
          },
          structure: {
            adhoc: "Estrutura operacional ad-hoc sugere alta dependência de julgamento individual e potencial inconsistência na execução.",
            semiStructured: "Processos semi-estruturados indicam padronização parcial com espaço para variação, o que pode levar a resultados inconsistentes.",
            fullyStructured: "Operações totalmente estruturadas fornecem uma base para execução previsível, embora a eficácia dependa da aderência e aplicação.",
          },
          tools: {
            fragmented: "Panorama de ferramentas fragmentado tipicamente leva a silos de informação, reconciliação manual de dados e sobrecarga de coordenação.",
            manual: "Forte dependência de processos manuais aumenta o risco de erro e limita a escalabilidade das operações.",
          },
        },
        risks: {
          predictability: "Risco de previsibilidade: Execução inconsistente e padrões de propriedade pouco claros podem levar a resultados operacionais imprevisíveis.",
          coordination: "Risco de coordenação: Coordenação manual e lacunas de comunicação podem resultar em atrasos, erros e falhas de escalamento.",
          downtime: "Exposição a tempo de inatividade: Alta sensibilidade a tempo de inatividade combinada com padrões operacionais atuais sugere risco elevado de interrupções custosas.",
          compliance: "Exposição a conformidade: Lacuna entre requisitos de conformidade e nível de estrutura atual pode criar riscos de auditoria e regulatórios.",
          escalation: "Risco de escalamento: Atrasos na resolução de problemas e visibilidade limitada podem permitir que problemas menores escalem para problemas operacionais significativos.",
        },
        readiness: {
          high: "A organização parece razoavelmente preparada para mudança operacional, com estrutura e ferramentas existentes fornecendo uma base para iniciativas de melhoria.",
          moderate: "A organização mostra indicadores de prontidão mistos. Alguns elementos fundamentais existem, mas lacunas em estrutura ou ferramentas podem requerer atenção antes de escalar mudanças.",
          low: "Os padrões operacionais atuais sugerem prontidão limitada para escala ou mudança significativa sem primeiro abordar lacunas fundamentais de coordenação e estrutura.",
        },
        advisory: {
          ownership: "Considere estabelecer estruturas de propriedade mais claras e frameworks de responsabilidade para reduzir sobrecarga de coordenação e melhorar previsibilidade de resposta.",
          standardization: "Foque na padronização de processos operacionais centrais para reduzir variação e estabelecer padrões de execução consistentes entre equipas.",
          escalation: "Implemente caminhos de escalamento estruturados com janelas de resposta definidas e propriedade clara em cada nível.",
          compliance: "Alinhe estrutura operacional com requisitos de conformidade para reduzir exposição a auditoria e garantir aderência consistente a normas regulatórias.",
          scale: "Para operações multi-local ou distribuídas, priorize mecanismos que garantam consistência e visibilidade entre localizações.",
        },
      },
    },
    submission: {
      title: "Submissão Recebida",
      body1: "A sua revisão operacional foi submetida com sucesso.",
      body2: "A nossa equipa analisará cuidadosamente o seu pedido e fará o seguimento adequado.",
      body3: "Para acelerar os próximos passos, pode agendar uma consulta estratégica abaixo.",
      scheduleBtn: "Agendar Consulta Estratégica",
      closeBtn: "Fechar",
      referenceLabel: "ID de Referência",
      emailSent: "Confirmação enviada para",
      pdfAttached: "Resumo PDF anexado para os seus registos",
    },
    governanceTrends: {
      title: "Operar numa Realidade Regulada — ISO 27001, NIS2 & SOC 2",
      subtitle: "À medida que os ambientes operacionais se tornam mais regulados, interconectados e escrutinados, a liderança é cada vez mais definida pelo controlo, documentação e consistência — não pela velocidade ou improvisação.",
      column1: {
        title: "O Que os Líderes Operacionais Priorizam",
        items: [
          "Instruções de trabalho claras e aplicáveis",
          "Execução previsível entre turnos e equipas",
          "Procedimentos de segurança que deixam pouco espaço para interpretação",
          "Menos decisões ad-hoc durante incidentes",
          "Documentação que fala por si"
        ]
      },
      column2: {
        title: "Normas, Conformidade e Responsabilidade",
        items: [
          "Sistemas de gestão formais baseados em normas reconhecidas",
          "Procedimentos e ações corretivas rastreáveis",
          "Registos de formação e competência verificáveis",
          "Proteção de dados e controlo de acesso por defeito",
          "Decisões que podem ser reconstruídas posteriormente"
        ]
      },
      column3: {
        title: "Segurança, Higiene e Realidade Operacional",
        items: [
          "As regras de segurança devem funcionar sob pressão, não apenas no papel",
          "Os procedimentos de higiene devem ser consistentes entre localizações",
          "Os desvios devem ser registados, não normalizados",
          "Soluções informais aumentam a exposição",
          "A responsabilidade pessoal está a aproximar-se do nível operacional"
        ]
      },
      trend1: "Olhando para o futuro, a tendência é clara: espera-se que as operações sejam explicáveis, auditáveis e repetíveis — mesmo sob interrupção.",
      trend2: "As organizações que terão sucesso serão aquelas que incorporam normas, segurança, higiene e disciplina de dados diretamente na execução diária. Não como sobrecarga, mas como estrutura operacional."
    },
    chatbot: {
      greeting: "Olá! Sou o Brooks. Como posso ajudá-lo hoje?\n\nPosso auxiliar com informações do produto, suporte operacional, agendar uma demo, experimentar o nosso simulador, Conformidade & Governança ou responder a perguntas sobre preços.",
      subtitle: "Assistente Ovelon Prime",
      inputPlaceholder: "Escreva uma mensagem...",
      buttons: {
        productInfo: "Info Produto",
        operationalSupport: "Suporte",
        scheduleDemo: "Agendar Demo",
        intake: "Diagnóstico",
        simulation: "Simulador",
        pricing: "Preços",
        contact: "Contacto",
        warehouseSolutions: "Soluções de Armazém",
        workflowSolutions: "Soluções de Fluxo",
        backToMain: "← Voltar",
        connectSupport: "Contactar Suporte",
        bookDemo: "Reservar connosco",
        startIntake: "Iniciar →",
        viewPricing: "Ver Preços",
        goToForm: "Ir para Formulário",
        openSimulator: "Abrir Simulador →",
      },
      responses: {
        productInfo: "Oferecemos soluções de armazém e fluxo de trabalho adaptadas às suas operações.\n\nLinks rápidos:\n• Soluções de Armazém\n• Soluções de Fluxo\n\nHá algo específico que gostaria de saber?",
        operationalSupport: "Pode indicar-me o tipo de problema? Posso orientá-lo ou conectá-lo com um especialista.",
        scheduleDemo: "Posso agendar uma demo para si. Clique no botão abaixo para escolher um horário que lhe convenha!",
        intake: "O nosso diagnóstico de 7 passos ajuda a identificar os seus desafios operacionais:\n\n1️⃣ Contexto Operacional\n2️⃣ Desafios Atuais\n3️⃣ Análise de Processos\n4️⃣ Stack Tecnológico\n5️⃣ Objetivos e Prioridades\n6️⃣ Análise Executiva\n7️⃣ Dados de Contacto\n\nReceberá um resumo PDF personalizado. Pronto para começar?",
        pricing: "A nossa plataforma começa em €4.999/mês e inclui a suite completa de soluções de armazém e fluxo de trabalho.\n\nQuer ver a secção de preços detalhada ou agendar uma chamada para discutir as suas necessidades?",
        contact: "Pode contactar-nos em info@ovelon-prime.com ou usar o nosso formulário de contacto. Quer que o leve até lá?",
        simulation: "O nosso Simulador de Projeto ajuda-o a visualizar melhorias potenciais nas suas operações. Experimente!",
        simulatorOpened: "Abri o Simulador de Projeto para si. Siga os passos para ver as suas melhorias potenciais!",
        connectSupport: "Estou a conectá-lo com a nossa equipa de suporte. Também pode contactar-nos diretamente em info@ovelon-prime.com",
        bookingOpened: "Abri o nosso calendário de reservas para si. Escolha o horário que mais lhe convém!",
        backToMain: "Sem problema! Em que mais posso ajudá-lo?",
        compliance: "Ajudamos organizações a navegar paisagens regulatórias complexas:\n\n🛡️ Diretiva NIS2 — Preparação para conformidade de segurança de redes e informação para infraestruturas críticas.\n\n📋 ISO 27001 — Implementação do sistema de gestão de segurança da informação e preparação para auditorias.\n\n🔒 SOC 2 — Auditorias SOC 2 (Tipo I e Tipo II) para critérios de serviços de confiança.\n\n📑 Governança operacional — Frameworks de políticas, trilhas de auditoria e dashboards de conformidade.\n\nPergunte-me o que quiser sobre estes frameworks!",
        complianceThinking: "Deixe-me verificar isso para si...",
        complianceError: "Não consegui encontrar uma resposta agora. Consulte os nossos artigos de conformidade ou contacte info@ovelon-prime.com.",
        fallback: "Não tenho essa informação, mas a equipa entrará em contacto consigo em breve. Gostaria de deixar os seus dados de contacto ou experimentar o formulário de contacto?",
      },
      complianceHint: "Pergunte-me sobre SOC 2, ISO 27001 ou NIS2 ↵",
    },
    subscribe: {
      title: "Mantenha-se à frente",
      subtitle: "Receba insights exclusivos sobre otimização de fluxos de trabalho, gestão de operações e tendências do setor diretamente na sua caixa de entrada.",
      promo: "🎁 E-Book de Operações gratuito se subscrever hoje!",
      fullName: "Nome completo",
      fullNamePlaceholder: "Introduza o seu nome completo",
      email: "Endereço de email",
      emailPlaceholder: "nome@email.pt",
      submit: "Subscrever agora",
      submitting: "A subscrever...",
      successTitle: "Bem-vindo a bordo!",
      successMessage: "Obrigado por subscrever. Receberá em breve os nossos últimos insights.",
      privacyText: "Ao subscrever, aceita a nossa",
      privacyLink: "Política de Privacidade",
      termsLink: "Termos e Condições",
      andText: "e",
      missingInfo: "Informação em falta",
      missingInfoDesc: "Por favor preencha o nome e o email.",
      failedTitle: "Subscrição falhou",
    },
    caseStudies: {
      badge: "Resultados comprovados",
      title: "Transformando operações para indústrias de missão crítica.",
      challengeLabel: "O desafio",
      solutionLabel: "A solução Ovelon Prime",
      resultsLabel: "Resultados quantificáveis",
      cases: {
        facility: {
          industry: "Gestão de instalações",
          client: "Operador multi-site",
          challenge: "A gestão de solicitações de serviço em mais de 40 sites distribuídos resultava em tempos de resposta inconsistentes, janelas de manutenção perdidas e comunicação fragmentada entre equipas no local e coordenação central.",
          solution: "Ovelon Prime unificou todas as solicitações num sistema de intake estruturado com encaminhamento automático, classificação por prioridade e rastreamento de estado em tempo real — permitindo supervisão centralizada sem substituir ferramentas existentes.",
          results: [
            { value: "42%", label: "Tempos de resposta mais rápidos" },
            { value: "95%", label: "Taxa de conformidade SLA" },
            { value: "60%", label: "Redução de erros de coordenação" },
          ],
        },
        supplyChain: {
          industry: "Cadeia de abastecimento",
          client: "Rede de distribuição regional",
          challenge: "A coordenação manual entre fornecedores, armazéns e parceiros de transporte criava pontos cegos na cadeia de abastecimento, resultando em envios atrasados, discrepâncias de inventário e custos operacionais crescentes.",
          solution: "Ovelon Prime implementou automação de fluxos de ponta a ponta ligando compras, gestão de inventário e planeamento logístico — proporcionando visibilidade em tempo real e alertas preditivos em toda a cadeia.",
          results: [
            { value: "38%", label: "Redução dos prazos de entrega" },
            { value: "99,2%", label: "Taxa de precisão de encomendas" },
            { value: "28%", label: "Custos operacionais menores" },
          ],
        },
        warehouse: {
          industry: "Armazém e Armazenamento",
          client: "Complexo de armazenamento industrial",
          challenge: "O rastreamento de inventário dependia de folhas de cálculo fragmentadas e contagens manuais, causando discrepâncias frequentes de stock, espaço mal alocado e atrasos no cumprimento de encomendas.",
          solution: "Ovelon Prime padronizou os fluxos de inventário com registo automático de entradas, regras de alocação por zona e visibilidade de stock em tempo real — reduzindo intervenções manuais enquanto melhorava a precisão e a produtividade.",
          results: [
            { value: "55%", label: "Redução de discrepâncias de stock" },
            { value: "40%", label: "Cumprimento de encomendas mais rápido" },
            { value: "35%", label: "Melhor utilização do espaço" },
          ],
        },
        maintenance: {
          industry: "Reparação e Manutenção",
          client: "Serviços de equipamentos industriais",
          challenge: "Os fluxos de manutenção reativa dominavam as operações. As ordens de trabalho eram registadas de forma inconsistente, o rastreamento de peças era manual e o agendamento de técnicos baseava-se na disponibilidade em vez da prioridade.",
          solution: "Ovelon Prime introduziu gestão estruturada de ordens de trabalho com encaminhamento automático por prioridade, integração de inventário de peças e despacho otimizado de técnicos — passando de manutenção reativa para preditiva.",
          results: [
            { value: "47%", label: "Redução do tempo de inatividade" },
            { value: "65%", label: "Conclusão mais rápida de ordens" },
            { value: "30%", label: "Menos incidentes recorrentes" },
          ],
        },
      },
    },
    complianceBlog: {
      badge: "Conformidade e Governança",
      title: "NIS2, ISO 27001 e SOC 2: O que os líderes operacionais devem saber agora.",
      subtitle: "À medida que a pressão regulatória se intensifica nas infraestruturas críticas, compreender a NIS2, a ISO 27001 e o SOC 2 deixou de ser opcional — é sobrevivência operacional. Veja como a Ovelon Prime alinha as suas operações com estes quadros.",
      articles: {
        nis2: {
          tag: "Diretiva UE 2022/2555",
          title: "Diretiva NIS2: A nova realidade operacional para infraestruturas críticas",
          excerpt: "A Diretiva NIS2 é a regulação de cibersegurança mais significativa da UE, abrangendo 18 setores críticos e impondo penalizações severas por incumprimento.",
          facts: [
            { label: "Setores abrangidos", value: "18" },
            { label: "Penalização máxima", value: "€10M ou 2% receita" },
            { label: "Responsabilidade da gestão", value: "Pessoal" },
          ],
          sections: [
            { heading: "O que é a NIS2 e porque importa?", text: "A NIS2 substitui a Diretiva NIS original de 2016, expandindo dramaticamente o âmbito dos requisitos de cibersegurança na UE. Abrange entidades essenciais (energia, transporte, saúde, água, infraestrutura digital) e entidades importantes (manufatura, produção alimentar, gestão de resíduos). As organizações devem implementar medidas de gestão de risco, reporte de incidentes, segurança da cadeia de abastecimento e planeamento de continuidade de negócio — ou enfrentar penalizações até €10 milhões ou 2% do volume de negócios global." },
            { heading: "A responsabilidade operacional é agora pessoal", text: "Sob a NIS2, a gestão de topo pode ser pessoalmente responsabilizada pelo incumprimento. CISOs, COOs e membros do conselho devem demonstrar governança ativa sobre cibersegurança e resiliência operacional — incluindo aprovação de medidas de gestão de risco e formação obrigatória em cibersegurança." },
            { heading: "Requisitos rigorosos de reporte de incidentes", text: "A NIS2 exige um quadro escalonado de reporte: alerta precoce em 24 horas, notificação detalhada em 72 horas e relatório final num mês. As organizações devem ter processos estruturados de deteção, classificação e escalamento de incidentes." },
            { heading: "Segurança da cadeia de abastecimento torna-se obrigatória", text: "A diretiva exige que as organizações avaliem e giram riscos de cibersegurança em toda a cadeia de abastecimento — incluindo avaliações de fornecedores, requisitos contratuais e monitorização contínua de riscos de terceiros." },
          ],
          ovelonTitle: "Como a Ovelon Prime apoia a conformidade NIS2",
          ovelonPoints: [
            "Admissão e classificação estruturada de incidentes com caminhos de escalamento automatizados que cumprem prazos de 24/72 horas",
            "Trilhos de auditoria completos para cada evento operacional, apoiando demonstrações de conformidade baseadas em evidências",
            "Procedimentos de execução padronizados que eliminam operações baseadas em interpretação e reduzem erro humano",
            "Visibilidade operacional em tempo real com painéis que apoiam obrigações de governança da gestão",
            "Fluxos de coordenação da cadeia de abastecimento que impõem requisitos de segurança nas interações com fornecedores",
            "Sistemas de notificação automatizados que garantem comunicação atempada com stakeholders durante incidentes",
          ],
        },
        iso27001: {
          tag: "Norma Internacional",
          title: "ISO 27001: Construir um SGSI que realmente funcione",
          excerpt: "A ISO 27001 é o padrão de ouro global para gestão de segurança da informação. Mas a certificação é apenas o início — manter um SGSI eficaz exige disciplina operacional e execução baseada em evidências.",
          facts: [
            { label: "Controlos no Anexo A", value: "93" },
            { label: "Organizações certificadas", value: "70.000+" },
            { label: "Ciclo de recertificação", value: "3 anos" },
          ],
          sections: [
            { heading: "Porque a ISO 27001 é essencial", text: "A ISO 27001 fornece uma abordagem sistemática para gerir informação sensível. A revisão de 2022 reorganizou controlos em quatro domínios (Organizacional, Pessoas, Físico e Tecnológico) com 93 controlos no Anexo A." },
            { heading: "O fosso entre certificação e realidade operacional", text: "Muitas organizações obtêm certificação mas lutam para manter a disciplina operacional entre auditorias. Políticas existem no papel mas não são aplicadas nas operações diárias — criando vulnerabilidades de segurança e exposição a auditorias." },
            { heading: "Controlos do Anexo A que exigem automação", text: "Vários controlos do Anexo A relacionam-se diretamente com processos operacionais: A.5.24-A.5.28 (gestão de incidentes e recolha de evidências) e A.8.16 (atividades de monitorização). Estes controlos exigem processos estruturados e repetíveis com evidência documentada." },
            { heading: "A melhoria contínua é obrigatória", text: "A Cláusula 10 exige melhoria contínua do SGSI — identificação sistemática de não-conformidades, ações corretivas e avaliação da eficácia. Sem dados operacionais estruturados, a melhoria contínua torna-se palpite em vez de gestão baseada em evidências." },
          ],
          ovelonTitle: "Como a Ovelon Prime apoia a conformidade ISO 27001",
          ovelonPoints: [
            "Classificação e resposta a incidentes automatizadas, alinhadas com os controlos A.5.24-A.5.28 do Anexo A",
            "Trilhos de auditoria completos para cada evento operacional, apoiando requisitos de recolha de evidências (A.5.28)",
            "Procedimentos de execução padronizados que impõem implementação consistente de controlos entre equipas",
            "Painéis de monitorização em tempo real para verificação contínua de conformidade (A.8.16)",
            "Fluxos de gestão de mudanças estruturados com cadeias de aprovação documentadas",
            "Métricas operacionais e análise de tendências para melhoria contínua baseada em evidências sob a Cláusula 10",
          ],
        },
        soc2: {
          tag: "Critérios de Serviços de Confiança AICPA",
          title: "SOC 2: Demonstrar confiança operacional através de controlos de segurança verificados",
          excerpt: "SOC 2 (Service Organization Control 2) é o principal framework de auditoria para verificar que as práticas de segurança da informação de uma organização cumprem padrões rigorosos em cinco Critérios de Serviços de Confiança. Um relatório SOC 2 é cada vez mais exigido por clientes enterprise e sectores regulados.",
          type1Label: "Tipo I",
          type2Label: "Tipo II",
          type1: {
            facts: [
              { label: "Âmbito de auditoria", value: "Ponto no tempo" },
              { label: "Duração", value: "Data única" },
              { label: "Critérios de confiança", value: "Até 5" },
            ],
            sections: [
              {
                heading: "O que é o SOC 2 Tipo I?",
                text: "Um relatório SOC 2 Tipo I avalia se os controlos de segurança de uma organização estão adequadamente concebidos num momento específico. Responde à pergunta: 'Os controlos certos estão implementados hoje?' O Tipo I é tipicamente o ponto de partida para organizações que buscam certificação SOC 2.",
              },
              {
                heading: "Os cinco Critérios de Serviços de Confiança",
                text: "O SOC 2 avalia controlos em cinco critérios: Segurança (obrigatório), Disponibilidade, Integridade do processamento, Confidencialidade e Privacidade. O Tipo I verifica que políticas, procedimentos e controlos técnicos estão adequadamente concebidos — não que funcionem de forma consistente ao longo do tempo.",
              },
              {
                heading: "Quando o Tipo I é apropriado",
                text: "O Tipo I é comummente utilizado quando uma organização precisa demonstrar rapidamente um nível base de garantia de segurança a clientes ou parceiros. É também o precursor natural do Tipo II.",
              },
            ],
          },
          type2: {
            facts: [
              { label: "Período de auditoria", value: "6–12 meses" },
              { label: "Critérios de confiança", value: "Até 5" },
              { label: "Padrão de mercado", value: "Enterprise" },
            ],
            sections: [
              {
                heading: "O que é o SOC 2 Tipo II?",
                text: "Um relatório SOC 2 Tipo II avalia se os controlos de segurança de uma organização não apenas existem, mas operam eficazmente durante um período alargado — tipicamente 6 a 12 meses. É o padrão de ouro para a confiança enterprise e é cada vez mais exigido por equipas de procurement em sectores como serviços financeiros e infraestruturas críticas.",
              },
              {
                heading: "A disciplina operacional é o fundamento",
                text: "Ao contrário do Tipo I, o Tipo II exige execução consistente, documentada e auditável de controlos durante todo o período de revisão. Os registos de incidentes devem estar completos, os controlos de acesso aplicados de forma consistente e a gestão de mudanças documentada.",
              },
              {
                heading: "Constatações frequentes que comprometem auditorias Tipo II",
                text: "As falhas mais comuns do Tipo II envolvem registos de incidentes incompletos, revisões de acesso de utilizadores inconsistentes, documentação de aprovação em falta e lacunas na monitorização de segurança.",
              },
              {
                heading: "SOC 2 Tipo II e conformidade contínua",
                text: "Ao contrário de uma certificação pontual, a conformidade Tipo II é uma disciplina operacional contínua. As organizações devem manter documentação pronta para auditoria durante todo o ano — não apenas nas semanas antes da janela de auditoria.",
              },
            ],
          },
          ovelonTitle: "Como a Ovelon Prime apoia a prontidão SOC 2",
          ovelonPoints: [
            "Fluxos de trabalho estruturados de intake e resposta a incidentes que produzem evidências de auditoria completas e com timestamp",
            "Fluxos de controlo de acesso automatizados para aprovisionamento e desaprovisionamento consistentes de utilizadores",
            "Documentação de gestão de mudanças com cadeias de aprovação e timestamps",
            "Monitorização operacional em tempo real alinhada com os critérios SOC 2 de Segurança e Disponibilidade",
            "Procedimentos de execução padronizados que eliminam workarounds não documentados",
            "Painéis de reporting de conformidade para visibilidade contínua sobre a eficácia dos controlos",
          ],
        },
      },
    },
  },
};
